import { supabase } from './src/lib/supabase.js';
import * as authLib from './src/lib/auth.js';
import { fetchCatalog, clearCatalogCache } from './src/lib/products.js';
import { placeOrder as placeOrderInDb, fetchMyOrders } from './src/lib/orders.js';
import * as adminLib from './src/lib/admin.js';
import { buildInsights } from './src/lib/adminInsights.js';
import { dashboardPage, wireTrendCharts } from './src/lib/adminDashboardView.js';
import { submitMessage } from './src/lib/messages.js';
import { validateCoupon } from './src/lib/coupons.js';
import { lookupPincode, rememberPincode, recallPincode, detectLocationByIP, detectLocationByGPS, rememberIpLocation, recallIpLocation } from './src/lib/pincode.js';
import { trackProductView, getRecentProductIds } from './src/lib/recentlyViewed.js';
import { fetchHeroSlides, fetchFeaturedProductIds, fetchBlogPosts } from './src/lib/content.js';
import { renderBlogBody } from './src/lib/blogBody.js';
import { socialImage } from './src/lib/ogImage.js';
import { fetchSiteSettings, updateSiteSettings, SOCIAL_KEYS } from './src/lib/siteSettings.js';
import { fetchProductReviews, submitReview, fetchMyReviewedOrderItemIds } from './src/lib/reviews.js';
import { payOrderWithRazorpay, razorpayConfigured, loadRazorpayScript } from './src/lib/razorpay.js';
import * as wishlistLib from './src/lib/wishlist.js';
import { inject } from '@vercel/analytics';

inject();

(async () => {
  // Expose for DevTools console only in dev builds — prod doesn't leak the
  // client globally. Wrapped in import.meta.env.DEV so Vite tree-shakes it
  // out of the production bundle.
  if (import.meta.env.DEV) window.supabase = supabase;

  // Dev-only diagnostic logger. Replaces scattered console.warn calls so
  // prod builds stay quiet. The supabase env-missing warning lives in
  // src/lib/supabase.js and stays unguarded — operators need that signal
  // even in prod.
  const dwarn = import.meta.env.DEV ? console.warn.bind(console) : () => {};

  // Cached session — updated via onAuthChange; read synchronously by helpers.
  let currentSession = null;
  let currentProfile = null;

  /* ── DATA ── */
  // CATEGORIES and PRODUCTS are mutable so they can be replaced with DB-fetched data on boot.
  // The hardcoded values below act as initial values + offline fallback.
  let CATEGORIES = [
    { id:1,  name:'Talbina Porridge/ Instant Mix',                      image:null, count:0 },
    { id:2,  name:'Pure Vinegars',                                      image:null, count:0 },
    { id:3,  name:'Herbal Syrup/ Herbal Heath Drinks',                  image:null, count:0 },
    { id:4,  name:'Ayurvedic Medicines',                                image:null, count:0 },
    { id:5,  name:'Herbal Intimate Wash',                               image:null, count:0 },
    { id:6,  name:'Herbal Juices',                                      image:null, count:0 },
    { id:7,  name:'SHIFA HONEY',                                        image:null, count:0 },
    { id:8,  name:'Extra Virgin Cold Pressed Oils',                     image:null, count:0 },
    { id:9,  name:'Ayurvedic Pain Relief Oil',                          image:null, count:0 },
    { id:10, name:'Fruit Preserves/ Marmalades/Murabbas',               image:null, count:0 },
    { id:11, name:'Food & Pickles',                                     image:null, count:0 },
    { id:12, name:'Ayurvedic Medicines/ Health Supplements',            image:null, count:0 },
    { id:13, name:'Spice & Culinary Herb',                              image:null, count:0 },
    { id:14, name:'Ayurvedic Personal Care',                            image:null, count:0 },
    { id:15, name:'Health Supplements & Natural Plant Rasin/Raw Herbs', image:null, count:0 },
    { id:16, name:'Personal Care',                                      image:null, count:0 },
    { id:17, name:'Herbal Tea/Infusions/Dried Herbs',                   image:null, count:0 },
    { id:18, name:'Weight Management Supplements/ Slimming Juice',      image:null, count:0 },
    { id:19, name:'Energy & Endurance/ Vitality Boosters',              image:null, count:0 },
    { id:20, name:'Health Supplements & Herbs',                         image:null, count:0 },
    { id:21, name:'Ayurvedic Balm',                                     image:null, count:0 },
    { id:22, name:'PREMIUM PRODUCTS',                                   image:null, count:0 },
    { id:23, name:'Ayurvedic Medicines/Plant-Based Supplements',        image:null, count:0 },
    { id:24, name:'Fresh Fruit & Dates',                                image:null, count:0 },
    { id:25, name:'Herbal Supplement/Traditional Medicinal',            image:null, count:0 },
  ];

  let PRODUCTS = [
    // Vinegars & Others (1)
    { id:101, name:'Apple Vinegar', subtitle:'', price:150, original:null, weight:'250ml', categoryId:2, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'vinegar' },
    { id:102, name:'Apple Vinegar', subtitle:'', price:280, original:null, weight:'500ml', categoryId:2, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'vinegar' },
    { id:103, name:'Jamun Vinegar', subtitle:'', price:130, original:null, weight:'250ml', categoryId:2, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'vinegar' },
    { id:104, name:'Jamun Vinegar', subtitle:'', price:240, original:null, weight:'500ml', categoryId:2, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'vinegar' },
    { id:105, name:'Grape Vinegar', subtitle:'', price:130, original:null, weight:'250ml', categoryId:2, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'vinegar' },
    { id:106, name:'Sugarcane Vinegar', subtitle:'', price:130, original:null, weight:'250ml', categoryId:2, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'vinegar' },
    { id:107, name:'Garlic Vinegar', subtitle:'', price:160, original:null, weight:'250ml', categoryId:2, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'vinegar' },
    { id:108, name:'Garlic Vinegar', subtitle:'', price:300, original:null, weight:'500ml', categoryId:2, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'vinegar' },
    { id:109, name:'Kalonji Vinegar', subtitle:'', price:160, original:null, weight:'250ml', categoryId:2, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'vinegar' },
    { id:110, name:'Olive Vinegar', subtitle:'', price:200, original:null, weight:'250ml', categoryId:2, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'vinegar' },
    { id:111, name:'Olive Vinegar', subtitle:'', price:380, original:null, weight:'500ml', categoryId:2, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'vinegar' },
    { id:112, name:'Black Grape Vinegar', subtitle:'', price:350, original:null, weight:'500ml', categoryId:2, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'vinegar' },
    { id:113, name:'Chana Vinegar', subtitle:'', price:320, original:null, weight:'500ml', categoryId:2, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'vinegar' },
    { id:114, name:'Chana Vinegar', subtitle:'', price:170, original:null, weight:'250ml', categoryId:2, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'vinegar' },
    { id:115, name:'Dates Vinegar', subtitle:'', price:320, original:null, weight:'500ml', categoryId:2, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'vinegar' },
    { id:116, name:'Nuskha-e-Arabia', subtitle:'', price:320, original:null, weight:'250ml', categoryId:2, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'vinegar' },
    { id:117, name:'Nuskha-e-Arabia', subtitle:'', price:600, original:null, weight:'500ml', categoryId:2, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'vinegar' },
    { id:118, name:'Nuskha-e-Arabia S/F', subtitle:'', price:600, original:null, weight:'500ml', categoryId:2, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'vinegar' },
    { id:119, name:'Amla Juice', subtitle:'', price:170, original:null, weight:'500ml', categoryId:6, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'juice' },
    { id:120, name:'Alovera Juice', subtitle:'', price:210, original:null, weight:'500ml', categoryId:6, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'juice' },
    { id:121, name:'Karela Jamun Juice', subtitle:'', price:240, original:null, weight:'500ml', categoryId:6, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'juice' },
    { id:122, name:'Papaya Juice', subtitle:'', price:240, original:null, weight:'500ml', categoryId:6, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'juice' },
    { id:123, name:'Triphala Juice', subtitle:'', price:240, original:null, weight:'500ml', categoryId:6, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'juice' },
    { id:124, name:'Noni Juice', subtitle:'', price:499, original:null, weight:'500ml', categoryId:6, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'juice' },
    { id:125, name:'Musli Safed Juice', subtitle:'', price:310, original:null, weight:'500ml', categoryId:6, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'juice' },
    { id:126, name:'Giloy Juice', subtitle:'', price:190, original:null, weight:'500ml', categoryId:6, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'juice' },
    { id:127, name:'Giloy Tulsi Juice', subtitle:'', price:199, original:null, weight:'500ml', categoryId:6, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'juice' },
    { id:128, name:'Thyro Cure', subtitle:'', price:799, original:null, weight:'500ml', categoryId:6, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'juice' },
    { id:129, name:'Tatheer', subtitle:'', price:160, original:null, weight:'500ml', categoryId:3, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'fiber' },
    { id:130, name:'Talbina Dryfruite', subtitle:'', price:250, original:null, weight:'250gm', categoryId:1, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'talbina' },
    { id:131, name:'Talbina Dryfruite', subtitle:'', price:480, original:null, weight:'500gm', categoryId:1, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'talbina' },
    { id:132, name:'Talbina Vanilla', subtitle:'', price:180, original:null, weight:'250gm', categoryId:1, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'talbina' },
    { id:133, name:'Talbina Vanilla', subtitle:'', price:350, original:null, weight:'500gm', categoryId:1, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'talbina' },
    { id:134, name:'Talbina Mango', subtitle:'', price:200, original:null, weight:'250gm', categoryId:1, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'talbina' },
    { id:135, name:'Talbina Mango', subtitle:'', price:380, original:null, weight:'500gm', categoryId:1, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'talbina' },
    { id:136, name:'Talbina Chocolate', subtitle:'', price:200, original:null, weight:'250gm', categoryId:1, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'talbina' },
    { id:137, name:'Talbina Chocolate', subtitle:'', price:380, original:null, weight:'500gm', categoryId:1, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'talbina' },
    { id:138, name:'Talbina For Baby', subtitle:'', price:399, original:null, weight:'400gm', categoryId:1, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'talbina' },
    { id:139, name:'Talbina For Kids', subtitle:'', price:399, original:null, weight:'400gm', categoryId:1, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'talbina' },
    { id:140, name:'Talbina For Women', subtitle:'', price:499, original:null, weight:'400gm', categoryId:1, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'talbina' },
    { id:141, name:'Barley Sattu', subtitle:'', price:130, original:null, weight:'250gm', categoryId:1, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'fiber' },
    { id:142, name:'Barley Sattu', subtitle:'', price:250, original:null, weight:'500gm', categoryId:1, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'fiber' },
    { id:143, name:'Herbo Slim', subtitle:'', price:600, original:null, weight:'500ml', categoryId:18, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'fiber' },
    { id:144, name:'Premium Zafran', subtitle:'', price:370, original:null, weight:'1gm', categoryId:13, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'saffron' },
    { id:145, name:'Premium Zafran', subtitle:'', price:720, original:null, weight:'2gm', categoryId:13, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'saffron' },
    { id:146, name:'Natural Shilajit', subtitle:'', price:300, original:null, weight:'10gm', categoryId:19, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'supplement' },
    { id:147, name:'Natural Shilajit', subtitle:'', price:590, original:null, weight:'20gm', categoryId:19, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'supplement' },
    { id:148, name:'Pleasure (4 doses)', subtitle:'', price:600, original:null, weight:'2gm', categoryId:19, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'supplement' },
    { id:149, name:'Dates Pickle', subtitle:'', price:160, original:null, weight:'200gm', categoryId:11, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'spice' },
    { id:150, name:'Foot Crack Cream', subtitle:'', price:100, original:null, weight:'25gm', categoryId:16, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'skincare' },
    { id:151, name:'Turab Shampoo', subtitle:'', price:99, original:null, weight:'50g', categoryId:16, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'skincare' },
    { id:152, name:'Turab Shampoo', subtitle:'', price:199, original:null, weight:'100g', categoryId:16, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'skincare' },
    { id:153, name:'Turab Shampoo', subtitle:'', price:350, original:null, weight:'200g', categoryId:16, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'skincare' },
    { id:154, name:'Turab Shampoo', subtitle:'', price:799, original:null, weight:'500g', categoryId:16, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'skincare' },
    { id:155, name:'Dryfruit Honey', subtitle:'', price:449, original:null, weight:'300gm', categoryId:7, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'honey' },
    { id:156, name:'Dryfruit Honey', subtitle:'', price:799, original:null, weight:'600gm', categoryId:7, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'honey' },
    { id:157, name:'Gond Siya', subtitle:'', price:890, original:null, weight:'50g', categoryId:15, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'seed' },
    { id:158, name:'Gond Siya', subtitle:'', price:1600, original:null, weight:'100g', categoryId:15, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'seed' },
    { id:159, name:'Gond Chilgoza', subtitle:'', price:999, original:null, weight:'50g', categoryId:15, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'seed' },
    { id:160, name:'Gond Moringa', subtitle:'', price:799, original:null, weight:'50g', categoryId:15, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'seed' },
    { id:161, name:'Namak Zaitoon', subtitle:'', price:450, original:null, weight:'50g', categoryId:13, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'spice' },
    { id:162, name:'Namak Zaitoon', subtitle:'', price:880, original:null, weight:'100g', categoryId:13, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'spice' },
    { id:163, name:'Rose Water (Spray)', subtitle:'', price:120, original:null, weight:'100ml', categoryId:14, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'skincare' },
    { id:164, name:'Rose Water (Spray)', subtitle:'', price:199, original:null, weight:'200ml', categoryId:14, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'skincare' },
    { id:165, name:'Sukoon Balm', subtitle:'', price:125, original:null, weight:'30gm', categoryId:21, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'skincare' },
    { id:166, name:'Saffron Velvet Soap', subtitle:'', price:199, original:null, weight:'95g', categoryId:16, category:'Vinegars & Others', rating:null, reviews:0, badge:null, img:'skincare' },

    // Oils (2)
    { id:201, name:'Olive Oil', subtitle:'', price:90, original:null, weight:'50ml', categoryId:8, category:'Oils', rating:null, reviews:0, badge:null, img:'oil' },
    { id:202, name:'Olive Oil', subtitle:'', price:170, original:null, weight:'100ml', categoryId:8, category:'Oils', rating:null, reviews:0, badge:null, img:'oil' },
    { id:203, name:'Olive Oil', subtitle:'', price:320, original:null, weight:'200ml', categoryId:8, category:'Oils', rating:null, reviews:0, badge:null, img:'oil' },
    { id:204, name:'Olive Oil', subtitle:'', price:720, original:null, weight:'500ml', categoryId:8, category:'Oils', rating:null, reviews:0, badge:null, img:'oil' },
    { id:205, name:'Olive Oil', subtitle:'', price:1300, original:null, weight:'1 Ltr', categoryId:8, category:'Oils', rating:null, reviews:0, badge:null, img:'oil' },
    { id:206, name:'Olive Oil', subtitle:'', price:7000, original:null, weight:'5 Ltr', categoryId:8, category:'Oils', rating:null, reviews:0, badge:null, img:'oil' },
    { id:207, name:'Kalonji Oil', subtitle:'', price:160, original:null, weight:'50ml', categoryId:8, category:'Oils', rating:null, reviews:0, badge:null, img:'oil' },
    { id:208, name:'Kalonji Oil', subtitle:'', price:300, original:null, weight:'100ml', categoryId:8, category:'Oils', rating:null, reviews:0, badge:null, img:'oil' },
    { id:209, name:'Kalonji Oil', subtitle:'', price:560, original:null, weight:'200ml', categoryId:8, category:'Oils', rating:null, reviews:0, badge:null, img:'oil' },
    { id:210, name:'Kalonji Oil', subtitle:'', price:1350, original:null, weight:'500ml', categoryId:8, category:'Oils', rating:null, reviews:0, badge:null, img:'oil' },
    { id:211, name:'Castor Oil', subtitle:'', price:50, original:null, weight:'50ml', categoryId:8, category:'Oils', rating:null, reviews:0, badge:null, img:'oil' },
    { id:212, name:'Castor Oil', subtitle:'', price:95, original:null, weight:'100ml', categoryId:8, category:'Oils', rating:null, reviews:0, badge:null, img:'oil' },
    { id:213, name:'Castor Oil', subtitle:'', price:160, original:null, weight:'200ml', categoryId:8, category:'Oils', rating:null, reviews:0, badge:null, img:'oil' },
    { id:214, name:'Castor Oil', subtitle:'', price:380, original:null, weight:'500ml', categoryId:8, category:'Oils', rating:null, reviews:0, badge:null, img:'oil' },
    { id:215, name:'Neem Oil', subtitle:'', price:140, original:null, weight:'100ml', categoryId:8, category:'Oils', rating:null, reviews:0, badge:null, img:'oil' },
    { id:216, name:'Sesame Oil', subtitle:'', price:190, original:null, weight:'100ml', categoryId:8, category:'Oils', rating:null, reviews:0, badge:null, img:'oil' },
    { id:217, name:'Flaxseed Oil', subtitle:'', price:160, original:null, weight:'100ml', categoryId:8, category:'Oils', rating:null, reviews:0, badge:null, img:'oil' },
    { id:218, name:'Almond Oil', subtitle:'', price:120, original:null, weight:'25ml', categoryId:8, category:'Oils', rating:null, reviews:0, badge:null, img:'oil' },
    { id:219, name:'Walnut Oil', subtitle:'', price:149, original:null, weight:'25ml', categoryId:8, category:'Oils', rating:null, reviews:0, badge:null, img:'oil' },
    { id:220, name:'Apricot Oil', subtitle:'', price:149, original:null, weight:'25ml', categoryId:8, category:'Oils', rating:null, reviews:0, badge:null, img:'oil' },
    { id:221, name:'Pain Oil', subtitle:'', price:175, original:null, weight:'75ml', categoryId:9, category:'Oils', rating:null, reviews:0, badge:null, img:'oil' },
    { id:222, name:'Clove Oil', subtitle:'', price:60, original:null, weight:'5ml', categoryId:8, category:'Oils', rating:null, reviews:0, badge:null, img:'oil' },

    // Shifa Honey (3)
    { id:301, name:'Shifa Honey', subtitle:'', price:40, original:null, weight:'50gm', categoryId:7, category:'Shifa Honey', rating:null, reviews:0, badge:null, img:'honey' },
    { id:302, name:'Shifa Honey', subtitle:'', price:70, original:null, weight:'100gm', categoryId:7, category:'Shifa Honey', rating:null, reviews:0, badge:null, img:'honey' },
    { id:303, name:'Shifa Honey', subtitle:'', price:130, original:null, weight:'200gm', categoryId:7, category:'Shifa Honey', rating:null, reviews:0, badge:null, img:'honey' },
    { id:304, name:'Shifa Honey', subtitle:'', price:280, original:null, weight:'500gm', categoryId:7, category:'Shifa Honey', rating:null, reviews:0, badge:null, img:'honey' },
    { id:305, name:'Shifa Honey', subtitle:'', price:550, original:null, weight:'1kg', categoryId:7, category:'Shifa Honey', rating:null, reviews:0, badge:null, img:'honey' },
    { id:306, name:'Litchi Honey', subtitle:'', price:350, original:null, weight:'500gm', categoryId:7, category:'Shifa Honey', rating:null, reviews:0, badge:null, img:'honey' },
    { id:307, name:'Acacia Kashmiri Honey', subtitle:'', price:599, original:null, weight:'500gm', categoryId:7, category:'Shifa Honey', rating:null, reviews:0, badge:null, img:'honey' },
    { id:308, name:'Ajwain Honey', subtitle:'', price:350, original:null, weight:'500gm', categoryId:7, category:'Shifa Honey', rating:null, reviews:0, badge:null, img:'honey' },
    { id:309, name:'Jamun Honey', subtitle:'', price:300, original:null, weight:'500gm', categoryId:7, category:'Shifa Honey', rating:null, reviews:0, badge:null, img:'honey' },
    { id:310, name:'Sidr Honey', subtitle:'', price:300, original:null, weight:'500gm', categoryId:7, category:'Shifa Honey', rating:null, reviews:0, badge:null, img:'honey' },
    { id:311, name:'Little Bee Honey', subtitle:'', price:100, original:null, weight:'50gm', categoryId:7, category:'Shifa Honey', rating:null, reviews:0, badge:null, img:'honey' },
    { id:312, name:'Little Bee Honey', subtitle:'', price:170, original:null, weight:'100gm', categoryId:7, category:'Shifa Honey', rating:null, reviews:0, badge:null, img:'honey' },
    { id:313, name:'Little Bee Honey', subtitle:'', price:300, original:null, weight:'200gm', categoryId:7, category:'Shifa Honey', rating:null, reviews:0, badge:null, img:'honey' },
    { id:314, name:'Little Bee Honey', subtitle:'', price:650, original:null, weight:'500gm', categoryId:7, category:'Shifa Honey', rating:null, reviews:0, badge:null, img:'honey' },
    { id:315, name:'Little Bee Honey', subtitle:'', price:1200, original:null, weight:'1kg', categoryId:7, category:'Shifa Honey', rating:null, reviews:0, badge:null, img:'honey' },

    // Premium Products (4)
    { id:401, name:'ACV with Mother (Premium)', subtitle:'', price:300, original:null, weight:'', categoryId:22, category:'Premium Products', rating:null, reviews:0, badge:null, img:'vinegar' },
    { id:402, name:'ACV Mixed (Premium)', subtitle:'', price:600, original:null, weight:'', categoryId:22, category:'Premium Products', rating:null, reviews:0, badge:null, img:'vinegar' },
    { id:403, name:'Nuskha (Premium)', subtitle:'', price:650, original:null, weight:'500ml', categoryId:22, category:'Premium Products', rating:null, reviews:0, badge:null, img:'vinegar' },
    { id:404, name:'Grape Vinegar (Premium)', subtitle:'', price:290, original:null, weight:'', categoryId:22, category:'Premium Products', rating:null, reviews:0, badge:null, img:'vinegar' },

    // Capsules (5)
    { id:501, name:'Alfalfa Capsules (60pc)', subtitle:'', price:140, original:null, weight:'', categoryId:12, category:'Capsules', rating:null, reviews:0, badge:null, img:'supplement' },
    { id:502, name:'Ashwagandha Capsules (60pc)', subtitle:'', price:140, original:null, weight:'', categoryId:12, category:'Capsules', rating:null, reviews:0, badge:null, img:'supplement' },
    { id:503, name:'Black Seed Capsules (60pc)', subtitle:'', price:140, original:null, weight:'', categoryId:12, category:'Capsules', rating:null, reviews:0, badge:null, img:'supplement' },
    { id:504, name:'Fenu Greek Capsules (60pc)', subtitle:'', price:140, original:null, weight:'', categoryId:12, category:'Capsules', rating:null, reviews:0, badge:null, img:'supplement' },
    { id:505, name:'Shatavari Capsules (60pc)', subtitle:'', price:170, original:null, weight:'', categoryId:12, category:'Capsules', rating:null, reviews:0, badge:null, img:'supplement' },
    { id:506, name:'Tulsi Capsules (60pc)', subtitle:'', price:140, original:null, weight:'', categoryId:12, category:'Capsules', rating:null, reviews:0, badge:null, img:'supplement' },
    { id:507, name:'Shakti Umang (10 Capsules)', subtitle:'', price:250, original:null, weight:'', categoryId:12, category:'Capsules', rating:null, reviews:0, badge:null, img:'supplement' },
    { id:508, name:'Arshkalp Capsules (30pc)', subtitle:'', price:150, original:null, weight:'', categoryId:12, category:'Capsules', rating:null, reviews:0, badge:null, img:'supplement' },
    { id:509, name:'Rahat Pain Relief Tablets', subtitle:'', price:200, original:null, weight:'', categoryId:12, category:'Capsules', rating:null, reviews:0, badge:null, img:'supplement' },
    { id:510, name:'Apple Cider Vinegar (15 Tablets)', subtitle:'', price:300, original:null, weight:'', categoryId:12, category:'Capsules', rating:null, reviews:0, badge:null, img:'supplement' },

    // Murabbas (6)
    { id:601, name:'Amla Murabba', subtitle:'', price:299, original:null, weight:'1kg', categoryId:10, category:'Murabbas', rating:null, reviews:0, badge:null, img:'murabba' },
    { id:602, name:'Amla Murabba', subtitle:'', price:175, original:null, weight:'500gm', categoryId:10, category:'Murabbas', rating:null, reviews:0, badge:null, img:'murabba' },
    { id:603, name:'Bamboo Murabba', subtitle:'', price:399, original:null, weight:'500gm', categoryId:10, category:'Murabbas', rating:null, reviews:0, badge:null, img:'murabba' },
    { id:604, name:'Behi Murabba', subtitle:'', price:200, original:null, weight:'250gm', categoryId:10, category:'Murabbas', rating:null, reviews:0, badge:null, img:'murabba' },
    { id:605, name:'Behi Murabba', subtitle:'', price:350, original:null, weight:'500gm', categoryId:10, category:'Murabbas', rating:null, reviews:0, badge:null, img:'murabba' },
    { id:606, name:'Harar Murabba', subtitle:'', price:299, original:null, weight:'500gm', categoryId:10, category:'Murabbas', rating:null, reviews:0, badge:null, img:'murabba' },
    { id:607, name:'Bel Murabba', subtitle:'', price:220, original:null, weight:'500gm', categoryId:10, category:'Murabbas', rating:null, reviews:0, badge:null, img:'murabba' },
  ];

  // Mutable: starts with the seeded default list, overwritten on boot by
  // featured_products table content when the migration has been applied.
  // See refreshFeaturedIds() below.
  let FEATURED_IDS = [117, 131, 209, 304, 401, 503];

  // Admin-pinned products/categories, ordered by pinned_at asc (click order).
  // Loaded at boot via refreshPinnedItems(); drives ordering on #view-products.
  let PINNED = [];

  const ILLUSTRATIONS = {
    honey:     { emoji:'🍯',  bg:'linear-gradient(135deg,#FFF3D6,#FFE08A)' },
    talbina:   { emoji:'🌾',  bg:'linear-gradient(135deg,#FFF8F0,#F5DDB4)' },
    blackseed: { emoji:'🌿',  bg:'linear-gradient(135deg,#E8F5E9,#C8E6C9)' },
    oil:       { emoji:'🫒',  bg:'linear-gradient(135deg,#F1F8E9,#C5E1A5)' },
    vinegar:   { emoji:'🍶',  bg:'linear-gradient(135deg,#F3E5F5,#CE93D8)' },
    syrup:     { emoji:'🧉',  bg:'linear-gradient(135deg,#E8F5E9,#A5D6A7)' },
    saffron:   { emoji:'🌺',  bg:'linear-gradient(135deg,#FFF8E1,#FFE082)' },
    spice:     { emoji:'🌶️', bg:'linear-gradient(135deg,#FFEBEE,#FFAB91)' },
    ajwa:      { emoji:'🌴',  bg:'linear-gradient(135deg,#FFF5EE,#FFCCBC)' },
    supplement:{ emoji:'💊',  bg:'linear-gradient(135deg,#E0F2F1,#80CBC4)' },
    skincare:  { emoji:'✨',  bg:'linear-gradient(135deg,#FCF0F8,#F8BBD0)' },
    fiber:     { emoji:'🌱',  bg:'linear-gradient(135deg,#EDF7EF,#A5D6A7)' },
    juice:     { emoji:'🥤',  bg:'linear-gradient(135deg,#FFEBEE,#EF9A9A)' },
    seed:      { emoji:'🫘',  bg:'linear-gradient(135deg,#F5F0EB,#BCAAA4)' },
    murabba:   { emoji:'🍯',  bg:'linear-gradient(135deg,#FFE8C8,#E0A872)' },
  };

  /* ── HELPERS ── */
  const $ = sel => document.querySelector(sel);
  const $$ = sel => document.querySelectorAll(sel);
  const esc = s => String(s).replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));
  // Render admin-entered plain-text descriptions: blank lines → paragraphs, single
  // newlines → <br>. Escapes everything so stored text can't inject markup.
  const descToHtml = s => {
    const t = String(s == null ? '' : s).replace(/\r\n?/g, '\n').trim();
    if (!t) return '';
    return t.split(/\n{2,}/).map(block =>
      `<p>${esc(block.trim()).replace(/\n/g, '<br>')}</p>`
    ).join('');
  };
  // Strip markup, emojis, stray quotes and extra whitespace from product copy.
  const cleanText = s => String(s == null ? '' : s)
    .replace(/<[^>]*>/g, ' ')
    .replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2190}-\u{21FF}\u{2B00}-\u{2BFF}️]/gu, ' ')
    .replace(/["“”]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  // Auto-build a clean ~155-char SEO/meta description from the full product
  // description: trimmed on a word boundary with an ellipsis.
  const metaFromDescription = (s, max = 155) => {
    const t = cleanText(s);
    if (!t) return '';
    if (t.length <= max) return t;
    const cut = t.slice(0, max);
    const lastSpace = cut.lastIndexOf(' ');
    return (lastSpace > 40 ? cut.slice(0, lastSpace) : cut).replace(/[\s,.;:–-]+$/, '') + '…';
  };

  // Auto-build an SEO title: "<name> — <key phrase from short description> | <brand>",
  // capped at ~70 chars. Drops "The X is …" lead-ins and leading repeats of the
  // product name so the title leads with the actual selling phrase.
  const SEO_STOP = new Set(['and','or','the','a','an','for','with','of','to','in','on','from','is','are','&','our','this','that','as','at','by','your']);
  const seoNorm = w => w.toLowerCase().replace(/[^a-z0-9]/g, '');
  const seoTitleCase = s => s.replace(/\b([a-z])([a-z']*)/g, (_, a, b) => a.toUpperCase() + b);
  const seoTitleFrom = (name, shortDesc, brand = 'Sunnah Enterprises', max = 70) => {
    name = String(name || '').trim();
    let phrase = cleanText(shortDesc);
    const fallback = name ? `${name} — ${brand}` : brand;
    if (!phrase) return fallback;

    const nameWords = name.split(/\s+/).map(seoNorm).filter(w => w.length > 2);
    const nameRe = nameWords.length ? new RegExp(nameWords.join('|'), 'i') : null;

    // 1. Drop a "The X is …" lead-in that just restates the product.
    const lead = phrase.match(/^(.{0,60}?)\b(?:is|are)\b\s+(.+)$/i);
    if (lead && nameRe && nameRe.test(lead[1])) phrase = lead[2].trim();

    // 2. Strip a leading run that exactly repeats the full product name.
    const nwNorm = name.toLowerCase().split(/\s+/).map(seoNorm).filter(Boolean);
    let words = phrase.split(' ');
    let i = 0;
    while (i < nwNorm.length && i < words.length && seoNorm(words[i]) === nwNorm[i]) i++;
    if (nwNorm.length && i === nwNorm.length && i < words.length) {
      words = words.slice(i);
      while (words.length > 1 && SEO_STOP.has(seoNorm(words[0]))) words.shift();
      phrase = words.join(' ');
    }

    // 3. First sentence only.
    const dot = phrase.search(/[.!?](\s|$)/);
    if (dot > 15) phrase = phrase.slice(0, dot);

    // 4. Whole-word truncate to the remaining char budget (never cut mid-word).
    const head = `${name} — `, tail = ` | ${brand}`;
    let budget = max - head.length - tail.length, withBrand = true;
    if (budget < 18) { budget = max - head.length; withBrand = false; }
    if (phrase.length > budget) {
      let out = '';
      for (const w of phrase.split(' ')) {
        if ((out ? out.length + 1 : 0) + w.length > budget) break;
        out = out ? `${out} ${w}` : w;
      }
      phrase = out;
    }

    // 5. Trim trailing connectors / punctuation.
    words = phrase.split(' ').filter(Boolean);
    while (words.length > 1 && SEO_STOP.has(seoNorm(words[words.length - 1]))) words.pop();
    phrase = words.join(' ').replace(/[\s,;:&\-–]+$/, '');
    if (!phrase) return fallback;

    phrase = seoTitleCase(phrase);
    let title = `${head}${phrase}`;
    if (withBrand && (title + tail).length <= max) title += tail;
    return title;
  };
  const hasDiscount = p => p && p.original && Number(p.original) > Number(p.price);
  const discount = p => Math.round((1 - p.price / p.original) * 100);
  const slugify = s => String(s).toLowerCase().trim().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  const starsHtml = (rating, size = 14) => {
    let html = '';
    const r = Math.round(rating);
    for (let i = 1; i <= 5; i++) {
      const filled = i <= r;
      html += `<svg width="${size}" height="${size}" viewBox="0 0 14 14" fill="${filled ? '#C9A84C' : 'none'}" stroke="#C9A84C" stroke-width="1"><polygon points="7,1 8.8,5.4 13.5,5.4 9.9,8.4 11.2,13 7,10.2 2.8,13 4.1,8.4 0.5,5.4 5.2,5.4"/></svg>`;
    }
    return html;
  };

  const illustrationHtml = (type, size = 100, photo = null, name = '') => {
    const ill = ILLUSTRATIONS[type] || { emoji:'📦', bg:'#F5F5F5' };
    if (photo) {
      return `<div class="product-illustration product-illustration--photo" style="background:#fff;"><img src="${esc(photo)}" alt="${esc(name || 'Product image')}" loading="lazy" /></div>`;
    }
    return `<div class="product-illustration" style="background:${ill.bg}; width:${size}px; height:${size}px; font-size:${Math.round(size*0.45)}px;">${ill.emoji}</div>`;
  };

  /* ── SKELETON LOADERS ───────────────────────────────────────────────── */
  const skBox = (style = '', cls = '') => `<div class="skeleton ${cls}" style="${style}" aria-hidden="true"></div>`;
  const skLine = (w = 100, h = 12, extra = '') => `<div class="skeleton sk-line" style="width:${w}%;height:${h}px;${extra}" aria-hidden="true"></div>`;
  const repeat = (n, fn) => Array.from({ length: n }, (_, i) => fn(i)).join('');
  // Toggle aria-busy on a loading region so screen readers announce it.
  // Call with `true` when painting a skeleton, `false` in every terminal
  // (success / empty / error) branch so no region stays stuck busy.
  const setRegionBusy = (el, on) => {
    if (!el) return;
    if (on) el.setAttribute('aria-busy', 'true');
    else el.removeAttribute('aria-busy');
  };

  const skelProductCard = () => `
    <article class="product-card skel-card" aria-hidden="true">
      <div class="product-img">${skBox('width:60%;aspect-ratio:1/1;border-radius:14px;margin:auto')}</div>
      <div class="product-body">
        ${skLine(40, 9, 'margin-bottom:10px')}
        ${skLine(85, 18, 'margin-bottom:6px')}
        ${skLine(55, 12, 'margin-bottom:14px')}
        <div class="product-foot">
          ${skBox('width:80px;height:22px')}
          ${skBox('width:64px;height:28px;border-radius:8px')}
        </div>
      </div>
    </article>`;
  const skelProductGrid = (n = 8) => repeat(n, skelProductCard);

  const skelCatCard = () => `
    <div class="cat-card skel-card" aria-hidden="true">
      ${skBox('width:72px;height:72px;border-radius:14px;margin:0 auto 12px')}
      ${skLine(60, 14, 'margin:0 auto 6px')}
      ${skLine(40, 10, 'margin:0 auto')}
    </div>`;
  const skelCatStrip = (n = 6) => repeat(n, skelCatCard);

  const skelCatPageCard = () => `
    <div class="cat-page-card skel-card" aria-hidden="true">
      ${skBox('width:100%;aspect-ratio:1/1;border-radius:0')}
      ${skLine(60, 18, 'margin:14px auto 6px')}
      ${skLine(35, 12, 'margin:0 auto 18px')}
    </div>`;
  const skelCatPageGrid = (n = 12) => repeat(n, skelCatPageCard);

  const skelBlogCard = () => `
    <article class="blog-card skel-card" aria-hidden="true">
      <div class="blog-card-cover">${skBox('width:100%;height:100%')}</div>
      <div class="blog-card-body" style="padding:24px;display:flex;flex-direction:column;gap:10px">
        ${skLine(40, 10)}
        ${skLine(85, 22)}
        ${skLine(100, 13)}
        ${skLine(90, 13)}
        ${skLine(60, 13)}
        ${skLine(30, 13, 'margin-top:6px')}
      </div>
    </article>`;
  const skelBlogGrid = (n = 3) => repeat(n, skelBlogCard);

  const skelHero = () => `<div class="skeleton hero-skel" aria-hidden="true"></div>`;

  // Admin skeletons
  const skelAdmStatCard = () => `
    <div class="adm-skel-stat">
      <div style="display:flex;justify-content:space-between;align-items:center">
        ${skLine(50, 11)}
        ${skBox('width:36px;height:36px;border-radius:8px')}
      </div>
      ${skLine(45, 26, 'margin-top:6px')}
      ${skLine(30, 10, 'margin-top:6px')}
    </div>`;
  const skelAdmDashboard = () => `
    <div class="adm-overview-hd">
      <div style="flex:1">
        ${skLine(25, 22, 'margin-bottom:8px')}
        ${skLine(45, 12)}
      </div>
      ${skBox('width:120px;height:36px;border-radius:8px')}
    </div>
    <div class="adm-skel-stats">${repeat(4, skelAdmStatCard)}</div>
    <div class="adm-skel-dash">
      <div class="adm-skel-card">
        ${skLine(30, 14, 'margin-bottom:14px')}
        <div class="adm-skel-table" style="border:none;background:transparent">
          ${repeat(6, () => `<div class="skeleton adm-skel-row" aria-hidden="true"></div>`)}
        </div>
      </div>
      <div class="adm-skel-card">
        ${skLine(40, 14, 'margin-bottom:14px')}
        ${repeat(5, () => `<div style="display:flex;gap:10px;padding:8px 0;align-items:center">
          ${skBox('width:32px;height:32px;border-radius:50%')}
          <div style="flex:1">${skLine(80, 10, 'margin-bottom:6px')}${skLine(40, 8)}</div>
        </div>`)}
      </div>
    </div>`;

  const skelAdmTable = (rows = 8) => `
    <div class="adm-section-title">${skLine(20, 18, 'display:inline-block;vertical-align:middle')}</div>
    <div class="adm-filter-row">
      ${repeat(5, () => skBox('width:84px;height:30px;border-radius:20px'))}
    </div>
    <div class="adm-skel-table">
      ${repeat(rows, () => `<div class="skeleton adm-skel-row" aria-hidden="true"></div>`)}
    </div>`;

  // Account skeletons
  const skelAccountOrderRow = () => `
    <div class="acc-skel-order">
      <div class="acc-skel-order-main">
        <div style="display:flex;gap:8px;align-items:center">
          ${skLine(20, 14)}
          ${skBox('width:70px;height:22px;border-radius:20px')}
        </div>
        ${skLine(60, 13)}
        ${skLine(25, 10)}
      </div>
      ${skBox('width:70px;height:18px')}
    </div>`;
  const skelAccountOrders = (n = 4) => `
    <div class="acc-card">
      <div class="acc-card-title">My Orders</div>
      ${repeat(n, skelAccountOrderRow)}
    </div>`;
  const skelAccountProfile = () => `
    <div class="acc-card">
      <div class="acc-card-title">Profile</div>
      ${repeat(3, () => `<div style="margin-bottom:18px">${skLine(20, 10, 'margin-bottom:8px')}${skBox('width:100%;height:42px;border-radius:8px')}</div>`)}
      ${skBox('width:140px;height:42px;border-radius:8px;margin-top:8px')}
    </div>`;
  const skelAccountOrderDetail = () => `
    <div class="acc-card">
      ${skLine(22, 12, 'margin-bottom:18px')}
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px">
        <div style="flex:1">${skLine(40, 16, 'margin-bottom:8px')}${skLine(30, 11)}</div>
        ${skBox('width:80px;height:22px;border-radius:20px')}
      </div>
      ${repeat(3, () => `<div style="display:flex;justify-content:space-between;padding:10px 0;border-top:1px solid #f3eee3">${skLine(50, 13)}${skBox('width:70px;height:13px')}</div>`)}
      <div style="margin-top:18px">${skLine(30, 12, 'margin-bottom:8px')}${skLine(70, 11, 'margin-bottom:4px')}${skLine(50, 11)}</div>
    </div>`;

  // PDP reviews skeleton — mirrors the real .pd-reviews-head grid + list so
  // the swap to real content (or the "No reviews yet" empty state) is reflow-free.
  const skelReviews = () => `
    <div class="pd-reviews-head" aria-hidden="true">
      ${skLine(28, 20, 'margin:0 0 4px;grid-column:1 / -1')}
      <div class="pd-reviews-summary">
        ${skBox('width:90px;height:40px;margin-bottom:6px')}
        ${skLine(60, 14)}${skLine(40, 11)}
      </div>
      <div class="pd-reviews-buckets" style="display:flex;flex-direction:column;gap:8px">
        ${repeat(5, () => skBox('width:100%;height:8px;border-radius:999px'))}
      </div>
    </div>
    <ul class="pd-reviews-list" aria-hidden="true">
      ${repeat(3, () => `<li class="pd-review">${skLine(40, 14, 'margin-bottom:8px')}${skLine(90, 11, 'margin-bottom:4px')}${skLine(70, 11)}</li>`)}
    </ul>`;

  // Wishlist tab skeleton — reuses the real .wl-grid/.wl-item layout.
  const skelWishlist = (n = 4) => `
    <div class="wl-grid" aria-hidden="true">
      ${repeat(n, () => `<div class="wl-item skel-card">
        ${skBox('width:72px;height:72px;border-radius:12px')}
        <div class="wl-item-info" style="flex:1">
          ${skLine(70, 14, 'margin-bottom:8px')}${skLine(40, 12, 'margin-bottom:8px')}${skLine(30, 11)}
        </div>
        ${skBox('width:96px;height:34px;border-radius:8px')}
      </div>`)}
    </div>`;

  /* ── PRODUCT CARD ── */
  const productCardHtml = p => {
    const isVariantProduct = p.hasVariants && Array.isArray(p.variants) && p.variants.length > 0;
    return `
    <article class="product-card" data-product-id="${p.id}">
      <div class="product-img">
        <svg class="pattern" aria-hidden="true" style="opacity:0.3"><rect width="100%" height="100%" fill="url(#honeycomb)"/></svg>
        ${illustrationHtml(p.img, 110, p.photo, p.name)}
        ${p.badge ? `<div class="product-badge">${esc(p.badge)}</div>` : ''}
        <button type="button" class="wl-heart ${wishlistLib.isWishlisted(p.id) ? 'wl-heart--active' : ''}" data-stop data-wl-toggle="${p.id}" aria-label="${wishlistLib.isWishlisted(p.id) ? 'Remove from wishlist' : 'Add to wishlist'}">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="${wishlistLib.isWishlisted(p.id) ? 'currentColor' : 'none'}" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
        </button>
      </div>
      <div class="product-body">
        <div class="product-cat">${esc(p.category)}</div>
        <div class="product-name">${esc(p.name)}</div>
        ${p.subtitle ? `<div class="product-sub">${esc(p.subtitle)}</div>` : ''}
        ${p.rating ? `<div class="stars">${starsHtml(p.rating, 12)}${p.reviews ? `<span class="stars-meta">(${p.reviews})</span>` : ''}</div>` : ''}
        <div class="product-foot">
          <div>
            ${isVariantProduct ? `<span class="price-from-label">from </span>` : ''}<span class="price"><span class="rupee">₹</span>${p.price}</span>
            ${hasDiscount(p) ? `<span class="price-original"><span class="rupee">₹</span>${p.original}</span>` : ''}
            ${(!isVariantProduct && p.weight) ? `<span class="price-weight">/ ${esc(p.weight)}</span>` : ''}
          </div>
          <button type="button" class="btn-mini" data-stop data-quick-add="${p.id}" aria-label="${isVariantProduct ? `Choose size for ${esc(p.name)}` : `Add ${esc(p.name)} to cart`}">
            ${isVariantProduct ? 'Choose size' : '+ Add'}
          </button>
        </div>
      </div>
    </article>
  `;};

  /* ── RENDER: CATEGORIES STRIP (home) ── */
  const catThumbHtml = cat => cat.image
    ? `<img class="cat-thumb cat-thumb--photo" src="${esc(cat.image)}" alt="${esc(cat.name)} category" loading="lazy" />`
    : `<div class="cat-thumb cat-thumb--placeholder">${esc(cat.name.charAt(0).toUpperCase())}</div>`;

  const renderCatStrip = () => {
    setRegionBusy($('#cats-strip'), false);
    $('#cats-strip').innerHTML = CATEGORIES.slice(0, 6).map(cat => `
      <button class="cat-card" data-cat-id="${cat.id}" type="button">
        ${catThumbHtml(cat)}
        <div class="cat-name">${esc(cat.name)}</div>
        <div class="cat-count">${cat.count} ${cat.count === 1 ? 'product' : 'products'}</div>
      </button>
    `).join('');
    const totalEl = $('#cats-total-count');
    if (totalEl) totalEl.textContent = CATEGORIES.length;
  };

  /* ── RENDER: ALL CATEGORIES PAGE ── */
  const catPageThumbHtml = cat => cat.image
    ? `<img class="cat-page-thumb cat-page-thumb--photo" src="${esc(cat.image)}" alt="${esc(cat.name)} category" loading="lazy" />`
    : `<div class="cat-page-thumb cat-page-thumb--placeholder">${esc(cat.name.charAt(0).toUpperCase())}</div>`;

  const renderCatsPage = () => {
    setRegionBusy($('#cats-page-grid'), false);
    $('#cats-page-grid').innerHTML = CATEGORIES.map((cat, i) => `
      <button class="cat-page-card" data-cat-id="${cat.id}" type="button" style="--i:${i}">
        ${catPageThumbHtml(cat)}
        <div class="cat-page-name">${esc(cat.name)}</div>
        <div class="cat-count">${cat.count} ${cat.count === 1 ? 'product' : 'products'}</div>
      </button>
    `).join('');
  };

  /* ── RENDER: FEATURED PRODUCTS (home) ── */
  const renderProducts = () => {
    setRegionBusy($('#products-grid'), false);
    $('#products-grid').innerHTML = FEATURED_IDS
      .map(id => PRODUCTS.find(p => p.id === id))
      .filter(Boolean)
      .map(productCardHtml)
      .join('');
  };

  /* ── RENDER: COMBO DEALS (home) ── */
  // Bundle products live in the same PRODUCTS array, flagged with isBundle.
  // The rail hides itself when there are none, so the home layout doesn't
  // collapse around an empty strip.
  const renderComboRail = () => {
    const rail = $('#combo-rail');
    const grid = $('#combo-grid');
    if (!rail || !grid) return;
    const bundles = (PRODUCTS || []).filter(p => p.isBundle);
    if (bundles.length === 0) { rail.hidden = true; return; }
    rail.hidden = false;
    grid.innerHTML = bundles.slice(0, 8).map(productCardHtml).join('');
  };

  /* ── RELATED PRODUCTS ──
     Tag-overlap first (descending overlap count), category fallback for ties
     and when the product has no tags. Excludes the product itself and any
     bundles (bundles already have their own rail). */
  const getRelatedProducts = (product, limit = 8) => {
    if (!product || !Array.isArray(PRODUCTS)) return [];
    const tags = (product.tags || []).map(t => String(t).toLowerCase());
    const pool = PRODUCTS.filter(p => p.id !== product.id && !p.isBundle);
    const scored = pool.map(p => {
      const pTags = (p.tags || []).map(t => String(t).toLowerCase());
      const overlap = tags.length
        ? pTags.filter(t => tags.includes(t)).length
        : 0;
      const sameCat = p.categoryId && p.categoryId === product.categoryId ? 1 : 0;
      return { p, overlap, sameCat };
    });
    scored.sort((a, b) => {
      if (b.overlap !== a.overlap) return b.overlap - a.overlap;
      if (b.sameCat !== a.sameCat) return b.sameCat - a.sameCat;
      return (Number(b.p.rating) || 0) - (Number(a.p.rating) || 0);
    });
    // Keep only items with at least one signal (tag overlap or same category)
    // so we don't pad with unrelated junk on small catalogs.
    return scored.filter(s => s.overlap > 0 || s.sameCat).slice(0, limit).map(s => s.p);
  };

  const renderRailInto = (railId, gridId, items) => {
    const rail = $(railId);
    const grid = $(gridId);
    if (!rail || !grid) return;
    if (!items.length) { rail.hidden = true; grid.innerHTML = ''; return; }
    rail.hidden = false;
    grid.innerHTML = items.map(productCardHtml).join('');
  };

  const renderRelatedRail = (railId, gridId, product) => {
    renderRailInto(railId, gridId, getRelatedProducts(product, 8));
  };

  const renderRecentRail = (railId, gridId, excludeId = null, minItems = 1) => {
    if (!Array.isArray(PRODUCTS)) return;
    const ids = getRecentProductIds({ excludeId, limit: 12 });
    const items = ids
      .map(id => PRODUCTS.find(p => p.id === id))
      .filter(Boolean)
      .slice(0, 8);
    // A single card stranded in a multi-column rail reads as broken — only show
    // the rail once there's enough to fill it out.
    renderRailInto(railId, gridId, items.length >= minItems ? items : []);
  };

  /* ── RENDER: ALL PRODUCTS PAGE (REDESIGNED) ── */
  let apFilter = 'all';
  let apQuery = '';
  let apSort = 'featured';
  let apViewMode = 'grid';
  // When the user arrives at /products by clicking a PDP tag chip, we
  // surface a dismissible "Tag: foo ×" pill on the toolbar so they know
  // why the result set is filtered and can clear it in one click.
  let apActiveTag = '';

  // Renders the left category sidebar (also the mobile slide-in drawer).
  // Shows "All Products" + EVERY category including those with a 0 count, so
  // shoppers always see the full taxonomy; an empty pick lands on the grid
  // empty-state. The active row is marked by class + aria-current.
  const renderApFilters = () => {
    const rows = [{ id: 'all', label: 'All Products', count: PRODUCTS.length }];
    CATEGORIES.forEach(c => {
      rows.push({
        id: c.id, label: c.name,
        count: PRODUCTS.filter(p => p.categoryId === c.id).length,
      });
    });
    const list = $('#ap-cat-list');
    if (!list) return;
    list.innerHTML = rows.map(r => {
      const active = apFilter === r.id;
      return `
      <li><button class="ap-cat ${active ? 'is-active' : ''}" data-filter="${r.id}" type="button"${active ? ' aria-current="true"' : ''}>
        <span class="ap-cat-name">${esc(r.label)}</span>
        <span class="ap-cat-count">${r.count}</span>
      </button></li>`;
    }).join('');
  };

  const sortProducts = (list) => {
    const sorted = [...list];
    switch (apSort) {
      case 'price-low':  return sorted.sort((a, b) => a.price - b.price);
      case 'price-high': return sorted.sort((a, b) => b.price - a.price);
      case 'rating':     return sorted.sort((a, b) =>
        (Number(b.rating) || 0) - (Number(a.rating) || 0)
        || (Number(b.reviews) || 0) - (Number(a.reviews) || 0));
      case 'name':       return sorted.sort((a, b) => a.name.localeCompare(b.name));
      default:           return sorted; // featured = original order
    }
  };

  // Reorder a (already filtered + sorted) product list so admin-pinned items
  // float to the top: individually pinned products first (by pinned_at asc),
  // then products from pinned categories (grouped, in their current order),
  // then everything else. A single `seen` Set guarantees no duplicates and
  // gives individual pins precedence over category pins (AC2.4). Operates on
  // the post-filter `list` so a pin never bleeds across a category/search view.
  const applyPinnedOrder = (list) => {
    if (!PINNED.length) return list;
    const byId = new Map(list.map(p => [p.id, p]));
    const seen = new Set();
    const pinned = [];

    // 1) Individual product pins (PINNED is already pinned_at asc).
    for (const row of PINNED) {
      if (row.item_type !== 'product') continue;
      const p = byId.get(row.item_id);
      if (p && !seen.has(p.id)) { pinned.push(p); seen.add(p.id); }
    }
    // 2) Category pins — surface their member products, in current list order.
    for (const row of PINNED) {
      if (row.item_type !== 'category') continue;
      for (const p of list) {
        if (p.categoryId === row.item_id && !seen.has(p.id)) {
          pinned.push(p); seen.add(p.id);
        }
      }
    }
    const rest = list.filter(p => !seen.has(p.id));
    return [...pinned, ...rest];
  };

  const renderProductsView = () => {
    const q = apQuery.trim().toLowerCase();
    let list = apFilter === 'all'
      ? PRODUCTS
      : PRODUCTS.filter(p => p.categoryId === apFilter);
    if (q) {
      list = list.filter(p => {
        const cat = CATEGORIES.find(c => c.id === p.categoryId);
        const tags = Array.isArray(p.tags) ? p.tags.join(' ') : '';
        const haystack = `${p.name || ''} ${p.subtitle || ''} ${cat ? cat.name : ''} ${tags}`.toLowerCase();
        return haystack.includes(q);
      });
    }
    list = sortProducts(list);
    list = applyPinnedOrder(list);

    // Update hero stats. Use the total category count so the header number and
    // the "across N categories" line below always agree (all categories show).
    const totalCats = CATEGORIES.length;
    const statEl = $('#ap-stat-products');
    if (statEl) statEl.textContent = PRODUCTS.length + '+';
    const catStatEl = $('#ap-stat-cat');
    if (catStatEl) catStatEl.textContent = totalCats;

    // Update toolbar meta
    const catName = apFilter !== 'all' ? (CATEGORIES.find(c => c.id === apFilter) || {}).name : null;
    const tagPill = apActiveTag
      ? ` <span class="ap-active-tag">Tag: ${esc(apActiveTag)}<button type="button" class="ap-active-tag-clear" data-ap-clear-tag aria-label="Clear tag filter">×</button></span>`
      : '';
    if (q) {
      $('#ap-meta').innerHTML = `<strong>${list.length}</strong> ${list.length === 1 ? 'result' : 'results'} for "${esc(apQuery)}"${tagPill}`;
    } else if (catName) {
      $('#ap-meta').innerHTML = `Showing <strong>${list.length}</strong> products in <strong>${esc(catName)}</strong>${tagPill}`;
    } else {
      $('#ap-meta').innerHTML = `Showing <strong>all ${PRODUCTS.length} products</strong> across ${totalCats} categories`;
    }

    // Apply view mode
    const grid = $('#ap-products');
    setRegionBusy(grid, false);
    grid.classList.toggle('ap-list-view', apViewMode === 'list');

    if (list.length) {
      // Insert promo banner after every 6th product
      const cards = list.map((p, i) => {
        let html = productCardHtml(p);
        // Add staggered animation delay
        html = html.replace('class="product-card"', `class="product-card" style="animation-delay:${Math.min(i * 0.06, 0.6)}s"`);
        return html;
      });
      grid.innerHTML = cards.join('');
      grid.hidden = false;
      $('#ap-empty').hidden = true;
    } else {
      grid.innerHTML = '';
      grid.hidden = true;
      $('#ap-empty').hidden = false;
    }
  };

  /* ── RENDER: CATEGORY DETAIL ── */
  const renderCategoryView = catId => {
    const cat = CATEGORIES.find(c => c.id === catId);
    if (!cat) return;
    const items = PRODUCTS.filter(p => p.categoryId === catId);
    const cdEmojiEl = $('#cd-emoji');
    if (cat.image) {
      cdEmojiEl.innerHTML = `<img src="${esc(cat.image)}" alt="${esc(cat.name)} category" loading="lazy" />`;
      cdEmojiEl.classList.add('cd-emoji--photo');
    } else {
      cdEmojiEl.textContent = cat.name.charAt(0).toUpperCase();
      cdEmojiEl.classList.remove('cd-emoji--photo');
    }
    $('#cd-title').textContent = cat.name;
    $('#cd-meta').textContent = items.length
      ? `${items.length} ${items.length === 1 ? 'product' : 'products'} available`
      : 'No products available yet';
    $('#cd-crumbs').textContent = `Home / All Categories / ${cat.name}`;
    setRegionBusy($('#cd-products'), false);
    if (items.length) {
      $('#cd-products').innerHTML = items.map(productCardHtml).join('');
      $('#cd-products').hidden = false;
      $('#cd-empty').hidden = true;
    } else {
      $('#cd-products').innerHTML = '';
      $('#cd-products').hidden = true;
      $('#cd-empty').hidden = false;
    }
  };

  /* ── PRODUCT DETAIL ── */
  /* ── CART (localStorage for guests + Supabase for signed-in users) ─────── */
  const CART_KEY = 'sunnah_cart_v1';
  let cart = [];
  try { cart = JSON.parse(localStorage.getItem(CART_KEY) || '[]'); } catch (_) { cart = []; }

  const cartCount = () => cart.reduce((s, i) => s + i.qty, 0);
  // Amazon-style line selection: only checked lines are priced, ordered and
  // cleared after payment. `sel` undefined means selected — legacy carts and
  // freshly added lines start checked.
  const isSel = i => i.sel !== false;
  const selCart = () => cart.filter(isSel);
  const selCount = () => selCart().reduce((s, i) => s + i.qty, 0);
  const lineUnitPrice = (i) => {
    const p = PRODUCTS.find(pp => pp.id === i.id);
    if (!p) return 0;
    if (p.hasVariants && Array.isArray(p.variants) && i.variantId) {
      const v = p.variants.find(vv => vv.id === i.variantId);
      if (v) return v.salePrice;
    }
    return p.price;
  };
  // Money always follows the selection, never the whole cart.
  const cartTotal = () => selCart().reduce((s, i) => s + lineUnitPrice(i) * i.qty, 0);
  // True when the cart is non-empty and EVERY line's product is flagged
  // free_shipping — mirrors the server rule (orders_recompute_totals) that
  // waives the flat fee only when nothing in the cart carries a charge.
  const cartAllFreeShip = () => {
    const sel = selCart();
    return sel.length > 0 && sel.every(i => {
      const p = PRODUCTS.find(pp => pp.id === i.id);
      return p && p.freeShipping;
    });
  };
  const saveCartLocal = () => {
    try { localStorage.setItem(CART_KEY, JSON.stringify(cart)); } catch (_) {}
  };
  // Server cart sync lands after the view is already on screen. Whichever
  // cart-driven view is showing has to be redrawn, or checkout keeps the
  // pre-sync numbers — that's the "5 items in the badge, ₹0 subtotal" screen.
  const refreshActiveCartView = () => {
    const isActive = id => { const el = $(id); return !!el && el.classList.contains('active'); };
    if (isActive('#view-cart')) renderCartView();
    else if (isActive('#view-checkout')) renderCheckoutSummary();
  };
  // Pushes the entire in-memory cart to server (replaces server cart). No-op when guest.
  // Aggregates same (product, variant) entries so two card-mashes of the
  // same size collapse into one row.
  const pushCartToServer = async () => {
    const s = getSession();
    if (!s) return;
    try {
      await supabase.from('cart_items').delete().eq('user_id', s.userId);
      if (cart.length === 0) return;
      const agg = new Map();
      for (const i of cart) {
        const key = `${i.id}:${i.variantId || ''}`;
        if (!agg.has(key)) {
          agg.set(key, { productId: i.id, variantId: i.variantId || null, qty: 0 });
        }
        agg.get(key).qty += i.qty;
      }
      const rows = Array.from(agg.values()).map(({ productId, variantId, qty }) => ({
        user_id: s.userId, product_id: productId, variant_id: variantId, quantity: qty,
      }));
      const { error } = await supabase.from('cart_items').insert(rows);
      if (error) throw error;
    } catch (e) { dwarn('[cart] push failed:', e); }
  };
  const saveCart = () => {
    saveCartLocal();
    updateCartBadge();
    // Fire-and-forget server sync
    pushCartToServer();
  };
  // Page-load / returning-session hydrate: REPLACE in-memory cart with server cart.
  // Never sum. Sum-on-load is what caused the "16 honey from nowhere" doubling bug
  // (each reload re-merged local into server, doubling every quantity).
  const syncCartFromServer = async () => {
    const s = getSession();
    if (!s) return;
    try {
      const { data: serverRows, error } = await supabase
        .from('cart_items').select('product_id, variant_id, quantity').eq('user_id', s.userId);
      if (error) throw error;
      // cart_items has no `selected` column — selection is local UI state, so
      // carry it across the server replace by (product, variant) key.
      const prevSel = new Map(cart.map(i => [`${i.id}:${i.variantId || ''}`, i.sel]));
      cart = (serverRows || []).map(r => {
        // Reconstruct the display label from the matching product variant
        // so cart rows can show "Size: 250ml" without an extra fetch.
        const p = PRODUCTS.find(pp => pp.id === r.product_id);
        const v = (p?.hasVariants && Array.isArray(p.variants) && r.variant_id)
          ? p.variants.find(vv => vv.id === r.variant_id) : null;
        return {
          id: r.product_id,
          variantId: r.variant_id || null,
          variant: v ? v.label : null,
          qty: r.quantity,
          sel: prevSel.get(`${r.product_id}:${r.variant_id || ''}`) !== false,
        };
      });
      saveCartLocal();
      updateCartBadge();
      refreshActiveCartView();
    } catch (e) { dwarn('[cart] sync failed:', e); }
  };
  // True guest → signed-in transition: sum guest cart with server cart, push merged.
  // Only safe to call on an actual sign-in event, NEVER on INITIAL_SESSION or reload.
  const mergeCartOnSignIn = async () => {
    const s = getSession();
    if (!s) return;
    if (cart.length === 0) return syncCartFromServer();
    try {
      const { data: serverRows, error } = await supabase
        .from('cart_items').select('product_id, variant_id, quantity').eq('user_id', s.userId);
      if (error) throw error;
      const keyOf = (pid, vid) => `${pid}:${vid || ''}`;
      const merged = new Map();
      (serverRows || []).forEach(r =>
        merged.set(keyOf(r.product_id, r.variant_id), { pid: r.product_id, vid: r.variant_id || null, qty: r.quantity }));
      cart.forEach(i => {
        const k = keyOf(i.id, i.variantId);
        const existing = merged.get(k);
        if (existing) existing.qty += i.qty;
        else merged.set(k, { pid: i.id, vid: i.variantId || null, qty: i.qty });
      });
      cart = Array.from(merged.values()).map(({ pid, vid, qty }) => {
        const p = PRODUCTS.find(pp => pp.id === pid);
        const v = (p?.hasVariants && Array.isArray(p.variants) && vid)
          ? p.variants.find(vv => vv.id === vid) : null;
        return { id: pid, variantId: vid, variant: v ? v.label : null, qty };
      });
      saveCartLocal();
      await pushCartToServer();
      updateCartBadge();
      refreshActiveCartView();
    } catch (e) { dwarn('[cart] merge failed:', e); }
  };
  const updateCartBadge = () => {
    const n = cartCount();
    const el = $('#amzn-cart-count');
    if (el) {
      el.textContent = n > 99 ? '99+' : n;
      el.hidden = n === 0;
    }
    const bn = $('#bn-cart-count');
    if (bn) bn.textContent = n > 0 ? `(${n})` : '';
  };

  // ── Wishlist badge ──
  const updateWishlistBadge = () => {
    const n = wishlistLib.wishlistCount();
    const el = $('#wl-badge-count');
    if (el) {
      el.textContent = n;
      el.hidden = n === 0;
    }
  };
  const addToCart = (productId, variantLabel, qty = 1, variantId = null) => {
    qty = Math.max(1, parseInt(qty) || 1);
    // Cart line identity is (product_id, variant_id). variantLabel is a
    // display-only snapshot — two carts shouldn't merge just because the
    // label happens to match.
    const existing = cart.find(i => i.id === productId && (i.variantId || null) === (variantId || null));
    if (existing) existing.qty += qty;
    else cart.push({ id: productId, variant: variantLabel, variantId: variantId || null, qty });
    saveCart();
  };
  const removeFromCart = idx => {
    cart.splice(idx, 1);
    saveCart();
    if ($('#view-cart').classList.contains('active')) renderCartView();
  };
  const setCartQty = (idx, qty) => {
    qty = parseInt(qty) || 0;
    if (qty <= 0) { removeFromCart(idx); return; }
    cart[idx].qty = qty;
    saveCart();
    if ($('#view-cart').classList.contains('active')) renderCartView();
  };
  // Selection never leaves the browser, so no server push here.
  const setCartSel = (idx, on) => {
    if (!cart[idx]) return;
    cart[idx].sel = on;
    saveCartLocal();
    renderCartView();
  };
  const setCartSelAll = (on) => {
    cart.forEach(i => { i.sel = on; });
    saveCartLocal();
    renderCartView();
  };

  const showToast = msg => {
    let t = $('#toast');
    if (!t) {
      t = document.createElement('div');
      t.id = 'toast';
      t.className = 'toast';
      document.body.appendChild(t);
    }
    t.textContent = msg;
    t.classList.add('show');
    clearTimeout(showToast._h);
    showToast._h = setTimeout(() => t.classList.remove('show'), 1800);
  };


  // Flat-rate shipping fee. No order-value threshold — shipping is waived only
  // by a free_shipping product or coupon. Must match `compute_shipping` in
  // supabase/schema.sql — change there too if this ever moves.
  const FLAT_SHIPPING_FEE = 100;

  /* ── Pincode serviceability widget ──────────────────────────────────
     Rendered on PDP and cart side panel. State (last-checked pincode +
     result) is persisted to localStorage so the customer doesn't have
     to re-enter on every page. The lookupPincode() helper hits India
     Post's free public API; ETA comes from a state→days table inside
     src/lib/pincode.js. */
  const pincodeWidgetHtml = ({ compact = false } = {}) => {
    const cached = recallPincode();
    const code = cached?.code || '';
    const result = cached?.result || null;
    return `
      <div class="pin-widget ${compact ? 'pin-widget--compact' : ''}" data-pin-widget>
        <div class="pin-widget-row">
          <label class="pin-widget-label">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
            Deliver to
          </label>
          <input type="text" inputmode="numeric" maxlength="6" pattern="[0-9]*"
            class="pin-widget-input" data-pin-input
            value="${esc(code)}" placeholder="6-digit pincode" autocomplete="postal-code" />
          <button type="button" class="pin-widget-btn" data-pin-check>Check</button>
        </div>
        <div class="pin-widget-result" data-pin-result>
          ${pincodeResultHtml(result)}
        </div>
      </div>`;
  };

  const pincodeResultHtml = (result) => {
    if (!result) return '';
    if (result.ok) {
      return `<div class="pin-result pin-result--ok">
        ✓ Delivers to <strong>${esc(result.city)}, ${esc(result.state)}</strong>
        — est. <strong>${result.etaMin}–${result.etaMax} business days</strong>
      </div>`;
    }
    const messages = {
      'invalid':      'Enter a valid 6-digit pincode.',
      'unknown':      'Pincode not found. Please check and try again.',
      'non-delivery': 'Sorry, this pincode is not currently serviceable.',
      'network':      'Could not check right now. Please try again.',
    };
    return `<div class="pin-result pin-result--err">${esc(messages[result.reason] || 'Could not check.')}</div>`;
  };

  // Event-delegated handler: lives at document level so the widget works
  // whether it was just inserted into the cart side or the PDP buy box.
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-pin-check]');
    if (!btn) return;
    const wrap = btn.closest('[data-pin-widget]');
    if (!wrap) return;
    const input = wrap.querySelector('[data-pin-input]');
    const code = (input?.value || '').trim();
    btn.disabled = true; btn.dataset._orig = btn.textContent; btn.textContent = '…';
    const result = await lookupPincode(code);
    btn.disabled = false; btn.textContent = btn.dataset._orig || 'Check';
    rememberPincode(code, result);
    // Sync every instance of the widget on screen so cart + PDP stay
    // in lockstep when the user pops between views.
    document.querySelectorAll('[data-pin-widget]').forEach(w => {
      const i = w.querySelector('[data-pin-input]');
      const o = w.querySelector('[data-pin-result]');
      if (i) i.value = code;
      if (o) o.innerHTML = pincodeResultHtml(result);
    });
    syncDeliverTo();
  });

  // Topnav "Deliver to <city>" label. Priority:
  //   1. Manual pincode (source of truth — entered by user)
  //   2. Cached auto-detected location (GPS or IP, 24h TTL)
  //   3. Live GPS (browser permission prompt) — fires once per session
  //   4. Live IP detection — fallback if GPS denied/unavailable
  //   5. "Hyderabad" — warehouse default, never blank
  const HYDERABAD_FALLBACK = 'Hyderabad';
  let autoDetectInFlight = false;
  const setNavDeliverText = (text) => {
    const node = document.getElementById('nav-deliver-loc');
    if (node) node.textContent = text;
  };
  const syncDeliverTo = () => {
    const el = document.getElementById('nav-deliver-loc');
    if (!el) return;
    const cached = recallPincode();
    if (cached?.result?.ok && cached.result.city) {
      el.textContent = `${cached.result.city} ${cached.code}`;
      return;
    }
    const ipLoc = recallIpLocation();
    if (ipLoc?.city) {
      el.textContent = ipLoc.city;
      return;
    }
    // Show the warehouse city up front so the header is never blank,
    // then try GPS → IP in the background and swap in something better.
    el.textContent = HYDERABAD_FALLBACK;
    if (autoDetectInFlight) return;
    autoDetectInFlight = true;
    (async () => {
      try {
        let loc = await detectLocationByGPS();
        if (!loc?.ok) {
          loc = await detectLocationByIP();
        }
        if (!loc?.ok) return;
        rememberIpLocation(loc);
        // Bail if the user picked a pincode while we were waiting.
        if (recallPincode()?.result?.ok) return;
        setNavDeliverText(loc.city);
      } finally {
        autoDetectInFlight = false;
      }
    })();
  };
  // Inline SVG glyphs for footer social icons. Pure stroke icons so a single
  // currentColor swap drives both default + hover states.
  const SOCIAL_ICON_SVG = {
    social_instagram: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="20" rx="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>',
    social_facebook:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>',
    social_twitter:   '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>',
    social_youtube:   '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"/><polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"/></svg>',
    social_linkedin:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>',
    social_whatsapp:  '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>',
  };
  const SOCIAL_LABELS_PUBLIC = {
    social_instagram: 'Instagram',
    social_facebook:  'Facebook',
    social_twitter:   'X (Twitter)',
    social_youtube:   'YouTube',
    social_linkedin:  'LinkedIn',
    social_whatsapp:  'WhatsApp',
  };
  const hydrateFooterSocial = async () => {
    const host = document.getElementById('footer-social');
    if (!host) return;
    const { data: settings, error } = await fetchSiteSettings();
    if (error) { host.hidden = true; return; }
    const links = SOCIAL_KEYS
      .filter(k => (settings[k] || '').trim().length > 0)
      .map(k => `<a href="${esc(settings[k].trim())}" target="_blank" rel="noopener noreferrer" aria-label="${esc(SOCIAL_LABELS_PUBLIC[k] || k)}">${SOCIAL_ICON_SVG[k] || ''}</a>`);
    if (!links.length) { host.hidden = true; host.innerHTML = ''; return; }
    host.innerHTML = links.join('');
    host.hidden = false;
  };

  // Run after DOM is ready; also runs implicitly on every successful pin check above.
  const hydrateChromeOnReady = () => {
    syncDeliverTo();
    const y = document.getElementById('footer-year');
    if (y) y.textContent = String(new Date().getFullYear());
    hydrateFooterSocial();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hydrateChromeOnReady, { once: true });
  } else {
    hydrateChromeOnReady();
  }
  // Allow Enter to trigger the check.
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter') return;
    const input = e.target.closest('[data-pin-input]');
    if (!input) return;
    e.preventDefault();
    input.closest('[data-pin-widget]')?.querySelector('[data-pin-check]')?.click();
  });

  const freeShipBarHtml = (subtotal) => {
    if (subtotal <= 0 || !cartAllFreeShip()) return '';
    return `<div class="ship-bar ship-bar--ok" role="status">
      <span class="ship-bar-icon">🎉</span>
      <div class="ship-bar-text">You've unlocked <strong>FREE shipping</strong>!</div>
    </div>`;
  };

  const renderCartView = () => {
    const main = $('#cart-main');
    const side = $('#cart-side');
    if (!main || !side) return;
    // Recently viewed rail belongs to the cart view regardless of cart state —
    // for empty carts it doubles as a "here's what you were looking at" nudge.
    renderRecentRail('#cart-recent-rail', '#cart-recent-grid', null, 2);
    if (!cart.length) {
      main.innerHTML = `
        <div class="cart-empty">
          <div style="font-size:48px;margin-bottom:8px">🛒</div>
          <h2>Your cart is empty</h2>
          <p>Browse our products and add your favourites here.</p>
          <button class="btn btn-green" data-nav="products">Shop Products</button>
        </div>`;
      side.innerHTML = '';
      side.style.display = 'none';
      return;
    }
    side.style.display = '';
    const rows = cart.map((i, idx) => {
      const p = PRODUCTS.find(pp => pp.id === i.id);
      if (!p) return '';
      // Resolve the line's price + thumbnail to the chosen variant when one
      // is bound; fall back to the parent product otherwise.
      const variant = (p.hasVariants && Array.isArray(p.variants) && i.variantId)
        ? p.variants.find(v => v.id === i.variantId)
        : null;
      const unitPrice = variant ? variant.salePrice : p.price;
      const thumbPhoto = variant?.mainImage || p.photo;
      const lineTotal = unitPrice * i.qty;
      const ill = (typeof ILLUSTRATIONS !== 'undefined' && ILLUSTRATIONS[p.img]) || { emoji: '📦' };
      const emoji = ill.emoji || '📦';
      const thumbInner = thumbPhoto
        ? `<img src="${esc(thumbPhoto)}" alt="${esc(p.name)}" loading="lazy" />`
        : emoji;
      return `
        <div class="cart-row${isSel(i) ? '' : ' cart-row--off'}">
          <label class="cart-sel">
            <input type="checkbox" data-cart-sel="${idx}" ${isSel(i) ? 'checked' : ''}
                   aria-label="Include ${esc(p.name)} in this order" />
          </label>
          <div class="cart-thumb${thumbPhoto ? ' cart-thumb--photo' : ''}" data-product-id="${p.id}">${thumbInner}</div>
          <div class="cart-info">
            <span class="cart-info-name" data-product-id="${p.id}">${esc(p.name)}</span>
            <span class="cart-info-sub">${esc(p.subtitle || '')}</span>
            ${i.variant ? `<span class="cart-info-variant">Size: <strong>${esc(i.variant)}</strong></span>` : ''}
            <span class="cart-info-stock">In Stock</span>
            <div class="cart-info-actions">
              <div class="cart-qty-box">
                <button class="cart-qty-btn" data-cart-dec="${idx}" type="button">−</button>
                <div class="cart-qty-val">${i.qty}</div>
                <button class="cart-qty-btn" data-cart-inc="${idx}" type="button">+</button>
              </div>
              <span style="color:rgba(0,0,0,0.18)">|</span>
              <button class="cart-link-btn" data-cart-remove="${idx}" type="button">Delete</button>
            </div>
          </div>
          <div class="cart-line-price">₹${lineTotal.toLocaleString('en-IN')}</div>
        </div>`;
    }).join('');
    const n = selCount();
    const label = `Subtotal (${n} ${n === 1 ? 'item' : 'items'})`;
    const allOn = cart.every(isSel);
    const none = n === 0;
    main.innerHTML = `
      <div class="cart-title">Shopping Cart</div>
      <div class="cart-subtitle">Tick the items you want to order now — only those are charged.</div>
      <button type="button" class="cart-link-btn" data-cart-sel-all="${allOn ? '0' : '1'}">${allOn ? 'Deselect all items' : 'Select all items'}</button>
      ${freeShipBarHtml(cartTotal())}
      ${rows}
      <div class="cart-subtotal-row">${label}: <strong>₹${cartTotal().toLocaleString('en-IN')}</strong></div>`;
    side.innerHTML = `
      <div class="cart-side-total">${label}:<br /><strong>₹${cartTotal().toLocaleString('en-IN')}</strong></div>
      ${freeShipBarHtml(cartTotal())}
      ${pincodeWidgetHtml({ compact: true })}
      <button type="button" class="btn-buy-now" data-nav="checkout" style="width:100%;border:none;cursor:pointer" ${none ? 'disabled' : ''}>Proceed to Checkout</button>
      <div class="cart-side-note">${none ? 'Select at least one item to continue.' : 'Secure online checkout — UPI, cards, or wallets via Razorpay.'}</div>`;
  };

  /* ── CHECKOUT ──────────────────────────────────────────────────── */
  const CHECKOUT_DRAFT_KEY = 'se_checkout_draft';

  const getCheckoutDraft = () => {
    try { return JSON.parse(localStorage.getItem(CHECKOUT_DRAFT_KEY)) || {}; }
    catch { return {}; }
  };
  const saveCheckoutDraft = (data) => {
    try { localStorage.setItem(CHECKOUT_DRAFT_KEY, JSON.stringify(data)); }
    catch {}
  };

  // Coupon state for the current checkout session.
  // appliedCoupon = { code, kind, freeShipping, discount } once a valid
  // code is applied. Cleared when the user removes it or empties the cart.
  let appliedCoupon = null;

  const renderCheckoutSummary = () => {
    const itemsEl = $('#ck-side-items');
    const subEl = $('#ck-subtotal');
    const totEl = $('#ck-total');
    const stickyTotEl = $('#ck-sticky-total-val');
    if (!itemsEl) return;
    const subtotal = cartTotal();
    let shipping = subtotal === 0 || cartAllFreeShip() ? 0 : FLAT_SHIPPING_FEE;
    let discount = 0;
    if (appliedCoupon) {
      if (appliedCoupon.freeShipping) shipping = 0;
      else discount = Math.min(appliedCoupon.discount || 0, subtotal);
    }
    const total = Math.max(subtotal + shipping - discount, 0);
    itemsEl.innerHTML = selCart().map(i => {
      const p = PRODUCTS.find(pp => pp.id === i.id);
      if (!p) return '';
      const variant = (p.hasVariants && Array.isArray(p.variants) && i.variantId)
        ? p.variants.find(v => v.id === i.variantId)
        : null;
      const unit = variant ? variant.salePrice : p.price;
      const thumbPhoto = variant?.mainImage || p.photo;
      const ill = (typeof ILLUSTRATIONS !== 'undefined' && ILLUSTRATIONS[p.img]) || { emoji: '📦' };
      const thumbInner = thumbPhoto
        ? `<img src="${esc(thumbPhoto)}" alt="${esc(p.name)}" loading="lazy" />`
        : (ill.emoji || '📦');
      return `
        <div class="ck-side-item">
          <div class="ck-side-thumb">
            ${thumbInner}
            <span class="ck-side-qty">${i.qty}</span>
          </div>
          <div>
            <div class="ck-side-name">${esc(p.name)}</div>
            <div class="ck-side-name-sub">${i.variant ? esc(i.variant) : esc(p.weight || '')}</div>
          </div>
          <div class="ck-side-price">₹${(unit * i.qty).toLocaleString('en-IN')}</div>
        </div>`;
    }).join('');
    if (subEl) subEl.textContent = '₹' + subtotal.toLocaleString('en-IN');
    const shippingEl = $('#ck-shipping');
    if (shippingEl) shippingEl.textContent = shipping === 0 ? 'FREE' : '₹' + shipping;
    if (totEl) totEl.textContent = '₹' + total.toLocaleString('en-IN');
    if (stickyTotEl) stickyTotEl.textContent = '₹' + total.toLocaleString('en-IN');

    // Discount line + applied-coupon pill.
    const discRow   = $('#ck-discount-row');
    const discCode  = $('#ck-discount-code');
    const discAmt   = $('#ck-discount');
    const appliedEl = $('#ck-coupon-applied');
    const inputRow  = $('#ck-coupon-input-row');
    const appliedCode = $('#ck-coupon-applied-code');
    const appliedDetail = $('#ck-coupon-applied-detail');
    if (discRow && discCode && discAmt) {
      if (appliedCoupon && (discount > 0 || appliedCoupon.freeShipping)) {
        discRow.hidden = false;
        discCode.textContent = `(${appliedCoupon.code})`;
        discAmt.textContent = appliedCoupon.freeShipping && discount === 0
          ? 'Free shipping'
          : '−₹' + discount.toLocaleString('en-IN');
      } else {
        discRow.hidden = true;
      }
    }
    if (appliedEl && inputRow && appliedCode && appliedDetail) {
      if (appliedCoupon) {
        appliedEl.hidden = false;
        inputRow.hidden = true;
        appliedCode.textContent = appliedCoupon.code;
        appliedDetail.textContent = appliedCoupon.freeShipping
          ? '— free shipping'
          : `— ₹${discount.toLocaleString('en-IN')} off`;
      } else {
        appliedEl.hidden = true;
        inputRow.hidden = false;
      }
    }
  };

  // Re-attach coupon box listeners every time the checkout view renders.
  // The DOM nodes themselves persist (they live in index.html), so this is
  // idempotent: we use a one-shot dataset flag to avoid stacking handlers.
  const wireCouponBox = () => {
    const input  = $('#ck-coupon-input');
    const apply  = $('#ck-coupon-apply');
    const remove = $('#ck-coupon-remove');
    if (!input || !apply || !remove) return;

    // Restore UI from any previously-applied coupon (e.g. user returned to
    // checkout after the order failed).
    if (appliedCoupon) input.value = appliedCoupon.code;

    if (input.dataset.wired === '1') return;
    input.dataset.wired = '1';

    const msg = $('#ck-coupon-msg');
    const showMsg = (text, ok) => {
      if (!msg) return;
      msg.textContent = text;
      msg.hidden = false;
      msg.classList.toggle('ck-coupon-msg--ok',  !!ok);
      msg.classList.toggle('ck-coupon-msg--err', !ok);
    };
    const hideMsg = () => { if (msg) msg.hidden = true; };

    const applyHandler = async () => {
      const code = (input.value || '').trim();
      if (!code) { showMsg('Enter a code first.', false); return; }
      apply.disabled = true; apply.textContent = '…';
      const subtotal = cartTotal();
      const res = await validateCoupon(code, subtotal);
      apply.disabled = false; apply.textContent = 'Apply';
      if (!res.valid) {
        appliedCoupon = null;
        renderCheckoutSummary();
        showMsg(res.reason || 'Invalid coupon', false);
        return;
      }
      appliedCoupon = {
        code: code.toUpperCase(),
        kind: res.kind,
        freeShipping: !!res.freeShipping,
        discount: res.discount,
      };
      hideMsg();
      renderCheckoutSummary();
    };

    apply.addEventListener('click', applyHandler);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); applyHandler(); }
    });
    input.addEventListener('input', () => {
      input.value = input.value.replace(/\s+/g, '').toUpperCase();
    });
    remove.addEventListener('click', () => {
      appliedCoupon = null;
      input.value = '';
      hideMsg();
      renderCheckoutSummary();
    });
  };

  const prefillCheckout = () => {
    const draft = getCheckoutDraft();
    const session = getSession();
    const setVal = (id, val) => { const el = $('#' + id); if (el && val != null) el.value = val; };
    setVal('ck-name', draft.name || (session && session.name) || '');
    setVal('ck-email', draft.email || (session && session.email) || '');
    setVal('ck-phone', draft.phone || (session && session.phone) || '');
    setVal('ck-address1', draft.address1);
    setVal('ck-address2', draft.address2);
    setVal('ck-city', draft.city);
    if (draft.state) setVal('ck-state', draft.state);
    // Fall back to the pincode the customer checked on PDP / cart if the
    // draft doesn't have one yet — saves them retyping it.
    const remembered = recallPincode();
    setVal('ck-pincode', draft.pincode || remembered?.code || '');
    if (!draft.city && remembered?.result?.ok)  setVal('ck-city',  remembered.result.city);
    if (!draft.state && remembered?.result?.ok) setVal('ck-state', remembered.result.state);
    setVal('ck-notes', draft.notes);
    if (draft.payment) {
      const radio = document.querySelector(`input[name="payment"][value="${draft.payment}"]`);
      if (radio) radio.checked = true;
    }
  };

  const clearCheckoutErrors = () => {
    $$('#ck-form .ck-error').forEach(e => e.textContent = '');
    $$('#ck-form .ck-input--err').forEach(i => i.classList.remove('ck-input--err'));
    const top = $('#ck-form-error');
    if (top) { top.hidden = true; top.textContent = ''; }
  };
  const setCkFieldError = (fieldId, msg) => {
    const input = $('#' + fieldId);
    const err = document.querySelector(`.ck-error[data-error-for="${fieldId}"]`);
    if (input) input.classList.add('ck-input--err');
    if (err) err.textContent = msg;
  };

  const renderCheckoutView = () => {
    // Warm the Razorpay Checkout script as soon as the user reaches checkout,
    // so it's ready by the time they hit Pay (no first-click delay).
    if (razorpayConfigured) { loadRazorpayScript().catch(() => {}); }
    // Mandatory sign-in before checkout. Stash the intent so the signin /
    // signup handlers (and the OAuth round-trip in onAuthChange) know to
    // bounce the user back here once they're authenticated.
    if (!getSession()) {
      try { sessionStorage.setItem('postAuthRedirect', 'checkout'); } catch {}
      showToast('Please sign in to continue to checkout.');
      setView('auth');
      return;
    }
    if (!selCart().length) {
      // bounce empty / nothing-selected checkout back to cart
      appliedCoupon = null;
      setView('cart');
      return;
    }
    renderCheckoutSummary();
    prefillCheckout();
    clearCheckoutErrors();
    wireCouponBox();
    // Razorpay is the only payment path. If keys aren't configured, show
    // a "temporarily unavailable" notice and disable the Place Order button.
    const onlineOpt = $('#ck-pay-opt-online');
    const unavail   = $('#ck-pay-unavailable');
    const placeBtns = [$('#ck-place-btn'), $('#ck-place-btn-mobile')].filter(Boolean);
    if (razorpayConfigured) {
      if (onlineOpt) onlineOpt.hidden = false;
      if (unavail)   unavail.hidden = true;
      placeBtns.forEach(b => { b.disabled = false; });
    } else {
      if (onlineOpt) onlineOpt.hidden = true;
      if (unavail)   unavail.hidden = false;
      placeBtns.forEach(b => { b.disabled = true; });
    }
  };

  const collectCheckoutData = () => ({
    name:     ($('#ck-name')     && $('#ck-name').value.trim())     || '',
    phone:    ($('#ck-phone')    && $('#ck-phone').value.trim())    || '',
    email:    ($('#ck-email')    && $('#ck-email').value.trim())    || '',
    address1: ($('#ck-address1') && $('#ck-address1').value.trim()) || '',
    address2: ($('#ck-address2') && $('#ck-address2').value.trim()) || '',
    city:     ($('#ck-city')     && $('#ck-city').value.trim())     || '',
    state:    ($('#ck-state')    && $('#ck-state').value)           || '',
    pincode:  ($('#ck-pincode')  && $('#ck-pincode').value.trim())  || '',
    notes:    ($('#ck-notes')    && $('#ck-notes').value.trim())    || '',
    payment:  'razorpay',
  });

  const validateCheckout = (d) => {
    let bad = false;
    clearCheckoutErrors();
    if (!d.name)                              { setCkFieldError('ck-name', 'Enter your full name.'); bad = true; }
    if (!d.phone)                             { setCkFieldError('ck-phone', 'Enter your mobile number.'); bad = true; }
    else if (!/^[0-9+\- ]{10,15}$/.test(d.phone)) { setCkFieldError('ck-phone', 'Enter a valid 10-digit number.'); bad = true; }
    if (d.email && !isValidEmail(d.email))    { setCkFieldError('ck-email', 'Enter a valid email or leave blank.'); bad = true; }
    if (!d.address1)                          { setCkFieldError('ck-address1', 'Enter your street address.'); bad = true; }
    if (!d.city)                              { setCkFieldError('ck-city', 'Enter your city.'); bad = true; }
    if (!d.state)                             { setCkFieldError('ck-state', 'Select your state.'); bad = true; }
    if (!d.pincode)                           { setCkFieldError('ck-pincode', 'Enter pincode.'); bad = true; }
    else if (!/^[0-9]{6}$/.test(d.pincode))   { setCkFieldError('ck-pincode', '6-digit pincode required.'); bad = true; }
    if (bad) {
      const top = $('#ck-form-error');
      if (top) { top.textContent = 'Please fix the highlighted fields and try again.'; top.hidden = false; }
      const firstErr = document.querySelector('#ck-form .ck-input--err');
      if (firstErr) firstErr.focus();
    }
    return !bad;
  };

  // Stash for the confirmation page (rendered after a successful order).
  let lastOrder = null;

  const placeOrder = async () => {
    if (!razorpayConfigured) {
      showToast('Payments are temporarily unavailable. Please try again later.');
      return;
    }
    const d = collectCheckoutData();
    if (!validateCheckout(d)) return;
    saveCheckoutDraft(d);

    // Pricing is enforced by the DB — only productId + quantity travel.
    const lines = selCart()
      .map(i => PRODUCTS.find(pp => pp.id === i.id)
        ? { productId: i.id, variantId: i.variantId || null, quantity: i.qty }
        : null)
      .filter(Boolean);
    if (lines.length === 0) { showToast('Select at least one item to order.'); return; }

    const placeBtns = [$('#ck-place-btn'), $('#ck-place-btn-mobile')].filter(Boolean);
    placeBtns.forEach(b => {
      b.disabled = true;
      b.dataset._orig = b.textContent;
      b.textContent = 'Starting payment…';
    });

    const { order, error, authRequired } = await placeOrderInDb({
      fullName: d.name,
      phone: d.phone,
      email: d.email || null,
      addressLine1: d.address1,
      addressLine2: d.address2 || null,
      city: d.city,
      state: d.state,
      pincode: d.pincode,
      notes: d.notes || null,
      lines,
      couponCode: appliedCoupon?.code || null,
    });

    if (error) {
      placeBtns.forEach(b => { b.disabled = false; b.textContent = b.dataset._orig || 'Place Order'; });
      showToast('Could not place order: ' + (error.message || 'unknown error'));
      // The cached session went stale (signed out elsewhere, refresh token
      // expired). Send them back through sign-in and straight to checkout.
      if (authRequired) {
        currentSession = null;
        try { sessionStorage.setItem('postAuthRedirect', 'checkout'); } catch {}
        setView('auth');
      }
      return;
    }

    // Razorpay flow: open the modal. If the user dismisses or fails, the
    // order row stays as payment_status='pending' — they can retry from
    // "My Orders" later.
    try {
      await payOrderWithRazorpay({
        order,
        customerName:  d.name,
        customerEmail: d.email,
        customerPhone: d.phone,
      });
      showToast('Payment received!');
    } catch (e) {
      placeBtns.forEach(b => { b.disabled = false; b.textContent = b.dataset._orig || 'Place Order'; });
      showToast(e.message || 'Payment was not completed.');
      return;
    }

    placeBtns.forEach(b => { b.disabled = false; b.textContent = b.dataset._orig || 'Place Order'; });

    // Paid — drop only the lines that were actually ordered (memory, storage,
    // server) and re-check whatever the customer left behind. Doing this
    // before payment wiped the cart of anyone who dismissed the Razorpay modal.
    cart = cart.filter(i => !isSel(i)).map(i => ({ ...i, sel: true }));
    saveCart();
    appliedCoupon = null;

    // Use the DB-computed totals on the confirmation page. The trigger
    // already rewrote unit_price + line_total + subtotal + shipping + total,
    // and orders.js returns the joined order_items rows.
    lastOrder = {
      order,
      lines: (order.order_items || []).map(i => ({
        name: i.product_name,
        weight: i.product_weight,
        unitPrice: Number(i.unit_price),
        quantity: i.quantity,
      })),
      customer: d,
      subtotal: Number(order.subtotal),
      shipping: Number(order.shipping),
      discount: Number(order.discount_amount || 0),
      couponCode: order.coupon_code || null,
      total: Number(order.total),
    };
    setView('order-confirmation');
  };

  const renderOrderConfirmation = () => {
    const host = $('#view-order-confirmation');
    if (!host) return;
    if (!lastOrder) { setView('home'); return; }
    const { order, lines, customer, subtotal, shipping, discount = 0, couponCode = null, total } = lastOrder;
    const shortId = String(order.id).slice(0, 8).toUpperCase();
    host.innerHTML = `
      <div class="oc-page">
        <div class="oc-card">
          <div class="oc-check">✓</div>
          <h1 class="oc-title">Order received</h1>
          <p class="oc-sub">Thank you, ${esc(customer.name.split(' ')[0])}. Your order <strong>#${esc(shortId)}</strong> has been received.</p>
          <p class="oc-sub">We'll send updates to <strong>${esc(customer.phone)}</strong> as it's packed and dispatched.</p>

          <div class="oc-summary">
            <div class="oc-summary-title">Order summary</div>
            ${lines.map(l => `
              <div class="oc-row">
                <span>${esc(l.name)}${l.weight ? ` <span class="oc-row-sub">(${esc(l.weight)})</span>` : ''} × ${l.quantity}</span>
                <span>₹${(l.unitPrice * l.quantity).toLocaleString('en-IN')}</span>
              </div>
            `).join('')}
            <div class="oc-row oc-row-total"><span>Subtotal</span><span>₹${subtotal.toLocaleString('en-IN')}</span></div>
            <div class="oc-row"><span>Shipping</span><span>${shipping === 0 ? 'FREE' : '₹' + shipping}</span></div>
            ${discount > 0 ? `<div class="oc-row"><span>Discount${couponCode ? ` (${esc(couponCode)})` : ''}</span><span>−₹${discount.toLocaleString('en-IN')}</span></div>` : ''}
            <div class="oc-row oc-row-grand"><span>Total</span><span>₹${total.toLocaleString('en-IN')}</span></div>
          </div>

          <div class="oc-deliver">
            <div class="oc-deliver-title">Delivering to</div>
            <div>${esc(customer.address1)}${customer.address2 ? ', ' + esc(customer.address2) : ''}</div>
            <div>${esc(customer.city)}, ${esc(customer.state)} – ${esc(customer.pincode)}</div>
          </div>

          <div class="oc-actions">
            <button class="btn btn-green" data-nav="products" type="button">Continue shopping</button>
            <button class="btn btn-outline" data-nav="home" type="button">Back to home</button>
          </div>
        </div>
      </div>`;
  };

  /* ── ADMIN PANEL ────────────────────────────────────────────────────── */
  // Source of truth: profiles.is_admin. To bootstrap the first admin, run
  // this once in the Supabase SQL Editor (a one-time service_role action):
  //   update public.profiles set is_admin = true
  //     where id = (select id from auth.users where email = 'YOU@example.com');
  // A BEFORE-UPDATE trigger (supabase/migrate_admin_security.sql) prevents
  // any signed-in user from self-promoting via the REST API.
  const isAdminUser = () => currentProfile?.is_admin === true;

  let adminTab = 'dashboard'; // 'dashboard' | 'orders' | 'payments' | 'messages' | 'products' | 'categories' | 'customers' | 'coupons' | 'reviews' | 'content' | 'social'
  let adminOrderFilter = '';  // status filter for orders tab
  let adminMessageFilter = ''; // '' | 'new' | 'read' | 'archived'
  let adminCustomerFilter = ''; // '' | 'has-orders' | 'no-orders' | 'admins' | 'new-week'
  let adminUnreadMessages = 0; // cached count for the left-nav badge
  let adminPendingReviews = 0; // cached count for the Reviews tab badge
  let adminPendingOrders  = 0; // cached count for the bell badge

  // ── Content (slides / featured / blog) refresh helpers ───────────────
  // Each pulls live rows from Supabase and overwrites the in-memory array
  // used by the storefront renderers. Map DB rows into the existing shape
  // so renderSlides / renderProducts / renderBlogView don't have to change.

  const _slideRowToView = (s) => {
    if (!s) return null;
    if (s.kind === 'banner') {
      // Banner slides expect `banner` (just the filename used in
      // images/slides/<name>) OR a full URL. Use the full URL form so the
      // existing template can just dump it in `src` after a small tweak.
      return {
        bannerUrl: s.image_url || '',
        alt:  s.alt_text || s.title || '',
        link: s.link_view || null,
      };
    }
    return {
      eyebrow:  s.eyebrow || '',
      title:    s.title || '',
      sub:      s.subtitle || '',
      price:    s.price_label || '',
      original: s.original_label || '',
      save:     s.save_label || '',
      cta:      s.cta_label || 'Shop Now',
      ctaTarget: s.cta_target || 'products',
      imageUrl: s.image_url || '',
      emoji:    s.emoji || '🍯',
    };
  };

  const refreshHeroSlides = async () => {
    try {
      const { data, error } = await fetchHeroSlides();
      if (error || !data) return;
      const mapped = data.map(_slideRowToView).filter(Boolean);
      // Only overwrite when we got real rows — keeps the seeded fallback
      // when the migration hasn't run yet.
      if (mapped.length) SLIDES = mapped;
      slideIdx = 0;
      try { renderSlides(); } catch {}
      try { startSlideAuto(); } catch {}
    } catch (e) { dwarn('[content] hero slides refresh failed:', e); }
  };

  const refreshFeaturedIds = async () => {
    try {
      const { data, error } = await fetchFeaturedProductIds();
      if (error || !data) return;
      if (data.length) FEATURED_IDS = data;
      try { renderProducts(); } catch {}
    } catch (e) { dwarn('[content] featured refresh failed:', e); }
  };

  const _blogRowToView = (p) => {
    const dateLabel = p.published_at
      ? new Date(p.published_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })
      : '';
    return {
      slug:     p.slug,
      title:    p.title,
      excerpt:  p.excerpt || '',
      category: p.category || 'Article',
      date:     dateLabel,
      readTime: p.read_time_min ? `${p.read_time_min} min read` : '',
      cover:    p.cover_url || 'placeholder:default',
      coverAlt: p.cover_alt || '',
      coverFit: p.cover_fit || 'cover',
      body:     p.body || '',
      // SEO fields — used by the /blog/<slug> page head + BlogPosting JSON-LD.
      metaTitle:       p.meta_title || '',
      metaDescription: p.meta_description || '',
      author:          p.author || '',
      publishedIso:    p.published_at || '',
      updatedIso:      p.updated_at || '',
    };
  };

  const refreshBlogPosts = async () => {
    try {
      const { data, error } = await fetchBlogPosts();
      if (error || !data) return;
      if (data.length) BLOGS = data.map(_blogRowToView);
      if ($('#view-blog') && $('#view-blog').classList.contains('active')) {
        try { renderBlogView(); } catch {}
      }
    } catch (e) { dwarn('[content] blog refresh failed:', e); }
  };

  // Pull a fresh catalog and update the in-memory PRODUCTS/CATEGORIES so the
  // storefront reflects admin edits without a full page reload.
  const refreshCatalogInMemory = async () => {
    try {
      const data = await fetchCatalog();
      if (data && data.products.length) {
        CATEGORIES = data.categories;
        PRODUCTS = data.products;
        // Repaint the storefront rails that depend on the catalog so the
        // admin's edits/additions land immediately without a page reload.
        try { renderProducts(); } catch {}
        try { renderComboRail(); } catch {}
        try { renderRecentRail('#home-recent-rail', '#home-recent-grid', null, 2); } catch {}
      }
    } catch (e) {
      dwarn('[catalog] refresh failed:', e);
    }
  };

  // Pull the latest pinned-items list into memory so #view-products reflects
  // admin pin/unpin without a full page reload. Does NOT touch the catalog.
  const refreshPinnedItems = async () => {
    try {
      const { data, error } = await adminLib.fetchPinnedItems();
      if (!error && Array.isArray(data)) PINNED = data;
    } catch (e) {
      dwarn('[pinned] refresh failed:', e);
    }
  };

  // Bump the "(N) pinned" count label in an admin section header by `delta`.
  const updatePinCountLabel = (selector, delta) => {
    const el = $(selector);
    if (!el) return;
    const cur = parseInt((el.textContent.match(/\d+/) || ['0'])[0], 10) || 0;
    el.textContent = `${Math.max(0, cur + delta)} pinned`;
  };

  // Toggle a pin button's visual state (used by product row + drawer buttons).
  const setPinBtnState = (btn, pinned) => {
    btn.classList.toggle('is-pinned', pinned);
    btn.setAttribute('aria-pressed', String(pinned));
    const label = btn.querySelector('.adm-pin-label');
    if (label) label.textContent = pinned ? 'Pinned' : 'Pin';
    btn.title = pinned
      ? 'Unpin from top of products page'
      : 'Pin to top of products page';
  };

  // Persist a product pin toggle, sync the button, refresh the in-memory list,
  // and repaint the products view if it is the active view. Does NOT clear the
  // catalog cache — pins don't change the catalog itself.
  const togglePinProduct = async (btn) => {
    const id = parseInt(btn.dataset.admPinProduct, 10);
    const pinned = btn.classList.contains('is-pinned');
    btn.disabled = true;
    const { error } = pinned
      ? await adminLib.unpinItem('product', id)
      : await adminLib.pinItem('product', id);
    btn.disabled = false;
    if (error) { showToast('Pin failed: ' + error.message); return; }
    setPinBtnState(btn, !pinned);
    updatePinCountLabel('#adm-prod-pin-count', pinned ? -1 : +1);
    showToast(pinned ? 'Unpinned' : 'Pinned to top');
    await refreshPinnedItems();
    if ($('#view-products')?.classList.contains('active')) { try { renderProductsView(); } catch {} }
  };

  const ORDER_STATUSES = ['pending','confirmed','shipped','delivered','cancelled'];
  const STATUS_LABELS  = { pending:'Pending', confirmed:'Confirmed', shipped:'Shipped', delivered:'Delivered', cancelled:'Cancelled' };

  // Single line-icon set for the whole admin panel (nav, stat cards, activity
  // feed, row actions, empty states) so nothing falls back to emoji.
  const ADM_ICON_PATHS = {
    dashboard: `<rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>`,
    orders:    `<rect x="8" y="2" width="8" height="4" rx="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><line x1="9" y1="12" x2="15" y2="12"/><line x1="9" y1="16" x2="13" y2="16"/>`,
    products:  `<path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/>`,
    categories:`<path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/>`,
    users:     `<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>`,
    messages:  `<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>`,
    coupons:   `<path d="M20 12a2 2 0 0 1 0-4V5a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v3a2 2 0 0 1 0 4v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2z"/><line x1="9" y1="9" x2="9" y2="9"/><line x1="15" y1="15" x2="15" y2="15"/><line x1="15" y1="9" x2="9" y2="15"/>`,
    content:   `<rect x="3" y="3" width="18" height="6" rx="1"/><rect x="3" y="13" width="10" height="8" rx="1"/><line x1="16" y1="13" x2="21" y2="13"/><line x1="16" y1="17" x2="21" y2="17"/><line x1="16" y1="21" x2="21" y2="21"/>`,
    reviews:   `<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>`,
    payments:  `<rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/><line x1="6" y1="15" x2="10" y2="15"/>`,
    back:      `<polyline points="15 18 9 12 15 6"/>`,
    social:    `<circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>`,
    bag:       `<path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/>`,
    clock:     `<circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15.5 14"/>`,
    rupee:     `<path d="M6 4h12"/><path d="M6 9h12"/><path d="M14.5 4a4.5 4.5 0 0 1 0 9H6l8 8"/>`,
    check:     `<circle cx="12" cy="12" r="9"/><polyline points="8.5 12.5 11 15 15.5 9.5"/>`,
    close:     `<circle cx="12" cy="12" r="9"/><line x1="9" y1="9" x2="15" y2="15"/><line x1="15" y1="9" x2="9" y2="15"/>`,
    truck:     `<rect x="1" y="6" width="13" height="10" rx="1"/><path d="M14 9h4l3 3v4h-7z"/><circle cx="5.5" cy="18" r="1.8"/><circle cx="17.5" cy="18" r="1.8"/>`,
    box:       `<path d="M21 16V8a2 2 0 0 0-1-1.73L13 2.27a2 2 0 0 0-2 0L4 6.27A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73L11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.3 7 12 12 20.7 7"/><line x1="12" y1="22" x2="12" y2="12"/>`,
    pin:       `<line x1="12" y1="17" x2="12" y2="22"/><path d="M9 3h6l-1 6 3 3v2H7v-2l3-3z"/>`,
    edit:      `<path d="M17 3a2.83 2.83 0 0 1 4 4L7.5 20.5 2 22l1.5-5.5z"/>`,
    trash:     `<polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>`,
    inbox:     `<polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>`,
    image:     `<rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>`,
    note:      `<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="13" y2="17"/>`,
  };
  const admIcon = (type, size = 16) => ADM_ICON_PATHS[type]
    ? `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${ADM_ICON_PATHS[type]}</svg>`
    : '';

  const renderAdminView = async () => {
    const host = $('#view-admin');
    if (!host) return;

    // Not logged in at all → send to sign-in, flag we want to return to /admin.
    if (!currentSession) {
      sessionStorage.setItem('adminRedirectPending', '1');
      setView('auth');
      return;
    }

    // Always re-fetch the profile here so /admin reflects the latest DB state,
    // not a cached `currentProfile` from page-load (which can lag behind a
    // promotion that happened in the SQL Editor seconds ago, or fail silently
    // if a row didn't exist at first paint).
    try {
      const { data: freshProfile, error: profErr } = await authLib.getProfile(currentSession.user.id);
      if (freshProfile) currentProfile = freshProfile;
      if (profErr) dwarn('[admin] profile re-fetch error:', profErr);
    } catch (e) { dwarn('[admin] profile re-fetch threw:', e); }

    // Logged in but not an admin → show access-denied panel inside the view.
    if (!isAdminUser()) {
      $$('.view').forEach(v => v.classList.remove('active'));
      host.classList.add('active');
      document.body.classList.remove('adm-active');
      const debugEmail   = currentSession?.user?.email || '(none)';
      const debugUserId  = currentSession?.user?.id    || '(none)';
      const debugLoaded  = currentProfile ? 'yes' : 'no';
      const debugIsAdmin = currentProfile ? String(currentProfile.is_admin) : '(no profile row)';
      host.innerHTML = `
        <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:60vh;gap:16px;padding:40px 20px;text-align:center">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#71717a" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="11" width="16" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>
          <h2 style="margin:0;font-size:1.4rem;color:var(--text-primary,#111)">Access Denied</h2>
          <p style="color:var(--text-muted,#666);max-width:340px;margin:0">Your account doesn't have admin privileges. Please sign in with the admin account.</p>
          <div style="font-family:monospace;font-size:12px;color:#888;background:#f4f4f4;padding:10px 14px;border-radius:6px;text-align:left;word-break:break-all;max-width:520px">
            <div>email:   ${debugEmail}</div>
            <div>user.id: ${debugUserId}</div>
            <div>profile loaded: ${debugLoaded}</div>
            <div>is_admin: ${debugIsAdmin}</div>
          </div>
          <button class="btn btn-green" data-nav="home" type="button">← Back to Store</button>
        </div>`;
      return;
    }

    const adminFirstLetter = (currentSession?.user?.email || 'A')[0].toUpperCase();

    // Refresh the unread-message + pending-review badges for the left nav.
    // Failure is non-fatal — the nav just renders without a badge.
    try {
      const { count } = await adminLib.countUnreadMessages();
      adminUnreadMessages = count;
    } catch (e) { /* ignore */ }
    try {
      const { count } = await adminLib.countPendingReviews();
      adminPendingReviews = count;
    } catch (e) { /* ignore */ }
    try {
      const { count } = await adminLib.countPendingOrders();
      adminPendingOrders = count;
    } catch (e) { /* ignore */ }

    // Authorized — activate the admin view.
    $$('.view').forEach(v => v.classList.remove('active'));
    host.classList.add('active');
    // Admin panel is its own chrome — hide the storefront header/footer/bottom nav.
    document.body.classList.add('adm-active');
    window.scrollTo({ top: 0, behavior: 'auto' });
    $$('.bn-btn').forEach(b => b.classList.toggle('active', b.dataset.nav === 'admin'));
    $$('.amzn-secondary-inner a').forEach(a => a.classList.toggle('active', a.dataset.nav === 'admin'));


    host.innerHTML = `
      <div class="adm-layout">
        <aside class="adm-nav">
          <div class="adm-nav-brand">
            <div class="adm-nav-brand-icon"><img src="/images/logo/sunnah_entrprises_logo-2.jpg?v=2" alt="" /></div>
            <div>
              <div class="adm-nav-brand-name">Sunnah Enterprises</div>
              <div class="adm-nav-brand-sub">Admin Panel</div>
            </div>
          </div>

          <button class="adm-nav-btn ${adminTab==='dashboard'?'active':''}" data-adm-tab="dashboard">
            ${admIcon('dashboard')} Dashboard
          </button>

          <div class="adm-nav-section-label">Management</div>
          <button class="adm-nav-btn ${adminTab==='orders'?'active':''}" data-adm-tab="orders">
            ${admIcon('orders')} Orders
          </button>
          <button class="adm-nav-btn ${adminTab==='payments'?'active':''}" data-adm-tab="payments">
            ${admIcon('payments')} Payments
          </button>
          <button class="adm-nav-btn ${adminTab==='products'?'active':''}" data-adm-tab="products">
            ${admIcon('products')} Products
          </button>
          <button class="adm-nav-btn ${adminTab==='categories'?'active':''}" data-adm-tab="categories">
            ${admIcon('categories')} Categories
          </button>
          <button class="adm-nav-btn ${adminTab==='customers'?'active':''}" data-adm-tab="customers">
            ${admIcon('users')} Customers
          </button>
          <button class="adm-nav-btn ${adminTab==='coupons'?'active':''}" data-adm-tab="coupons">
            ${admIcon('coupons')} Coupons
          </button>
          <button class="adm-nav-btn ${adminTab==='reviews'?'active':''}" data-adm-tab="reviews">
            ${admIcon('reviews')} Reviews
            ${adminPendingReviews > 0
              ? `<span class="adm-nav-badge" id="adm-nav-rev-badge">${adminPendingReviews > 99 ? '99+' : adminPendingReviews}</span>`
              : ''}
          </button>
          <button class="adm-nav-btn ${adminTab==='content'?'active':''}" data-adm-tab="content">
            ${admIcon('content')} Content
          </button>
          <button class="adm-nav-btn ${adminTab==='social'?'active':''}" data-adm-tab="social">
            ${admIcon('social')} Social Media
          </button>
          <button class="adm-nav-btn ${adminTab==='messages'?'active':''}" data-adm-tab="messages">
            ${admIcon('messages')} Messages
            ${adminUnreadMessages > 0
              ? `<span class="adm-nav-badge" id="adm-nav-msg-badge">${adminUnreadMessages > 99 ? '99+' : adminUnreadMessages}</span>`
              : ''}
          </button>

          <div class="adm-nav-spacer"></div>
          <div class="adm-nav-divider"></div>
          <button class="adm-nav-btn adm-nav-btn--exit" data-nav="home">
            ${admIcon('back')} Back to Store
          </button>
        </aside>

        <div class="adm-content">
          <header class="adm-topbar">
            <div class="adm-search-wrap">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input class="adm-search" placeholder="Search orders, products, categories…" id="adm-search-input" />
            </div>
            <div class="adm-topbar-right">
              <button class="adm-bell" id="adm-bell" type="button" aria-label="Notifications">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                ${(adminPendingOrders + adminUnreadMessages) > 0
                  ? `<span class="adm-bell-badge">${adminPendingOrders + adminUnreadMessages}</span>`
                  : ''}
              </button>
              <div class="adm-bell-dropdown" id="adm-bell-dropdown" hidden>
                <div class="adm-bell-title">Notifications</div>
                ${adminPendingOrders > 0 ? `
                  <button class="adm-bell-item" data-adm-tab="orders">
                    <span class="adm-bell-dot adm-bell-dot--orange"></span>
                    <span>${adminPendingOrders} pending order${adminPendingOrders !== 1 ? 's' : ''}</span>
                  </button>` : ''}
                ${adminUnreadMessages > 0 ? `
                  <button class="adm-bell-item" data-adm-tab="messages">
                    <span class="adm-bell-dot adm-bell-dot--red"></span>
                    <span>${adminUnreadMessages} unread message${adminUnreadMessages !== 1 ? 's' : ''}</span>
                  </button>` : ''}
                ${adminPendingReviews > 0 ? `
                  <button class="adm-bell-item" data-adm-tab="reviews">
                    <span class="adm-bell-dot adm-bell-dot--yellow"></span>
                    <span>${adminPendingReviews} pending review${adminPendingReviews !== 1 ? 's' : ''}</span>
                  </button>` : ''}
                ${(adminPendingOrders + adminUnreadMessages + adminPendingReviews) === 0
                  ? `<div class="adm-bell-empty">All caught up!</div>` : ''}
              </div>
              <div class="adm-avatar">${adminFirstLetter}</div>
            </div>
          </header>
          <div class="adm-body" id="adm-content">${skelAdmDashboard()}</div>
        </div>
      </div>`;

    if (adminTab === 'dashboard')        await renderAdmDashboard();
    else if (adminTab === 'orders')      await renderAdmOrders();
    else if (adminTab === 'payments')    await renderAdmPayments();
    else if (adminTab === 'products')    await renderAdmProducts();
    else if (adminTab === 'categories')  await renderAdmCategories();
    else if (adminTab === 'customers')   await renderAdmCustomers();
    else if (adminTab === 'coupons')     await renderAdmCoupons();
    else if (adminTab === 'reviews')     await renderAdmReviews();
    else if (adminTab === 'content')     await renderAdmContent();
    else if (adminTab === 'social')      await renderAdmSocial();
    else if (adminTab === 'messages')    await renderAdmMessages();

    // Wire admin search after the shell renders (once per shell render).
    const searchInput = $('#adm-search-input');
    if (searchInput) {
      let t;
      searchInput.addEventListener('input', () => {
        clearTimeout(t);
        t = setTimeout(() => {
          const query = searchInput.value.toLowerCase().trim();
          $$('.adm-table tbody tr').forEach(row => {
            const text = row.textContent.toLowerCase();
            row.style.display = text.includes(query) ? '' : 'none';
          });
        }, 200);
      });
    }
  };




  /* ── Slide-out drawer (shared by Order + Customer detail views) ──
     Drawers stack: opening a customer from inside an order pushes a panel
     on top with a back arrow rather than replacing the order. */
  const admDrawerStack = [];

  const _drawerEsc = (e) => { if (e.key === 'Escape') closeAdmDrawer(false); };

  const _syncDrawerStackUI = () => {
    admDrawerStack.forEach((entry, idx) => {
      const back = entry.panel.querySelector('.adm-dr-back');
      if (back) back.style.display = idx > 0 ? '' : 'none';
    });
  };

  function closeAdmDrawer(closeAll = true) {
    if (!admDrawerStack.length) return;
    if (closeAll) {
      admDrawerStack.forEach(e => e.panel.remove());
      admDrawerStack.length = 0;
    } else {
      const top = admDrawerStack.pop();
      top.panel.remove();
    }
    if (!admDrawerStack.length) {
      document.querySelector('.adm-dr-overlay')?.remove();
      document.removeEventListener('keydown', _drawerEsc);
    } else {
      _syncDrawerStackUI();
    }
  }

  function openAdmDrawer({ kind, id, wide = false, full = false, render }) {
    let overlay = document.querySelector('.adm-dr-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'adm-dr-overlay';
      overlay.innerHTML = '<div class="adm-dr-stage"></div>';
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) closeAdmDrawer(true);
      });
      document.body.appendChild(overlay);
      document.addEventListener('keydown', _drawerEsc);
    }
    const stage = overlay.querySelector('.adm-dr-stage');
    const panel = document.createElement('div');
    panel.className = 'adm-dr-panel'
      + (full ? ' adm-dr-panel--full' : wide ? ' adm-dr-panel--wide' : '');
    panel.dataset.kind = kind;
    panel.dataset.id = id;
    panel.innerHTML = `
      <div class="adm-dr-head">
        <button class="adm-dr-back" data-adm-dr-back style="display:none" aria-label="Back" type="button">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <div class="adm-dr-head-text">
          <div class="adm-dr-eyebrow"></div>
          <div class="adm-dr-title">Loading…</div>
        </div>
        <button class="adm-dr-close" data-adm-dr-close aria-label="Close" type="button">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>
      <div class="adm-dr-body"></div>
    `;
    stage.appendChild(panel);
    admDrawerStack.push({ panel, kind, id });
    _syncDrawerStackUI();

    Promise.resolve(render(panel)).catch(err => {
      dwarn('[drawer] render failed:', err);
      panel.querySelector('.adm-dr-body').innerHTML =
        `<div class="adm-dr-sec adm-dr-empty">Failed to load. ${esc(err?.message || '')}</div>`;
    });
  }

  const _fmtMoney = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  const _fmtDate  = (s) => s ? new Date(s).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';
  const _fmtDT    = (s) => s ? new Date(s).toLocaleString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : '—';
  const PAY_LABELS = { paid:'Paid', pending:'Pending', failed:'Failed', refunded:'Refunded' };

  // ── Couriers + tracking-URL templates ──
  // {tracking} is substituted with the AWB / consignment number. Add more
  // here as we onboard new logistics partners. The 'custom' / '' entries
  // skip the auto-URL and let the admin paste a full URL manually.
  const CARRIERS = [
    { id: '',            label: '— Select carrier —', template: null },
    { id: 'delhivery',   label: 'Delhivery',          template: 'https://www.delhivery.com/track-v2/package/{tracking}' },
    { id: 'bluedart',    label: 'Blue Dart',          template: 'https://www.bluedart.com/tracking?trackFor=0&trackNo={tracking}' },
    { id: 'dtdc',        label: 'DTDC',               template: 'https://www.dtdc.in/tracking/tracking_results.asp?strCnno={tracking}' },
    { id: 'india_post',  label: 'India Post',         template: 'https://www.indiapost.gov.in/_layouts/15/dop.portal.tracking/trackconsignment.aspx?txtConsign={tracking}' },
    { id: 'xpressbees',  label: 'Xpressbees',         template: 'https://www.xpressbees.com/track?trackid={tracking}' },
    { id: 'ekart',       label: 'Ekart',              template: 'https://ekartlogistics.com/shipmenttrack/{tracking}' },
    { id: 'shiprocket',  label: 'Shiprocket',         template: 'https://shiprocket.co/tracking/{tracking}' },
    // No public tracking URL — admin pastes one in manually if there is one.
    { id: 'thirupathi',  label: 'Shree Tirupati Courier', template: null },
    { id: 'custom',      label: 'Custom URL',         template: null },
  ];
  const carrierById = (id) => CARRIERS.find(c => c.id === (id || '')) || CARRIERS[0];
  // All carriers route through track91's multi-carrier auto-detect page — one
  // URL covers India Post, Delhivery, Blue Dart, DTDC, Xpressbees, Ekart, etc.
  // (carrierId kept for signature compatibility; admin can still override with a
  // manually-pasted tracking_url). Local couriers with no public tracker (e.g.
  // Shree Tirupati) simply won't resolve — same as before.
  const buildTrackingUrl = (carrierId, tracking) => {
    // Strip anything that isn't part of a real AWB (spaces, stray < > brackets a
    // pasted placeholder leaves behind) — track91 can't auto-detect otherwise.
    const awb = (tracking || '').replace(/[^A-Za-z0-9]/g, '');
    if (!awb) return '';
    return `https://track91.com/track?n=${awb}`;
  };

  /**
   * Wire the auto-build + auto-save behaviour for the order tracking form
   * inside the Order drawer. The URL field starts in "auto" mode — typing a
   * tracking number or changing the carrier rebuilds it. Once the admin
   * edits the URL by hand it switches to manual and we stop touching it.
   */
  const wireOrderTrackingForm = (form) => {
    if (!form) return;
    const orderId = form.dataset.orderId;
    const carrierSel = form.querySelector('select[name="carrier"]');
    const trackInput = form.querySelector('input[name="tracking_number"]');
    const urlInput   = form.querySelector('input[name="tracking_url"]');
    const hint = form.parentElement?.querySelector('[data-adm-dr-ship-hint]');

    // URL is manual once it diverges from what auto-build would produce.
    const autoUrl = () => buildTrackingUrl(carrierSel.value, trackInput.value);
    let manual = !!urlInput.value && urlInput.value !== autoUrl();

    const refreshUrl = () => {
      if (manual) return;
      urlInput.value = autoUrl();
    };
    carrierSel.addEventListener('change', refreshUrl);
    trackInput.addEventListener('input', refreshUrl);
    urlInput.addEventListener('input', () => { manual = true; });

    const save = async () => {
      if (hint) { hint.textContent = 'Saving…'; hint.classList.remove('adm-dr-notes-hint--saved', 'adm-dr-notes-hint--error'); }
      const { error } = await adminLib.updateOrderTracking(orderId, {
        carrier:         carrierSel.value,
        tracking_number: trackInput.value,
        tracking_url:    urlInput.value,
      });
      if (!hint) return;
      if (error) {
        hint.textContent = 'Save failed: ' + (error.message || 'unknown');
        hint.classList.add('adm-dr-notes-hint--error');
      } else {
        hint.textContent = 'Saved.';
        hint.classList.add('adm-dr-notes-hint--saved');
      }
    };
    carrierSel.addEventListener('change', save);
    trackInput.addEventListener('blur', save);
    urlInput.addEventListener('blur', save);
  };

  async function openOrderDrawer(orderId) {
    openAdmDrawer({
      kind: 'order',
      id: orderId,
      // Orders carry the most detail (customer, items, address, payment,
      // tracking, notes) — give them the whole screen in a column layout.
      full: true,
      render: async (panel) => {
        const { data: o, error } = await adminLib.fetchOrderDetail(orderId);
        if (error || !o) {
          panel.querySelector('.adm-dr-body').innerHTML =
            `<div class="adm-dr-sec adm-dr-empty">Failed to load order: ${esc(error?.message || 'not found')}</div>`;
          return;
        }
        const shortId = String(o.id).slice(0, 8).toUpperCase();
        panel.querySelector('.adm-dr-eyebrow').textContent = 'Order';
        panel.querySelector('.adm-dr-title').textContent = `#${shortId}`;

        const payCls = o.payment_status || 'pending';
        const tel = (o.phone || '').replace(/[^+0-9]/g, '');
        const wa  = tel ? tel.replace(/^\+?/, '') : '';
        const fullAddr = [o.address_line1, o.address_line2, o.city, o.state, o.pincode].filter(Boolean).join(', ');

        panel.querySelector('.adm-dr-body').innerHTML = `
          <div class="adm-dr-sec">
            <div class="adm-dr-status-row">
              <span class="adm-badge adm-badge--${esc(o.status)}" id="adm-dr-badge-${esc(o.id)}">${STATUS_LABELS[o.status] || o.status}</span>
              <select class="adm-status-sel" data-order-id="${esc(o.id)}" data-current-status="${esc(o.status)}">
                ${ORDER_STATUSES.map(s => `<option value="${s}" ${o.status === s ? 'selected' : ''}>${STATUS_LABELS[s]}</option>`).join('')}
              </select>
              <span class="adm-dr-pay-chip adm-dr-pay-chip--${esc(payCls)}">${PAY_LABELS[payCls] || payCls}</span>
              <span style="flex:1"></span>
              <span style="font-size:12px;color:#6b7280">${esc(_fmtDT(o.created_at))}</span>
            </div>
          </div>

          <div class="adm-dr-sec">
            <div class="adm-dr-sec-hd">
              <span class="adm-dr-sec-title">Customer</span>
              ${o.user_id
                ? `<button class="adm-dr-sec-action" data-adm-dr-open-customer="${esc(o.user_id)}" type="button">View customer →</button>`
                : `<span style="font-size:11px;color:#9ca3af">Guest order</span>`}
            </div>
            <dl class="adm-dr-kv">
              <dt>Name</dt><dd>${esc(o.full_name || '—')}</dd>
              <dt>Phone</dt><dd>${tel ? `<a href="tel:${esc(tel)}">${esc(o.phone)}</a>` : '—'}</dd>
              <dt>Email</dt><dd>${o.email ? `<a href="mailto:${esc(o.email)}">${esc(o.email)}</a>` : '—'}</dd>
              ${wa ? `<dt>WhatsApp</dt><dd><a href="https://wa.me/${esc(wa)}" target="_blank" rel="noopener">Open chat ↗</a></dd>` : ''}
            </dl>
          </div>

          <div class="adm-dr-sec">
            <div class="adm-dr-sec-hd"><span class="adm-dr-sec-title">Items (${(o.order_items || []).length})</span></div>
            ${(o.order_items || []).map(i => `
              <div class="adm-dr-row">
                <div class="adm-dr-row-body">
                  <div class="adm-dr-row-title">${esc(i.product_name)}</div>
                  <div class="adm-dr-row-sub">${i.product_weight ? esc(i.product_weight) + ' · ' : ''}Qty ${i.quantity} · ${_fmtMoney(i.unit_price)} each</div>
                </div>
                <div class="adm-dr-row-right">${_fmtMoney(i.line_total)}</div>
              </div>
            `).join('')}
            <div class="adm-dr-totals">
              <div class="adm-dr-totals-row"><span>Subtotal</span><span>${_fmtMoney(o.subtotal)}</span></div>
              <div class="adm-dr-totals-row"><span>Shipping</span><span>${Number(o.shipping) > 0 ? _fmtMoney(o.shipping) : 'Free'}</span></div>
              <div class="adm-dr-totals-row adm-dr-totals-row--grand"><span>Total</span><span>${_fmtMoney(o.total)}</span></div>
            </div>
          </div>

          <div class="adm-dr-sec">
            <div class="adm-dr-sec-hd">
              <span class="adm-dr-sec-title">Shipping address</span>
              <button class="adm-dr-sec-action" data-adm-dr-copy="${esc(fullAddr)}" type="button">Copy</button>
            </div>
            <div style="font-size:13px;color:#1f2937;line-height:1.55">
              ${esc(o.address_line1)}${o.address_line2 ? '<br>' + esc(o.address_line2) : ''}<br>
              ${esc(o.city)}${o.state ? ', ' + esc(o.state) : ''} – ${esc(o.pincode)}
            </div>
          </div>

          <div class="adm-dr-sec">
            <div class="adm-dr-sec-hd"><span class="adm-dr-sec-title">Payment</span></div>
            <dl class="adm-dr-kv">
              <dt>Method</dt><dd>${esc(o.payment_method || 'razorpay')}</dd>
              <dt>Status</dt><dd><span class="adm-dr-pay-chip adm-dr-pay-chip--${esc(payCls)}">${PAY_LABELS[payCls] || payCls}</span></dd>
              ${o.razorpay_order_id   ? `<dt>RZP order</dt><dd class="adm-dr-mono">${esc(o.razorpay_order_id)}</dd>` : ''}
              ${o.razorpay_payment_id ? `<dt>RZP payment</dt><dd class="adm-dr-mono">${esc(o.razorpay_payment_id)}</dd>` : ''}
            </dl>
            ${o.razorpay_payment_id && payCls === 'paid' ? `
              <div class="adm-dr-refund" data-adm-dr-refund-host data-order-id="${esc(o.id)}" data-order-total="${esc(o.total)}">
                <button class="adm-dr-refund-trigger" type="button" data-adm-dr-refund-open>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
                  Issue refund
                </button>
                <div class="adm-dr-refund-form" hidden>
                  <p class="adm-dr-refund-note">Sends a Razorpay refund for this payment. Customer's money lands back in their account in 5–7 days.</p>
                  <label class="adm-dr-ship-field">
                    <span class="adm-dr-ship-label">Refund amount (₹)</span>
                    <input type="number" name="amount" min="1" max="${esc(o.total)}" step="0.01" value="${esc(o.total)}" />
                  </label>
                  <label class="adm-dr-ship-field">
                    <span class="adm-dr-ship-label">Note (optional, sent to Razorpay)</span>
                    <input type="text" name="reason" maxlength="200" placeholder="e.g. Damaged on arrival" />
                  </label>
                  <div class="adm-dr-refund-actions">
                    <button class="adm-dr-refund-cancel" type="button" data-adm-dr-refund-cancel>Cancel</button>
                    <button class="adm-dr-refund-submit" type="button" data-adm-dr-refund-submit>Refund ₹${esc(o.total)}</button>
                  </div>
                  <div class="adm-dr-refund-msg" hidden></div>
                </div>
              </div>
            ` : (payCls === 'refunded' ? `
              <div class="adm-dr-refund-already">Already refunded.</div>
            ` : '')}
          </div>

          <div class="adm-dr-sec" data-adm-dr-tracking-host>
            <div class="adm-dr-sec-hd">
              <span class="adm-dr-sec-title">Shipping &amp; tracking</span>
              ${o.shipped_at ? `<span style="font-size:12px;color:#6b7280">Shipped ${_fmtDT(o.shipped_at)}</span>` : ''}
            </div>
            <div class="adm-dr-ship-grid" data-adm-dr-ship-form data-order-id="${esc(o.id)}">
              <label class="adm-dr-ship-field">
                <span class="adm-dr-ship-label">Carrier</span>
                <select name="carrier">
                  ${CARRIERS.map(c => `<option value="${esc(c.id)}" ${o.carrier === c.id ? 'selected' : ''}>${esc(c.label)}</option>`).join('')}
                </select>
              </label>
              <label class="adm-dr-ship-field">
                <span class="adm-dr-ship-label">Tracking number / AWB</span>
                <input name="tracking_number" type="text" value="${esc(o.tracking_number || '')}" placeholder="e.g. 12345678901" />
              </label>
              <label class="adm-dr-ship-field adm-dr-ship-field--full">
                <span class="adm-dr-ship-label">Tracking URL <span style="color:#9ca3af;font-weight:400">(auto-filled for known carriers — override if needed)</span></span>
                <input name="tracking_url" type="url" value="${esc(o.tracking_url || '')}" placeholder="https://…" />
              </label>
            </div>
            <div class="adm-dr-notes-hint" data-adm-dr-ship-hint>Auto-saves when you click away.</div>
          </div>

          ${o.notes ? `
          <div class="adm-dr-sec">
            <div class="adm-dr-sec-hd"><span class="adm-dr-sec-title">Customer notes</span></div>
            <div style="font-size:13px;color:#374151;white-space:pre-wrap">${esc(o.notes)}</div>
          </div>` : ''}

          <div class="adm-dr-sec">
            <div class="adm-dr-sec-hd"><span class="adm-dr-sec-title">Internal admin notes</span></div>
            <textarea class="adm-dr-notes" data-adm-dr-order-notes="${esc(o.id)}" placeholder="Private notes — only visible to admins (e.g. called customer, asked to ship Tuesday).">${esc(o.admin_notes || '')}</textarea>
            <div class="adm-dr-notes-hint" data-adm-dr-notes-hint>Auto-saves when you click away.</div>
          </div>
        `;

        // Wire the tracking form: when carrier or number change, auto-build
        // the tracking URL (if the field hasn't been manually overridden) and
        // save to the DB.
        wireOrderTrackingForm(panel.querySelector('[data-adm-dr-ship-form]'));
      }
    });
  }

  async function openCustomerDrawer(userId) {
    openAdmDrawer({
      kind: 'customer',
      id: userId,
      wide: true,
      render: async (panel) => {
        const [detail, cartRes, ordersRes, msgsRes, addrRes] = await Promise.all([
          adminLib.fetchCustomerDetail(userId),
          adminLib.fetchCustomerCart(userId),
          adminLib.fetchCustomerOrders(userId),
          adminLib.fetchCustomerMessages(userId),
          adminLib.fetchCustomerAddresses(userId),
        ]);
        const profile = detail.profile;
        if (!profile) {
          panel.querySelector('.adm-dr-body').innerHTML =
            `<div class="adm-dr-sec adm-dr-empty">Customer not found.</div>`;
          return;
        }
        const summary = detail.summary || {};
        panel.querySelector('.adm-dr-eyebrow').textContent = profile.is_admin ? 'Admin' : 'Customer';
        panel.querySelector('.adm-dr-title').textContent = profile.full_name || profile.email || 'Unnamed customer';

        const tel = (profile.phone || '').replace(/[^+0-9]/g, '');
        const wa  = tel ? tel.replace(/^\+?/, '') : '';

        const cart = cartRes.data || [];
        const cartTotal = cart.reduce((s, ci) => {
          const sale = Number(ci.products?.sale_price ?? ci.products?.price ?? 0);
          return s + sale * ci.quantity;
        }, 0);
        const orders = ordersRes.data || [];
        const messages = msgsRes.data || [];
        const addresses = addrRes.data || [];

        panel.querySelector('.adm-dr-body').innerHTML = `
          <div class="adm-dr-sec">
            <div class="adm-dr-sec-hd">
              <span class="adm-dr-sec-title">Profile</span>
              <button class="adm-dr-sec-action" data-adm-dr-toggle-admin="${esc(profile.id)}" data-current-is-admin="${profile.is_admin}" type="button">${profile.is_admin ? 'Demote admin' : 'Make admin'}</button>
            </div>
            <dl class="adm-dr-kv">
              <dt>Name</dt><dd>${esc(profile.full_name || '—')}</dd>
              <dt>Email</dt><dd>${profile.email ? `<a href="mailto:${esc(profile.email)}">${esc(profile.email)}</a>` : '—'}</dd>
              <dt>Phone</dt><dd>${tel ? `<a href="tel:${esc(tel)}">${esc(profile.phone)}</a>` : '—'}</dd>
              ${wa ? `<dt>WhatsApp</dt><dd><a href="https://wa.me/${esc(wa)}" target="_blank" rel="noopener">Open chat ↗</a></dd>` : ''}
              <dt>Joined</dt><dd>${esc(_fmtDate(profile.created_at))}</dd>
            </dl>
          </div>

          <div class="adm-dr-sec">
            <div class="adm-dr-sec-hd"><span class="adm-dr-sec-title">Stats</span></div>
            <div class="adm-dr-stats">
              <div class="adm-dr-stat"><div class="adm-dr-stat-label">Orders</div><div class="adm-dr-stat-value">${summary.order_count || 0}</div></div>
              <div class="adm-dr-stat"><div class="adm-dr-stat-label">Lifetime spend</div><div class="adm-dr-stat-value">${_fmtMoney(summary.lifetime_spend || 0)}</div></div>
              <div class="adm-dr-stat"><div class="adm-dr-stat-label">Avg order</div><div class="adm-dr-stat-value">${_fmtMoney(summary.avg_order_value || 0)}</div></div>
              <div class="adm-dr-stat"><div class="adm-dr-stat-label">Last ordered</div><div class="adm-dr-stat-value" style="font-size:14px">${esc(_fmtDate(summary.last_ordered_at))}</div></div>
            </div>
          </div>

          <div class="adm-dr-sec">
            <div class="adm-dr-sec-hd">
              <span class="adm-dr-sec-title">Current cart (${cart.length})</span>
              ${cart.length ? `<span style="font-size:12px;color:#6b7280">${_fmtMoney(cartTotal)}</span>` : ''}
            </div>
            ${cart.length === 0
              ? `<div class="adm-dr-empty">Cart is empty.</div>`
              : cart.map(ci => {
                const p = ci.products || {};
                const img = p.main_image_url || p.photo || '';
                const sale = Number(p.sale_price ?? p.price ?? 0);
                return `
                  <div class="adm-dr-row">
                    ${img ? `<img class="adm-dr-row-thumb" src="${esc(img)}" alt="${esc(p.name || 'Product')}">` : `<div class="adm-dr-row-thumb"></div>`}
                    <div class="adm-dr-row-body">
                      <div class="adm-dr-row-title">${esc(p.name || 'Unknown product')}</div>
                      <div class="adm-dr-row-sub">Qty ${ci.quantity} · ${_fmtMoney(sale)} each</div>
                    </div>
                    <div class="adm-dr-row-right">${_fmtMoney(sale * ci.quantity)}</div>
                  </div>`;
              }).join('')}
          </div>

          <div class="adm-dr-sec">
            <div class="adm-dr-sec-hd"><span class="adm-dr-sec-title">Order history (${orders.length})</span></div>
            ${orders.length === 0
              ? `<div class="adm-dr-empty">No orders yet.</div>`
              : orders.map(o => {
                const shortId = String(o.id).slice(0, 8).toUpperCase();
                const items = (o.order_items || []).length;
                return `
                  <div class="adm-dr-row adm-dr-row--clickable" data-adm-dr-open-order="${esc(o.id)}">
                    <div class="adm-dr-row-body">
                      <div class="adm-dr-row-title">#${esc(shortId)} <span class="adm-badge adm-badge--${esc(o.status)}" style="font-size:9px;padding:1px 6px;margin-left:6px">${STATUS_LABELS[o.status] || o.status}</span></div>
                      <div class="adm-dr-row-sub">${esc(_fmtDT(o.created_at))} · ${items} item${items === 1 ? '' : 's'}</div>
                    </div>
                    <div class="adm-dr-row-right">${_fmtMoney(o.total)}</div>
                  </div>`;
              }).join('')}
          </div>

          <div class="adm-dr-sec">
            <div class="adm-dr-sec-hd"><span class="adm-dr-sec-title">Messages (${messages.length})</span></div>
            ${messages.length === 0
              ? `<div class="adm-dr-empty">No messages from this customer.</div>`
              : messages.map(m => `
                <div class="adm-dr-row" style="align-items:flex-start">
                  <div class="adm-dr-row-body">
                    <div class="adm-dr-row-title">${esc(m.topic || 'Enquiry')} <span style="font-size:11px;color:#9ca3af;font-weight:500;margin-left:4px">${esc(_fmtDT(m.created_at))}</span></div>
                    <div class="adm-dr-row-sub" style="white-space:pre-wrap;color:#374151">${esc(m.message)}</div>
                  </div>
                </div>`).join('')}
          </div>

          <div class="adm-dr-sec">
            <div class="adm-dr-sec-hd"><span class="adm-dr-sec-title">Address book (${addresses.length})</span></div>
            ${addresses.length === 0
              ? `<div class="adm-dr-empty">No saved addresses yet.</div>`
              : addresses.map(a => `
                <div class="adm-dr-row" style="align-items:flex-start">
                  <div class="adm-dr-row-body">
                    <div class="adm-dr-row-title">${esc(a.address_line1)}${a.address_line2 ? ', ' + esc(a.address_line2) : ''}</div>
                    <div class="adm-dr-row-sub">${esc(a.city)}${a.state ? ', ' + esc(a.state) : ''} – ${esc(a.pincode)} · last used ${esc(_fmtDate(a.last_used))}</div>
                  </div>
                </div>`).join('')}
          </div>

          <div class="adm-dr-sec">
            <div class="adm-dr-sec-hd"><span class="adm-dr-sec-title">Internal admin notes</span></div>
            <textarea class="adm-dr-notes" data-adm-dr-customer-notes="${esc(profile.id)}" placeholder="Private notes about this customer (preferences, complaints, VIP, etc).">${esc(profile.admin_notes || '')}</textarea>
            <div class="adm-dr-notes-hint" data-adm-dr-notes-hint>Auto-saves when you click away.</div>
          </div>
        `;
      }
    });
  }

  /* ── Orders tab ── */
  const renderAdmOrders = async () => {
    const body = $('#adm-content');
    if (!body) return;
    body.innerHTML = skelAdmTable(8);
    const filter = adminOrderFilter || null;
    const { data: orders, error } = await adminLib.fetchAllOrders({ status: filter });
    if (error) { body.innerHTML = `<div class="adm-error">Failed to load orders: ${esc(error.message)}</div>`; return; }

    body.innerHTML = `
      <div class="adm-section-title">Orders <span class="adm-count">(${orders.length})</span></div>
      <div class="adm-filter-row">
        ${['','pending','confirmed','shipped','delivered','cancelled'].map(s => `
          <button class="adm-chip ${adminOrderFilter===s?'active':''}" data-adm-status-filter="${s}">${s?STATUS_LABELS[s]:'All'}</button>`).join('')}
      </div>
      <div class="adm-table-wrap">
        <table class="adm-table">
          <thead><tr><th>Order ID</th><th>Customer</th><th>Items</th><th>Date</th><th>Total</th><th>Status</th><th>Action</th></tr></thead>
          <tbody id="adm-orders-tbody">
            ${orders.map(o => {
              const d = new Date(o.created_at).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'});
              const shortId = String(o.id).slice(0,8).toUpperCase();
              const itemCount = (o.order_items || []).length;
              return `<tr id="adm-row-${esc(o.id)}" class="adm-row--clickable" data-adm-open-order="${esc(o.id)}">
                <td class="adm-mono">#${esc(shortId)}</td>
                <td>${esc(o.full_name)}<div class="adm-sub">${esc(o.phone || '')}</div></td>
                <td>${itemCount} item${itemCount===1?'':'s'}</td>
                <td>${d}</td>
                <td>₹${Number(o.total).toLocaleString('en-IN')}</td>
                <td><span class="adm-badge adm-badge--${esc(o.status)}" id="adm-badge-${esc(o.id)}">${STATUS_LABELS[o.status]||o.status}</span></td>
                <td>
                  <select class="adm-status-sel" data-order-id="${esc(o.id)}" data-current-status="${esc(o.status)}">
                    ${ORDER_STATUSES.map(s=>`<option value="${s}" ${o.status===s?'selected':''}>${STATUS_LABELS[s]}</option>`).join('')}
                  </select>
                </td>
              </tr>`;
            }).join('')}
          </tbody>
        </table>
      </div>`;
  };

  /* ── Payments tab ──
   * Read-only view over paid + refunded orders: four KPI tiles (Today /
   * 7d / 30d / All-time within the 90-day window we fetch), a 14-day
   * daily breakdown table with a bar chart per row, and a recent-payments
   * table. Rows in the recent-payments table are clickable and open the
   * Order drawer so the admin can issue a refund from there. */
  const renderAdmPayments = async () => {
    const body = $('#adm-content');
    if (!body) return;
    body.innerHTML = skelAdmTable(8);

    const { data: rows, error } = await adminLib.fetchPaymentsOverview({ days: 90 });
    if (error) {
      body.innerHTML = `<div class="adm-section-title">Payments</div>
        <div class="adm-error">Failed to load payments: ${esc(error.message)}</div>`;
      return;
    }

    const inr = (n) => '₹' + Math.round(Number(n) || 0).toLocaleString('en-IN');
    const aov = (count, rev) => count > 0 ? inr(rev / count) : '—';
    const dayKey = (d) => {
      const x = new Date(d);
      return `${x.getFullYear()}-${String(x.getMonth()+1).padStart(2,'0')}-${String(x.getDate()).padStart(2,'0')}`;
    };
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const weekStart = new Date(startOfToday);  weekStart.setDate(weekStart.getDate() - 6);
    const monthStart = new Date(startOfToday); monthStart.setDate(monthStart.getDate() - 29);

    let today  = { count: 0, rev: 0, refunds: 0 };
    let week   = { count: 0, rev: 0, refunds: 0 };
    let month  = { count: 0, rev: 0, refunds: 0 };
    let total  = { count: 0, rev: 0, refunds: 0 };
    const daily = new Map();

    for (const o of rows) {
      const created = new Date(o.created_at);
      const amount = Number(o.total) || 0;
      const refunded = o.payment_status === 'refunded';
      const bump = (bucket) => {
        if (refunded) { bucket.refunds += amount; return; }
        bucket.count++;
        bucket.rev += amount;
      };
      bump(total);
      if (created >= startOfToday) bump(today);
      if (created >= weekStart)    bump(week);
      if (created >= monthStart)   bump(month);
      const k = dayKey(created);
      const slot = daily.get(k) || { count: 0, rev: 0 };
      if (!refunded) { slot.count++; slot.rev += amount; }
      daily.set(k, slot);
    }

    const dailyRows = [];
    for (let i = 0; i < 14; i++) {
      const d = new Date(startOfToday);
      d.setDate(d.getDate() - i);
      const slot = daily.get(dayKey(d)) || { count: 0, rev: 0 };
      dailyRows.push({ date: d, count: slot.count, rev: slot.rev });
    }
    const maxRev = Math.max(1, ...dailyRows.map(r => r.rev));

    const recent = rows.slice(0, 50);
    const PAY_METHOD_LABEL = { razorpay: 'Razorpay', cod: 'COD', bank_transfer: 'Bank' };

    const kpiIcons = [
      `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`,
      `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>`,
      `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>`,
      `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>`,
    ];
    const kpiColors = ['#166534', '#0369a1', '#7c3aed', '#b45309'];

    const kpiTile = (label, bucket, sub, idx) => `
      <div class="adm-pay-kpi">
        <div class="adm-pay-kpi-top">
          <div class="adm-pay-kpi-icon" style="background:${kpiColors[idx]}15;color:${kpiColors[idx]}">${kpiIcons[idx]}</div>
          <div class="adm-pay-kpi-label">${label}</div>
        </div>
        <div class="adm-pay-kpi-value">${inr(bucket.rev)}</div>
        <div class="adm-pay-kpi-meta">
          <span class="adm-pay-kpi-orders">${bucket.count} order${bucket.count===1?'':'s'}</span>
          <span class="adm-pay-kpi-dot">·</span>
          <span>AOV ${aov(bucket.count, bucket.rev)}</span>
          ${bucket.refunds > 0 ? `<span class="adm-pay-kpi-dot">·</span><span class="adm-pay-refund">−${inr(bucket.refunds)}</span>` : ''}
        </div>
        ${sub ? `<div class="adm-pay-kpi-tip">${sub}</div>` : ''}
      </div>`;

    const hasAnyData = rows.length > 0;
    const hasRecentPayments = recent.length > 0;

    body.innerHTML = `
      <div class="adm-overview-hd">
        <div>
          <h1 class="adm-overview-title">Payments</h1>
          <p class="adm-overview-sub">Revenue overview and transaction history.</p>
        </div>
        <span class="adm-pay-range">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          Last 90 days
        </span>
      </div>

      <div class="adm-pay-kpis">
        ${kpiTile('Today', today, '', 0)}
        ${kpiTile('Last 7 days', week, '', 1)}
        ${kpiTile('Last 30 days', month, '', 2)}
        ${kpiTile('Last 90 days', total, '', 3)}
      </div>

      <div class="adm-card">
        <div class="adm-card-hd">
          <div>
            <p class="adm-card-title">Daily breakdown</p>
            <p class="adm-card-sub">Last 14 days · bars scaled to busiest day</p>
          </div>
        </div>
        ${!hasAnyData ? `
          <div class="adm-pay-empty-chart">
            <div class="adm-pay-empty-bars">
              ${Array.from({length: 14}, (_, i) => `<div class="adm-pay-empty-bar" style="height:${12 + Math.random() * 40}px"></div>`).join('')}
            </div>
            <div class="adm-pay-empty-overlay">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" stroke-width="1.5"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
              <p>No payment data yet</p>
              <span>Revenue will show here once orders come in</span>
            </div>
          </div>
        ` : `
        <div class="adm-table-wrap">
          <table class="adm-table adm-pay-daily">
            <thead><tr><th>Date</th><th class="adm-pay-num">Orders</th><th class="adm-pay-num">Revenue</th><th class="adm-pay-num">AOV</th><th class="adm-pay-bar-col"></th></tr></thead>
            <tbody>
              ${dailyRows.map(r => {
                const pct = r.rev > 0 ? (r.rev / maxRev) * 100 : 0;
                const label = r.date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', weekday: 'short' });
                return `<tr${r.count === 0 ? ' class="adm-pay-row--empty"' : ''}>
                  <td>${esc(label)}</td>
                  <td class="adm-pay-num">${r.count}</td>
                  <td class="adm-pay-num"><strong>${inr(r.rev)}</strong></td>
                  <td class="adm-pay-num">${aov(r.count, r.rev)}</td>
                  <td class="adm-pay-bar-col"><div class="adm-pay-bar"><div class="adm-pay-bar-fill" style="width:${pct.toFixed(1)}%"></div></div></td>
                </tr>`;
              }).join('')}
            </tbody>
          </table>
        </div>`}
      </div>

      <div class="adm-card">
        <div class="adm-card-hd">
          <div>
            <p class="adm-card-title">Recent payments</p>
            <p class="adm-card-sub">${hasRecentPayments ? `${recent.length} transactions · click to open order` : 'No transactions yet'}</p>
          </div>
        </div>
        <div class="adm-table-wrap">
          ${!hasRecentPayments ? `
            <div class="adm-pay-empty-transactions">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" stroke-width="1.5">
                <rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/>
              </svg>
              <h3>No paid orders yet</h3>
              <p>Transactions will appear here once customers complete checkout.</p>
            </div>
          ` : `
          <table class="adm-table adm-pay-recent">
            <thead><tr><th>When</th><th>Order</th><th>Customer</th><th class="adm-pay-num">Amount</th><th>Method</th><th>Razorpay ID</th><th>Status</th></tr></thead>
            <tbody>
              ${recent.map(o => {
                const dt = _fmtDT(o.created_at);
                const shortId = String(o.id).slice(0, 8).toUpperCase();
                const refunded = o.payment_status === 'refunded';
                const methodLbl = PAY_METHOD_LABEL[o.payment_method] || (o.payment_method || '—');
                return `<tr data-adm-open-order="${esc(o.id)}" class="adm-pay-recent-row${refunded ? ' adm-pay-recent-row--refunded' : ''}">
                  <td>${esc(dt)}</td>
                  <td class="adm-mono">#${esc(shortId)}</td>
                  <td>${esc(o.full_name || o.email || '—')}</td>
                  <td class="adm-pay-num"><strong>${inr(o.total)}</strong></td>
                  <td>${esc(methodLbl)}</td>
                  <td class="adm-mono adm-pay-rzp">${o.razorpay_payment_id ? esc(o.razorpay_payment_id) : '—'}</td>
                  <td>
                    <span class="adm-dr-pay-chip adm-dr-pay-chip--${esc(o.payment_status || 'paid')}">${PAY_LABELS[o.payment_status] || o.payment_status || 'Paid'}</span>
                  </td>
                </tr>`;
              }).join('')}
            </tbody>
          </table>`}
        </div>
      </div>
    `;
  };

  /* ── Products tab — WordPress-style editor ── */

  const stockBadgeFor = (qty, thr) => {
    if (qty <= 0)        return { cls: 'cancelled', label: 'Out' };
    if (qty <= (thr|0))  return { cls: 'pending',   label: `Only ${qty}` };
    return                       { cls: 'delivered', label: `${qty} in stock` };
  };

  /* ── Dashboard tab ── (range / metric live here so a tab switch keeps your last view) */
  let admAnDays = 30;
  let admAnMetric = 'revenue';
  let admAnRun = 0; // newest request wins when range buttons are clicked quickly
  let admAnData = null;
  const renderAdmDashboard = async ({ cached = false } = {}) => {
    const body = $('#adm-content');
    if (!body) return;
    const run = ++admAnRun;
    if (!cached || !admAnData) {
      body.innerHTML = skelAdmDashboard();
      const raw = await adminLib.fetchInsightsData({ days: admAnDays });
      if (run !== admAnRun || adminTab !== 'dashboard') return;
      if (raw.error) { body.innerHTML = `<div class="adm-error">Failed: ${esc(raw.error.message)}</div>`; return; }
      admAnData = { insights: buildInsights(raw, admAnDays), stamp: new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) };
    }
    body.innerHTML = dashboardPage(admAnData.insights, { metric: admAnMetric, stamp: admAnData.stamp });
    wireTrendCharts(body);
    body.querySelectorAll('[data-an-restock-save]').forEach(btn => btn.addEventListener('click', async () => {
      const pid = parseInt(btn.dataset.anRestockSave);
      const qty = parseInt(body.querySelector(`[data-an-restock-id="${pid}"]`)?.value);
      if (!qty || qty < 1) return;
      btn.disabled = true; btn.textContent = '…';
      const { error } = await adminLib.restockProduct(pid, qty);
      if (error) { btn.textContent = 'Retry'; btn.disabled = false; return; }
      showToast('Stock updated');
      renderAdmDashboard();
    }));
    body.querySelectorAll('[data-an-range]').forEach(b => b.addEventListener('click', () => {
      const d = Number(b.dataset.anRange);
      if (d !== admAnDays) { admAnDays = d; renderAdmDashboard(); }
    }));
    body.querySelectorAll('[data-an-metric]').forEach(b => b.addEventListener('click', () => {
      admAnMetric = b.dataset.anMetric;
      renderAdmDashboard({ cached: true });
    }));
    body.querySelector('[data-an-refresh]')?.addEventListener('click', () => renderAdmDashboard());
  };

  const renderAdmProducts = async () => {
    const body = $('#adm-content');
    if (!body) return;
    body.innerHTML = skelAdmTable(10);
    const { data: prods, error } = await adminLib.fetchAllProductsAdmin();
    if (error) { body.innerHTML = `<div class="adm-error">Failed: ${esc(error.message)}</div>`; return; }

    const { data: pins } = await adminLib.fetchPinnedItems();
    const pinnedProductIds = new Set((pins || []).filter(r => r.item_type === 'product').map(r => r.item_id));

    body.innerHTML = `
      <div class="adm-section-title">Products <span class="adm-count">(${prods.length})</span> <span class="adm-count adm-count--pinned" id="adm-prod-pin-count">${pinnedProductIds.size} pinned</span>
        <div class="adm-title-actions">
          <button class="adm-add-btn adm-add-btn--secondary" id="adm-csv-export">Export CSV</button>
          <label class="adm-add-btn adm-add-btn--secondary" style="cursor:pointer">
            Import CSV <input type="file" accept=".csv" id="adm-csv-import" hidden />
          </label>
          <button class="adm-add-btn" id="adm-add-product">+ Add Product</button>
        </div>
      </div>
      <div id="adm-prod-form-container"></div>
      <div class="adm-table-wrap">
        <table class="adm-table">
          <thead><tr><th>ID</th><th>Image</th><th>Name</th><th>Category</th><th>Sale</th><th>MRP</th><th>Stock</th><th>Actions</th></tr></thead>
          <tbody>
            ${prods.map(p => {
              const hasVariants = !!p.has_variants && Array.isArray(p.variants) && p.variants.length > 0;
              const sale = hasVariants
                ? Math.min(...p.variants.map(v => Number(v.sale_price)))
                : Number(p.sale_price ?? p.price);
              const mrp  = hasVariants
                ? (p.variants.some(v => v.mrp != null)
                    ? Math.max(...p.variants.map(v => Number(v.mrp ?? v.sale_price)))
                    : null)
                : (p.mrp != null ? Number(p.mrp) : (p.original_price != null ? Number(p.original_price) : null));
              const qty  = hasVariants
                ? p.variants.reduce((s, v) => s + (Number(v.stock_qty) || 0), 0)
                : (p.stock_qty != null ? Number(p.stock_qty) : (p.in_stock ? 1 : 0));
              const thr  = p.low_stock_threshold ?? 5;
              const sb   = stockBadgeFor(qty, thr);
              const discount = (mrp && mrp > sale) ? Math.round(((mrp - sale) / mrp) * 100) : null;
              const thumb = hasVariants
                ? (p.variants[0].main_image_url || p.main_image_url || p.photo || '')
                : (p.main_image_url || p.photo || '');
              const variantsBadge = hasVariants
                ? `<span class="adm-variant-badge">${p.variants.length} variants</span>`
                : '';
              const bundleBadge = p.is_bundle
                ? `<span class="adm-variant-badge adm-variant-badge--bundle">${(p.bundle_items || []).length} in bundle</span>`
                : '';
              return `<tr>
                <td class="adm-mono">${p.id}</td>
                <td>${thumb ? `<img src="${esc(thumb)}" class="adm-prod-thumb" alt="${esc(p.name)}" />` : `<div class="adm-prod-thumb adm-prod-thumb--empty">—</div>`}</td>
                <td>${esc(p.name)} ${variantsBadge}${bundleBadge}<div class="adm-sub">${esc(p.subtitle||p.weight||'')}</div></td>
                <td>${p.categories?esc(p.categories.name):(p.category_id ?? '—')}</td>
                <td>₹${sale.toLocaleString('en-IN')}${discount ? ` <span class="adm-pe-pct-pill">−${discount}%</span>` : ''}</td>
                <td>${mrp != null ? '₹'+mrp.toLocaleString('en-IN') : '—'}</td>
                <td><span class="adm-badge adm-badge--${sb.cls}">${sb.label}</span></td>
                <td class="adm-actions-cell">
                  <button class="adm-pin-btn${pinnedProductIds.has(p.id) ? ' is-pinned' : ''}" data-adm-pin-product="${p.id}" title="${pinnedProductIds.has(p.id) ? 'Unpin from top of products page' : 'Pin to top of products page'}" aria-pressed="${pinnedProductIds.has(p.id)}">${admIcon('pin', 14)} <span class="adm-pin-label">${pinnedProductIds.has(p.id) ? 'Pinned' : 'Pin'}</span></button>
                  <button class="adm-edit-btn" data-adm-edit-product="${p.id}">${admIcon('edit', 14)} Edit</button>
                  <button class="adm-del-btn" data-adm-del-product="${p.id}">${admIcon('trash', 14)}</button>
                </td>
              </tr>`;
            }).join('')}
          </tbody>
        </table>
      </div>`;

    body._products = prods;

    $('#adm-add-product').addEventListener('click', () => openProductEditor({}));

    // CSV Export
    $('#adm-csv-export')?.addEventListener('click', () => {
      const csv = adminLib.exportProductsCsv(prods);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `products-${new Date().toISOString().slice(0,10)}.csv`;
      a.click(); URL.revokeObjectURL(url);
      showToast(`Exported ${prods.length} products`);
    });

    // CSV Import
    $('#adm-csv-import')?.addEventListener('change', async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const text = await file.text();
      const rows = adminLib.parseProductsCsv(text);
      if (rows.length === 0) { showToast('No valid rows found in CSV'); return; }
      if (!confirm(`Import ${rows.length} products? Existing products with matching IDs will be updated.`)) return;
      let ok = 0, fail = 0;
      for (const row of rows) {
        const { error } = await adminLib.upsertProduct(row);
        if (error) fail++; else ok++;
      }
      showToast(`Imported: ${ok} succeeded, ${fail} failed`);
      await clearCatalogCache();
      renderAdmProducts();
    });
  };

  /**
   * WordPress-style product editor. Single form factory used for both
   * "Add new" and "Edit" — kills the old duplicate inline modal HTML.
   * `p` is empty `{}` for create, or an existing product row for edit.
   */
  const openProductEditor = (p = {}) => {
    let c = $('#adm-prod-form-container');
    if (!c) {
      // Container is normally rendered by renderAdmProducts. If it's missing
      // (timing race, or editor invoked from a non-products view), attach
      // one to body so the editor still opens instead of silently failing.
      c = document.createElement('div');
      c.id = 'adm-prod-form-container';
      document.body.appendChild(c);
    }
    const isEdit = !!p.id;
    const initialTags = Array.isArray(p.tags) ? p.tags : [];
    const initialGallery = Array.isArray(p.gallery_image_urls) ? p.gallery_image_urls : [];
    const initialSale = p.sale_price ?? p.price ?? '';
    const initialMrp  = p.mrp ?? p.original_price ?? '';
    const initialStock = p.stock_qty != null ? p.stock_qty : (p.in_stock === false ? 0 : 0);
    const initialThresh = p.low_stock_threshold ?? 5;
    const initialMain = p.main_image_url || p.photo || '';
    const initialHasVariants = !!p.has_variants;
    const initialVariants = Array.isArray(p.variants) ? p.variants : [];
    const isPinned = isEdit && PINNED.some(r => r.item_type === 'product' && r.item_id === p.id);

    c.innerHTML = `
      <div class="adm-pe-overlay" id="adm-pe-overlay">
        <div class="adm-pe-panel">
          <div class="adm-pe-head">
            <div>
              <div class="adm-pe-eyebrow">Products</div>
              <div class="adm-pe-title">${isEdit ? 'Edit product' : 'Add new product'}</div>
            </div>
            <button type="button" class="adm-pe-close" id="adm-pe-close" aria-label="Close">✕</button>
          </div>
          <form class="adm-pe-form" id="adm-pe-form" novalidate>
            <div class="adm-pe-cols">
              <!-- LEFT: main content -->
              <div class="adm-pe-col adm-pe-col--main">
                <section class="adm-pe-section">
                  <input class="adm-pe-input adm-pe-input--title" name="name" placeholder="Product name" value="${esc(p.name||'')}" required />
                  <div class="adm-pe-permalink">
                    <span class="adm-pe-permalink-label">Permalink:</span>
                    <span class="adm-pe-permalink-url">/product/</span>
                    <input class="adm-pe-input adm-pe-input--slug" name="slug" placeholder="auto-generated" value="${esc(p.slug||'')}" />
                  </div>
                  <div class="adm-pe-section-hint">Auto-fills from the name. Edit it to override.</div>
                </section>

                <section class="adm-pe-section">
                  <div class="adm-pe-section-title">Short description</div>
                  <div class="adm-pe-section-hint">Shown on the product page and in the WhatsApp share message. No length limit. (SEO description is auto-generated from the full description.)</div>
                  <textarea class="adm-pe-input adm-pe-textarea adm-pe-textarea--short" name="short_description" placeholder="One or two lines that hook the customer…">${esc(p.short_description||'')}</textarea>
                  <div class="adm-pe-counter" data-counter-for="short_description"><span data-count>0</span> characters</div>
                </section>

                <section class="adm-pe-section">
                  <div class="adm-pe-section-title">Full description</div>
                  <div class="adm-pe-section-hint">Plain text. Leave a blank line between paragraphs to keep them separate on the website.</div>
                  <textarea class="adm-pe-input adm-pe-textarea adm-pe-textarea--long" name="description" placeholder="Tell customers what makes this product special — sourcing, benefits, why it's different.">${esc(p.description||'')}</textarea>
                </section>

                <section class="adm-pe-section">
                  <div class="adm-pe-section-title">How to use</div>
                  <div class="adm-pe-section-hint">Plain text. Leave a blank line between steps/paragraphs. Shown in its own section on the product page. Leave empty to hide.</div>
                  <textarea class="adm-pe-input adm-pe-textarea adm-pe-textarea--long" name="how_to_use" placeholder="e.g. Take 1 teaspoon daily with warm water, preferably in the morning.">${esc(p.how_to_use||'')}</textarea>
                </section>

                <section class="adm-pe-section adm-pe-section--single" id="adm-pe-single-pricing">
                  <div class="adm-pe-section-title">Pricing</div>
                  <div class="adm-pe-grid adm-pe-grid--2">
                    <label class="adm-pe-field">
                      <span class="adm-pe-label">MRP (₹) <span class="adm-pe-label-hint">Original list price — shown struck through</span></span>
                      <input class="adm-pe-input" name="mrp" type="number" min="0" step="0.01" placeholder="e.g. 350" value="${initialMrp}" />
                    </label>
                    <label class="adm-pe-field">
                      <span class="adm-pe-label">Sale price (₹) <span class="adm-pe-label-req">required</span></span>
                      <input class="adm-pe-input" name="sale_price" type="number" min="0" step="0.01" placeholder="e.g. 280" value="${initialSale}" />
                    </label>
                  </div>
                  <div class="adm-pe-discount-preview" id="adm-pe-discount-preview" hidden></div>
                </section>

                <section class="adm-pe-section adm-pe-section--single" id="adm-pe-single-inventory">
                  <div class="adm-pe-section-title">Inventory</div>
                  <div class="adm-pe-grid adm-pe-grid--3">
                    <label class="adm-pe-field">
                      <span class="adm-pe-label">Stock quantity</span>
                      <input class="adm-pe-input" name="stock_qty" type="number" min="0" step="1" placeholder="0" value="${initialStock}" />
                    </label>
                    <label class="adm-pe-field">
                      <span class="adm-pe-label">Low-stock alert when ≤</span>
                      <input class="adm-pe-input" name="low_stock_threshold" type="number" min="0" step="1" placeholder="5" value="${initialThresh}" />
                    </label>
                    <div class="adm-pe-field">
                      <span class="adm-pe-label">Live preview</span>
                      <div class="adm-pe-stock-preview" id="adm-pe-stock-preview">—</div>
                    </div>
                  </div>
                </section>

                <section class="adm-pe-section adm-pe-section--single" id="adm-pe-single-shipping">
                  <div class="adm-pe-section-title">Shipping</div>
                  <label class="adm-pe-variants-toggle">
                    <input type="checkbox" id="adm-pe-free-shipping" name="free_shipping" ${p.free_shipping ? 'checked' : ''} />
                    <span>Free shipping on this product</span>
                  </label>
                  <div class="adm-pe-section-hint">
                    When on, the ₹100 flat fee is waived for a cart where every
                    item is a free-shipping product. If the cart also has a
                    product without this flag, the flat fee still applies.
                  </div>
                </section>

                <section class="adm-pe-section" id="adm-pe-section-variants">
                  <div class="adm-pe-section-title">Variants
                    <span class="adm-pe-summary-hint">(sizes, weights, flavours…)</span>
                  </div>
                  <label class="adm-pe-variants-toggle">
                    <input type="checkbox" id="adm-pe-has-variants" name="has_variants" ${initialHasVariants ? 'checked' : ''} />
                    <span>This product has multiple sizes / variants</span>
                  </label>
                  <div class="adm-pe-section-hint">
                    When on, the Pricing, Inventory, Main image, and Gallery
                    sections above are managed <em>per variant</em> instead
                    of on the product itself.
                  </div>
                  <div class="adm-pe-variants" id="adm-pe-variants" hidden></div>
                  <input type="hidden" id="adm-pe-variants-data" name="variants_data" value="" />
                  <button type="button" class="adm-pe-btn adm-pe-btn--outline" id="adm-pe-add-variant" hidden>+ Add variant</button>
                </section>

                <section class="adm-pe-section" id="adm-pe-section-bundle">
                  <div class="adm-pe-section-title">Bundle
                    <span class="adm-pe-summary-hint">(combo of multiple products sold as one)</span>
                  </div>
                  <label class="adm-pe-variants-toggle">
                    <input type="checkbox" id="adm-pe-is-bundle" name="is_bundle" ${p.is_bundle ? 'checked' : ''} />
                    <span>This product is a bundle / combo</span>
                  </label>
                  <div class="adm-pe-section-hint">
                    When on, the Pricing / Inventory / Images above describe
                    the <em>combo pack itself</em>. Add the member products
                    below — each one's stock is decremented when the bundle
                    is sold. Mutually exclusive with variants.
                  </div>
                  <div class="adm-pe-variants" id="adm-pe-bundle-items" hidden></div>
                  <button type="button" class="adm-pe-btn adm-pe-btn--outline" id="adm-pe-add-bundle-item" hidden>+ Add member</button>
                </section>

                <details class="adm-pe-section adm-pe-section--collapsible">
                  <summary class="adm-pe-section-title">SEO settings <span class="adm-pe-summary-hint">(optional — auto-generated from the product copy if left blank)</span></summary>
                  <div class="adm-pe-section-body">
                    <label class="adm-pe-field">
                      <span class="adm-pe-label">Meta title</span>
                      <input class="adm-pe-input" name="meta_title" maxlength="70" placeholder="Auto: name + key phrase from short description" value="${esc(p.meta_title||'')}" />
                      <div class="adm-pe-counter" data-counter-for="meta_title"><span data-count>0</span>/70 — sweet spot: 50–60</div>
                    </label>
                    <label class="adm-pe-field">
                      <span class="adm-pe-label">Meta description</span>
                      <textarea class="adm-pe-input adm-pe-textarea adm-pe-textarea--short" name="meta_description" maxlength="200" placeholder="Auto: summary of the full description">${esc(p.meta_description||'')}</textarea>
                      <div class="adm-pe-counter" data-counter-for="meta_description"><span data-count>0</span>/200 — sweet spot: 150–160</div>
                    </label>
                  </div>
                </details>
              </div>

              <!-- RIGHT: sidebar -->
              <div class="adm-pe-col adm-pe-col--side">
                <section class="adm-pe-side-card">
                  <div class="adm-pe-side-card-title">Publish</div>
                  <div class="adm-pe-side-card-body">
                    <button type="submit" class="adm-pe-btn adm-pe-btn--primary" id="adm-pe-save">${isEdit ? 'Update product' : 'Publish product'}</button>
                    ${isEdit ? `<button type="button" class="adm-pe-btn adm-pe-btn--outline adm-pin-btn${isPinned ? ' is-pinned' : ''}" data-adm-pin-product="${p.id}" title="${isPinned ? 'Unpin from top of products page' : 'Pin to top of products page'}" aria-pressed="${isPinned}">${admIcon('pin', 14)} <span class="adm-pin-label">${isPinned ? 'Pinned' : 'Pin to top'}</span></button>` : ''}
                    ${isEdit ? `<button type="button" class="adm-pe-btn adm-pe-btn--danger" id="adm-pe-delete">Delete</button>` : ''}
                    <div class="adm-pe-side-card-msg" id="adm-pe-msg" hidden></div>
                  </div>
                </section>

                <section class="adm-pe-side-card adm-pe-section--single" id="adm-pe-single-main-img">
                  <div class="adm-pe-side-card-title">Main image
                    <div class="adm-pe-side-card-hint">Featured image for WhatsApp / social shares + OG tags.</div>
                  </div>
                  <div class="adm-pe-side-card-body">
                    <div class="adm-pe-main-preview" id="adm-pe-main-preview">
                      ${initialMain
                        ? `<img src="${esc(initialMain)}" alt="" />`
                        : `<div class="adm-pe-img-empty">No image yet</div>`}
                    </div>
                    <label class="adm-pe-btn adm-pe-btn--outline adm-pe-upload-btn" for="adm-pe-main-file">Choose file…</label>
                    <input type="file" id="adm-pe-main-file" accept="image/*" hidden />
                    <input class="adm-pe-input adm-pe-input--sm" type="text" name="main_image_url" value="${esc(initialMain)}" placeholder="…or paste an image URL" />
                    <div class="adm-pe-upload-status" id="adm-pe-main-status"></div>
                  </div>
                </section>

                <section class="adm-pe-side-card adm-pe-section--single" id="adm-pe-single-gallery">
                  <div class="adm-pe-side-card-title">Gallery
                    <div class="adm-pe-side-card-hint">Additional product photos shown in the gallery strip.</div>
                  </div>
                  <div class="adm-pe-side-card-body">
                    <div class="adm-pe-gallery" id="adm-pe-gallery"></div>
                    <label class="adm-pe-btn adm-pe-btn--outline adm-pe-upload-btn" for="adm-pe-gallery-file">+ Add images</label>
                    <input type="file" id="adm-pe-gallery-file" accept="image/*" multiple hidden />
                    <div class="adm-pe-upload-status" id="adm-pe-gallery-status"></div>
                    <input type="hidden" name="gallery_image_urls" id="adm-pe-gallery-urls" value="${esc(initialGallery.join('|'))}" />
                  </div>
                </section>

                <section class="adm-pe-side-card">
                  <div class="adm-pe-side-card-title">Organization</div>
                  <div class="adm-pe-side-card-body">
                    <label class="adm-pe-field">
                      <span class="adm-pe-label">Category</span>
                      <select class="adm-pe-input" name="category_id">
                        ${CATEGORIES.map(cc=>`<option value="${cc.id}" ${(p.category_id||p.categoryId)===cc.id?'selected':''}>${esc(cc.name)}</option>`).join('')}
                      </select>
                    </label>
                    <label class="adm-pe-field">
                      <span class="adm-pe-label">Tags <span class="adm-pe-label-hint">Comma-separated keywords for search + SEO.</span></span>
                      <input class="adm-pe-input adm-pe-tags-input" id="adm-pe-tags-input" placeholder="e.g. honey, raw, organic" value="${esc(initialTags.join(', '))}" />
                      <div class="adm-pe-tags-chips" id="adm-pe-tags-chips"></div>
                      <input type="hidden" name="tags" id="adm-pe-tags-hidden" value="${esc(initialTags.join('|'))}" />
                    </label>
                    <label class="adm-pe-field">
                      <span class="adm-pe-label">Weight / Size</span>
                      <input class="adm-pe-input" name="weight" placeholder="250ml, 500g…" value="${esc(p.weight||'')}" />
                    </label>
                    <label class="adm-pe-field">
                      <span class="adm-pe-label">Badge <span class="adm-pe-label-hint">Optional callout shown on product cards.</span></span>
                      <input class="adm-pe-input" name="badge" placeholder="Bestseller, New, Premium…" value="${esc(p.badge||'')}" />
                    </label>
                    <label class="adm-pe-field">
                      <span class="adm-pe-label">Subtitle <span class="adm-pe-label-hint">Optional tagline under the name.</span></span>
                      <input class="adm-pe-input" name="subtitle" value="${esc(p.subtitle||'')}" />
                    </label>
                    <label class="adm-pe-field">
                      <span class="adm-pe-label">Illustration fallback <span class="adm-pe-label-hint">Used when no main image is set.</span></span>
                      <select class="adm-pe-input" name="img_key">
                        ${Object.keys(ILLUSTRATIONS).map(k=>`<option value="${k}" ${(p.img_key||p.img)===k?'selected':''}>${k}</option>`).join('')}
                      </select>
                    </label>
                  </div>
                </section>
              </div>
            </div>
          </form>
        </div>
      </div>`;

    wireProductEditor(p);
  };

  const wireProductEditor = (existing) => {
    const overlay = $('#adm-pe-overlay');
    const form = $('#adm-pe-form');
    if (!overlay || !form) return;

    // Set when a pin toggle completes inside the drawer, so close() can repaint
    // the admin list row (otherwise it keeps the stale .is-pinned state).
    let pinChangedInDrawer = false;

    const close = async () => {
      if (pinChangedInDrawer) await renderAdmProducts();
      const c = $('#adm-prod-form-container'); if (c) c.innerHTML = '';
    };
    $('#adm-pe-close')?.addEventListener('click', close);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

    // The drawer's Publish pin button is toggled by the delegated admin click
    // handler (shared [data-adm-pin-product] branch). This listener only records
    // that a toggle happened so close() can repaint the stale list row.
    form.querySelector('[data-adm-pin-product]')?.addEventListener('click', () => {
      pinChangedInDrawer = true;
    });

    // Char counters
    form.querySelectorAll('[data-counter-for]').forEach(counter => {
      const fieldName = counter.dataset.counterFor;
      const field = form.querySelector(`[name="${fieldName}"]`);
      const out = counter.querySelector('[data-count]');
      if (!field || !out) return;
      const sync = () => { out.textContent = (field.value || '').length; };
      field.addEventListener('input', sync);
      sync();
    });

    // Auto-slug from name (stops auto-syncing once the user edits slug manually)
    const nameInp = form.querySelector('[name="name"]');
    const slugInp = form.querySelector('[name="slug"]');
    if (nameInp && slugInp) {
      let auto = !slugInp.value || slugInp.value === slugify(existing?.name);
      slugInp.addEventListener('input', () => { auto = false; });
      nameInp.addEventListener('input', () => {
        if (auto) slugInp.value = slugify(nameInp.value);
      });
    }

    // Discount preview
    const mrpInp = form.querySelector('[name="mrp"]');
    const saleInp = form.querySelector('[name="sale_price"]');
    const discountPrev = $('#adm-pe-discount-preview');
    const refreshDiscount = () => {
      const mrp = parseFloat(mrpInp.value);
      const sale = parseFloat(saleInp.value);
      if (!Number.isFinite(mrp) || !Number.isFinite(sale) || mrp <= sale || mrp <= 0) {
        discountPrev.hidden = true; return;
      }
      const pct = Math.round(((mrp - sale) / mrp) * 100);
      discountPrev.hidden = false;
      discountPrev.innerHTML = `Customers see <strong>₹${sale.toLocaleString('en-IN')}</strong> <s>₹${mrp.toLocaleString('en-IN')}</s> — <span class="adm-pe-pct-pill">−${pct}%</span>`;
    };
    [mrpInp, saleInp].forEach(i => i?.addEventListener('input', refreshDiscount));
    refreshDiscount();

    // Stock live preview
    const stockInp = form.querySelector('[name="stock_qty"]');
    const threshInp = form.querySelector('[name="low_stock_threshold"]');
    const stockPrev = $('#adm-pe-stock-preview');
    const refreshStock = () => {
      const qty = parseInt(stockInp.value, 10) || 0;
      const thr = parseInt(threshInp.value, 10) || 0;
      stockPrev.className = 'adm-pe-stock-preview';
      if (qty <= 0) {
        stockPrev.textContent = 'Out of stock';
        stockPrev.classList.add('adm-pe-stock-preview--out');
      } else if (qty <= thr) {
        stockPrev.textContent = `Only ${qty} left`;
        stockPrev.classList.add('adm-pe-stock-preview--low');
      } else {
        stockPrev.textContent = 'In stock';
        stockPrev.classList.add('adm-pe-stock-preview--ok');
      }
    };
    [stockInp, threshInp].forEach(i => i?.addEventListener('input', refreshStock));
    refreshStock();

    // Tags chips
    const tagsInput  = $('#adm-pe-tags-input');
    const tagsChips  = $('#adm-pe-tags-chips');
    const tagsHidden = $('#adm-pe-tags-hidden');
    const refreshTags = () => {
      const list = (tagsInput.value || '').split(',').map(t => t.trim()).filter(Boolean);
      tagsHidden.value = list.join('|');
      tagsChips.innerHTML = list.map(t => `<span class="adm-pe-tag-chip">${esc(t)}</span>`).join('');
    };
    tagsInput?.addEventListener('input', refreshTags);
    refreshTags();

    // Main image upload
    const mainPrev    = $('#adm-pe-main-preview');
    const mainUrlInp  = form.querySelector('[name="main_image_url"]');
    const mainStatus  = $('#adm-pe-main-status');
    const mainFile    = $('#adm-pe-main-file');
    const setMainPreview = (url) => {
      mainPrev.innerHTML = url
        ? `<img src="${esc(url)}" alt="" />`
        : `<div class="adm-pe-img-empty">No image yet</div>`;
    };
    mainUrlInp?.addEventListener('input', () => setMainPreview(mainUrlInp.value.trim()));
    mainFile?.addEventListener('change', async () => {
      const file = mainFile.files?.[0];
      if (!file) return;
      mainStatus.textContent = 'Converting & uploading…';
      mainStatus.className = 'adm-pe-upload-status adm-pe-upload-status--busy';
      const { url, error } = await adminLib.uploadProductImage(file, { productId: existing?.id || 'new', kind: 'main', name: nameInp.value });
      mainFile.value = '';
      if (error) {
        mainStatus.textContent = 'Failed: ' + (error.message || error);
        mainStatus.className = 'adm-pe-upload-status adm-pe-upload-status--err';
        return;
      }
      mainUrlInp.value = url;
      setMainPreview(url);
      mainStatus.textContent = 'Uploaded ✓';
      mainStatus.className = 'adm-pe-upload-status adm-pe-upload-status--ok';
    });

    // Gallery
    const galleryUrls   = (existing?.gallery_image_urls || []).slice();
    const galleryHidden = $('#adm-pe-gallery-urls');
    const galleryWrap   = $('#adm-pe-gallery');
    const galleryStatus = $('#adm-pe-gallery-status');
    const galleryFile   = $('#adm-pe-gallery-file');
    const refreshGallery = () => {
      galleryHidden.value = galleryUrls.join('|');
      galleryWrap.innerHTML = galleryUrls.length === 0
        ? `<div class="adm-pe-img-empty adm-pe-img-empty--gallery">No gallery images yet</div>`
        : galleryUrls.map((url, idx) => `
            <div class="adm-pe-gallery-item">
              <img src="${esc(url)}" alt="" />
              <button type="button" class="adm-pe-gallery-remove" data-gallery-remove="${idx}" aria-label="Remove">✕</button>
            </div>`).join('');
    };
    galleryWrap?.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-gallery-remove]');
      if (!btn) return;
      const idx = parseInt(btn.dataset.galleryRemove, 10);
      galleryUrls.splice(idx, 1);
      refreshGallery();
    });
    galleryFile?.addEventListener('change', async () => {
      const files = Array.from(galleryFile.files || []);
      if (files.length === 0) return;
      galleryStatus.textContent = `Uploading ${files.length} image${files.length>1?'s':''}…`;
      galleryStatus.className = 'adm-pe-upload-status adm-pe-upload-status--busy';
      let ok = 0, lastErr = null;
      for (const f of files) {
        const { url, error } = await adminLib.uploadProductImage(f, { productId: existing?.id || 'new', kind: 'gallery', name: nameInp.value });
        if (error) { lastErr = error; continue; }
        galleryUrls.push(url); ok++;
      }
      galleryFile.value = '';
      refreshGallery();
      if (ok === files.length) {
        galleryStatus.textContent = `Uploaded ${ok} ✓`;
        galleryStatus.className = 'adm-pe-upload-status adm-pe-upload-status--ok';
      } else {
        galleryStatus.textContent = `Uploaded ${ok}/${files.length}. ${lastErr?.message ? '— ' + lastErr.message : ''}`;
        galleryStatus.className = 'adm-pe-upload-status adm-pe-upload-status--err';
      }
    });
    refreshGallery();

    /* ── Variants editor ─────────────────────────────────────────────
       State: `variantsState` is the editable working set of variants.
       Each item: { id?, label, mrp, sale_price, stock_qty,
                    low_stock_threshold, main_image_url,
                    gallery_image_urls: [] }
       The "ID" is the existing UUID for update, or undefined for new rows.
       UI-only `uid` lets us key DOM nodes without leaking it to the DB.
       ──────────────────────────────────────────────────────────────── */
    const variantsState = (existing?.variants || []).map((v, i) => ({
      uid: `v${Date.now()}_${i}`,
      id: v.id,
      label: v.label || '',
      mrp: v.mrp ?? '',
      sale_price: v.sale_price ?? '',
      stock_qty: v.stock_qty ?? 0,
      low_stock_threshold: v.low_stock_threshold ?? 5,
      main_image_url: v.main_image_url || '',
      gallery_image_urls: Array.isArray(v.gallery_image_urls) ? v.gallery_image_urls.slice() : [],
    }));

    const hasVariantsChk = $('#adm-pe-has-variants');
    const variantsWrap   = $('#adm-pe-variants');
    const addVariantBtn  = $('#adm-pe-add-variant');

    const singleSections = () => document.querySelectorAll('.adm-pe-section--single');

    const applyVariantMode = () => {
      const on = hasVariantsChk.checked;
      singleSections().forEach(el => { el.hidden = on; });
      variantsWrap.hidden = !on;
      addVariantBtn.hidden = !on;
      // Single-product fields are no longer "required" when variants are on
      // (the variant rows carry pricing). Toggle the attribute so the form
      // doesn't refuse to submit on an empty sale_price.
      const singleSale = form.querySelector('[name="sale_price"]');
      if (singleSale) {
        if (on) singleSale.removeAttribute('required');
        else    singleSale.setAttribute('required', '');
      }
    };

    const variantCardHtml = (v) => `
      <div class="adm-pe-variant" data-vuid="${v.uid}">
        <div class="adm-pe-variant-head">
          <input class="adm-pe-input adm-pe-variant-label" data-vfield="label" placeholder="Size label (e.g. 250ml)" value="${esc(v.label)}" />
          <button type="button" class="adm-pe-variant-del" data-vdel="${v.uid}" aria-label="Remove variant">✕</button>
        </div>
        <div class="adm-pe-grid adm-pe-grid--2">
          <label class="adm-pe-field">
            <span class="adm-pe-label">MRP (₹)</span>
            <input class="adm-pe-input" data-vfield="mrp" type="number" min="0" step="0.01" placeholder="350" value="${v.mrp}" />
          </label>
          <label class="adm-pe-field">
            <span class="adm-pe-label">Sale price (₹) <span class="adm-pe-label-req">required</span></span>
            <input class="adm-pe-input" data-vfield="sale_price" type="number" min="0" step="0.01" placeholder="280" value="${v.sale_price}" />
          </label>
        </div>
        <div class="adm-pe-grid adm-pe-grid--2">
          <label class="adm-pe-field">
            <span class="adm-pe-label">Stock quantity</span>
            <input class="adm-pe-input" data-vfield="stock_qty" type="number" min="0" step="1" placeholder="0" value="${v.stock_qty}" />
          </label>
          <label class="adm-pe-field">
            <span class="adm-pe-label">Low-stock alert when ≤</span>
            <input class="adm-pe-input" data-vfield="low_stock_threshold" type="number" min="0" step="1" placeholder="5" value="${v.low_stock_threshold}" />
          </label>
        </div>
        <div class="adm-pe-variant-imgs">
          <div class="adm-pe-variant-imgs-col">
            <div class="adm-pe-label">Main image</div>
            <div class="adm-pe-main-preview adm-pe-main-preview--sm" data-vmain-prev>
              ${v.main_image_url
                ? `<img src="${esc(v.main_image_url)}" alt="" />`
                : `<div class="adm-pe-img-empty">No image yet</div>`}
            </div>
            <label class="adm-pe-btn adm-pe-btn--outline adm-pe-upload-btn" data-vmain-upload>Choose file…</label>
            <input class="adm-pe-input adm-pe-input--sm" data-vfield="main_image_url" type="text" placeholder="…or paste image URL" value="${esc(v.main_image_url)}" />
            <div class="adm-pe-upload-status" data-vmain-status></div>
          </div>
          <div class="adm-pe-variant-imgs-col">
            <div class="adm-pe-label">Gallery</div>
            <div class="adm-pe-gallery adm-pe-gallery--sm" data-vgallery></div>
            <label class="adm-pe-btn adm-pe-btn--outline adm-pe-upload-btn" data-vgallery-upload>+ Add images</label>
            <div class="adm-pe-upload-status" data-vgallery-status></div>
          </div>
        </div>
      </div>
    `;

    const renderVariantCard = (v) => {
      const card = document.createElement('div');
      card.innerHTML = variantCardHtml(v);
      const el = card.firstElementChild;
      wireVariantCard(el, v);
      return el;
    };

    const renderVariantGallery = (el, v) => {
      const wrap = el.querySelector('[data-vgallery]');
      wrap.innerHTML = v.gallery_image_urls.length === 0
        ? `<div class="adm-pe-img-empty adm-pe-img-empty--gallery">No gallery images yet</div>`
        : v.gallery_image_urls.map((url, idx) => `
            <div class="adm-pe-gallery-item">
              <img src="${esc(url)}" alt="" />
              <button type="button" class="adm-pe-gallery-remove" data-vgallery-remove="${idx}" aria-label="Remove">✕</button>
            </div>`).join('');
    };

    const wireVariantCard = (el, v) => {
      // Field input → write back to state on every change.
      el.querySelectorAll('[data-vfield]').forEach(inp => {
        inp.addEventListener('input', () => {
          const k = inp.dataset.vfield;
          v[k] = inp.value;
          if (k === 'main_image_url') {
            const prev = el.querySelector('[data-vmain-prev]');
            prev.innerHTML = inp.value.trim()
              ? `<img src="${esc(inp.value.trim())}" alt="" />`
              : `<div class="adm-pe-img-empty">No image yet</div>`;
          }
        });
      });

      // Delete this variant.
      el.querySelector('[data-vdel]')?.addEventListener('click', () => {
        const idx = variantsState.findIndex(x => x.uid === v.uid);
        if (idx >= 0) variantsState.splice(idx, 1);
        el.remove();
      });

      // Main image upload (hidden file input created on demand).
      el.querySelector('[data-vmain-upload]')?.addEventListener('click', () => {
        const inp = document.createElement('input');
        inp.type = 'file'; inp.accept = 'image/*';
        inp.addEventListener('change', async () => {
          const file = inp.files?.[0];
          if (!file) return;
          const status = el.querySelector('[data-vmain-status]');
          status.textContent = 'Converting & uploading…';
          status.className = 'adm-pe-upload-status adm-pe-upload-status--busy';
          const { url, error } = await adminLib.uploadProductImage(file, {
            productId: existing?.id || 'new', kind: `var-main-${v.uid}`, name: nameInp.value,
          });
          if (error) {
            status.textContent = 'Failed: ' + (error.message || error);
            status.className = 'adm-pe-upload-status adm-pe-upload-status--err';
            return;
          }
          v.main_image_url = url;
          const urlInp = el.querySelector('[data-vfield="main_image_url"]');
          urlInp.value = url;
          const prev = el.querySelector('[data-vmain-prev]');
          prev.innerHTML = `<img src="${esc(url)}" alt="" />`;
          status.textContent = 'Uploaded ✓';
          status.className = 'adm-pe-upload-status adm-pe-upload-status--ok';
        });
        inp.click();
      });

      // Gallery uploads (multiple).
      el.querySelector('[data-vgallery-upload]')?.addEventListener('click', () => {
        const inp = document.createElement('input');
        inp.type = 'file'; inp.accept = 'image/*'; inp.multiple = true;
        inp.addEventListener('change', async () => {
          const files = Array.from(inp.files || []);
          if (files.length === 0) return;
          const status = el.querySelector('[data-vgallery-status]');
          status.textContent = `Uploading ${files.length} image${files.length>1?'s':''}…`;
          status.className = 'adm-pe-upload-status adm-pe-upload-status--busy';
          let ok = 0, lastErr = null;
          for (const f of files) {
            const { url, error } = await adminLib.uploadProductImage(f, {
              productId: existing?.id || 'new', kind: `var-gal-${v.uid}`, name: nameInp.value,
            });
            if (error) { lastErr = error; continue; }
            v.gallery_image_urls.push(url); ok++;
          }
          renderVariantGallery(el, v);
          if (ok === files.length) {
            status.textContent = `Uploaded ${ok} ✓`;
            status.className = 'adm-pe-upload-status adm-pe-upload-status--ok';
          } else {
            status.textContent = `Uploaded ${ok}/${files.length}. ${lastErr?.message ? '— ' + lastErr.message : ''}`;
            status.className = 'adm-pe-upload-status adm-pe-upload-status--err';
          }
        });
        inp.click();
      });

      // Gallery remove buttons (event-delegated).
      el.querySelector('[data-vgallery]')?.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-vgallery-remove]');
        if (!btn) return;
        const idx = parseInt(btn.dataset.vgalleryRemove, 10);
        v.gallery_image_urls.splice(idx, 1);
        renderVariantGallery(el, v);
      });

      // Initial gallery paint.
      renderVariantGallery(el, v);
    };

    const renderAllVariants = () => {
      variantsWrap.innerHTML = '';
      variantsState.forEach(v => variantsWrap.appendChild(renderVariantCard(v)));
    };

    hasVariantsChk.addEventListener('change', () => {
      // First time turning it on, seed an empty variant so the user has
      // something to fill in (less surprising than an empty section).
      if (hasVariantsChk.checked && variantsState.length === 0) {
        variantsState.push({
          uid: `v${Date.now()}`, label: '', mrp: '', sale_price: '',
          stock_qty: 0, low_stock_threshold: 5,
          main_image_url: '', gallery_image_urls: [],
        });
        renderAllVariants();
      }
      applyVariantMode();
    });

    addVariantBtn.addEventListener('click', () => {
      const v = {
        uid: `v${Date.now()}_${variantsState.length}`,
        label: '', mrp: '', sale_price: '',
        stock_qty: 0, low_stock_threshold: 5,
        main_image_url: '', gallery_image_urls: [],
      };
      variantsState.push(v);
      variantsWrap.appendChild(renderVariantCard(v));
    });

    // Initial paint
    renderAllVariants();
    applyVariantMode();

    /* ── Bundle editor ───────────────────────────────────────────────
       Mirror of the variants editor: per-row member cards with product
       + variant + qty. State `bundleState` is the editable working set.
       Mutually exclusive with variants — toggling one OFFs the other. */
    const isBundleChk = $('#adm-pe-is-bundle');
    const bundleWrap  = $('#adm-pe-bundle-items');
    const addBundleBtn = $('#adm-pe-add-bundle-item');
    const variantsSection = $('#adm-pe-section-variants');
    const bundleSection   = $('#adm-pe-section-bundle');

    // Catalog of pickable products (everything except this product itself
    // + any other bundle, so we can't nest combos).
    const memberCandidates = (PRODUCTS || []).filter(pr =>
      pr.id !== (existing?.id || -1) && !pr.isBundle && pr.id != null
    );

    const bundleState = [];

    // Seed from existing.bundle_items if editing (admin loads bundle later
    // because the catalog refresh exposes them via products.bundleItems).
    const seedBundle = () => {
      const items = (existing?.bundleItems || existing?.bundle_items || []);
      items.forEach((bi, i) => {
        bundleState.push({
          uid: `bi${Date.now()}_${i}`,
          id: bi.id,
          member_product_id: bi.memberProductId ?? bi.member_product_id ?? null,
          member_variant_id: bi.memberVariantId ?? bi.member_variant_id ?? null,
          quantity: bi.quantity || 1,
        });
      });
    };
    seedBundle();

    const variantOptionsFor = (productId, currentVariantId) => {
      const prod = memberCandidates.find(pr => pr.id === Number(productId));
      if (!prod || !prod.hasVariants || !prod.variants?.length) return '';
      return `<option value="">— Pick a size —</option>` +
        prod.variants.map(v =>
          `<option value="${esc(v.id)}" ${v.id === currentVariantId ? 'selected' : ''}>${esc(v.label)}</option>`
        ).join('');
    };

    const bundleCardHtml = (m) => {
      const opts = memberCandidates.map(pr =>
        `<option value="${pr.id}" ${pr.id === Number(m.member_product_id) ? 'selected' : ''}>${esc(pr.name)}${pr.hasVariants ? ' (sizes)' : ''}</option>`
      ).join('');
      const variantOpts = variantOptionsFor(m.member_product_id, m.member_variant_id);
      return `
        <div class="adm-pe-variant" data-biuid="${m.uid}">
          <div class="adm-pe-variant-head">
            <span class="adm-pe-label" style="margin:0">Bundle member</span>
            <button type="button" class="adm-pe-variant-del" data-bidel="${m.uid}" aria-label="Remove member">✕</button>
          </div>
          <div class="adm-pe-grid adm-pe-grid--2">
            <label class="adm-pe-field">
              <span class="adm-pe-label">Product <span class="adm-pe-label-req">required</span></span>
              <select class="adm-pe-input" data-bifield="member_product_id">
                <option value="">— Pick a product —</option>
                ${opts}
              </select>
            </label>
            <label class="adm-pe-field" data-bivariant-wrap ${variantOpts ? '' : 'hidden'}>
              <span class="adm-pe-label">Size / variant</span>
              <select class="adm-pe-input" data-bifield="member_variant_id">
                ${variantOpts}
              </select>
            </label>
          </div>
          <div class="adm-pe-grid adm-pe-grid--2">
            <label class="adm-pe-field">
              <span class="adm-pe-label">Quantity in bundle</span>
              <input class="adm-pe-input" data-bifield="quantity" type="number" min="1" step="1" value="${m.quantity || 1}" />
            </label>
          </div>
        </div>`;
    };

    const renderBundleCard = (m) => {
      const wrap = document.createElement('div');
      wrap.innerHTML = bundleCardHtml(m);
      const el = wrap.firstElementChild;
      wireBundleCard(el, m);
      return el;
    };

    const wireBundleCard = (el, m) => {
      el.querySelectorAll('[data-bifield]').forEach(inp => {
        inp.addEventListener('change', () => {
          const k = inp.dataset.bifield;
          m[k] = inp.value;
          // Picking a new product → refresh the variant select.
          if (k === 'member_product_id') {
            m.member_variant_id = null;
            const variantSelWrap = el.querySelector('[data-bivariant-wrap]');
            const variantSel = el.querySelector('[data-bifield="member_variant_id"]');
            const variantOpts = variantOptionsFor(inp.value, null);
            if (variantOpts) {
              variantSel.innerHTML = variantOpts;
              variantSelWrap.hidden = false;
            } else {
              variantSel.innerHTML = '';
              variantSelWrap.hidden = true;
            }
          }
        });
      });
      el.querySelector('[data-bidel]')?.addEventListener('click', () => {
        const idx = bundleState.findIndex(x => x.uid === m.uid);
        if (idx >= 0) bundleState.splice(idx, 1);
        el.remove();
      });
    };

    const renderAllBundle = () => {
      bundleWrap.innerHTML = '';
      bundleState.forEach(m => bundleWrap.appendChild(renderBundleCard(m)));
    };

    const applyBundleMode = () => {
      const on = isBundleChk.checked;
      bundleWrap.hidden = !on;
      addBundleBtn.hidden = !on;
      // Mutually exclusive with variants — hide that whole section while
      // bundle is on, and force-uncheck the variants toggle.
      variantsSection.hidden = on;
      if (on && hasVariantsChk.checked) {
        hasVariantsChk.checked = false;
        applyVariantMode();
      }
    };

    isBundleChk.addEventListener('change', () => {
      if (isBundleChk.checked && bundleState.length === 0) {
        bundleState.push({
          uid: `bi${Date.now()}`,
          member_product_id: '', member_variant_id: null, quantity: 1,
        });
        renderAllBundle();
      }
      applyBundleMode();
    });

    // Symmetrical: turning variants ON force-OFFs bundle.
    hasVariantsChk.addEventListener('change', () => {
      if (hasVariantsChk.checked && isBundleChk.checked) {
        isBundleChk.checked = false;
        applyBundleMode();
      }
      bundleSection.hidden = hasVariantsChk.checked;
    });

    addBundleBtn.addEventListener('click', () => {
      const m = {
        uid: `bi${Date.now()}_${bundleState.length}`,
        member_product_id: '', member_variant_id: null, quantity: 1,
      };
      bundleState.push(m);
      bundleWrap.appendChild(renderBundleCard(m));
    });

    // Initial paint
    renderAllBundle();
    applyBundleMode();
    bundleSection.hidden = hasVariantsChk.checked;

    // Delete
    $('#adm-pe-delete')?.addEventListener('click', async () => {
      if (!existing?.id) return;
      if (!confirm(`Delete "${existing.name}"? This cannot be undone.`)) return;
      const { error } = await adminLib.deleteProduct(existing.id);
      if (error) { showToast('Delete failed: ' + error.message); return; }
      showToast('Product deleted.');
      clearCatalogCache?.();
      await refreshCatalogInMemory();
      close();
      adminTab = 'products';
      renderAdminView();
    });

    // Submit
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const d = Object.fromEntries(new FormData(form));
      const msg = $('#adm-pe-msg');
      const btn = $('#adm-pe-save');
      const hasVariants = hasVariantsChk.checked;
      const isBundle    = isBundleChk.checked;

      if (hasVariants && isBundle) {
        msg.textContent = 'A product can be variants OR a bundle, not both.';
        msg.hidden = false;
        msg.className = 'adm-pe-side-card-msg adm-pe-side-card-msg--err';
        return;
      }

      // When bundle is on, validate members client-side.
      if (isBundle) {
        if (bundleState.length === 0) {
          msg.textContent = 'Add at least one member to the bundle.';
          msg.hidden = false;
          msg.className = 'adm-pe-side-card-msg adm-pe-side-card-msg--err';
          return;
        }
        for (const m of bundleState) {
          if (!m.member_product_id) {
            msg.textContent = 'Every bundle member needs a product.';
            msg.hidden = false;
            msg.className = 'adm-pe-side-card-msg adm-pe-side-card-msg--err';
            return;
          }
          const prod = memberCandidates.find(pr => pr.id === Number(m.member_product_id));
          if (prod?.hasVariants && !m.member_variant_id) {
            msg.textContent = `"${prod.name}" has sizes — pick which one goes in the bundle.`;
            msg.hidden = false;
            msg.className = 'adm-pe-side-card-msg adm-pe-side-card-msg--err';
            return;
          }
          if (!(parseInt(m.quantity, 10) > 0)) {
            msg.textContent = 'Bundle member quantity must be at least 1.';
            msg.hidden = false;
            msg.className = 'adm-pe-side-card-msg adm-pe-side-card-msg--err';
            return;
          }
        }
        // No duplicate (product, variant) within the same bundle.
        const seen = new Set();
        for (const m of bundleState) {
          const key = `${m.member_product_id}|${m.member_variant_id || ''}`;
          if (seen.has(key)) {
            msg.textContent = 'Each member can only appear once — increase the quantity instead.';
            msg.hidden = false;
            msg.className = 'adm-pe-side-card-msg adm-pe-side-card-msg--err';
            return;
          }
          seen.add(key);
        }
      }

      // When variants are on, validate them client-side so a confusing DB
      // error doesn't pop up on the user. Each must have a label + sale_price.
      if (hasVariants) {
        if (variantsState.length === 0) {
          msg.textContent = 'Add at least one variant or uncheck "multiple sizes".';
          msg.hidden = false;
          msg.className = 'adm-pe-side-card-msg adm-pe-side-card-msg--err';
          return;
        }
        for (const v of variantsState) {
          if (!v.label || !String(v.label).trim()) {
            msg.textContent = 'Every variant needs a label (e.g. "250ml").';
            msg.hidden = false;
            msg.className = 'adm-pe-side-card-msg adm-pe-side-card-msg--err';
            return;
          }
          const sp = parseFloat(v.sale_price);
          if (!Number.isFinite(sp) || sp < 0) {
            msg.textContent = `Variant "${v.label}" needs a sale price.`;
            msg.hidden = false;
            msg.className = 'adm-pe-side-card-msg adm-pe-side-card-msg--err';
            return;
          }
        }
        const labels = variantsState.map(v => v.label.trim().toLowerCase());
        if (new Set(labels).size !== labels.length) {
          msg.textContent = 'Variant labels must be unique within a product.';
          msg.hidden = false;
          msg.className = 'adm-pe-side-card-msg adm-pe-side-card-msg--err';
          return;
        }
      }

      btn.disabled = true; btn.textContent = 'Saving…';
      const payload = {
        id: existing?.id || undefined,
        name: (d.name || '').trim(),
        slug: (d.slug || '').trim() || null,
        subtitle: (d.subtitle || '').trim() || null,
        short_description: (d.short_description || '').trim() || null,
        description: (d.description || '').trim() || null,
        how_to_use: (d.how_to_use || '').trim() || null,
        // When variants are on, single-product price/stock fields are ignored
        // by the storefront and the DB triggers; send the cheapest variant
        // values so the legacy in_stock / price columns stay meaningful for
        // any old read path that doesn't know about variants yet.
        sale_price: hasVariants
          ? Math.min(...variantsState.map(v => parseFloat(v.sale_price) || 0))
          : parseFloat(d.sale_price),
        mrp: hasVariants
          ? (variantsState.some(v => v.mrp)
              ? Math.max(...variantsState.map(v => parseFloat(v.mrp) || 0))
              : null)
          : (d.mrp ? parseFloat(d.mrp) : null),
        stock_qty: hasVariants
          ? variantsState.reduce((s, v) => s + (parseInt(v.stock_qty, 10) || 0), 0)
          : (parseInt(d.stock_qty, 10) || 0),
        low_stock_threshold: parseInt(d.low_stock_threshold, 10) || 0,
        category_id: parseInt(d.category_id, 10),
        img_key: d.img_key,
        main_image_url: hasVariants
          ? (variantsState[0]?.main_image_url || null)
          : ((d.main_image_url || '').trim() || null),
        gallery_image_urls: hasVariants
          ? []
          : (d.gallery_image_urls || '').split('|').map(s => s.trim()).filter(Boolean),
        tags: (d.tags || '').split('|').map(s => s.trim()).filter(Boolean),
        weight: (d.weight || '').trim() || null,
        badge: (d.badge || '').trim() || null,
        meta_title: (d.meta_title || '').trim() || null,
        meta_description: (d.meta_description || '').trim() || null,
        has_variants: hasVariants,
        is_bundle: isBundle,
        free_shipping: !!d.free_shipping,
      };
      const { data: savedProduct, error } = await adminLib.upsertProduct(payload);
      if (error) {
        btn.disabled = false; btn.textContent = existing?.id ? 'Update product' : 'Publish product';
        msg.textContent = 'Error: ' + error.message;
        msg.hidden = false;
        msg.className = 'adm-pe-side-card-msg adm-pe-side-card-msg--err';
        return;
      }

      // Now upsert/delete variants if applicable.
      if (hasVariants && savedProduct?.id) {
        const variantsPayload = variantsState.map((v, idx) => ({
          id: v.id,
          label: String(v.label).trim(),
          mrp: v.mrp === '' || v.mrp == null ? null : parseFloat(v.mrp),
          sale_price: parseFloat(v.sale_price),
          stock_qty: parseInt(v.stock_qty, 10) || 0,
          low_stock_threshold: parseInt(v.low_stock_threshold, 10) || 5,
          main_image_url: (v.main_image_url || '').trim() || null,
          gallery_image_urls: v.gallery_image_urls || [],
          sort_order: idx,
        }));
        const { error: vErr } = await adminLib.saveProductVariants(savedProduct.id, variantsPayload);
        if (vErr) {
          btn.disabled = false; btn.textContent = existing?.id ? 'Update product' : 'Publish product';
          msg.textContent = 'Product saved but variant save failed: ' + vErr.message;
          msg.hidden = false;
          msg.className = 'adm-pe-side-card-msg adm-pe-side-card-msg--err';
          return;
        }
      } else if (!hasVariants && existing?.id && existing?.variants?.length) {
        // Toggle turned OFF on an existing product with variants — wipe them
        // so they don't keep affecting pricing/stock.
        await adminLib.saveProductVariants(existing.id, []);
      }

      // Bundle items: same diff-replace dance as variants.
      if (isBundle && savedProduct?.id) {
        const itemsPayload = bundleState.map((m, idx) => ({
          id: m.id,
          member_product_id: parseInt(m.member_product_id, 10),
          member_variant_id: m.member_variant_id || null,
          quantity: parseInt(m.quantity, 10) || 1,
          sort_order: idx,
        }));
        const { error: bErr } = await adminLib.saveBundleItems(savedProduct.id, itemsPayload);
        if (bErr) {
          btn.disabled = false; btn.textContent = existing?.id ? 'Update product' : 'Publish product';
          msg.textContent = 'Product saved but bundle save failed: ' + bErr.message;
          msg.hidden = false;
          msg.className = 'adm-pe-side-card-msg adm-pe-side-card-msg--err';
          return;
        }
      } else if (!isBundle && existing?.id && (existing?.bundleItems?.length || existing?.bundle_items?.length)) {
        // Toggle turned OFF — wipe bundle members.
        await adminLib.saveBundleItems(existing.id, []);
      }

      btn.disabled = false; btn.textContent = existing?.id ? 'Update product' : 'Publish product';
      showToast(existing?.id ? 'Product updated!' : 'Product published!');
      // Invalidate cached catalog + refresh in-memory PRODUCTS so the
      // storefront sees the edit immediately without a full reload.
      clearCatalogCache?.();
      await refreshCatalogInMemory();
      close();
      adminTab = 'products';
      await renderAdminView();
    });
  };

  /* ── Categories tab ── */
  const renderAdmCategories = async () => {
    const body = $('#adm-content');
    if (!body) return;
    body.innerHTML = skelAdmTable(12);
    const { data: cats, error } = await adminLib.fetchAllCategoriesAdmin();
    if (error) { body.innerHTML = `<div class="adm-error">Failed: ${esc(error.message)}</div>`; return; }

    const { data: pins } = await adminLib.fetchPinnedItems();
    const pinnedCategoryIds = new Set((pins || []).filter(r => r.item_type === 'category').map(r => r.item_id));

    const previewHtml = (url, name = 'Category image') => url
      ? `<img src="${esc(url)}" alt="${esc(name)}" class="adm-cat-thumb-img" />`
      : `<div class="adm-cat-thumb-empty">—</div>`;

    body.innerHTML = `
      <div class="adm-section-title">Categories <span class="adm-count">(${cats.length})</span> <span class="adm-count adm-count--pinned" id="adm-cat-pin-count">${pinnedCategoryIds.size} pinned</span>
        <div class="adm-hint">Upload (auto-WebP, max 1200px) or paste a URL.</div>
      </div>
      <div class="adm-table-wrap">
        <table class="adm-table">
          <thead><tr><th>ID</th><th>Image</th><th>Upload / URL</th><th>Name</th><th>Sort</th><th>Action</th></tr></thead>
          <tbody>
            ${cats.map(c => `<tr id="adm-cat-row-${c.id}">
              <td class="adm-mono">${c.id}</td>
              <td><div class="adm-cat-thumb" id="adm-cat-thumb-${c.id}">${previewHtml(c.image_url, c.name)}</div></td>
              <td>
                <div class="adm-cat-img-controls">
                  <label class="adm-edit-btn adm-cat-upload-btn" for="adm-cat-file-${c.id}">Upload…</label>
                  <input type="file" id="adm-cat-file-${c.id}" data-cat-file="${c.id}" accept="image/*" hidden />
                  <input class="adm-inline-input" data-cat-id="${c.id}" data-field="image_url" value="${esc(c.image_url||'')}" placeholder="…or paste URL" style="min-width:220px" />
                  <span class="adm-cat-upload-status" id="adm-cat-status-${c.id}"></span>
                </div>
              </td>
              <td><input class="adm-inline-input" data-cat-id="${c.id}" data-field="name" value="${esc(c.name)}" /></td>
              <td><input class="adm-inline-input" data-cat-id="${c.id}" data-field="sort_order" type="number" value="${c.sort_order}" style="width:60px" /></td>
              <td>
                <button class="adm-pin-btn${pinnedCategoryIds.has(c.id) ? ' is-pinned' : ''}" data-cat-pin="${c.id}" title="${pinnedCategoryIds.has(c.id) ? 'Unpin this category from the products page' : 'Pin all products in this category to the top'}" aria-pressed="${pinnedCategoryIds.has(c.id)}">${admIcon('pin', 14)} <span class="adm-pin-label">${pinnedCategoryIds.has(c.id) ? 'Pinned' : 'Pin'}</span></button>
                <button class="adm-save-cat-btn adm-edit-btn" data-cat-save="${c.id}">Save</button>
                <button class="adm-del-btn" data-cat-del="${c.id}">Delete</button>
                <span class="adm-cat-upload-status" id="adm-cat-save-status-${c.id}"></span>
              </td>
            </tr>`).join('')}
          </tbody>
        </table>
      </div>`;

    // Live preview as URL is typed
    body.querySelectorAll('[data-field="image_url"]').forEach(inp => {
      inp.addEventListener('input', () => {
        const id = inp.dataset.catId;
        const thumb = $(`#adm-cat-thumb-${id}`);
        if (thumb) thumb.innerHTML = previewHtml(inp.value.trim());
      });
    });

    // Wire file uploads: convert to WebP, upload to Supabase Storage, fill URL field
    body.querySelectorAll('[data-cat-file]').forEach(input => {
      input.addEventListener('change', async () => {
        const id = parseInt(input.dataset.catFile);
        const file = input.files?.[0];
        if (!file) return;
        const status = $(`#adm-cat-status-${id}`);
        const thumb = $(`#adm-cat-thumb-${id}`);
        const urlInp = body.querySelector(`[data-cat-id="${id}"][data-field="image_url"]`);
        status.textContent = 'Converting & uploading…';
        status.className = 'adm-cat-upload-status adm-cat-upload-status--busy';
        const nameInp = body.querySelector(`[data-cat-id="${id}"][data-field="name"]`);
        const { url, error } = await adminLib.uploadCategoryImage(file, id, nameInp?.value || '');
        input.value = ''; // reset so picking the same file again still fires
        if (error) {
          status.textContent = 'Failed: ' + (error.message || error);
          status.className = 'adm-cat-upload-status adm-cat-upload-status--err';
          return;
        }
        urlInp.value = url;
        thumb.innerHTML = previewHtml(url);
        status.textContent = 'Uploaded — click Save';
        status.className = 'adm-cat-upload-status adm-cat-upload-status--ok';
      });
    });

    // Wire save buttons
    body.querySelectorAll('[data-cat-save]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const id = parseInt(btn.dataset.catSave);
        const row = $(`#adm-cat-row-${id}`);
        const status = $(`#adm-cat-save-status-${id}`);
        const image_url = row.querySelector('[data-field="image_url"]').value.trim() || null;
        const name  = row.querySelector('[data-field="name"]').value.trim();
        const sort  = parseInt(row.querySelector('[data-field="sort_order"]').value) || 0;
        if (!name) {
          status.textContent = "Name can't be empty";
          status.className = 'adm-cat-upload-status adm-cat-upload-status--err';
          return;
        }
        status.textContent = 'Saving…';
        status.className = 'adm-cat-upload-status adm-cat-upload-status--busy';
        btn.textContent = '…'; btn.disabled = true;
        const { data, error } = await adminLib.upsertCategory({ id, image_url, name, sort_order: sort });
        btn.textContent = 'Save'; btn.disabled = false;
        if (error) {
          status.textContent = 'Failed: ' + error.message;
          status.className = 'adm-cat-upload-status adm-cat-upload-status--err';
          showToast('Error: ' + error.message);
          return;
        }
        // Repaint the row from the server-returned row so the UI reflects
        // exactly what persisted (AC2.2), not just local input state.
        row.querySelector('[data-field="name"]').value = data.name;
        row.querySelector('[data-field="sort_order"]').value = data.sort_order;
        row.querySelector('[data-field="image_url"]').value = data.image_url || '';
        const thumb = $(`#adm-cat-thumb-${id}`);
        if (thumb) thumb.innerHTML = previewHtml(data.image_url, data.name);
        status.textContent = 'Saved ✓';
        status.className = 'adm-cat-upload-status adm-cat-upload-status--ok';
        showToast('Category saved!');
        clearCatalogCache?.();
        await refreshCatalogInMemory();
      });
    });

    // Wire pin buttons — pin/unpin a category (all its products) to the top of
    // the public products page. Pins don't change the catalog, so no cache bust.
    body.querySelectorAll('[data-cat-pin]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const id = parseInt(btn.dataset.catPin, 10);
        const pinned = btn.classList.contains('is-pinned');
        btn.disabled = true;
        try {
          const { error } = pinned
            ? await adminLib.unpinItem('category', id)
            : await adminLib.pinItem('category', id);
          btn.disabled = false;
          if (error) { showToast('Pin failed: ' + error.message); return; }
          setPinBtnState(btn, !pinned);
          updatePinCountLabel('#adm-cat-pin-count', pinned ? -1 : +1);
          showToast(pinned ? 'Category unpinned' : 'Category pinned to top');
          await refreshPinnedItems();
          if ($('#view-products')?.classList.contains('active')) { try { renderProductsView(); } catch {} }
        } catch (e) {
          btn.disabled = false;
          showToast('Pin failed — please try again');
        }
      });
    });

    // Wire delete buttons (count → confirm → delete → DOM removal + cache bust)
    body.querySelectorAll('[data-cat-del]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const id = parseInt(btn.dataset.catDel);
        const row = $(`#adm-cat-row-${id}`);
        const status = $(`#adm-cat-save-status-${id}`);
        const name = row.querySelector('[data-field="name"]').value.trim() || `Category ${id}`;
        btn.disabled = true; // guard double-click (engaged before the count/confirm await)
        // A failed count must not block delete (FK set-null keeps products safe).
        const { count, error: countErr } = await adminLib.countProductsInCategory(id);
        let msg;
        if (count > 0) {
          msg = `Delete "${name}"? Its ${count} product(s) will become uncategorized (they stay visible on the storefront). This cannot be undone.`;
        } else if (countErr) {
          // Count unavailable — warn generically rather than imply zero products.
          msg = `Delete "${name}"? Any products in it will become uncategorized (they stay visible on the storefront). This cannot be undone.`;
        } else {
          msg = `Delete "${name}"? This cannot be undone.`;
        }
        if (!confirm(msg)) { btn.disabled = false; return; }
        status.textContent = 'Deleting…';
        status.className = 'adm-cat-upload-status adm-cat-upload-status--busy';
        const { error } = await adminLib.deleteCategory(id);
        if (error) {
          status.textContent = 'Failed: ' + error.message;
          status.className = 'adm-cat-upload-status adm-cat-upload-status--err';
          btn.disabled = false;
          showToast('Delete failed: ' + error.message);
          return;
        }
        row.remove();
        const countEl = body.querySelector('.adm-count');
        if (countEl) {
          const remaining = body.querySelectorAll('[id^="adm-cat-row-"]').length;
          countEl.textContent = `(${remaining})`;
        }
        showToast('Category deleted.');
        clearCatalogCache?.();
        await refreshCatalogInMemory();
      });
    });
  };

  /* ── Users tab ── */
  const renderAdmCustomers = async () => {
    const body = $('#adm-content');
    if (!body) return;
    body.innerHTML = skelAdmTable(8);
    const { data: customers, error } = await adminLib.fetchCustomerSummaries();
    if (error) { body.innerHTML = `<div class="adm-error">Failed: ${esc(error.message)}</div>`; return; }

    const filter = adminCustomerFilter || '';
    const weekAgo = Date.now() - 7 * 24 * 3600 * 1000;
    const thirtyDaysAgo = Date.now() - 30 * 24 * 3600 * 1000;
    const ninetyDaysAgo = Date.now() - 90 * 24 * 3600 * 1000;

    // Compute segment for each customer
    const segmentOf = (c) => {
      const orders = c.order_count || 0;
      const spend  = Number(c.lifetime_spend || 0);
      const last   = c.last_ordered_at ? new Date(c.last_ordered_at).getTime() : 0;
      const joined = new Date(c.created_at).getTime();
      if (orders === 0 && joined >= weekAgo) return { label: 'New', cls: 'adm-seg--new' };
      if (orders === 0) return { label: 'Prospect', cls: 'adm-seg--prospect' };
      if (spend >= 5000 || orders >= 5) return { label: 'VIP', cls: 'adm-seg--vip' };
      if (orders >= 2 && last >= thirtyDaysAgo) return { label: 'Repeat', cls: 'adm-seg--repeat' };
      if (last > 0 && last < ninetyDaysAgo) return { label: 'Lapsed', cls: 'adm-seg--lapsed' };
      return { label: 'Customer', cls: '' };
    };

    const filtered = (customers || []).filter(c => {
      if (filter === 'has-orders') return (c.order_count || 0) > 0;
      if (filter === 'no-orders')  return (c.order_count || 0) === 0;
      if (filter === 'admins')     return !!c.is_admin;
      if (filter === 'new-week')   return new Date(c.created_at).getTime() >= weekAgo;
      if (filter === 'vip')        return segmentOf(c).label === 'VIP';
      if (filter === 'repeat')     return segmentOf(c).label === 'Repeat';
      if (filter === 'lapsed')     return segmentOf(c).label === 'Lapsed';
      return true;
    });

    const fmtINR0 = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
    const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

    body.innerHTML = `
      <div class="adm-section-title">Customers <span class="adm-count">(${filtered.length})</span></div>
      <div class="adm-filter-row">
        ${[
          ['',           'All'],
          ['has-orders', 'Has orders'],
          ['no-orders',  'No orders'],
          ['vip',        'VIP'],
          ['repeat',     'Repeat'],
          ['lapsed',     'Lapsed'],
          ['admins',     'Admins'],
          ['new-week',   'New this week'],
        ].map(([k, label]) => `<button class="adm-chip ${filter === k ? 'active' : ''}" data-adm-cust-filter="${k}">${label}</button>`).join('')}
      </div>
      <div class="adm-table-wrap">
        <table class="adm-table">
          <thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Orders</th><th>Lifetime</th><th>Last ordered</th><th>Segment</th></tr></thead>
          <tbody>
            ${filtered.length === 0
              ? `<tr><td colspan="7" style="text-align:center;color:#aaa;padding:32px 0">No customers match this filter.</td></tr>`
              : filtered.map(c => {
                const seg = segmentOf(c);
                return `
                <tr class="adm-row--clickable" data-adm-open-customer="${esc(c.id)}">
                  <td>${esc(c.full_name || 'Unnamed')}${c.is_admin ? ' <span class="adm-badge adm-badge--confirmed" style="font-size:10px">Admin</span>' : ''}</td>
                  <td class="adm-mono" style="font-size:12px">${esc(c.email || '—')}</td>
                  <td class="adm-mono">${esc(c.phone || '—')}</td>
                  <td>${c.order_count || 0}</td>
                  <td>${fmtINR0(c.lifetime_spend)}</td>
                  <td>${esc(fmtDate(c.last_ordered_at))}</td>
                  <td><span class="adm-seg ${seg.cls}">${seg.label}</span></td>
                </tr>`;
              }).join('')}
          </tbody>
        </table>
      </div>`;
  };

  /* ── Messages tab ── */
  const MSG_STATUS_LABELS = { new: 'New', read: 'Read', archived: 'Archived' };

  const renderAdmMessages = async () => {
    const body = $('#adm-content');
    if (!body) return;
    body.innerHTML = skelAdmTable(8);
    const filter = adminMessageFilter || null;
    const { data: messages, error } = await adminLib.fetchAllMessages({ status: filter });
    if (error) { body.innerHTML = `<div class="adm-error">Failed: ${esc(error.message)}</div>`; return; }

    const fmt = (iso) => {
      const d = new Date(iso);
      return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    };

    body.innerHTML = `
      <div class="adm-section-title">Messages <span class="adm-count">(${messages.length})</span></div>
      <div class="adm-filter-row">
        ${['', 'new', 'read', 'archived'].map(s => `
          <button class="adm-chip ${adminMessageFilter === s ? 'active' : ''}" data-adm-msg-filter="${s}">${s ? MSG_STATUS_LABELS[s] : 'All'}</button>`).join('')}
      </div>
      ${messages.length === 0 ? `
        <div class="adm-empty-state">
          <div style="color:#d1d5db;margin-bottom:8px">${admIcon('inbox', 44)}</div>
          <div>No messages${filter ? ` with status "${MSG_STATUS_LABELS[filter]}"` : ''} yet.</div>
        </div>
      ` : `
      <div class="adm-msg-list">
        ${messages.map(m => `
          <article class="adm-msg-card ${m.status === 'new' ? 'adm-msg-card--new' : ''}" data-msg-id="${esc(m.id)}">
            <header class="adm-msg-head">
              <div class="adm-msg-from">
                <div class="adm-msg-name">${esc(m.name || 'Anonymous')}</div>
                <div class="adm-msg-meta">
                  ${m.email ? `<a href="mailto:${esc(m.email)}">${esc(m.email)}</a>` : ''}
                  ${m.email && m.phone ? ' · ' : ''}
                  ${m.phone ? `<a href="https://wa.me/${esc(String(m.phone).replace(/[^0-9]/g, ''))}" target="_blank" rel="noopener">${esc(m.phone)}</a>` : ''}
                </div>
              </div>
              <div class="adm-msg-side">
                <span class="adm-badge adm-badge--${m.status === 'new' ? 'pending' : (m.status === 'read' ? 'confirmed' : 'cancelled')}">${MSG_STATUS_LABELS[m.status] || m.status}</span>
                <div class="adm-msg-time">${fmt(m.created_at)}</div>
              </div>
            </header>
            ${m.topic ? `<div class="adm-msg-topic">${esc(m.topic)}</div>` : ''}
            <p class="adm-msg-body">${esc(m.message)}</p>
            <footer class="adm-msg-foot">
              ${m.status !== 'read' ? `<button class="adm-edit-btn" data-adm-msg-status="read" data-adm-msg-id="${esc(m.id)}">Mark as read</button>` : ''}
              ${m.status !== 'new' ? `<button class="adm-edit-btn" data-adm-msg-status="new" data-adm-msg-id="${esc(m.id)}">Mark as new</button>` : ''}
              ${m.status !== 'archived' ? `<button class="adm-edit-btn" data-adm-msg-status="archived" data-adm-msg-id="${esc(m.id)}">Archive</button>` : ''}
              <button class="adm-del-btn" data-adm-msg-delete="${esc(m.id)}">${admIcon('trash', 14)} Delete</button>
            </footer>
          </article>
        `).join('')}
      </div>`}`;
  };

  /* ── Coupons tab ── */
  const COUPON_KIND_LABELS = { percent: '% off', fixed: '₹ off', free_shipping: 'Free shipping' };
  const couponStatusFor = (c) => {
    if (!c.active) return { cls: 'cancelled', label: 'Inactive' };
    const now = Date.now();
    if (c.starts_at && new Date(c.starts_at).getTime() > now) return { cls: 'pending', label: 'Scheduled' };
    if (c.expires_at && new Date(c.expires_at).getTime() < now) return { cls: 'cancelled', label: 'Expired' };
    return { cls: 'confirmed', label: 'Active' };
  };
  const couponValueLabel = (c) => {
    if (c.kind === 'percent') return `${Number(c.value)}% off${c.max_discount ? ` (max ₹${Number(c.max_discount).toLocaleString('en-IN')})` : ''}`;
    if (c.kind === 'fixed')   return `₹${Number(c.value).toLocaleString('en-IN')} off`;
    if (c.kind === 'free_shipping') return 'Free shipping';
    return '—';
  };

  const renderAdmCoupons = async () => {
    const body = $('#adm-content');
    if (!body) return;
    body.innerHTML = skelAdmTable(10);
    const { data: coupons, error } = await adminLib.fetchAllCoupons();
    if (error) { body.innerHTML = `<div class="adm-error">Failed: ${esc(error.message)}</div>`; return; }

    body.innerHTML = `
      <div class="adm-section-title">Coupons <span class="adm-count">(${coupons.length})</span>
        <button class="adm-add-btn" id="adm-add-coupon">+ New coupon</button>
      </div>
      ${coupons.length === 0 ? `
        <div class="adm-empty-state">
          <div style="color:#d1d5db;margin-bottom:8px">${admIcon('coupons', 44)}</div>
          <div>No coupons yet. Create one to run a promo.</div>
        </div>
      ` : `
      <div class="adm-table-wrap">
        <table class="adm-table">
          <thead><tr><th>Code</th><th>Type</th><th>Discount</th><th>Min cart</th><th>Uses</th><th>Expires</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            ${coupons.map(c => {
              const st = couponStatusFor(c);
              return `<tr>
                <td class="adm-mono"><strong>${esc(c.code)}</strong>${c.first_order_only ? ' <span class="adm-pe-pct-pill" title="First-order only">1st</span>' : ''}</td>
                <td>${COUPON_KIND_LABELS[c.kind] || c.kind}${c.scope === 'category' ? ' · cat' : ''}</td>
                <td>${esc(couponValueLabel(c))}</td>
                <td>${c.min_subtotal && Number(c.min_subtotal) > 0 ? '₹' + Number(c.min_subtotal).toLocaleString('en-IN') : '—'}</td>
                <td>${c.usage_limit ? `${c.usage_limit} max` : '∞'}${c.per_user_limit ? ` · ${c.per_user_limit}/user` : ''}</td>
                <td>${c.expires_at ? _fmtDate(c.expires_at) : '—'}</td>
                <td><span class="adm-badge adm-badge--${st.cls}">${st.label}</span></td>
                <td class="adm-actions-cell">
                  <button class="adm-edit-btn" data-adm-edit-coupon="${esc(c.id)}">${admIcon('edit', 14)} Edit</button>
                  <button class="adm-del-btn" data-adm-del-coupon="${esc(c.id)}" data-adm-del-coupon-code="${esc(c.code)}">${admIcon('trash', 14)}</button>
                </td>
              </tr>`;
            }).join('')}
          </tbody>
        </table>
      </div>`}`;

    $('#adm-add-coupon')?.addEventListener('click', () => openCouponDrawer(null));
  };

  const _toLocalDT = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const openCouponDrawer = (couponId) => {
    openAdmDrawer({
      kind: 'coupon',
      id: couponId || 'new',
      render: async (panel) => {
        let c = {
          code: '', description: '', kind: 'percent', value: '',
          scope: 'cart', category_id: null,
          min_subtotal: 0, max_discount: '',
          usage_limit: '', per_user_limit: 1,
          first_order_only: false, starts_at: null, expires_at: null,
          active: true,
        };
        let uses = 0;
        if (couponId) {
          const [{ data, error }, usesRes] = await Promise.all([
            adminLib.fetchCoupon(couponId),
            adminLib.fetchCouponRedemptionCounts(couponId),
          ]);
          if (error) {
            panel.querySelector('.adm-dr-body').innerHTML =
              `<div class="adm-dr-sec adm-dr-empty">Failed to load coupon: ${esc(error.message)}</div>`;
            return;
          }
          c = { ...c, ...data };
          uses = usesRes.count || 0;
        }
        panel.querySelector('.adm-dr-eyebrow').textContent = 'Coupon';
        panel.querySelector('.adm-dr-title').textContent = couponId ? c.code : 'New coupon';

        const catOptions = CATEGORIES.map(cc =>
          `<option value="${cc.id}" ${c.category_id === cc.id ? 'selected' : ''}>${esc(cc.name)}</option>`
        ).join('');

        panel.querySelector('.adm-dr-body').innerHTML = `
          <form class="adm-cp-form" id="adm-cp-form" novalidate>
            <div class="adm-dr-sec">
              <label class="adm-pe-field">
                <span class="adm-pe-label">Code <span class="adm-pe-label-req">required</span></span>
                <input class="adm-pe-input adm-pe-input--title adm-cp-code" name="code" value="${esc(c.code || '')}" placeholder="RAMADAN10" autocomplete="off" required />
                <div class="adm-pe-label-hint">Customers type this at checkout. Case-insensitive.</div>
              </label>
              <label class="adm-pe-field">
                <span class="adm-pe-label">Description <span class="adm-pe-label-hint">(internal note — not shown to customers)</span></span>
                <input class="adm-pe-input" name="description" value="${esc(c.description || '')}" placeholder="e.g. Ramadan launch promo, 10% off everything" />
              </label>
            </div>

            <div class="adm-dr-sec">
              <div class="adm-dr-sec-title">Discount</div>
              <label class="adm-pe-field">
                <span class="adm-pe-label">Type</span>
                <select class="adm-pe-input" name="kind">
                  <option value="percent"       ${c.kind === 'percent' ? 'selected' : ''}>Percent off cart (%)</option>
                  <option value="fixed"         ${c.kind === 'fixed' ? 'selected' : ''}>Flat amount off cart (₹)</option>
                  <option value="free_shipping" ${c.kind === 'free_shipping' ? 'selected' : ''}>Free shipping</option>
                </select>
              </label>
              <div class="adm-pe-grid adm-pe-grid--2" data-cp-value-row ${c.kind === 'free_shipping' ? 'hidden' : ''}>
                <label class="adm-pe-field">
                  <span class="adm-pe-label" data-cp-value-label>${c.kind === 'percent' ? 'Percent off (%)' : 'Amount off (₹)'}</span>
                  <input class="adm-pe-input" name="value" type="number" min="0" step="0.01" value="${c.value ?? ''}" placeholder="${c.kind === 'percent' ? '10' : '100'}" />
                </label>
                <label class="adm-pe-field" data-cp-max-row ${c.kind === 'percent' ? '' : 'hidden'}>
                  <span class="adm-pe-label">Max discount (₹) <span class="adm-pe-label-hint">cap for % coupons</span></span>
                  <input class="adm-pe-input" name="max_discount" type="number" min="0" step="0.01" value="${c.max_discount ?? ''}" placeholder="optional" />
                </label>
              </div>
              <div class="adm-pe-grid adm-pe-grid--2">
                <label class="adm-pe-field">
                  <span class="adm-pe-label">Scope</span>
                  <select class="adm-pe-input" name="scope">
                    <option value="cart"     ${c.scope === 'cart' ? 'selected' : ''}>Whole cart</option>
                    <option value="category" ${c.scope === 'category' ? 'selected' : ''}>Specific category</option>
                  </select>
                </label>
                <label class="adm-pe-field" data-cp-cat-row ${c.scope === 'category' ? '' : 'hidden'}>
                  <span class="adm-pe-label">Category</span>
                  <select class="adm-pe-input" name="category_id">
                    <option value="">— Pick a category —</option>
                    ${catOptions}
                  </select>
                </label>
              </div>
            </div>

            <div class="adm-dr-sec">
              <div class="adm-dr-sec-title">Limits</div>
              <div class="adm-pe-grid adm-pe-grid--2">
                <label class="adm-pe-field">
                  <span class="adm-pe-label">Min cart subtotal (₹)</span>
                  <input class="adm-pe-input" name="min_subtotal" type="number" min="0" step="0.01" value="${c.min_subtotal ?? 0}" />
                </label>
                <label class="adm-pe-field">
                  <span class="adm-pe-label">Total use limit <span class="adm-pe-label-hint">(blank = unlimited)</span></span>
                  <input class="adm-pe-input" name="usage_limit" type="number" min="0" step="1" value="${c.usage_limit ?? ''}" placeholder="∞" />
                </label>
              </div>
              <div class="adm-pe-grid adm-pe-grid--2">
                <label class="adm-pe-field">
                  <span class="adm-pe-label">Per-user limit <span class="adm-pe-label-hint">(blank = unlimited)</span></span>
                  <input class="adm-pe-input" name="per_user_limit" type="number" min="0" step="1" value="${c.per_user_limit ?? ''}" placeholder="1" />
                </label>
                <label class="adm-pe-field" style="display:flex;align-items:center;gap:8px;padding-top:22px">
                  <input type="checkbox" name="first_order_only" ${c.first_order_only ? 'checked' : ''} />
                  <span>First-time customers only</span>
                </label>
              </div>
            </div>

            <div class="adm-dr-sec">
              <div class="adm-dr-sec-title">Schedule</div>
              <div class="adm-pe-grid adm-pe-grid--2">
                <label class="adm-pe-field">
                  <span class="adm-pe-label">Starts at <span class="adm-pe-label-hint">(blank = now)</span></span>
                  <input class="adm-pe-input" name="starts_at" type="datetime-local" value="${_toLocalDT(c.starts_at)}" />
                </label>
                <label class="adm-pe-field">
                  <span class="adm-pe-label">Expires at <span class="adm-pe-label-hint">(blank = never)</span></span>
                  <input class="adm-pe-input" name="expires_at" type="datetime-local" value="${_toLocalDT(c.expires_at)}" />
                </label>
              </div>
              <label class="adm-pe-field" style="display:flex;align-items:center;gap:8px;margin-top:8px">
                <input type="checkbox" name="active" ${c.active ? 'checked' : ''} />
                <span>Active</span>
              </label>
            </div>

            ${couponId ? `<div class="adm-dr-sec"><div class="adm-dr-sec-title">Usage</div><div>${uses} order${uses === 1 ? '' : 's'} so far</div></div>` : ''}

            <div class="adm-dr-sec" style="display:flex;gap:8px;justify-content:flex-end">
              ${couponId ? `<button type="button" class="adm-pe-btn adm-pe-btn--danger" id="adm-cp-delete">Delete</button>` : ''}
              <button type="submit" class="adm-pe-btn adm-pe-btn--primary" id="adm-cp-save">${couponId ? 'Update coupon' : 'Create coupon'}</button>
            </div>
            <div class="adm-dr-sec" id="adm-cp-msg" hidden></div>
          </form>
        `;

        const form = panel.querySelector('#adm-cp-form');
        const kindSel = form.querySelector('[name="kind"]');
        const valueRow = form.querySelector('[data-cp-value-row]');
        const valueLabel = form.querySelector('[data-cp-value-label]');
        const maxRow = form.querySelector('[data-cp-max-row]');
        const scopeSel = form.querySelector('[name="scope"]');
        const catRow = form.querySelector('[data-cp-cat-row]');

        const syncKind = () => {
          const k = kindSel.value;
          if (k === 'free_shipping') {
            valueRow.hidden = true;
          } else {
            valueRow.hidden = false;
            valueLabel.textContent = k === 'percent' ? 'Percent off (%)' : 'Amount off (₹)';
            maxRow.hidden = (k !== 'percent');
          }
        };
        kindSel.addEventListener('change', syncKind);
        scopeSel.addEventListener('change', () => { catRow.hidden = scopeSel.value !== 'category'; });

        const codeInp = form.querySelector('[name="code"]');
        codeInp.addEventListener('input', () => {
          codeInp.value = codeInp.value.replace(/\s+/g, '').toUpperCase();
        });

        form.querySelector('#adm-cp-delete')?.addEventListener('click', async () => {
          if (!couponId) return;
          if (!confirm(`Delete coupon ${c.code}? Historical orders keep their snapshot but the code stops working.`)) return;
          const { error } = await adminLib.deleteCoupon(couponId);
          if (error) { showToast('Delete failed: ' + error.message); return; }
          showToast('Coupon deleted.');
          closeAdmDrawer(false);
          if (adminTab === 'coupons') renderAdmCoupons();
        });

        form.addEventListener('submit', async (e) => {
          e.preventDefault();
          const d = Object.fromEntries(new FormData(form));
          const msg = panel.querySelector('#adm-cp-msg');
          const btn = panel.querySelector('#adm-cp-save');
          msg.hidden = true;
          if (!(d.code || '').trim()) {
            msg.textContent = 'Code is required.'; msg.hidden = false; return;
          }
          if (d.scope === 'category' && !d.category_id) {
            msg.textContent = 'Pick a category for category-scoped coupons.'; msg.hidden = false; return;
          }
          if ((d.kind === 'percent' || d.kind === 'fixed') && (d.value === '' || Number(d.value) < 0)) {
            msg.textContent = 'Discount value is required.'; msg.hidden = false; return;
          }
          btn.disabled = true; btn.textContent = 'Saving…';
          const payload = {
            id: couponId || undefined,
            code: d.code.trim().toUpperCase(),
            description: d.description,
            kind: d.kind,
            value: d.value,
            scope: d.scope,
            category_id: d.category_id || null,
            min_subtotal: d.min_subtotal,
            max_discount: d.max_discount,
            usage_limit: d.usage_limit,
            per_user_limit: d.per_user_limit,
            first_order_only: !!form.querySelector('[name="first_order_only"]').checked,
            starts_at: d.starts_at ? new Date(d.starts_at).toISOString() : null,
            expires_at: d.expires_at ? new Date(d.expires_at).toISOString() : null,
            active: !!form.querySelector('[name="active"]').checked,
          };
          const { error } = await adminLib.upsertCoupon(payload);
          btn.disabled = false; btn.textContent = couponId ? 'Update coupon' : 'Create coupon';
          if (error) {
            msg.textContent = 'Error: ' + (error.message || error);
            msg.hidden = false;
            return;
          }
          showToast(couponId ? 'Coupon updated!' : 'Coupon created!');
          closeAdmDrawer(false);
          if (adminTab === 'coupons') renderAdmCoupons();
        });
      },
    });
  };

  /* ── Reviews moderation ── */
  let adminReviewFilter = ''; // '' | 'pending' | 'approved' | 'hidden'
  const REVIEW_STATUS_LABELS = { pending: 'Pending', approved: 'Approved', hidden: 'Hidden' };

  const renderAdmReviews = async () => {
    const body = $('#adm-content');
    if (!body) return;
    body.innerHTML = skelAdmTable(8);
    const { data: reviews, error } = await adminLib.fetchAllReviewsAdmin({ status: adminReviewFilter || null });
    if (error) {
      body.innerHTML = `<div class="adm-error">Failed: ${esc(error.message)}</div>
        <p class="adm-empty-state-hint">If you haven't run <code>supabase/migrate_reviews.sql</code> yet, do that first.</p>`;
      return;
    }
    const filter = adminReviewFilter;
    const fmt = (iso) => new Date(iso).toLocaleString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' });

    body.innerHTML = `
      <div class="adm-section-title">Reviews <span class="adm-count">(${reviews.length})</span></div>
      <div class="adm-filter-row">
        ${['', 'pending', 'approved', 'hidden'].map(s => `
          <button class="adm-chip ${adminReviewFilter === s ? 'active' : ''}" data-adm-rev-filter="${s}">${s ? REVIEW_STATUS_LABELS[s] : 'All'}</button>`).join('')}
      </div>
      ${reviews.length === 0 ? `
        <div class="adm-empty-state">
          <div style="color:#d1d5db;margin-bottom:8px">${admIcon('reviews', 44)}</div>
          <div>No reviews${filter ? ` with status "${REVIEW_STATUS_LABELS[filter]}"` : ''} yet.</div>
        </div>
      ` : `
      <div class="adm-msg-list">
        ${reviews.map(r => {
          const prod = r.products || {};
          const prof = r.profile || {};
          const thumb = prod.main_image_url || prod.photo || '';
          return `
          <article class="adm-msg-card ${r.status === 'pending' ? 'adm-msg-card--new' : ''}" data-review-id="${esc(r.id)}">
            <header class="adm-msg-head">
              <div class="adm-msg-from" style="display:flex;gap:10px;align-items:center;min-width:0">
                ${thumb ? `<img src="${esc(thumb)}" alt="" style="width:44px;height:44px;object-fit:cover;border-radius:6px;border:1px solid #e5e5e5" />` : ''}
                <div style="min-width:0">
                  <div class="adm-msg-name">${esc(prod.name || `Product #${r.product_id}`)}</div>
                  <div class="adm-msg-meta">
                    <span style="color:#C9A84C">${'★'.repeat(r.rating)}${'☆'.repeat(5 - r.rating)}</span>
                    ·
                    ${esc(prof.full_name || prof.email || 'Verified buyer')}
                  </div>
                </div>
              </div>
              <div class="adm-msg-side">
                <span class="adm-badge adm-badge--${r.status === 'pending' ? 'pending' : (r.status === 'approved' ? 'confirmed' : 'cancelled')}">${REVIEW_STATUS_LABELS[r.status] || r.status}</span>
                <div class="adm-msg-time">${fmt(r.created_at)}</div>
              </div>
            </header>
            ${r.title ? `<div class="adm-msg-topic">${esc(r.title)}</div>` : ''}
            ${r.body  ? `<p class="adm-msg-body">${esc(r.body)}</p>` : ''}
            <footer class="adm-msg-foot">
              ${r.status !== 'approved' ? `<button class="adm-edit-btn" data-adm-rev-status="approved" data-adm-rev-id="${esc(r.id)}">Approve</button>` : ''}
              ${r.status !== 'pending'  ? `<button class="adm-edit-btn" data-adm-rev-status="pending"  data-adm-rev-id="${esc(r.id)}">Mark pending</button>` : ''}
              ${r.status !== 'hidden'   ? `<button class="adm-edit-btn" data-adm-rev-status="hidden"   data-adm-rev-id="${esc(r.id)}">Hide</button>` : ''}
              <button class="adm-del-btn" data-adm-rev-delete="${esc(r.id)}">${admIcon('trash', 14)} Delete</button>
            </footer>
          </article>`;
        }).join('')}
      </div>`}`;
  };

  /* ── Content tab (Hero Slides / Featured Products / Blog Posts) ── */
  let adminContentTab = 'slides'; // 'slides' | 'featured' | 'blog'

  // ─── Social Media (footer links, single key/value table) ────────────────
  const SOCIAL_LABELS = {
    social_instagram: { name: 'Instagram', placeholder: 'https://instagram.com/sunnahenterprises' },
    social_facebook:  { name: 'Facebook',  placeholder: 'https://facebook.com/sunnahenterprises' },
    social_twitter:   { name: 'X (Twitter)', placeholder: 'https://x.com/sunnahenterprises' },
    social_youtube:   { name: 'YouTube',   placeholder: 'https://youtube.com/@sunnahenterprises' },
    social_linkedin:  { name: 'LinkedIn',  placeholder: 'https://linkedin.com/company/sunnahenterprises' },
    social_whatsapp:  { name: 'WhatsApp',  placeholder: 'https://wa.me/918639060986' },
  };
  const renderAdmSocial = async () => {
    const body = $('#adm-content');
    if (!body) return;
    body.innerHTML = `<div class="adm-section-title">Social Media</div>
      <div id="adm-social-pane">${skelAdmTable(3)}</div>`;
    const pane = $('#adm-social-pane');
    const { data: settings, error } = await fetchSiteSettings();
    if (error) {
      pane.innerHTML = `<div class="adm-error">Failed to load: ${esc(error.message)}</div>
        <p class="adm-empty-state-hint">If you haven't run <code>supabase/migrate_site_settings.sql</code> yet, do that first.</p>`;
      return;
    }
    pane.innerHTML = `
      <div class="adm-subsection-head">
        <div>
          <div class="adm-subsection-title">Footer social links</div>
          <div class="adm-subsection-sub">Public URLs shown as icon buttons in the site footer. Leave blank to hide an icon.</div>
        </div>
      </div>
      <form id="adm-social-form" style="max-width:680px">
        <div class="adm-form-grid" style="grid-template-columns:1fr">
          ${SOCIAL_KEYS.map(k => `
            <label class="adm-field adm-field--full">
              <span>${esc(SOCIAL_LABELS[k]?.name || k)}</span>
              <input class="adm-input" type="url" name="${esc(k)}"
                     value="${esc(settings[k] || '')}"
                     placeholder="${esc(SOCIAL_LABELS[k]?.placeholder || '')}" />
            </label>
          `).join('')}
        </div>
        <div class="adm-form-actions" style="justify-content:flex-start">
          <button type="submit" class="btn btn-green">Save</button>
          <span id="adm-social-status" aria-live="polite" style="color:#166534;font-size:13px;align-self:center"></span>
        </div>
      </form>`;
    const form = $('#adm-social-form');
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const status = $('#adm-social-status');
      const btn = form.querySelector('button[type="submit"]');
      if (btn) btn.disabled = true;
      if (status) status.textContent = 'Saving…';
      const entries = {};
      for (const k of SOCIAL_KEYS) {
        entries[k] = (form.elements[k]?.value || '').trim();
      }
      const { error: saveErr } = await updateSiteSettings(entries);
      if (btn) btn.disabled = false;
      if (saveErr) {
        if (status) status.textContent = 'Save failed: ' + (saveErr.message || 'unknown error');
        return;
      }
      if (status) status.textContent = 'Saved ✓';
      // Refresh the footer immediately so the admin sees their change.
      hydrateFooterSocial();
      setTimeout(() => { if (status) status.textContent = ''; }, 2500);
    });
  };

  const renderAdmContent = async () => {
    const body = $('#adm-content');
    if (!body) return;
    body.innerHTML = `
      <div class="adm-section-title">Content</div>
      <div class="adm-filter-row">
        <button class="adm-chip ${adminContentTab==='slides'?'active':''}" data-adm-content-tab="slides">Hero Slides</button>
        <button class="adm-chip ${adminContentTab==='featured'?'active':''}" data-adm-content-tab="featured">Featured Products</button>
        <button class="adm-chip ${adminContentTab==='blog'?'active':''}" data-adm-content-tab="blog">Blog Posts</button>
      </div>
      <div id="adm-content-pane">${skelAdmTable(6)}</div>`;
    if (adminContentTab === 'slides')   return renderAdmSlidesPane();
    if (adminContentTab === 'featured') return renderAdmFeaturedPane();
    if (adminContentTab === 'blog')     return renderAdmBlogPane();
  };

  /* ── Hero Slides ── */
  const renderAdmSlidesPane = async () => {
    const pane = $('#adm-content-pane');
    if (!pane) return;
    const { data: slides, error } = await adminLib.fetchAllHeroSlides();
    if (error) {
      pane.innerHTML = `<div class="adm-error">Failed: ${esc(error.message)}</div>
        <p class="adm-empty-state-hint">If you haven't run <code>supabase/migrate_content.sql</code> yet, do that first.</p>`;
      return;
    }
    pane.innerHTML = `
      <div class="adm-subsection-head">
        <div>
          <div class="adm-subsection-title">Hero Slides <span class="adm-count">(${slides.length})</span></div>
          <div class="adm-subsection-sub">Slides shown in the homepage hero carousel.</div>
        </div>
        <button class="adm-add-btn adm-add-btn--lg" data-adm-add-slide>+ New slide</button>
      </div>
      ${slides.length === 0 ? `
        <div class="adm-empty-state">
          <div style="color:#d1d5db;margin-bottom:8px">${admIcon('image', 44)}</div>
          <h3 class="adm-empty-title">No slides yet</h3>
          <p>Add one to drive traffic from the homepage banner.</p>
          <button class="btn btn-green" data-adm-add-slide type="button">+ Create your first slide</button>
        </div>
      ` : `
      <div class="adm-slide-grid">
        ${slides.map(s => `
          <article class="adm-slide-card ${s.active ? '' : 'adm-slide-card--inactive'}" data-adm-edit-slide="${esc(s.id)}">
            <div class="adm-slide-card-preview">
              ${s.image_url
                ? `<img src="${esc(s.image_url)}" alt="${esc(s.alt_text || s.title || '')}" loading="lazy" />`
                : `<div class="adm-slide-card-emoji">${esc(s.emoji || '🌟')}</div>`}
              <span class="adm-slide-card-kind">${s.kind === 'split' ? 'Split' : 'Banner'}</span>
              ${!s.active ? `<span class="adm-slide-card-status">Inactive</span>` : ''}
            </div>
            <div class="adm-slide-card-body">
              <div class="adm-slide-card-title">${esc(s.title || s.alt_text || '(untitled)')}</div>
              <div class="adm-slide-card-meta">
                ${s.link_view ? `<span>↗ ${esc(s.link_view)}</span>` : `<span style="color:#9ca3af">No link</span>`}
                <span class="adm-slide-card-sort">Sort: ${s.sort_order}</span>
              </div>
            </div>
            <div class="adm-slide-card-actions">
              <button class="adm-edit-btn" data-adm-edit-slide="${esc(s.id)}" data-stop type="button">Edit</button>
              <button class="adm-del-btn" data-adm-del-slide="${esc(s.id)}" data-stop type="button" aria-label="Delete slide">${admIcon('trash', 14)}</button>
            </div>
          </article>
        `).join('')}
      </div>`}`;
  };

  const openSlideDrawer = (slideId) => {
    openAdmDrawer({
      kind: 'slide',
      id: slideId || 'new',
      render: async (panel) => {
        let s = {
          kind: 'banner', image_url: '', alt_text: '', link_view: '',
          eyebrow: '', title: '', subtitle: '',
          price_label: '', original_label: '', save_label: '',
          cta_label: '', cta_target: '', emoji: '',
          sort_order: 0, active: true,
        };
        if (slideId) {
          const { data, error } = await supabase.from('hero_slides').select('*').eq('id', slideId).single();
          if (error) {
            panel.querySelector('.adm-dr-body').innerHTML =
              `<div class="adm-dr-sec adm-dr-empty">Failed to load slide: ${esc(error.message)}</div>`;
            return;
          }
          s = { ...s, ...data };
        }
        panel.querySelector('.adm-dr-eyebrow').textContent = 'Hero Slide';
        panel.querySelector('.adm-dr-title').textContent = slideId ? (s.title || s.alt_text || 'Edit slide') : 'New slide';

        panel.querySelector('.adm-dr-body').innerHTML = `
          <form id="adm-slide-form" class="adm-cp-form" novalidate>
            <div class="adm-dr-sec">
              <label class="adm-pe-field">
                <span class="adm-pe-label">Kind</span>
                <select name="kind">
                  <option value="banner" ${s.kind==='banner'?'selected':''}>Banner (full image)</option>
                  <option value="split"  ${s.kind==='split'?'selected':''}>Split (text + image)</option>
                </select>
                <span class="adm-pe-help">Banner = one big image. Split = left-side text + right-side image.</span>
              </label>
              <label class="adm-pe-field">
                <span class="adm-pe-label">Image URL</span>
                <input name="image_url" type="text" value="${esc(s.image_url || '')}" placeholder="https://… or upload below" />
              </label>
              <label class="adm-pe-field">
                <span class="adm-pe-label">Upload image</span>
                <input type="file" accept="image/*" data-adm-slide-upload />
                <span class="adm-pe-help">Auto-converts to WebP at 1600px max. Replaces the URL above.</span>
              </label>
              <label class="adm-pe-field">
                <span class="adm-pe-label">Alt text</span>
                <input name="alt_text" type="text" value="${esc(s.alt_text || '')}" placeholder="Describe the image for accessibility" />
              </label>
              <label class="adm-pe-field">
                <span class="adm-pe-label">Click destination</span>
                <input name="link_view" type="text" value="${esc(s.link_view || '')}" placeholder="products, category/honey, blog, …" />
                <span class="adm-pe-help">Hash-route target. Examples: <code>products</code>, <code>category/honey</code>, <code>blog</code>.</span>
              </label>
            </div>

            <div class="adm-dr-sec" data-slide-split-only style="${s.kind==='banner'?'display:none':''}">
              <div class="adm-dr-sec-title">Split-layout text</div>
              <label class="adm-pe-field"><span class="adm-pe-label">Eyebrow</span><input name="eyebrow" type="text" value="${esc(s.eyebrow||'')}" /></label>
              <label class="adm-pe-field"><span class="adm-pe-label">Title</span><input name="title" type="text" value="${esc(s.title||'')}" /></label>
              <label class="adm-pe-field"><span class="adm-pe-label">Subtitle</span><input name="subtitle" type="text" value="${esc(s.subtitle||'')}" /></label>
              <div class="adm-pe-row-3">
                <label class="adm-pe-field"><span class="adm-pe-label">Price</span><input name="price_label" type="text" value="${esc(s.price_label||'')}" placeholder="₹499" /></label>
                <label class="adm-pe-field"><span class="adm-pe-label">Original</span><input name="original_label" type="text" value="${esc(s.original_label||'')}" placeholder="₹699" /></label>
                <label class="adm-pe-field"><span class="adm-pe-label">Save</span><input name="save_label" type="text" value="${esc(s.save_label||'')}" placeholder="29% off" /></label>
              </div>
              <div class="adm-pe-row-2">
                <label class="adm-pe-field"><span class="adm-pe-label">CTA label</span><input name="cta_label" type="text" value="${esc(s.cta_label||'')}" placeholder="Shop Now" /></label>
                <label class="adm-pe-field"><span class="adm-pe-label">CTA target</span><input name="cta_target" type="text" value="${esc(s.cta_target||'')}" placeholder="products" /></label>
              </div>
              <label class="adm-pe-field"><span class="adm-pe-label">Emoji fallback</span><input name="emoji" type="text" value="${esc(s.emoji||'')}" placeholder="🍯" maxlength="4" /></label>
            </div>

            <div class="adm-dr-sec">
              <label class="adm-pe-field">
                <span class="adm-pe-label">Sort order</span>
                <input name="sort_order" type="number" value="${s.sort_order || 0}" />
                <span class="adm-pe-help">Lower = shown first.</span>
              </label>
              <label class="adm-pe-check">
                <input name="active" type="checkbox" ${s.active !== false ? 'checked' : ''} />
                <span>Active</span>
              </label>
            </div>

            <div class="adm-dr-foot">
              <div class="adm-dr-msg" id="adm-slide-msg" hidden></div>
              <button class="btn btn-green" type="submit">${slideId ? 'Update slide' : 'Create slide'}</button>
            </div>
          </form>`;

        // Toggle split-layout fields based on kind.
        panel.querySelector('select[name="kind"]').addEventListener('change', (ev) => {
          const splitSec = panel.querySelector('[data-slide-split-only]');
          splitSec.style.display = ev.target.value === 'split' ? '' : 'none';
        });

        // Upload handler.
        panel.querySelector('[data-adm-slide-upload]').addEventListener('change', async (ev) => {
          const file = ev.target.files && ev.target.files[0];
          if (!file) return;
          const msg = panel.querySelector('#adm-slide-msg');
          msg.textContent = 'Uploading…'; msg.hidden = false;
          const { url, error } = await adminLib.uploadProductImage(file, { productId: 'slide', kind: 'hero' });
          if (error) {
            msg.textContent = 'Upload failed: ' + error.message;
            return;
          }
          panel.querySelector('input[name="image_url"]').value = url;
          msg.textContent = 'Uploaded.';
        });

        panel.querySelector('#adm-slide-form').addEventListener('submit', async (ev) => {
          ev.preventDefault();
          const f = ev.currentTarget;
          const payload = {
            id: slideId || undefined,
            kind: f.kind.value,
            image_url:      f.image_url.value,
            alt_text:       f.alt_text.value,
            link_view:      f.link_view.value,
            eyebrow:        f.eyebrow.value,
            title:          f.title.value,
            subtitle:       f.subtitle.value,
            price_label:    f.price_label.value,
            original_label: f.original_label.value,
            save_label:     f.save_label.value,
            cta_label:      f.cta_label.value,
            cta_target:     f.cta_target.value,
            emoji:          f.emoji.value,
            sort_order:     parseInt(f.sort_order.value, 10) || 0,
            active:         f.active.checked,
          };
          const btn = f.querySelector('button[type="submit"]');
          const msg = panel.querySelector('#adm-slide-msg');
          btn.disabled = true; btn.textContent = 'Saving…';
          const { error } = await adminLib.upsertHeroSlide(payload);
          btn.disabled = false; btn.textContent = slideId ? 'Update slide' : 'Create slide';
          if (error) {
            msg.textContent = 'Error: ' + error.message; msg.hidden = false; return;
          }
          showToast(slideId ? 'Slide updated!' : 'Slide created!');
          closeAdmDrawer(false);
          if (adminTab === 'content' && adminContentTab === 'slides') renderAdmSlidesPane();
          // Repaint the live hero slider so the homepage shows the new state.
          await refreshHeroSlides();
        });
      },
    });
  };

  /* ── Featured Products ──
     Two-pane visual picker. Left: the currently-featured list (ordered).
     Right: searchable grid of every catalog product — click a tile to
     toggle it in/out of the featured set. */
  let admFeatPickerQuery = '';

  const renderAdmFeaturedPane = async () => {
    const pane = $('#adm-content-pane');
    if (!pane) return;
    const { data: rows, error } = await adminLib.fetchFeaturedRows();
    if (error) {
      pane.innerHTML = `<div class="adm-error">Failed: ${esc(error.message)}</div>
        <p class="adm-empty-state-hint">If you haven't run <code>supabase/migrate_content.sql</code> yet, do that first.</p>`;
      return;
    }
    const ids = rows.map(r => r.product_id);
    const featured = ids.map(id => PRODUCTS.find(p => p.id === id)).filter(Boolean);

    pane.innerHTML = `
      <div class="adm-subsection-head">
        <div>
          <div class="adm-subsection-title">Featured Products <span class="adm-count">(${featured.length})</span></div>
          <div class="adm-subsection-sub">These appear in the "Featured Products" rail on the homepage. Click a product on the right to add it; click again (or use the ✕) to remove.</div>
        </div>
      </div>

      <div class="adm-feat-layout">
        <!-- LEFT: currently featured (ordered) -->
        <section class="adm-feat-selected">
          <header class="adm-feat-selected-head">
            <span>Featured order</span>
            <span class="adm-feat-selected-count">${featured.length}</span>
          </header>
          ${featured.length === 0 ? `
            <div class="adm-feat-selected-empty">
              <div style="color:#d1d5db;margin-bottom:6px">${admIcon('reviews', 30)}</div>
              <div>No featured products yet.</div>
              <div style="font-size:12px;color:#6b7280;margin-top:4px">Click any product on the right to feature it.</div>
            </div>
          ` : `
          <ol class="adm-feat-selected-list">
            ${featured.map((p, i) => `
              <li class="adm-feat-selected-row" data-feat-id="${p.id}">
                <span class="adm-feat-selected-rank">${i + 1}</span>
                <div class="adm-feat-selected-thumb">
                  ${p.photo
                    ? `<img src="${esc(p.photo)}" alt="" loading="lazy" />`
                    : `<div class="adm-feat-selected-thumb-fallback">${admIcon('box', 18)}</div>`}
                </div>
                <div class="adm-feat-selected-body">
                  <div class="adm-feat-selected-name">${esc(p.name)}</div>
                  <div class="adm-feat-selected-meta">${esc(p.category || '')} · ₹${p.price}</div>
                </div>
                <div class="adm-feat-selected-actions">
                  <button class="adm-feat-arrow" data-adm-feat-up="${p.id}" ${i===0?'disabled':''} title="Move up" type="button">↑</button>
                  <button class="adm-feat-arrow" data-adm-feat-dn="${p.id}" ${i===featured.length-1?'disabled':''} title="Move down" type="button">↓</button>
                  <button class="adm-feat-rm" data-adm-feat-rm="${p.id}" title="Remove" type="button">✕</button>
                </div>
              </li>
            `).join('')}
          </ol>`}
        </section>

        <!-- RIGHT: searchable product picker -->
        <section class="adm-feat-picker">
          <header class="adm-feat-picker-head">
            <input type="search" id="adm-feat-search" class="adm-feat-search" placeholder="Search ${(PRODUCTS || []).length} products by name, category, or tag…" value="${esc(admFeatPickerQuery)}" />
          </header>
          <div class="adm-feat-grid" id="adm-feat-grid"></div>
        </section>
      </div>`;

    const featuredSet = new Set(ids);
    const renderGrid = () => {
      const q = admFeatPickerQuery.trim().toLowerCase();
      const list = (PRODUCTS || []).filter(p => {
        if (!q) return true;
        const tags = (p.tags || []).join(' ').toLowerCase();
        return `${p.name || ''} ${p.category || ''} ${tags}`.toLowerCase().includes(q);
      });
      const grid = $('#adm-feat-grid');
      if (!grid) return;
      if (!list.length) {
        grid.innerHTML = `<div class="adm-feat-grid-empty">No products match "${esc(admFeatPickerQuery)}".</div>`;
        return;
      }
      grid.innerHTML = list.map(p => {
        const isFeat = featuredSet.has(p.id);
        return `
          <button type="button" class="adm-feat-tile ${isFeat ? 'adm-feat-tile--on' : ''}" data-adm-feat-toggle="${p.id}" aria-pressed="${isFeat}">
            <div class="adm-feat-tile-thumb">
              ${p.photo
                ? `<img src="${esc(p.photo)}" alt="" loading="lazy" />`
                : `<div class="adm-feat-tile-thumb-fallback">${admIcon('box', 22)}</div>`}
              ${isFeat ? `<span class="adm-feat-tile-badge">✓ Featured</span>` : ''}
            </div>
            <div class="adm-feat-tile-name">${esc(p.name)}</div>
            <div class="adm-feat-tile-meta">${esc(p.category || '')} · ₹${p.price}</div>
          </button>`;
      }).join('');
    };
    renderGrid();

    // Search input — live filter, no debounce needed for a few hundred rows.
    const searchEl = $('#adm-feat-search');
    if (searchEl) {
      searchEl.addEventListener('input', () => {
        admFeatPickerQuery = searchEl.value;
        renderGrid();
      });
    }
  };

  // Helper: persist the current featured-ids list, then repaint the panes
  // and the live homepage rail. Used by toggle/up/down/remove handlers.
  const _saveFeaturedAndRefresh = async (newIds) => {
    const { error } = await adminLib.replaceFeaturedProducts(newIds);
    if (error) { showToast('Save failed: ' + error.message); return; }
    await renderAdmFeaturedPane();
    try { await refreshFeaturedIds(); } catch {}
  };

  /* ── Blog Posts ── */
  const renderAdmBlogPane = async () => {
    const pane = $('#adm-content-pane');
    if (!pane) return;
    const { data: posts, error } = await adminLib.fetchAllBlogPosts();
    if (error) {
      pane.innerHTML = `<div class="adm-error">Failed: ${esc(error.message)}</div>
        <p class="adm-empty-state-hint">If you haven't run <code>supabase/migrate_content.sql</code> yet, do that first.</p>`;
      return;
    }
    pane.innerHTML = `
      <div class="adm-subsection-head">
        <div>
          <div class="adm-subsection-title">Blog Posts <span class="adm-count">(${posts.length})</span></div>
          <div class="adm-subsection-sub">Articles shown on the /blog page. Draft posts (no published date) are hidden from the storefront.</div>
        </div>
        <button class="adm-add-btn adm-add-btn--lg" data-adm-add-blog>+ New post</button>
      </div>
      ${posts.length === 0 ? `
        <div class="adm-empty-state">
          <div style="color:#d1d5db;margin-bottom:8px">${admIcon('note', 44)}</div>
          <h3 class="adm-empty-title">No blog posts yet</h3>
          <p>Tell your customers about the wellness story behind every product.</p>
          <button class="btn btn-green" data-adm-add-blog type="button">+ Write your first post</button>
        </div>
      ` : `
      <div class="adm-blog-grid">
        ${posts.map(p => {
          const isDraft = !p.published_at;
          const fit = p.cover_fit === 'contain' ? 'contain' : 'cover';
          return `
          <article class="adm-blog-card ${p.active ? '' : 'adm-blog-card--inactive'}" data-adm-edit-blog="${esc(p.id)}">
            <div class="adm-blog-card-cover">
              ${p.cover_url
                ? `<img src="${esc(p.cover_url)}" alt="" loading="lazy" style="object-fit:${fit}" />`
                : `<div class="adm-blog-card-cover-fallback">${admIcon('note', 24)}</div>`}
              ${p.category ? `<span class="adm-blog-card-cat">${esc(p.category)}</span>` : ''}
              ${isDraft ? `<span class="adm-blog-card-draft">Draft</span>` : ''}
              ${!p.active ? `<span class="adm-blog-card-status">Inactive</span>` : ''}
            </div>
            <div class="adm-blog-card-body">
              <div class="adm-blog-card-title">${esc(p.title)}</div>
              ${p.excerpt ? `<p class="adm-blog-card-excerpt">${esc(p.excerpt)}</p>` : ''}
              <div class="adm-blog-card-meta">
                ${p.published_at ? _fmtDate(p.published_at) : 'Not published'}
                ${p.read_time_min ? ` · ${p.read_time_min} min read` : ''}
              </div>
            </div>
            <div class="adm-blog-card-actions">
              <button class="adm-edit-btn" data-adm-edit-blog="${esc(p.id)}" data-stop type="button">Edit</button>
              <button class="adm-del-btn" data-adm-del-blog="${esc(p.id)}" data-adm-del-blog-title="${esc(p.title)}" data-stop type="button" aria-label="Delete post">${admIcon('trash', 14)}</button>
            </div>
          </article>`;
        }).join('')}
      </div>`}`;
  };

  const openBlogDrawer = (postId) => {
    openAdmDrawer({
      kind: 'blog',
      id: postId || 'new',
      wide: true,
      render: async (panel) => {
        let p = {
          slug: '', title: '', category: '', excerpt: '', body: '',
          cover_url: '', cover_fit: 'cover', cover_alt: '', read_time_min: '',
          meta_title: '', meta_description: '', author: '',
          published_at: null, sort_order: 0, active: true,
        };
        if (postId) {
          const { data, error } = await supabase.from('blog_posts').select('*').eq('id', postId).single();
          if (error) {
            panel.querySelector('.adm-dr-body').innerHTML =
              `<div class="adm-dr-sec adm-dr-empty">Failed to load post: ${esc(error.message)}</div>`;
            return;
          }
          p = { ...p, ...data };
        }
        panel.querySelector('.adm-dr-eyebrow').textContent = 'Blog post';
        panel.querySelector('.adm-dr-title').textContent = postId ? (p.title || 'Edit post') : 'New blog post';

        panel.querySelector('.adm-dr-body').innerHTML = `
          <form id="adm-blog-form" class="adm-cp-form" novalidate>
            <div class="adm-dr-sec">
              <label class="adm-pe-field">
                <span class="adm-pe-label">Title</span>
                <input name="title" type="text" value="${esc(p.title)}" required />
              </label>
              <label class="adm-pe-field">
                <span class="adm-pe-label">Slug</span>
                <input name="slug" type="text" value="${esc(p.slug)}" placeholder="auto-generated from title" />
                <span class="adm-pe-help">The article's URL: <code>/blog/<span data-adm-slug-preview>${esc(p.slug || 'your-slug')}</span></code>. Lower-case, hyphens only. Changing it on a published post breaks existing links.</span>
              </label>
              <div class="adm-pe-row-2">
                <label class="adm-pe-field">
                  <span class="adm-pe-label">Category</span>
                  <input name="category" type="text" value="${esc(p.category||'')}" placeholder="Wellness, Sunnah Foods, …" />
                </label>
                <label class="adm-pe-field">
                  <span class="adm-pe-label">Read time (min)</span>
                  <input name="read_time_min" type="number" value="${p.read_time_min || ''}" placeholder="5" min="1" />
                </label>
              </div>
              <label class="adm-pe-field">
                <span class="adm-pe-label">Excerpt</span>
                <textarea name="excerpt" rows="3">${esc(p.excerpt||'')}</textarea>
                <span class="adm-pe-help">One or two sentences. Shown on the blog card, under the headline on the article page, and used as the meta description when the SEO field below is blank.</span>
              </label>
            </div>

            <div class="adm-dr-sec">
              <div class="adm-dr-sec-title">Cover image</div>
              <label class="adm-pe-field">
                <span class="adm-pe-label">Cover URL</span>
                <input name="cover_url" type="text" value="${esc(p.cover_url||'')}" placeholder="https://… or upload below" />
              </label>
              <label class="adm-pe-field">
                <span class="adm-pe-label">Upload cover</span>
                <input type="file" accept="image/*" data-adm-blog-upload />
              </label>
              <label class="adm-pe-field">
                <span class="adm-pe-label">Cover alt text</span>
                <input name="cover_alt" type="text" value="${esc(p.cover_alt||'')}" placeholder="Describe the image for screen readers & image search" />
              </label>
              <label class="adm-pe-field">
                <span class="adm-pe-label">Cover fit</span>
                <select name="cover_fit">
                  <option value="cover"   ${p.cover_fit!=='contain'?'selected':''}>Cover (fill)</option>
                  <option value="contain" ${p.cover_fit==='contain'?'selected':''}>Contain (letterbox)</option>
                </select>
              </label>
            </div>

            <div class="adm-dr-sec">
              <label class="adm-pe-field">
                <span class="adm-pe-label">Body (HTML allowed)</span>
                <textarea name="body" rows="14" style="font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:13px">${esc(p.body||'')}</textarea>
                <span class="adm-pe-help">Plain text is fine — a blank line starts a new paragraph, <code>## Heading</code> starts a section, <code>- item</code> makes a bullet list, <code>**bold**</code>, and <code>[text](/product/slug)</code> links a product. Raw HTML (&lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;) also works. Two or more headings automatically build the "On this page" jump menu, and every heading gets its own anchor for Google.</span>
              </label>
              <div class="adm-pe-help" style="margin-top:6px">Skeleton to copy:
                <pre style="white-space:pre-wrap;font-size:12px;margin:6px 0 0">Opening paragraph — answer the reader's question in the first two lines.

## First section

Explain it here.

- A point
- Another point

## How to use it

Half a teaspoon daily. Shop [Sidr honey](/product/sidr-honey).</pre>
              </div>
              <div class="adm-pe-field" style="margin-top:10px">
                <span class="adm-pe-label">Link a product</span>
                <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center">
                  <select data-adm-blog-product style="flex:1 1 220px;min-width:0">
                    <option value="">Choose a product…</option>
                    ${(PRODUCTS || [])
                      .filter(pr => !pr.isBundle)
                      .slice()
                      .sort((a, b) => String(a.name).localeCompare(String(b.name)))
                      .map(pr => `<option value="${esc(productSlug(pr))}" data-name="${esc(pr.name)}">${esc(pr.name)}${pr.weight ? ' — ' + esc(pr.weight) : ''}</option>`)
                      .join('')}
                  </select>
                  <button class="adm-edit-btn" type="button" data-adm-blog-insert-product>Insert link</button>
                </div>
                <span class="adm-pe-help">Drops <code>[Product name](/product/slug)</code> where the cursor is. Select words in the body first to turn those words into the link.</span>
              </div>
              <button class="adm-edit-btn" type="button" data-adm-blog-preview style="margin-top:10px">Preview article</button>
              <div class="blog-reader-content" data-adm-blog-preview-out hidden
                   style="margin-top:12px;padding:16px 18px;background:#fff;border:1px solid rgba(11,74,36,0.12);border-radius:12px;max-height:420px;overflow:auto"></div>
            </div>

            <div class="adm-dr-sec">
              <div class="adm-dr-sec-title">SEO</div>
              <label class="adm-pe-field">
                <span class="adm-pe-label">Meta title</span>
                <input name="meta_title" type="text" value="${esc(p.meta_title||'')}" placeholder="Leave blank to use the post title" maxlength="70" />
                <span class="adm-pe-help">Shown as the Google result headline. Keep under ~60 characters.</span>
              </label>
              <label class="adm-pe-field">
                <span class="adm-pe-label">Meta description</span>
                <textarea name="meta_description" rows="2" maxlength="200">${esc(p.meta_description||'')}</textarea>
                <span class="adm-pe-help">The grey snippet under the headline. ~155 characters. Falls back to the excerpt.</span>
              </label>
              <label class="adm-pe-field">
                <span class="adm-pe-label">Author</span>
                <input name="author" type="text" value="${esc(p.author||'')}" placeholder="Sunnah Enterprises" />
              </label>
            </div>

            <div class="adm-dr-sec">
              <div class="adm-pe-row-2">
                <label class="adm-pe-field">
                  <span class="adm-pe-label">Published at</span>
                  <input name="published_at" type="datetime-local" value="${esc(_toLocalDT(p.published_at))}" />
                  <span class="adm-pe-help">Leave blank to keep as draft.</span>
                </label>
                <label class="adm-pe-field">
                  <span class="adm-pe-label">Sort order</span>
                  <input name="sort_order" type="number" value="${p.sort_order || 0}" />
                </label>
              </div>
              <label class="adm-pe-check">
                <input name="active" type="checkbox" ${p.active !== false ? 'checked' : ''} />
                <span>Active — show this post on the storefront</span>
              </label>
            </div>

            <div class="adm-dr-foot">
              <div class="adm-dr-msg" id="adm-blog-msg" hidden></div>
              <button class="btn btn-green" type="submit">${postId ? 'Update post' : 'Create post'}</button>
            </div>
          </form>`;

        // Auto-slug from title when slug is empty.
        const titleEl = panel.querySelector('input[name="title"]');
        const slugEl  = panel.querySelector('input[name="slug"]');
        let slugDirty = !!p.slug;
        const slugPreview = panel.querySelector('[data-adm-slug-preview]');
        const paintSlug = () => { if (slugPreview) slugPreview.textContent = slugEl.value || 'your-slug'; };
        slugEl.addEventListener('input', () => { slugDirty = true; paintSlug(); });
        titleEl.addEventListener('input', () => {
          if (slugDirty) return;
          slugEl.value = slugify(titleEl.value);
          paintSlug();
        });

        // Insert a product link at the cursor. The body renderer already turns
        // [text](/product/slug) into a real link — this is just the picker so
        // nobody has to hand-type slugs.
        panel.querySelector('[data-adm-blog-insert-product]').addEventListener('click', () => {
          const sel = panel.querySelector('[data-adm-blog-product]');
          const opt = sel.selectedOptions[0];
          if (!opt || !opt.value) { sel.focus(); return; }
          const ta = panel.querySelector('textarea[name="body"]');
          const start = ta.selectionStart ?? ta.value.length;
          const end   = ta.selectionEnd ?? start;
          const label = ta.value.slice(start, end).trim() || opt.dataset.name;
          const snippet = `[${label}](/product/${opt.value})`;
          ta.value = ta.value.slice(0, start) + snippet + ta.value.slice(end);
          ta.focus();
          ta.selectionStart = ta.selectionEnd = start + snippet.length;
        });

        // Live preview — renders the body exactly the way the article page will.
        panel.querySelector('[data-adm-blog-preview]').addEventListener('click', () => {
          const out = panel.querySelector('[data-adm-blog-preview-out]');
          const btn = panel.querySelector('[data-adm-blog-preview]');
          if (!out.hidden) { out.hidden = true; btn.textContent = 'Preview article'; return; }
          out.innerHTML = renderBlogBody(panel.querySelector('textarea[name="body"]').value);
          out.hidden = false;
          btn.textContent = 'Hide preview';
        });

        // Upload handler.
        panel.querySelector('[data-adm-blog-upload]').addEventListener('change', async (ev) => {
          const file = ev.target.files && ev.target.files[0];
          if (!file) return;
          const msg = panel.querySelector('#adm-blog-msg');
          msg.textContent = 'Uploading…'; msg.hidden = false;
          const { url, error } = await adminLib.uploadProductImage(file, { productId: 'blog', kind: 'cover' });
          if (error) { msg.textContent = 'Upload failed: ' + error.message; return; }
          panel.querySelector('input[name="cover_url"]').value = url;
          msg.textContent = 'Uploaded.';
        });

        panel.querySelector('#adm-blog-form').addEventListener('submit', async (ev) => {
          ev.preventDefault();
          const f = ev.currentTarget;
          const dtLocal = f.published_at.value;
          // Convert datetime-local → ISO; left empty = null draft.
          const publishedAt = dtLocal ? new Date(dtLocal).toISOString() : null;
          const payload = {
            id: postId || undefined,
            slug:          (f.slug.value.trim() || slugify(f.title.value)),
            title:         f.title.value,
            category:      f.category.value,
            excerpt:       f.excerpt.value,
            body:          f.body.value,
            cover_url:     f.cover_url.value,
            cover_fit:     f.cover_fit.value,
            cover_alt:     f.cover_alt.value,
            meta_title:       f.meta_title.value,
            meta_description: f.meta_description.value,
            author:           f.author.value,
            read_time_min: f.read_time_min.value,
            published_at:  publishedAt,
            sort_order:    parseInt(f.sort_order.value, 10) || 0,
            active:        f.active.checked,
          };
          const btn = f.querySelector('button[type="submit"]');
          const msg = panel.querySelector('#adm-blog-msg');
          btn.disabled = true; btn.textContent = 'Saving…';
          const { error } = await adminLib.upsertBlogPost(payload);
          btn.disabled = false; btn.textContent = postId ? 'Update post' : 'Create post';
          if (error) { msg.textContent = 'Error: ' + error.message; msg.hidden = false; return; }
          showToast(postId ? 'Post updated!' : 'Post created!');
          closeAdmDrawer(false);
          if (adminTab === 'content' && adminContentTab === 'blog') renderAdmBlogPane();
          await refreshBlogPosts();
        });
      },
    });
  };

  /* ── Admin event delegation ── */
  document.addEventListener('click', e => {
    // Tab switching — route through setView so the URL deep-links the tab
    // (/admin/orders) and a refresh keeps you on it.
    const tabBtn = e.target.closest('[data-adm-tab]');
    if (tabBtn && $('#view-admin')?.classList.contains('active')) {
      setView('admin', tabBtn.dataset.admTab);
      return;
    }

    // Bell dropdown toggle
    const bellBtn = e.target.closest('#adm-bell');
    if (bellBtn) {
      const dd = $('#adm-bell-dropdown');
      if (dd) dd.hidden = !dd.hidden;
      return;
    }
    // Close bell dropdown when clicking outside
    if (!e.target.closest('#adm-bell-dropdown') && !e.target.closest('#adm-bell')) {
      const dd = $('#adm-bell-dropdown');
      if (dd) dd.hidden = true;
    }

    // ── Slide-out drawer ───────────────────────────────────────────
    // Drawer close / back chrome buttons.
    if (e.target.closest('[data-adm-dr-close]')) { closeAdmDrawer(true); return; }
    if (e.target.closest('[data-adm-dr-back]'))  { closeAdmDrawer(false); return; }

    // Copy address (or any small "Copy" pill inside the drawer).
    const copyBtn = e.target.closest('[data-adm-dr-copy]');
    if (copyBtn) {
      const text = copyBtn.dataset.admDrCopy || '';
      navigator.clipboard?.writeText(text).then(
        () => { const orig = copyBtn.textContent; copyBtn.textContent = 'Copied!'; setTimeout(() => copyBtn.textContent = orig, 1200); },
        () => showToast('Copy failed'),
      );
      return;
    }

    // Refund flow inside the Order drawer.
    const refundOpen = e.target.closest('[data-adm-dr-refund-open]');
    if (refundOpen) {
      const host = refundOpen.closest('[data-adm-dr-refund-host]');
      if (host) {
        host.querySelector('.adm-dr-refund-form').hidden = false;
        refundOpen.style.display = 'none';
      }
      return;
    }
    const refundCancel = e.target.closest('[data-adm-dr-refund-cancel]');
    if (refundCancel) {
      const host = refundCancel.closest('[data-adm-dr-refund-host]');
      if (host) {
        host.querySelector('.adm-dr-refund-form').hidden = true;
        host.querySelector('[data-adm-dr-refund-open]').style.display = '';
        const msg = host.querySelector('.adm-dr-refund-msg');
        if (msg) { msg.hidden = true; msg.textContent = ''; msg.classList.remove('adm-dr-refund-msg--err'); }
      }
      return;
    }
    const refundSubmit = e.target.closest('[data-adm-dr-refund-submit]');
    if (refundSubmit) {
      const host = refundSubmit.closest('[data-adm-dr-refund-host]');
      if (!host) return;
      const orderId = host.dataset.orderId;
      const orderTotal = parseFloat(host.dataset.orderTotal || '0');
      const amountInput = host.querySelector('input[name="amount"]');
      const reasonInput = host.querySelector('input[name="reason"]');
      const amount = parseFloat(amountInput?.value || '');
      const reason = (reasonInput?.value || '').trim();
      const msg = host.querySelector('.adm-dr-refund-msg');
      msg.classList.remove('adm-dr-refund-msg--err');
      msg.hidden = true; msg.textContent = '';

      if (!Number.isFinite(amount) || amount <= 0) {
        msg.textContent = 'Enter a positive amount.';
        msg.classList.add('adm-dr-refund-msg--err'); msg.hidden = false;
        return;
      }
      if (amount > orderTotal + 0.001) {
        msg.textContent = `Amount can't exceed the order total (₹${orderTotal}).`;
        msg.classList.add('adm-dr-refund-msg--err'); msg.hidden = false;
        return;
      }
      const isFull = Math.abs(amount - orderTotal) < 0.01;
      const confirmMsg = isFull
        ? `Refund the full ₹${orderTotal} to the customer? This is irreversible.`
        : `Issue a partial refund of ₹${amount} (of ₹${orderTotal})? This is irreversible.`;
      if (!confirm(confirmMsg)) return;

      (async () => {
        refundSubmit.disabled = true;
        refundSubmit.textContent = 'Refunding…';
        const payload = isFull ? {} : { amount };
        if (reason) payload.notes = { reason };
        const { data, error } = await adminLib.refundOrder(orderId, payload);
        refundSubmit.disabled = false;
        refundSubmit.textContent = `Refund ₹${orderTotal}`;
        if (error) {
          msg.textContent = 'Refund failed: ' + error.message;
          msg.classList.add('adm-dr-refund-msg--err'); msg.hidden = false;
          return;
        }
        showToast(isFull ? 'Full refund issued.' : 'Partial refund issued.');
        // Best-effort WhatsApp notification — see note in the status-change handler.
        adminLib.sendOrderWhatsApp(orderId, 'refunded').then(({ error: waErr }) => {
          if (waErr) dwarn('[wa] refund notify failed:', waErr.message);
        });
        adminLib.sendOrderEmail(orderId, 'refunded').then(({ error: mailErr }) => {
          if (mailErr) dwarn('[mail] refund notify failed:', mailErr.message);
        });
        adminLib.sendOrderSms(orderId, 'refunded').then(({ error: smsErr }) => {
          if (smsErr) dwarn('[sms] refund notify failed:', smsErr.message);
        });
        closeAdmDrawer(false);
        // Repaint whichever admin view is current.
        if (adminTab === 'orders')   renderAdmOrders();
        if (adminTab === 'payments') renderAdmPayments();
        // Avoid lint complaining about unused `data`.
        void data;
      })();
      return;
    }

    // Order row clicked in the Orders table → open order drawer.
    // Ignore clicks on the inline status <select> + its options.
    const orderRow = e.target.closest('[data-adm-open-order]');
    if (orderRow && !e.target.closest('.adm-status-sel') && !e.target.closest('select, option')) {
      openOrderDrawer(orderRow.dataset.admOpenOrder);
      return;
    }

    // Customer row clicked in the Customers table → open customer drawer.
    const custRow = e.target.closest('[data-adm-open-customer]');
    if (custRow) {
      openCustomerDrawer(custRow.dataset.admOpenCustomer);
      return;
    }

    // Edit coupon button → open the coupon drawer.
    const editCp = e.target.closest('[data-adm-edit-coupon]');
    if (editCp) { openCouponDrawer(editCp.dataset.admEditCoupon); return; }

    // Delete coupon button.
    const delCp = e.target.closest('[data-adm-del-coupon]');
    if (delCp) {
      const id = delCp.dataset.admDelCoupon;
      const code = delCp.dataset.admDelCouponCode || '';
      if (!confirm(`Delete coupon ${code}? Historical orders keep their snapshot but the code stops working.`)) return;
      (async () => {
        delCp.disabled = true;
        const { error } = await adminLib.deleteCoupon(id);
        delCp.disabled = false;
        if (error) { showToast('Delete failed: ' + error.message); return; }
        showToast('Coupon deleted.');
        renderAdmCoupons();
      })();
      return;
    }

    // ── Reviews moderation ──────────────────────────────────────
    const revFilter = e.target.closest('[data-adm-rev-filter]');
    if (revFilter) {
      adminReviewFilter = revFilter.dataset.admRevFilter;
      renderAdmReviews();
      return;
    }
    const revStatusBtn = e.target.closest('[data-adm-rev-status]');
    if (revStatusBtn) {
      const id = revStatusBtn.dataset.admRevId;
      const status = revStatusBtn.dataset.admRevStatus;
      (async () => {
        revStatusBtn.disabled = true;
        const { error } = await adminLib.updateReviewStatus(id, status);
        revStatusBtn.disabled = false;
        if (error) { showToast('Update failed: ' + error.message); return; }
        showToast('Review ' + (status === 'approved' ? 'approved' : status === 'hidden' ? 'hidden' : 'marked pending') + '.');
        renderAdmReviews();
        // Refresh the pending-badge count after the status change.
        try {
          const { count } = await adminLib.countPendingReviews();
          adminPendingReviews = count;
          const badge = $('#adm-nav-rev-badge');
          if (badge) badge.textContent = count > 99 ? '99+' : count;
        } catch {}
      })();
      return;
    }
    const revDel = e.target.closest('[data-adm-rev-delete]');
    if (revDel) {
      if (!confirm('Delete this review? This cannot be undone.')) return;
      (async () => {
        revDel.disabled = true;
        const { error } = await adminLib.deleteReview(revDel.dataset.admRevDelete);
        revDel.disabled = false;
        if (error) { showToast('Delete failed: ' + error.message); return; }
        showToast('Review deleted.');
        renderAdmReviews();
      })();
      return;
    }

    // ── Content tab: sub-tab switch ─────────────────────────────
    const contentTabBtn = e.target.closest('[data-adm-content-tab]');
    if (contentTabBtn) {
      adminContentTab = contentTabBtn.dataset.admContentTab;
      renderAdmContent();
      return;
    }

    // Slides: add / edit / delete. Delete is checked BEFORE edit so the
    // 🗑️ button nested inside a clickable card opens the confirm rather
    // than the edit drawer.
    if (e.target.closest('[data-adm-add-slide]')) { openSlideDrawer(null); return; }
    const delSlide = e.target.closest('[data-adm-del-slide]');
    if (delSlide) {
      if (!confirm('Delete this slide? It will stop showing on the homepage.')) return;
      (async () => {
        delSlide.disabled = true;
        const { error } = await adminLib.deleteHeroSlide(delSlide.dataset.admDelSlide);
        delSlide.disabled = false;
        if (error) { showToast('Delete failed: ' + error.message); return; }
        showToast('Slide deleted.');
        renderAdmSlidesPane();
        await refreshHeroSlides();
      })();
      return;
    }
    const editSlide = e.target.closest('[data-adm-edit-slide]');
    if (editSlide) { openSlideDrawer(editSlide.dataset.admEditSlide); return; }

    // Featured products: toggle tile (add or remove) from the picker grid
    const featToggle = e.target.closest('[data-adm-feat-toggle]');
    if (featToggle) {
      const targetId = parseInt(featToggle.dataset.admFeatToggle, 10);
      (async () => {
        const { data: rows } = await adminLib.fetchFeaturedRows();
        let ids = rows.map(r => r.product_id);
        const i = ids.indexOf(targetId);
        if (i >= 0) ids.splice(i, 1);
        else        ids.push(targetId);
        await _saveFeaturedAndRefresh(ids);
      })();
      return;
    }

    // Featured products: move up / down / remove (left-pane controls)
    const featUp = e.target.closest('[data-adm-feat-up]');
    const featDn = e.target.closest('[data-adm-feat-dn]');
    const featRm = e.target.closest('[data-adm-feat-rm]');
    if (featUp || featDn || featRm) {
      (async () => {
        const { data: rows } = await adminLib.fetchFeaturedRows();
        let ids = rows.map(r => r.product_id);
        const targetId = parseInt(
          (featUp?.dataset.admFeatUp) ||
          (featDn?.dataset.admFeatDn) ||
          (featRm?.dataset.admFeatRm),
          10,
        );
        const i = ids.indexOf(targetId);
        if (i < 0) return;
        if (featUp && i > 0)                 { [ids[i-1], ids[i]] = [ids[i], ids[i-1]]; }
        else if (featDn && i < ids.length-1) { [ids[i+1], ids[i]] = [ids[i], ids[i+1]]; }
        else if (featRm)                     { ids.splice(i, 1); }
        else return;
        await _saveFeaturedAndRefresh(ids);
      })();
      return;
    }

    // Blog: add / edit / delete. Delete before edit so 🗑️ on a clickable
    // card opens the confirm rather than the edit drawer.
    if (e.target.closest('[data-adm-add-blog]')) { openBlogDrawer(null); return; }
    const delBlog = e.target.closest('[data-adm-del-blog]');
    if (delBlog) {
      const title = delBlog.dataset.admDelBlogTitle || '';
      if (!confirm(`Delete blog post "${title}"? This can't be undone.`)) return;
      (async () => {
        delBlog.disabled = true;
        const { error } = await adminLib.deleteBlogPost(delBlog.dataset.admDelBlog);
        delBlog.disabled = false;
        if (error) { showToast('Delete failed: ' + error.message); return; }
        showToast('Post deleted.');
        renderAdmBlogPane();
        await refreshBlogPosts();
      })();
      return;
    }
    const editBlog = e.target.closest('[data-adm-edit-blog]');
    if (editBlog) { openBlogDrawer(editBlog.dataset.admEditBlog); return; }

    // Inside the order drawer: "View customer →" link.
    const viewCustBtn = e.target.closest('[data-adm-dr-open-customer]');
    if (viewCustBtn) {
      openCustomerDrawer(viewCustBtn.dataset.admDrOpenCustomer);
      return;
    }

    // Inside the customer drawer: a clickable order row in their history.
    const drOrderRow = e.target.closest('[data-adm-dr-open-order]');
    if (drOrderRow) {
      openOrderDrawer(drOrderRow.dataset.admDrOpenOrder);
      return;
    }

    // Inside the customer drawer: toggle admin role.
    const toggleAdminBtn = e.target.closest('[data-adm-dr-toggle-admin]');
    if (toggleAdminBtn) {
      const id = toggleAdminBtn.dataset.admDrToggleAdmin;
      const isAdmin = toggleAdminBtn.dataset.currentIsAdmin === 'true';
      if (!confirm(`${isAdmin ? 'Remove' : 'Grant'} admin access for this user?`)) return;
      (async () => {
        toggleAdminBtn.disabled = true;
        const orig = toggleAdminBtn.textContent;
        toggleAdminBtn.textContent = '…';
        const { error } = await adminLib.updateProfileAdmin(id, { is_admin: !isAdmin });
        if (error) {
          showToast('Error: ' + error.message);
          toggleAdminBtn.textContent = orig;
          toggleAdminBtn.disabled = false;
          return;
        }
        showToast('Role updated.');
        // Re-render the customer drawer (pop + re-open) and the customers list.
        closeAdmDrawer(false);
        openCustomerDrawer(id);
        if (adminTab === 'customers') renderAdmCustomers();
      })();
      return;
    }

    // Customers tab filter chips.
    const custChip = e.target.closest('[data-adm-cust-filter]');
    if (custChip && $('#view-admin')?.classList.contains('active')) {
      adminCustomerFilter = custChip.dataset.admCustFilter;
      renderAdmCustomers();
      return;
    }

    // Messages filter chips
    const msgChip = e.target.closest('[data-adm-msg-filter]');
    if (msgChip && $('#view-admin')?.classList.contains('active')) {
      adminMessageFilter = msgChip.dataset.admMsgFilter;
      renderAdmMessages();
      return;
    }

    // Message status flip
    const msgStatusBtn = e.target.closest('[data-adm-msg-status]');
    if (msgStatusBtn) {
      const id = msgStatusBtn.dataset.admMsgId;
      const status = msgStatusBtn.dataset.admMsgStatus;
      (async () => {
        msgStatusBtn.disabled = true;
        const { error } = await adminLib.updateMessageStatus(id, status);
        if (error) { showToast('Error: ' + error.message); msgStatusBtn.disabled = false; return; }
        // Refresh badge + list.
        const { count } = await adminLib.countUnreadMessages();
        adminUnreadMessages = count;
        const badge = $('#adm-nav-msg-badge');
        if (badge) {
          if (count > 0) badge.textContent = count > 99 ? '99+' : count;
          else badge.remove();
        }
        renderAdmMessages();
      })();
      return;
    }

    // Message delete
    const msgDelBtn = e.target.closest('[data-adm-msg-delete]');
    if (msgDelBtn) {
      const id = msgDelBtn.dataset.admMsgDelete;
      if (!confirm('Delete this message? This cannot be undone.')) return;
      (async () => {
        msgDelBtn.disabled = true;
        const { error } = await adminLib.deleteMessage(id);
        if (error) { showToast('Error: ' + error.message); msgDelBtn.disabled = false; return; }
        const { count } = await adminLib.countUnreadMessages();
        adminUnreadMessages = count;
        renderAdmMessages();
      })();
      return;
    }

    // More orders button (on dashboard)
    if (e.target.closest('[data-adm-tab="orders"]') && !$('#view-admin')?.classList.contains('active')) return;

    // Status filter chips (orders tab)
    const statusChip = e.target.closest('[data-adm-status-filter]');
    if (statusChip) {
      adminOrderFilter = statusChip.dataset.admStatusFilter;
      renderAdmOrders();
      return;
    }

    // Pin/unpin product (row button + product-editor drawer button)
    const pinBtn = e.target.closest('[data-adm-pin-product]');
    if (pinBtn) {
      (async () => {
        try {
          await togglePinProduct(pinBtn);
        } catch (e) {
          pinBtn.disabled = false;
          showToast('Pin failed — please try again');
        }
      })();
      return;
    }

    // Edit product button
    const editBtn = e.target.closest('[data-adm-edit-product]');
    if (editBtn) {
      const pid = parseInt(editBtn.dataset.admEditProduct);
      const body = $('#adm-content');
      const cached = (body?._products || []).find(x => x.id === pid);
      if (cached) {
        openProductEditor(cached);
      } else {
        // Cache miss — fetch fresh and open. Avoids the silent-fail bug where
        // a missing _products array caused Edit clicks to do nothing.
        editBtn.disabled = true;
        const orig = editBtn.textContent;
        editBtn.textContent = '…';
        adminLib.fetchAllProductsAdmin().then(({ data, error }) => {
          editBtn.disabled = false;
          editBtn.textContent = orig;
          if (error) { showToast('Failed to load product: ' + error.message); return; }
          if (body) body._products = data;
          const p = (data || []).find(x => x.id === pid);
          if (!p) { showToast('Product #' + pid + ' not found.'); return; }
          openProductEditor(p);
        });
      }
      return;
    }

    // Delete product
    const delBtn = e.target.closest('[data-adm-del-product]');
    if (delBtn) {
      const pid = parseInt(delBtn.dataset.admDelProduct);
      if (!confirm('Delete this product? This cannot be undone.')) return;
      adminLib.deleteProduct(pid).then(async ({ error }) => {
        if (error) { showToast('Delete failed: ' + error.message); return; }
        showToast('Product deleted.');
        clearCatalogCache?.();
        await refreshCatalogInMemory();
        renderAdmProducts();
      });
      return;
    }
  });

  // Status dropdown change (orders tab + order drawer).
  document.addEventListener('change', async e => {
    const sel = e.target.closest('.adm-status-sel');
    if (!sel) return;
    const orderId = sel.dataset.orderId;
    const newStatus = sel.value;
    // Update both possible badge locations: the table row + the drawer header.
    [`#adm-badge-${orderId}`, `#adm-dr-badge-${orderId}`].forEach(selector => {
      const badge = document.querySelector(selector);
      if (badge) {
        badge.textContent = STATUS_LABELS[newStatus] || newStatus;
        badge.className = `adm-badge adm-badge--${newStatus}`;
      }
    });
    // Keep any other selects for the same order in sync.
    document.querySelectorAll(`.adm-status-sel[data-order-id="${CSS.escape(orderId)}"]`).forEach(s => {
      if (s !== sel) s.value = newStatus;
    });
    const { error } = await adminLib.updateOrderStatus(orderId, newStatus);
    if (error) { showToast('Update failed: ' + error.message); return; }
    showToast('Status updated to ' + STATUS_LABELS[newStatus]);

    // Best-effort WhatsApp template notification — only fires for the
    // statuses we have approved templates for. Failures (BSP not set up
    // yet, template not approved, customer has no phone) are swallowed
    // silently because WhatsApp is a side channel; the status change
    // itself already succeeded.
    const WA_STATUS_EVENTS = {
      confirmed: 'confirmed',
      shipped:   'shipped',
      delivered: 'delivered',
      cancelled: 'cancelled',
    };
    const waEvent = WA_STATUS_EVENTS[newStatus];
    if (waEvent) {
      adminLib.sendOrderWhatsApp(orderId, waEvent).then(({ error: waErr }) => {
        if (waErr) dwarn('[wa] order status notify failed:', waErr.message);
      });
    }

    // Same idea over email. 'confirmed' is missing on purpose — the
    // confirmation mail already went out when payment cleared, so resending
    // it here would just duplicate it.
    // Unlike WhatsApp this is not silent: a status mail that never goes out
    // looks identical to one that did, so surface both the hard errors and
    // the "nothing to send" no-op (no Resend key, order has no email).
    if (['shipped', 'delivered', 'cancelled'].includes(newStatus)) {
      // SMS rides the same events as the mail. Kept quiet like WhatsApp:
      // MSG91 is a side channel and the DLT templates go live one at a time,
      // so an unconfigured event must not nag the admin on every update.
      adminLib.sendOrderSms(orderId, newStatus).then(({ error: smsErr }) => {
        if (smsErr) dwarn('[sms] order status notify failed:', smsErr.message);
      });
      adminLib.sendOrderEmail(orderId, newStatus).then(({ data, error: mailErr }) => {
        if (mailErr) {
          dwarn('[mail] order status notify failed:', mailErr.message);
          showToast('Status saved, but the email failed: ' + mailErr.message);
        } else if (data && data.sent === false) {
          dwarn('[mail] order status notify skipped (no email on order / mailer not configured)');
          showToast('Status saved — no email sent (order has no email address, or the mailer is not configured).');
        }
      });
    }
  });

  // Drawer notes auto-save on blur (both order + customer admin notes).
  document.addEventListener('blur', async (e) => {
    const ta = e.target.closest('.adm-dr-notes');
    if (!ta) return;
    const orderId = ta.dataset.admDrOrderNotes;
    const profileId = ta.dataset.admDrCustomerNotes;
    if (!orderId && !profileId) return;
    if (ta.dataset.lastSaved === ta.value) return; // no change since last save
    const hint = ta.parentElement?.querySelector('[data-adm-dr-notes-hint]');
    if (hint) { hint.textContent = 'Saving…'; hint.classList.remove('adm-dr-notes-hint--saved', 'adm-dr-notes-hint--error'); }
    const { error } = orderId
      ? await adminLib.updateOrderAdminNotes(orderId, ta.value)
      : await adminLib.updateCustomerAdminNotes(profileId, ta.value);
    if (error) {
      if (hint) { hint.textContent = 'Save failed: ' + error.message; hint.classList.add('adm-dr-notes-hint--error'); }
    } else {
      ta.dataset.lastSaved = ta.value;
      if (hint) { hint.textContent = 'Saved.'; hint.classList.add('adm-dr-notes-hint--saved'); }
    }
  }, true);


  /* ── ACCOUNT (profile + my orders) ──────────────────────────────────── */
  let accountTab = 'profile'; // 'profile' | 'orders'
  let accountOrderId = null;  // when set, show single order detail

  const renderAccountView = async () => {
    const host = $('#view-account');
    if (!host) return;

    const session = getSession();
    if (!session) { setView('auth'); return; }

    // Show skeleton while we load
    const accSkel = accountTab === 'profile'
      ? skelAccountProfile()
      : accountTab === 'wishlist'
      ? skelWishlist()
      : (accountOrderId ? skelAccountOrderDetail() : skelAccountOrders(4));
    host.innerHTML = `
      <div class="acc-page">
        <div class="acc-header">
          <h1 class="acc-title">My Account</h1>
          <div class="acc-tabs">
            <button class="acc-tab ${accountTab==='profile'?'active':''}" data-acc-tab="profile" type="button">Profile</button>
            <button class="acc-tab ${accountTab==='orders'?'active':''}" data-acc-tab="orders" type="button">My Orders</button>
            <button class="acc-tab ${accountTab==='wishlist'?'active':''}" data-acc-tab="wishlist" type="button">Wishlist</button>
          </div>
        </div>
        <div class="acc-body" id="acc-body">${accSkel}</div>
      </div>`;
    setRegionBusy($('#acc-body'), true);

    if (accountTab === 'profile') {
      await renderAccountProfile();
    } else if (accountTab === 'wishlist') {
      await renderAccountWishlist();
    } else {
      if (accountOrderId) await renderAccountOrderDetail(accountOrderId);
      else await renderAccountOrders();
    }
  };

  const renderAccountProfile = async () => {
    const body = $('#acc-body');
    if (!body) return;
    const session = getSession();
    if (!session) return;

    body.innerHTML = `
      <div class="acc-card">
        <div class="acc-card-title">Profile</div>
        <form class="acc-form" id="acc-profile-form" novalidate>
          <label class="acc-field">
            <span class="acc-label">Full name</span>
            <input class="acc-input" id="acc-name" type="text" value="${esc(session.name || '')}" autocomplete="name" />
          </label>
          <label class="acc-field">
            <span class="acc-label">Email</span>
            <input class="acc-input" id="acc-email" type="email" value="${esc(session.email || '')}" disabled />
            <span class="acc-hint">Contact support to change your email.</span>
          </label>
          <label class="acc-field">
            <span class="acc-label">Phone</span>
            <input class="acc-input" id="acc-phone" type="tel" value="${esc(session.phone || '')}" autocomplete="tel" placeholder="10-digit mobile number" />
          </label>
          <div class="acc-form-msg" id="acc-form-msg" hidden></div>
          <div class="acc-form-actions">
            <button class="btn btn-green" type="submit" id="acc-save-btn">Save changes</button>
          </div>
        </form>
      </div>`;
    setRegionBusy(body, false);

    const form = $('#acc-profile-form');
    if (!form) return;
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const name = $('#acc-name').value.trim();
      const phone = $('#acc-phone').value.trim();
      const msgEl = $('#acc-form-msg');
      const btn = $('#acc-save-btn');
      if (!name || name.length < 2) {
        msgEl.textContent = 'Name must be at least 2 characters.';
        msgEl.className = 'acc-form-msg acc-form-msg--err';
        msgEl.hidden = false;
        return;
      }
      if (phone && !isValidPhone(phone)) {
        msgEl.textContent = 'Enter a valid 10–15 digit phone number, or leave blank.';
        msgEl.className = 'acc-form-msg acc-form-msg--err';
        msgEl.hidden = false;
        return;
      }
      btn.disabled = true;
      btn.textContent = 'Saving…';
      const { data: profile, error } = await authLib.updateProfile(session.userId, {
        full_name: name, phone: phone || null,
      });
      btn.disabled = false;
      btn.textContent = 'Save changes';
      if (error) {
        msgEl.textContent = error.message || 'Could not save. Please try again.';
        msgEl.className = 'acc-form-msg acc-form-msg--err';
        msgEl.hidden = false;
        return;
      }
      currentProfile = profile;
      refreshHelloBlock();
      msgEl.textContent = 'Saved.';
      msgEl.className = 'acc-form-msg acc-form-msg--ok';
      msgEl.hidden = false;
    });
  };

  // ── Wishlist tab (My Account) ──
  const renderAccountWishlist = async () => {
    const body = $('#acc-body');
    if (!body) return;
    const s = getSession();
    const ids = await wishlistLib.getWishlistIds(s?.userId || null);
    const items = ids.map(id => PRODUCTS.find(p => p.id === id)).filter(Boolean);

    if (items.length === 0) {
      body.innerHTML = `
        <div class="acc-card wl-empty">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
          <p class="wl-empty-title">Your wishlist is empty</p>
          <p class="wl-empty-sub">Browse products and tap the heart to save items here.</p>
          <a href="/products" class="btn btn-green" data-nav="products">Browse Products</a>
        </div>`;
      setRegionBusy(body, false);
      return;
    }

    body.innerHTML = `
      <div class="wl-grid" id="wl-grid">
        ${items.map(p => `
          <div class="wl-item" data-wl-product="${p.id}">
            <a href="/product/${encodeURIComponent(p.slug || p.id)}" class="wl-item-img" data-nav="product" data-product-id="${p.id}">
              ${p.photo ? `<img src="${esc(p.photo)}" alt="${esc(p.name)}" loading="lazy" />` : `<div class="wl-item-emoji">${(ILLUSTRATIONS[p.img] || {emoji:'📦'}).emoji}</div>`}
            </a>
            <div class="wl-item-info">
              <a href="/product/${encodeURIComponent(p.slug || p.id)}" class="wl-item-name" data-nav="product" data-product-id="${p.id}">${esc(p.name)}</a>
              <div class="wl-item-price">
                <span class="price"><span class="rupee">₹</span>${p.price}</span>
                ${hasDiscount(p) ? `<span class="price-original"><span class="rupee">₹</span>${p.original}</span>` : ''}
              </div>
              <div class="wl-item-stock ${p.stockQty != null && p.stockQty <= 0 ? 'wl-item-stock--oos' : ''}">
                ${p.stockQty != null && p.stockQty <= 0 ? 'Out of stock' : 'In stock'}
              </div>
            </div>
            <div class="wl-item-actions">
              <button type="button" class="btn btn-green btn-sm wl-add-cart" data-wl-add="${p.id}" ${p.stockQty != null && p.stockQty <= 0 ? 'disabled' : ''}>
                ${(p.hasVariants && p.variants?.length) ? 'Choose size' : 'Add to Cart'}
              </button>
              <button type="button" class="wl-remove" data-wl-remove="${p.id}" aria-label="Remove from wishlist">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
          </div>
        `).join('')}
      </div>`;
    setRegionBusy(body, false);

    // Wire remove buttons
    body.addEventListener('click', async (e) => {
      const removeBtn = e.target.closest('[data-wl-remove]');
      if (removeBtn) {
        const pid = parseInt(removeBtn.dataset.wlRemove);
        await wishlistLib.removeFromWishlist(s?.userId || null, pid);
        // Update card hearts
        document.querySelectorAll(`[data-wl-toggle="${pid}"]`).forEach(btn => {
          btn.classList.remove('wl-heart--active');
          const svg = btn.querySelector('svg');
          if (svg) svg.setAttribute('fill', 'none');
        });
        updateWishlistBadge();
        // Remove the row with animation
        const row = body.querySelector(`[data-wl-product="${pid}"]`);
        if (row) {
          row.style.transition = 'opacity .2s, transform .2s';
          row.style.opacity = '0';
          row.style.transform = 'translateX(20px)';
          setTimeout(() => {
            row.remove();
            if (!body.querySelector('.wl-item')) renderAccountWishlist();
          }, 200);
        }
        return;
      }

      // Add to cart from wishlist
      const addBtn = e.target.closest('[data-wl-add]');
      if (addBtn) {
        const pid = parseInt(addBtn.dataset.wlAdd);
        const p = PRODUCTS.find(x => x.id === pid);
        if (!p) return;
        if (p.hasVariants && Array.isArray(p.variants) && p.variants.length > 0) {
          setView('product', p.id);
          return;
        }
        addToCart(p.id, p.weight || '', 1, null);
        showToast(`Added to cart · ${p.name}`);
        return;
      }
    });
  };

  const orderStatusLabel = (s) => ({
    pending: 'Pending',
    confirmed: 'Confirmed',
    shipped: 'Shipped',
    delivered: 'Delivered',
    cancelled: 'Cancelled',
  }[s] || s);

  const renderAccountOrders = async () => {
    const body = $('#acc-body');
    if (!body) return;
    body.innerHTML = skelAccountOrders(4);
    setRegionBusy(body, true);

    const { data: orders, error } = await fetchMyOrders();
    if (error) {
      body.innerHTML = `<div class="acc-card">Couldn't load orders: ${esc(error.message || 'unknown error')}</div>`;
      setRegionBusy(body, false);
      return;
    }
    if (!orders.length) {
      body.innerHTML = `
        <div class="acc-card acc-empty">
          <div style="font-size:48px;margin-bottom:8px">📦</div>
          <h2 class="acc-empty-title">No orders yet</h2>
          <p class="acc-empty-sub">When you place an order, it'll show up here.</p>
          <button class="btn btn-green" data-nav="products" type="button">Start shopping</button>
        </div>`;
      setRegionBusy(body, false);
      return;
    }

    body.innerHTML = `
      <div class="acc-card">
        <div class="acc-card-title">My Orders</div>
        <div class="acc-orders">
          ${orders.map(o => {
            const items = o.order_items || [];
            const itemCount = items.reduce((s, i) => s + i.quantity, 0);
            const firstName = items[0] ? items[0].product_name : '—';
            const more = items.length > 1 ? ` + ${items.length - 1} more` : '';
            const date = new Date(o.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
            const shortId = String(o.id).slice(0, 8).toUpperCase();
            return `
              <button class="acc-order-row" data-order-id="${esc(o.id)}" type="button">
                <div class="acc-order-main">
                  <div class="acc-order-top">
                    <span class="acc-order-id">#${esc(shortId)}</span>
                    <span class="acc-order-status acc-status--${esc(o.status)}">${esc(orderStatusLabel(o.status))}</span>
                  </div>
                  <div class="acc-order-items">${esc(firstName)}${esc(more)} <span class="acc-order-count">(${itemCount} ${itemCount===1?'item':'items'})</span></div>
                  <div class="acc-order-meta">${esc(date)}</div>
                </div>
                <div class="acc-order-total">₹${Number(o.total).toLocaleString('en-IN')}</div>
              </button>`;
          }).join('')}
        </div>
      </div>`;
    setRegionBusy(body, false);
  };

  const renderAccountOrderDetail = async (orderId) => {
    const body = $('#acc-body');
    if (!body) return;
    body.innerHTML = skelAccountOrderDetail();
    setRegionBusy(body, true);
    const { data, error } = await supabase
      .from('orders').select('*, order_items(*)').eq('id', orderId).single();
    if (error || !data) {
      body.innerHTML = `<div class="acc-card">Couldn't load order: ${esc(error?.message || 'not found')}<div style="margin-top:12px"><button class="btn btn-outline" data-acc-back type="button">← Back to orders</button></div></div>`;
      setRegionBusy(body, false);
      return;
    }
    const items = data.order_items || [];
    const date = new Date(data.created_at).toLocaleString('en-IN', { day:'numeric', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' });
    const shortId = String(data.id).slice(0, 8).toUpperCase();
    // Reviewable when the order has been at least confirmed. Pull already-
    // reviewed line item ids so we mark them with "Reviewed ✓" rather than
    // letting the customer hit the trigger's unique-key error.
    const canReview = ['confirmed', 'shipped', 'delivered'].includes(data.status);
    let reviewedIds = new Set();
    if (canReview) {
      const { ids } = await fetchMyReviewedOrderItemIds();
      reviewedIds = new Set(ids || []);
    }
    // Tracking block (only shown once admin has filled in an AWB or URL).
    const trackingHtml = (() => {
      if (!data.tracking_number && !data.tracking_url) return '';
      const carrier = carrierById(data.carrier);
      const carrierLabel = (carrier && carrier.id && carrier.id !== 'custom')
        ? carrier.label
        : (data.carrier ? esc(data.carrier) : 'Courier');
      // Prefer a fresh track91 link built from the AWB. Only fall back to a
      // saved tracking_url when it's a real absolute http(s) link (a legit
      // manual override) — ignore stale/relative junk like "?n=<…>" that would
      // otherwise resolve against our own origin and 404.
      const manualUrl = /^https?:\/\//i.test(data.tracking_url || '') ? data.tracking_url : '';
      const cleanAwb = (data.tracking_number || '').replace(/[^A-Za-z0-9]/g, '');
      const url = data.tracking_number ? buildTrackingUrl(data.carrier, data.tracking_number) : manualUrl;
      return `
        <div class="acc-track">
          <div class="acc-track-icon" aria-hidden="true">📦</div>
          <div class="acc-track-body">
            <div class="acc-track-title">${esc(carrierLabel)}</div>
            ${cleanAwb ? `<div class="acc-track-awb">AWB: <strong>${esc(cleanAwb)}</strong></div>` : ''}
            ${data.shipped_at ? `<div class="acc-track-meta">Shipped ${new Date(data.shipped_at).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}</div>` : ''}
          </div>
          ${url ? `<a class="btn btn-green acc-track-btn" href="${esc(url)}" target="_blank" rel="noopener">Track shipment →</a>` : ''}
        </div>`;
    })();

    body.innerHTML = `
      <div class="acc-card">
        <button class="acc-back" data-acc-back type="button">← Back to orders</button>
        <div class="acc-order-detail-head">
          <div>
            <div class="acc-card-title">Order #${esc(shortId)}</div>
            <div class="acc-order-meta">Placed ${esc(date)}</div>
          </div>
          <span class="acc-order-status acc-status--${esc(data.status)}">${esc(orderStatusLabel(data.status))}</span>
        </div>
        ${trackingHtml}
        <div class="acc-order-items-detail">
          ${items.map(i => {
            const reviewed = reviewedIds.has(i.id);
            const canRev = canReview && i.product_id != null && !reviewed;
            return `
            <div class="acc-order-item-row" data-order-item-id="${esc(i.id)}" data-product-id="${esc(i.product_id || '')}" data-product-name="${esc(i.product_name)}">
              <div>
                <div class="acc-item-name">${esc(i.product_name)}</div>
                <div class="acc-item-sub">${i.product_weight ? esc(i.product_weight) + ' · ' : ''}× ${i.quantity}</div>
                ${canRev ? `<button class="acc-review-btn" type="button" data-write-review="${esc(i.id)}">★ Write a review</button>` : ''}
                ${reviewed ? `<span class="acc-review-done">★ Reviewed</span>` : ''}
              </div>
              <div class="acc-item-price">₹${Number(i.line_total).toLocaleString('en-IN')}</div>
            </div>
          `;}).join('')}
        </div>
        <div class="acc-order-totals">
          <div class="acc-tot-row"><span>Subtotal</span><span>₹${Number(data.subtotal).toLocaleString('en-IN')}</span></div>
          <div class="acc-tot-row"><span>Shipping</span><span>${Number(data.shipping) === 0 ? 'FREE' : '₹' + Number(data.shipping).toLocaleString('en-IN')}</span></div>
          <div class="acc-tot-row acc-tot-grand"><span>Total</span><span>₹${Number(data.total).toLocaleString('en-IN')}</span></div>
        </div>
        <div class="acc-deliver">
          <div class="acc-deliver-title">Delivery address</div>
          <div>${esc(data.full_name)}</div>
          <div>${esc(data.phone)}</div>
          <div>${esc(data.address_line1)}${data.address_line2 ? ', ' + esc(data.address_line2) : ''}</div>
          <div>${esc(data.city)}, ${esc(data.state || '')} – ${esc(data.pincode)}</div>
        </div>
        ${data.notes ? `<div class="acc-notes"><span class="acc-notes-label">Notes:</span> ${esc(data.notes)}</div>` : ''}
      </div>`;
    setRegionBusy(body, false);
  };

  // Account view event delegation
  document.addEventListener('click', (e) => {
    // Dropdown buttons or in-page tab buttons can both set the active tab.
    // data-account-tab is used in the nav dropdown alongside data-nav="account".
    const tabSetter = e.target.closest('[data-acc-tab], [data-account-tab]');
    if (tabSetter) {
      const tab = tabSetter.dataset.accTab || tabSetter.dataset.accountTab;
      if (tab === 'profile' || tab === 'orders' || tab === 'wishlist') {
        accountTab = tab;
        accountOrderId = null;
        // If already on account view, re-render. Otherwise data-nav handler will navigate.
        if ($('#view-account')?.classList.contains('active')) {
          if (e.target.closest('[data-acc-tab]')) e.preventDefault();
          renderAccountView();
        }
      }
    }
    const orderRow = e.target.closest('[data-order-id]');
    if (orderRow && $('#view-account')?.classList.contains('active')) {
      accountOrderId = orderRow.dataset.orderId;
      renderAccountOrderDetail(accountOrderId);
      return;
    }
    if (e.target.closest('[data-acc-back]')) {
      e.preventDefault();
      accountOrderId = null;
      renderAccountOrders();
    }

    // "Write a review" button on an order item row — opens the inline form.
    const writeRevBtn = e.target.closest('[data-write-review]');
    if (writeRevBtn) {
      const row = writeRevBtn.closest('[data-order-item-id]');
      if (!row) return;
      const oiId      = row.dataset.orderItemId;
      const productId = row.dataset.productId;
      const prodName  = row.dataset.productName || 'this product';
      // If a form already exists for this row, just focus it.
      if (row.querySelector('.acc-review-form')) {
        row.querySelector('textarea[name="body"]')?.focus();
        return;
      }
      const form = document.createElement('div');
      form.className = 'acc-review-form';
      form.innerHTML = `
        <div class="acc-review-form-title">Review ${esc(prodName)}</div>
        <div class="acc-review-stars-pick" role="radiogroup" aria-label="Your rating">
          ${[1,2,3,4,5].map(n => `<button type="button" class="acc-review-star" data-star="${n}" aria-label="${n} star${n>1?'s':''}">★</button>`).join('')}
        </div>
        <input type="text" class="acc-review-title" placeholder="Title (optional)" maxlength="120" />
        <textarea name="body" class="acc-review-body" placeholder="Tell other customers what you thought." rows="3" maxlength="4000"></textarea>
        <div class="acc-review-actions">
          <button type="button" class="btn btn-outline" data-rev-cancel>Cancel</button>
          <button type="button" class="btn btn-green" data-rev-submit data-rev-oi="${esc(oiId)}" data-rev-pid="${esc(productId)}">Submit review</button>
        </div>
        <div class="acc-review-msg" hidden></div>`;
      row.appendChild(form);
      // Wire star picker (delegation here is fine since each form is short-lived).
      let chosen = 0;
      const paint = (n) => {
        form.querySelectorAll('.acc-review-star').forEach((el, i) => {
          el.classList.toggle('is-on', i < n);
        });
      };
      form.querySelectorAll('.acc-review-star').forEach((el, i) => {
        el.addEventListener('mouseenter', () => paint(i + 1));
        el.addEventListener('mouseleave', () => paint(chosen));
        el.addEventListener('click', () => { chosen = i + 1; paint(chosen); });
      });
      form.querySelector('[data-rev-cancel]').addEventListener('click', () => form.remove());
      form.querySelector('[data-rev-submit]').addEventListener('click', async () => {
        const msg = form.querySelector('.acc-review-msg');
        const btn = form.querySelector('[data-rev-submit]');
        if (chosen < 1) { msg.textContent = 'Pick a rating from 1 to 5 stars.'; msg.hidden = false; return; }
        btn.disabled = true; btn.textContent = 'Submitting…';
        const { error } = await submitReview({
          orderItemId: oiId,
          productId,
          rating: chosen,
          title: form.querySelector('.acc-review-title').value,
          body:  form.querySelector('.acc-review-body').value,
        });
        btn.disabled = false; btn.textContent = 'Submit review';
        if (error) {
          msg.textContent = 'Could not submit: ' + (error.message || 'unknown error');
          msg.hidden = false;
          return;
        }
        showToast('Thanks — your review is live.');
        // Refresh the order detail so the row flips to "Reviewed".
        if (accountOrderId) renderAccountOrderDetail(accountOrderId);
      });
      return;
    }
  });

  // wire place-order buttons + draft auto-save
  document.addEventListener('click', e => {
    if (e.target.closest('#ck-place-btn') || e.target.closest('#ck-place-btn-mobile')) {
      e.preventDefault();
      placeOrder();
    }
  });
  document.addEventListener('input', e => {
    if (!e.target.closest('#ck-form')) return;
    saveCheckoutDraft(collectCheckoutData());
  });
  document.addEventListener('change', e => {
    if (!e.target.closest('#ck-form')) return;
    saveCheckoutDraft(collectCheckoutData());
  });

  let pdState = { product: null, variant: 0, qty: 1, variants: [], photos: [], activePhotoIdx: 0 };

  /* ── PRODUCT IMAGE LIGHTBOX ─────────────────────────────────────────
     Bound once at startup. Reads pdState.photos / pdState.activePhotoIdx
     so any PDP re-render (variant switch, navigation) Just Works.
  */
  const lb = {
    el: null, img: null, prev: null, next: null, close: null,
    counter: null, thumbs: null,
    idx: 0, photos: [],
  };
  const lbOpen = (photos, startIdx = 0) => {
    if (!photos || !photos.length || !lb.el) return;
    lb.photos = photos;
    lb.idx = Math.max(0, Math.min(startIdx, photos.length - 1));
    lbRender();
    lb.el.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  };
  const lbClose = () => {
    if (!lb.el) return;
    lb.el.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  };
  const lbRender = () => {
    if (!lb.photos.length) return;
    const src = lb.photos[lb.idx];
    if (lb.img) {
      lb.img.src = src;
      lb.img.alt = `Product image ${lb.idx + 1}`;
    }
    if (lb.counter) {
      lb.counter.textContent = lb.photos.length > 1
        ? `${lb.idx + 1} / ${lb.photos.length}`
        : '';
    }
    if (lb.prev) lb.prev.disabled = lb.idx === 0;
    if (lb.next) lb.next.disabled = lb.idx === lb.photos.length - 1;
    if (lb.prev) lb.prev.style.display = lb.photos.length > 1 ? '' : 'none';
    if (lb.next) lb.next.style.display = lb.photos.length > 1 ? '' : 'none';
    if (lb.thumbs) {
      if (lb.photos.length > 1) {
        lb.thumbs.innerHTML = lb.photos.map((ph, i) => `
          <button class="pd-lb-thumb ${i === lb.idx ? 'active' : ''}" type="button" data-lb-thumb="${i}" aria-label="Image ${i + 1}">
            <img src="${esc(ph)}" alt="" loading="lazy" />
          </button>
        `).join('');
        lb.thumbs.style.display = '';
      } else {
        lb.thumbs.innerHTML = '';
        lb.thumbs.style.display = 'none';
      }
    }
  };
  const lbStep = dir => {
    if (!lb.photos.length) return;
    lb.idx = Math.max(0, Math.min(lb.idx + dir, lb.photos.length - 1));
    lbRender();
  };
  (function bindLightbox() {
    lb.el = document.getElementById('pd-lightbox');
    if (!lb.el) return;
    lb.img = document.getElementById('pd-lb-img');
    lb.prev = document.getElementById('pd-lb-prev');
    lb.next = document.getElementById('pd-lb-next');
    lb.close = document.getElementById('pd-lb-close');
    lb.counter = document.getElementById('pd-lb-counter');
    lb.thumbs = document.getElementById('pd-lb-thumbs');
    lb.close?.addEventListener('click', lbClose);
    lb.prev?.addEventListener('click', () => lbStep(-1));
    lb.next?.addEventListener('click', () => lbStep(1));
    // Backdrop click closes (but not clicks on image / controls)
    lb.el.addEventListener('click', e => {
      if (e.target === lb.el || e.target.id === 'pd-lb-stage') lbClose();
    });
    // Thumb click jumps to image
    lb.thumbs?.addEventListener('click', e => {
      const btn = e.target.closest('[data-lb-thumb]');
      if (!btn) return;
      const i = parseInt(btn.getAttribute('data-lb-thumb'), 10);
      if (!Number.isNaN(i)) { lb.idx = i; lbRender(); }
    });
    // Keyboard
    document.addEventListener('keydown', e => {
      if (lb.el.getAttribute('aria-hidden') !== 'false') return;
      if (e.key === 'Escape') lbClose();
      else if (e.key === 'ArrowLeft') lbStep(-1);
      else if (e.key === 'ArrowRight') lbStep(1);
    });
    // Basic swipe on the stage
    let touchX = null;
    const stage = document.getElementById('pd-lb-stage');
    stage?.addEventListener('touchstart', e => {
      if (e.touches.length === 1) touchX = e.touches[0].clientX;
    }, { passive: true });
    stage?.addEventListener('touchend', e => {
      if (touchX == null) return;
      const dx = (e.changedTouches[0]?.clientX ?? touchX) - touchX;
      if (Math.abs(dx) > 40) lbStep(dx < 0 ? 1 : -1);
      touchX = null;
    });
  })();

  // View overlay: when a variant is selected, its price/stock/images override
  // the product's. For products without real variants, this is just `p`.
  const productViewWithVariant = (p, variantIdx) => {
    if (!p?.hasVariants || !Array.isArray(p.variants) || p.variants.length === 0) {
      return p;
    }
    const v = p.variants[Math.max(0, Math.min(variantIdx, p.variants.length - 1))];
    return {
      ...p,
      price: v.salePrice,
      original: v.mrp ?? v.salePrice,
      photo: v.mainImage || p.photo,
      photos: v.gallery && v.gallery.length ? v.gallery : p.photos,
      stockQty: v.stockQty,
      lowStockThreshold: v.lowStockThreshold ?? p.lowStockThreshold,
      weight: v.label || p.weight,
    };
  };

  const renderProductDetail = (rawP, opts = {}) => {
    const variantIdx = Number.isInteger(opts.variantIdx) ? opts.variantIdx : 0;
    const variantLabels = (rawP?.hasVariants && rawP.variants?.length)
      ? rawP.variants.map(v => v.label)
      : (rawP?.weight ? [rawP.weight] : []);
    pdState = { product: rawP, variant: variantIdx, qty: 1, variants: variantLabels };

    // Apply variant overrides so all downstream UI sees a single coherent
    // "view" object instead of having to special-case hasVariants everywhere.
    const p = productViewWithVariant(rawP, variantIdx);

    const ill = ILLUSTRATIONS[p.img] || { emoji:'📦', bg:'#F5F5F5' };
    const illEl = $('#pd-illustration');
    illEl.style.background = p.photo ? '#fff' : ill.bg;
    if (p.photo) {
      illEl.classList.add('product-illustration--photo');
      illEl.style.width = '';
      illEl.style.height = '';
      illEl.style.fontSize = '';
      illEl.style.borderRadius = '';
      illEl.innerHTML = `<img src="${esc(p.photo)}" alt="${esc(p.name)}" />`;
    } else {
      illEl.classList.remove('product-illustration--photo');
      illEl.style.width = '200px';
      illEl.style.height = '200px';
      illEl.style.fontSize = '90px';
      illEl.style.borderRadius = '18px';
      illEl.textContent = ill.emoji;
    }

    const badge = $('#pd-badge');
    if (p.badge) {
      badge.textContent = p.badge;
      badge.style.display = 'inline-block';
    } else {
      badge.style.display = 'none';
    }

    $('#pd-crumbs').textContent = `Home / ${p.category} / ${p.name}`;
    $('#pd-cat').textContent = p.category;
    $('#pd-name').textContent = p.name;
    updateShareMeta(p);
    const subEl = $('#pd-sub');
    if (subEl) { subEl.textContent = p.subtitle || ''; subEl.style.display = p.subtitle ? '' : 'none'; }
    const shortEl = $('#pd-short');
    if (shortEl) {
      const short = (p.shortDescription || '').trim();
      shortEl.textContent = short;
      shortEl.hidden = !short;
    }
    const starsEl = $('#pd-stars');
    const rNum = $('#pd-rating-num');
    const rMeta = $('#pd-rating-meta');
    const ratingRow = starsEl ? starsEl.closest('.pd-rating') : null;
    if (p.rating) {
      if (ratingRow) ratingRow.style.display = '';
      if (starsEl) { starsEl.innerHTML = starsHtml(p.rating, 16); starsEl.style.display = ''; }
      if (rNum) { rNum.textContent = p.rating; rNum.style.display = ''; }
      if (rMeta) { rMeta.textContent = `(${p.reviews} reviews)`; rMeta.style.display = ''; }
    } else {
      // No reviews yet — hide the whole rating row so it doesn't leave a dead gap.
      if (ratingRow) ratingRow.style.display = 'none';
      if (starsEl) starsEl.style.display = 'none';
      if (rNum) rNum.style.display = 'none';
      if (rMeta) rMeta.style.display = 'none';
    }
    $('#pd-price').textContent = `₹${p.price}`;
    const origEl = $('#pd-original');
    const saveEl = $('#pd-save');
    if (hasDiscount(p)) {
      if (origEl) { origEl.textContent = `₹${p.original}`; origEl.style.display = ''; }
      if (saveEl) { saveEl.textContent = `Save ${discount(p)}%`; saveEl.style.display = ''; }
    } else {
      if (origEl) origEl.style.display = 'none';
      if (saveEl) saveEl.style.display = 'none';
    }
    // buy box
    if ($('#pd-bb-price')) $('#pd-bb-price').textContent = p.price.toLocaleString('en-IN');
    // Pincode serviceability widget — paint into the deliver-to slot.
    const pinSlot = $('#pd-bb-pincode-slot');
    if (pinSlot) pinSlot.innerHTML = pincodeWidgetHtml({ compact: true });
    pdState.qty = 1;

    // Qty selector — cap at stock_qty (max 10). Out of stock → no options, hidden.
    const qtySelEl = $('#pd-bb-qty');
    if (qtySelEl) {
      const stockCap = p.stockQty != null ? Math.max(0, p.stockQty) : 10;
      const maxQty = Math.min(stockCap, 10);
      if (maxQty <= 0) {
        qtySelEl.innerHTML = '<option value="0">—</option>';
        qtySelEl.disabled = true;
        qtySelEl.value = '0';
      } else {
        qtySelEl.innerHTML = Array.from({ length: maxQty }, (_, i) =>
          `<option value="${i + 1}">${i + 1}</option>`).join('');
        qtySelEl.disabled = false;
        qtySelEl.value = '1';
      }
    }

    // Disable Add to Cart / Buy Now when out of stock (desktop + mobile sticky).
    const outOfStock = p.stockQty != null && p.stockQty <= 0;
    ['#pd-add-cart', '#pd-add-cart-mobile', '#pd-buy-now', '#pd-buy-now-mobile'].forEach(sel => {
      const el = $(sel);
      if (!el) return;
      el.disabled = outOfStock;
      el.classList.toggle('btn-disabled', outOfStock);
      if (outOfStock && (sel === '#pd-add-cart' || sel === '#pd-add-cart-mobile')) {
        el.textContent = 'Out of Stock';
      } else if (sel === '#pd-add-cart' || sel === '#pd-add-cart-mobile') {
        el.textContent = 'Add to Cart';
      }
    });

    // thumbs — main + gallery photos, deduped. Hide strip when only one image.
    const galleryPhotos = Array.isArray(p.photos) ? p.photos.filter(Boolean) : [];
    const allPhotos = [p.photo, ...galleryPhotos].filter(Boolean);
    const seen = new Set();
    const thumbPhotos = allPhotos.filter(ph => {
      if (seen.has(ph)) return false;
      seen.add(ph);
      return true;
    });
    const thumbsEl = $('#pd-thumbs');
    // Persist the photo list for the lightbox to consume.
    pdState.photos = thumbPhotos;
    pdState.activePhotoIdx = 0;
    if (thumbPhotos.length > 1) {
      thumbsEl.hidden = false;
      thumbsEl.innerHTML = thumbPhotos.map((ph, i) => `
        <button class="pd-thumb ${i===0?'active':''}" type="button" data-thumb="${i}" data-photo="${esc(ph)}" aria-label="View image ${i+1}">
          <img src="${esc(ph)}" alt="${esc(p.name)} — image ${i+1}" loading="lazy" />
        </button>
      `).join('');
      $$('#pd-thumbs .pd-thumb').forEach(el => {
        el.addEventListener('click', () => {
          $$('#pd-thumbs .pd-thumb').forEach(t => t.classList.remove('active'));
          el.classList.add('active');
          const ph = el.getAttribute('data-photo');
          if (!ph) return;
          pdState.activePhotoIdx = parseInt(el.getAttribute('data-thumb'), 10) || 0;
          const img = illEl.querySelector('img');
          if (img) img.src = ph;
          if (zoomLens) zoomLens.style.backgroundImage = `url("${ph.replace(/"/g, '\\"')}")`;
        });
      });
    } else {
      thumbsEl.hidden = true;
      thumbsEl.innerHTML = '';
    }

    // Hover-to-zoom on product image
    const heroImg = $('#pd-hero-img');
    const zoomLens = $('#pd-zoom-lens');
    if (heroImg && zoomLens) {
      // Create a canvas-based zoom image from the illustration
      const setupZoom = () => {
        if (p.photo) {
          zoomLens.style.backgroundImage = `url("${p.photo.replace(/"/g, '\\"')}")`;
          zoomLens.style.backgroundSize = '200%';
          return;
        }
        const canvas = document.createElement('canvas');
        const sz = 600;
        canvas.width = sz; canvas.height = sz;
        const ctx = canvas.getContext('2d');
        // Draw background gradient
        const grad = ctx.createLinearGradient(0,0,sz,sz);
        grad.addColorStop(0, '#F5EDDE');
        grad.addColorStop(1, '#FFFDF8');
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, sz, sz);
        // Draw emoji
        ctx.font = `${sz * 0.5}px serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(ill.emoji, sz/2, sz/2);
        zoomLens.style.backgroundImage = `url(${canvas.toDataURL()})`;
        zoomLens.style.backgroundSize = '';
      };
      setupZoom();

      heroImg.addEventListener('mousemove', e => {
        const rect = heroImg.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        zoomLens.style.backgroundPosition = `${x}% ${y}%`;
      });

      // Click hero → open full-screen lightbox at the currently-active image.
      heroImg.style.cursor = 'zoom-in';
      if (!heroImg.dataset.lbBound) {
        heroImg.dataset.lbBound = '1';
        heroImg.addEventListener('click', () => {
          if (!pdState.photos?.length) return;
          lbOpen(pdState.photos, pdState.activePhotoIdx || 0);
        });
      }
    }

    // description
    const descSection = $('#pd-description-section');
    const descBody = $('#pd-description');
    if (descSection && descBody) {
      if (p.description) {
        descBody.innerHTML = descToHtml(p.description);
        descSection.hidden = false;
      } else {
        descBody.innerHTML = '';
        descSection.hidden = true;
      }
    }

    // how to use
    const howtoSection = $('#pd-howto-section');
    const howtoBody = $('#pd-howto');
    if (howtoSection && howtoBody) {
      if (p.howToUse) {
        howtoBody.innerHTML = descToHtml(p.howToUse);
        howtoSection.hidden = false;
      } else {
        howtoBody.innerHTML = '';
        howtoSection.hidden = true;
      }
    }

    // tags — clickable chips that route to filtered products view.
    const tagsEl = $('#pd-tags');
    if (tagsEl) {
      const tags = Array.isArray(p.tags) ? p.tags.filter(Boolean) : [];
      if (tags.length) {
        tagsEl.innerHTML = tags.map(t =>
          `<button type="button" class="pd-tag" data-pd-tag="${esc(t)}">${esc(t)}</button>`
        ).join('');
        tagsEl.hidden = false;
      } else {
        tagsEl.innerHTML = '';
        tagsEl.hidden = true;
      }
    }

    // Stock state (uses stock_qty + low-stock threshold from admin).
    const stockEl = $('#pd-bb-stock');
    if (stockEl) {
      const qty = p.stockQty;
      const thr = p.lowStockThreshold ?? 5;
      stockEl.classList.remove('pd-bb-stock--low', 'pd-bb-stock--out');
      if (qty != null && qty <= 0) {
        stockEl.textContent = 'Out of stock';
        stockEl.classList.add('pd-bb-stock--out');
      } else if (qty != null && qty <= thr) {
        stockEl.innerHTML = `Only ${qty} left <span class="pd-bb-eta-inline">· Ships within 24 hours</span>`;
        stockEl.classList.add('pd-bb-stock--low');
      } else {
        stockEl.innerHTML = `In Stock <span class="pd-bb-eta-inline">· Ships within 24 hours</span>`;
      }
    }

    // SEO: per-product title + meta description (falls back to short_description).
    const seoTitle = (p.metaTitle || '').trim() || seoTitleFrom(p.name, p.shortDescription);
    document.title = seoTitle;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', (p.metaDescription || metaFromDescription(p.description) || p.shortDescription || '').trim().slice(0, 200));

    // variants
    renderVariants();

    // Sync PDP wishlist button state
    const pdWlBtn = $('#pd-wishlist');
    if (pdWlBtn && rawP) {
      const wled = wishlistLib.isWishlisted(rawP.id);
      pdWlBtn.classList.toggle('btn-wishlist--active', wled);
      const wlSvg = pdWlBtn.querySelector('svg');
      if (wlSvg) wlSvg.setAttribute('fill', wled ? 'currentColor' : 'none');
      const wlLabel = $('#pd-wishlist-label');
      if (wlLabel) wlLabel.textContent = wled ? 'Wishlisted' : 'Wishlist';
    }

    // Track this view + paint Related + Recently Viewed rails.
    // Track using the parent product id (not the variant-scoped view) so
    // every variant click on the same product collapses to one history entry.
    if (rawP && rawP.id != null) trackProductView(rawP.id);
    renderRelatedRail('#pd-related-rail', '#pd-related-grid', rawP);
    renderRecentRail('#pd-recent-rail', '#pd-recent-grid', rawP?.id ?? null);

    // Reviews — async; the section paints as soon as the data is back.
    renderProductReviews(rawP);
  };

  /* ── Product reviews (PDP section) ──
     Renders the average + count + a list of approved reviews. Hides
     itself entirely when the migration isn't applied yet, so a fresh
     install never shows a broken section. */
  const renderProductReviews = async (product) => {
    const host = $('#pd-reviews');
    if (!host || !product || product.id == null) return;
    host.innerHTML = skelReviews();
    setRegionBusy(host, true);
    const { data, error } = await fetchProductReviews(product.id);
    if (error) {
      // Either the table doesn't exist yet (migration pending) or RLS
      // blocked it. In both cases the friendliest move is to hide.
      host.innerHTML = '';
      host.hidden = true;
      setRegionBusy(host, false);
      return;
    }
    host.hidden = false;
    const reviews = data || [];
    const count = reviews.length;
    const avg = count
      ? Math.round((reviews.reduce((s, r) => s + r.rating, 0) / count) * 10) / 10
      : 0;
    // 5-row breakdown (5★, 4★, … 1★) so customers see distribution at a glance.
    const buckets = [5,4,3,2,1].map(stars => ({
      stars,
      count: reviews.filter(r => r.rating === stars).length,
    }));
    const fmtDate = (iso) => new Date(iso).toLocaleDateString('en-IN', { year:'numeric', month:'short', day:'numeric' });
    host.innerHTML = `
      <div class="pd-reviews-head">
        <h2 class="pd-reviews-title">Customer Reviews</h2>
        ${count ? `
          <div class="pd-reviews-summary">
            <div class="pd-reviews-avg">${avg.toFixed(1)}<span class="pd-reviews-avg-of">/5</span></div>
            <div class="pd-reviews-stars">${starsHtml(avg, 18)}</div>
            <div class="pd-reviews-count">Based on ${count} ${count === 1 ? 'review' : 'reviews'}</div>
          </div>
          <div class="pd-reviews-buckets">
            ${buckets.map(b => `
              <div class="pd-reviews-bucket">
                <span class="pd-reviews-bucket-label">${b.stars}★</span>
                <div class="pd-reviews-bucket-track"><div class="pd-reviews-bucket-fill" style="width:${count ? Math.round(b.count / count * 100) : 0}%"></div></div>
                <span class="pd-reviews-bucket-count">${b.count}</span>
              </div>
            `).join('')}
          </div>
        ` : `<p class="pd-reviews-empty">No reviews yet. Be the first to share your experience after your next order.</p>`}
      </div>
      ${count ? `
        <ul class="pd-reviews-list">
          ${reviews.slice(0, 20).map(r => `
            <li class="pd-review">
              <div class="pd-review-head">
                <span class="pd-review-stars">${starsHtml(r.rating, 14)}</span>
                ${r.title ? `<span class="pd-review-title">${esc(r.title)}</span>` : ''}
                <span class="pd-review-date">${fmtDate(r.created_at)}</span>
              </div>
              ${r.body ? `<p class="pd-review-body">${esc(r.body)}</p>` : ''}
              <div class="pd-review-foot"><span class="pd-review-verified">✓ Verified buyer</span></div>
            </li>
          `).join('')}
        </ul>
      ` : ''}
      <p class="pd-reviews-hint">Reviews can only be left after a confirmed order. Visit <a href="/account" data-nav="account">My Orders</a> to review what you've bought.</p>
    `;
    setRegionBusy(host, false);
  };

  const renderVariants = () => {
    const wrap = $('#pd-variants');
    if (!wrap) return;
    const section = $('#pd-size-section');
    // Hide the variants strip when there's only a single (or zero) option —
    // a lone "—" button is just visual noise on single-size products.
    if (!pdState.variants || pdState.variants.length <= 1) {
      wrap.innerHTML = '';
      wrap.hidden = true;
      if (section) section.hidden = true;
      return;
    }
    wrap.hidden = false;
    if (section) section.hidden = false;
    wrap.innerHTML = pdState.variants.map((v, i) => `
      <button class="variant ${i===pdState.variant?'active':''}" data-variant="${i}" type="button">${esc(v)}</button>
    `).join('');
    $$('#pd-variants .variant').forEach(el => {
      el.addEventListener('click', () => {
        const idx = parseInt(el.dataset.variant);
        // For real (DB-backed) variants, re-render so price/image/stock swap.
        // For legacy single-product weight derivations, just flip the active class.
        if (pdState.product?.hasVariants) {
          renderProductDetail(pdState.product, { variantIdx: idx });
        } else {
          pdState.variant = idx;
          renderVariants();
        }
      });
    });
  };

  /* ── HASH-BASED ROUTING ── */
  const VIEW_NAMES = ['home', 'categories', 'about', 'contact', 'products', 'category', 'product', 'cart', 'checkout', 'blog', 'auth', 'order-confirmation', 'account', 'admin', 'privacy', 'terms', 'refund', 'shipping', 'certificates'];

  const productSlug = p => (p.slug && String(p.slug).trim()) || slugify(p.name);
  const categorySlug = c => slugify(c.name);

  // Keep document title + OG/Twitter meta in sync with the current view so
  // in-app browsers / Googlebot that re-scrape after JS execution see
  // product-specific share previews (the static /p/<slug>/index.html files
  // already cover the no-JS crawler case).
  const SITE_ORIGIN = location.origin;
  const DEFAULT_OG = {
    title: 'Sunnah Enterprises — Pure Sunnah Wellness',
    desc: 'Premium halal wellness products from Hyderabad — Sidr honey, black seed oil, talbina, ajwa dates, saffron and more.',
    image: `${SITE_ORIGIN}/images/logo/sunnah_entrprises_logo-2.jpg?v=2`,
    url: `${SITE_ORIGIN}/`,
    type: 'website',
  };
  const setMeta = (selector, attr, value) => {
    let el = document.head.querySelector(selector);
    if (!el) {
      el = document.createElement('meta');
      const [, key, val] = selector.match(/\[(.+?)=["'](.+?)["']\]/) || [];
      if (key && val) el.setAttribute(key, val);
      document.head.appendChild(el);
    }
    el.setAttribute(attr, value);
  };
  // Google reads the rendered DOM, so a canonical written here counts. Every SPA
  // route needs a self-referencing one — without it Google reported the whole
  // catalogue as "Duplicate without user-selected canonical".
  const setCanonical = url => {
    let el = document.head.querySelector('link[rel="canonical"]');
    if (!el) {
      el = document.createElement('link');
      el.setAttribute('rel', 'canonical');
      document.head.appendChild(el);
    }
    el.setAttribute('href', url);
  };
  const writeShareMeta = ({ title, desc, image, url, type }) => {
    // WhatsApp/Facebook won't render a WebP og:image and every uploaded image is
    // WebP — socialImage() swaps in Supabase's JPEG transform of the same object.
    image = socialImage(image).url;
    document.title = title;
    setCanonical(url);
    setMeta('meta[name="description"]', 'content', desc);
    setMeta('meta[property="og:type"]', 'content', type);
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', desc);
    setMeta('meta[property="og:image"]', 'content', image);
    setMeta('meta[property="og:url"]', 'content', url);
    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', desc);
    setMeta('meta[name="twitter:image"]', 'content', image);
  };
  const updateShareMeta = p => {
    if (!p) return resetShareMeta();
    const slug = productSlug(p);
    const title = (p.metaTitle || '').trim() || seoTitleFrom(p.name, p.shortDescription);
    const desc = (p.metaDescription || metaFromDescription(p.description) || p.shortDescription || p.subtitle ||
      `${p.name} — premium halal wellness from Sunnah Enterprises.`).slice(0, 200);
    const photo = p.photo && /^https?:\/\//i.test(p.photo)
      ? p.photo
      : (p.photo ? SITE_ORIGIN + (p.photo.startsWith('/') ? '' : '/') + p.photo : DEFAULT_OG.image);
    const url = `${SITE_ORIGIN}/product/${slug}`;
    writeShareMeta({ title, desc, image: photo, url, type: 'product' });
    removeArticleJsonLd();
    writeProductJsonLd(p, { url, image: photo, desc });
  };
  const resetShareMeta = () => { writeShareMeta(DEFAULT_OG); removeProductJsonLd(); removeArticleJsonLd(); };

  // JSON-LD Product schema. One <script type="application/ld+json" id="ld-product">
  // is owned by this pair of helpers; we replace it wholesale on every PDP render
  // and remove it when navigating to any non-product view.
  const writeProductJsonLd = (p, ctx) => {
    if (!p) return removeProductJsonLd();
    const price = Number(p.salePrice ?? p.price ?? 0);
    const inStock = (p.stockQty != null ? Number(p.stockQty) > 0 : !!p.inStock);
    const ld = {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      name: p.name,
      image: [ctx.image],
      description: ctx.desc,
      sku: String(p.id),
      brand: { '@type': 'Brand', name: 'Sunnah Enterprises' },
      category: p.category || undefined,
      offers: {
        '@type': 'Offer',
        url: ctx.url,
        priceCurrency: 'INR',
        price: price > 0 ? price.toFixed(2) : '0.00',
        availability: inStock
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
        itemCondition: 'https://schema.org/NewCondition',
      },
    };
    const rating = Number(p.rating) || 0;
    const reviews = Number(p.reviews) || 0;
    if (rating > 0 && reviews > 0) {
      ld.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: rating.toFixed(1),
        reviewCount: reviews,
      };
    }
    let tag = document.getElementById('ld-product');
    if (!tag) {
      tag = document.createElement('script');
      tag.type = 'application/ld+json';
      tag.id = 'ld-product';
      document.head.appendChild(tag);
    }
    tag.textContent = JSON.stringify(ld);
  };
  const removeProductJsonLd = () => {
    const tag = document.getElementById('ld-product');
    if (tag) tag.remove();
  };

  // Per-route document.title overrides. `product` is owned by updateShareMeta;
  // `category` is dynamic from the catalog. Everything else is a static string.
  const ROUTE_TITLES = {
    home: 'Sunnah Enterprises — Pure Sunnah Wellness',
    products: 'Shop All Products — Sunnah Enterprises',
    categories: 'Browse Categories — Sunnah Enterprises',
    about: 'About Us — Sunnah Enterprises',
    cart: 'Your Cart — Sunnah Enterprises',
    checkout: 'Checkout — Sunnah Enterprises',
    account: 'My Account — Sunnah Enterprises',
    auth: 'Sign In — Sunnah Enterprises',
    blog: 'Blog — Sunnah Enterprises',
    contact: 'Contact Us — Sunnah Enterprises',
    'order-confirmation': 'Order Confirmed — Sunnah Enterprises',
    admin: 'Admin Panel — Sunnah Enterprises',
    privacy: 'Privacy Policy — Sunnah Enterprises',
    terms: 'Terms of Service — Sunnah Enterprises',
    refund: 'Refund & Cancellation Policy — Sunnah Enterprises',
    shipping: 'Shipping Policy — Sunnah Enterprises',
    certificates: 'Certificates & Licenses — Sunnah Enterprises',
  };
  // Descriptions mirror the build-time copy in scripts/prerender-products.mjs.
  // Keep the two in sync — the prerender covers no-JS crawlers, this covers the
  // rendered pass and the routes that aren't prerendered (/category/<id>).
  const ROUTE_DESCRIPTIONS = {
    home: 'Premium halal wellness products from Hyderabad, trusted since 2006. Raw Sidr honey, black seed (kalonji) oil, talbina, Ajwa dates, Kashmiri saffron and prophetic medicine. Ships across India.',
    products: 'Browse the full Sunnah Enterprises catalogue — raw honey, black seed oil and seeds, Ajwa and Medjool dates, talbina, apple cider vinegar, cold-pressed olive oil, saffron, hijama cups, miswak and organic spices.',
    categories: 'Shop Sunnah wellness by category: honey, black seed (kalonji), dates, vinegars, talbina and barley, olive oil, saffron, herbal teas, hijama and miswak — all halal, sourced and packed in Hyderabad.',
    blog: 'Guides on prophetic medicine and Sunnah nutrition — how to use black seed oil, the benefits of Sidr honey, preparing talbina, choosing real Ajwa dates, and hijama basics.',
    contact: 'Get in touch with Sunnah Enterprises, Hyderabad. Questions about orders, bulk and wholesale enquiries, or product guidance — email contact@sunnahenterprises.com or message us directly.',
    certificates: 'Our FSSAI licence, halal certification, GST registration and lab test reports. Sunnah Enterprises, Regd. No. 2146/2009, Hyderabad — verified quality since 2006.',
    shipping: 'How and when we ship: dispatch timelines, delivery partners (Delhivery, Blue Dart, India Post), shipping charges and pan-India coverage for Sunnah Enterprises orders.',
    refund: 'Our refund, return and cancellation terms — eligibility windows, damaged or incorrect item claims, and how refunds are processed back to your original payment method.',
    privacy: 'How Sunnah Enterprises collects, uses, stores and protects the personal information you share when browsing sunnahenterprises.com or placing an order.',
    terms: 'The terms governing your use of sunnahenterprises.com — orders, pricing, payments via Razorpay, product information, liability and governing law.',
  };
  const applyRouteTitle = (name, payload) => {
    if (name === 'product') return;    // handled by updateShareMeta
    if (name === 'blog-post') return;  // handled by renderBlogPost
    let title = ROUTE_TITLES[name] || DEFAULT_OG.title;
    let desc = ROUTE_DESCRIPTIONS[name] || DEFAULT_OG.desc;
    if (name === 'category' && payload != null) {
      const c = CATEGORIES.find(x => x.id === parseInt(payload));
      if (c) {
        title = `${c.name} — Sunnah Enterprises`;
        desc = `Shop ${c.name} at Sunnah Enterprises — premium halal ${String(c.name).toLowerCase()} sourced and packed in Hyderabad, trusted since 2006. Ships across India.`;
      }
    }
    document.title = title;
    const url = SITE_ORIGIN + (location.pathname.replace(/\/+$/, '') || '/');
    setCanonical(url);
    setMeta('meta[name="description"]', 'content', desc);
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', desc);
    setMeta('meta[property="og:url"]', 'content', url);
    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', desc);
  };

  const findProduct = key => {
    if (key == null) return null;
    const id = parseInt(key);
    if (!isNaN(id) && String(id) === String(key)) return PRODUCTS.find(p => p.id === id) || null;
    const slug = String(key).toLowerCase();
    // Match either the admin-set slug or the auto-derived slug, so old links keep working.
    return PRODUCTS.find(p => productSlug(p) === slug || slugify(p.name) === slug) || null;
  };

  const findCategory = key => {
    if (key == null) return null;
    const id = parseInt(key);
    if (!isNaN(id) && String(id) === String(key)) return CATEGORIES.find(c => c.id === id) || null;
    const slug = String(key).toLowerCase();
    return CATEGORIES.find(c => categorySlug(c) === slug) || null;
  };

  // ── Products page: ?cat=<slug> round-trip ──
  // The selected category lives in the URL (slug preferred, falls back to id
  // via findCategory) so /products links are shareable and back/forward work.
  // 'all' carries no param. Read on view entry; write on selection.
  const readCatFromUrl = () => {
    const raw = new URLSearchParams(location.search).get('cat');
    if (!raw) return 'all';
    const c = findCategory(raw); // resolves slug OR id; null when unknown
    return c ? c.id : 'all';     // unknown slug → graceful fallback to All
  };
  // Push the current apFilter into ?cat without disturbing other params.
  const syncCatUrl = () => {
    const params = new URLSearchParams(location.search);
    if (apFilter === 'all') {
      params.delete('cat');
    } else {
      const c = CATEGORIES.find(x => x.id === apFilter);
      if (c) params.set('cat', categorySlug(c)); else params.delete('cat');
    }
    const qs = params.toString();
    history.pushState(null, '', location.pathname + (qs ? '?' + qs : ''));
  };
  // Used by entry points (search form, PDP tag) that set apFilter THEN navigate
  // via setView('products'). setView preserves location.search and applyView
  // re-reads ?cat, so we must write the intended cat — or strip a stale one —
  // into the current URL before navigating. catId === 'all' clears ?cat.
  const setCatSearch = catId => {
    const params = new URLSearchParams(location.search);
    const c = catId === 'all' ? null : CATEGORIES.find(x => x.id === catId);
    if (c) params.set('cat', categorySlug(c)); else params.delete('cat');
    const qs = params.toString();
    history.replaceState(null, '', location.pathname + (qs ? '?' + qs : ''));
  };

  // Admin tabs that get their own deep-link (dashboard is the bare /admin).
  const ADMIN_TABS = ['orders', 'payments', 'products', 'categories',
    'customers', 'coupons', 'reviews', 'content', 'social', 'messages'];

  // Clean URLs (History API) — no "#". home → "/", product → "/product/<slug>".
  const pathFor = (name, payload) => {
    if (name === 'home') return '/';
    if (name === 'product' && payload != null) {
      const p = findProduct(payload);
      return p ? `/product/${productSlug(p)}` : '/products';
    }
    if (name === 'category' && payload != null) {
      const c = findCategory(payload);
      return c ? `/category/${categorySlug(c)}` : '/categories';
    }
    // One article, one URL: /blog/<slug>. The listing stays at /blog.
    if (name === 'blog-post') return payload ? `/blog/${payload}` : '/blog';
    // Admin deep-links: /admin/<tab>, but the default dashboard stays at /admin.
    if (name === 'admin') {
      return payload && payload !== 'dashboard' ? `/admin/${payload}` : '/admin';
    }
    return `/${name}`;
  };

  const parseLocation = () => {
    // Normalize legacy "#/..." hash links to a clean path so old shared/bookmarked
    // links keep working.
    let pathname = location.pathname;
    if ((pathname === '/' || pathname === '') && /^#\/?./.test(location.hash || '')) {
      pathname = '/' + location.hash.replace(/^#\/?/, '');
    }
    const segs = pathname.replace(/^\/+|\/+$/g, '').split('/');
    const seg = segs[0] || '';
    const val = segs[1] || '';
    if (!seg) return { name: 'home', payload: null };
    // /p/<slug> is the social-share prerender route — treat it as a product.
    if ((seg === 'product' || seg === 'p') && val) {
      const p = findProduct(decodeURIComponent(val));
      return p ? { name: 'product', payload: p.id } : { name: 'home', payload: null };
    }
    if (seg === 'category' && val) {
      const c = findCategory(decodeURIComponent(val));
      return c ? { name: 'category', payload: c.id } : { name: 'home', payload: null };
    }
    if (seg === 'blog' && val) {
      return { name: 'blog-post', payload: decodeURIComponent(val) };
    }
    if (seg === 'admin') {
      return { name: 'admin', payload: ADMIN_TABS.includes(val) ? val : null };
    }
    if (VIEW_NAMES.includes(seg)) return { name: seg, payload: null };
    return { name: 'home', payload: null };
  };

  let viewToken = 0;
  const applyView = (name, payload) => {
    // Bumped on every applyView. A renderer below may bounce us elsewhere via
    // a nested setView (checkout → auth when signed out, order-confirmation →
    // home with no order). That nested call already activated the right view,
    // so the outer call must stop before re-activating this one on top of it —
    // which is what put a signed-out visitor on a live checkout screen.
    const token = ++viewToken;
    if (name !== 'product' && name !== 'blog-post') resetShareMeta();
    if (name === 'product' && payload != null) {
      const p = PRODUCTS.find(x => x.id === parseInt(payload));
      if (p) renderProductDetail(p);
      else { name = 'home'; payload = null; }
    }
    applyRouteTitle(name, payload);
    if (name === 'category' && payload != null) renderCategoryView(payload);
    if (name === 'products') {
      apFilter = readCatFromUrl();
      // Scrub a stale/unknown ?cat that fell back to All so URL + active row match.
      if (apFilter === 'all' && new URLSearchParams(location.search).has('cat')) setCatSearch('all');
      renderApFilters();
      renderProductsView();
    }
    if (name === 'blog') renderBlogView();
    if (name === 'blog-post') renderBlogPost(payload);
    if (name === 'cart') renderCartView();
    if (name === 'checkout') renderCheckoutView();
    if (name === 'auth') resetAuthView();
    if (name === 'order-confirmation') renderOrderConfirmation();
    if (name === 'account') renderAccountView();
    // Admin is async and manages its own view-switching (may redirect to auth or show
    // access-denied). Don't touch .active classes here — renderAdminView handles them.
    if (name === 'admin') { if (payload) adminTab = payload; renderAdminView(); return; }
    if (token !== viewToken) return; // a renderer redirected us — it owns the view now
    document.body.classList.toggle('ck-active', name === 'checkout');
    document.body.classList.remove('adm-active');
    $$('.view').forEach(v => v.classList.remove('active'));
    const view = $(`#view-${name}`);
    if (view) view.classList.add('active');
    window.scrollTo({ top: 0, behavior: 'auto' });
    const highlight = name === 'category' ? 'categories' : (name === 'blog-post' ? 'blog' : name);
    $$('.bn-btn').forEach(b => b.classList.toggle('active', b.dataset.nav === highlight));
    $$('.amzn-secondary-inner a').forEach(a => a.classList.toggle('active', a.dataset.nav === highlight));
  };

  // All views use clean pathnames via the History API. pushState when the path
  // changes; replaceState to silently strip a leftover legacy hash.
  const setView = (name, payload = null) => {
    const path = pathFor(name, payload);
    const target = path + location.search;
    if (location.pathname !== path) {
      history.pushState(null, '', target);
    } else if (location.hash) {
      history.replaceState(null, '', target);
    }
    applyView(name, payload);
  };

  const goBack = () => {
    if (history.length > 1) history.back();
    else setView('home');
  };

  // Back/forward navigation. pushState/replaceState don't fire popstate, so
  // every popstate here is a genuine user history move.
  window.addEventListener('popstate', () => {
    const { name, payload } = parseLocation();
    applyView(name, payload);
  });

  /* ── EVENT WIRING ── */
  document.addEventListener('click', e => {
    // hero slideshow controls
    const dot = e.target.closest('[data-slide-idx]');
    if (dot) { goSlide(parseInt(dot.dataset.slideIdx)); startSlideAuto(); return; }
    if (e.target.closest('#hero-prev')) { goSlide(slideIdx - 1); startSlideAuto(); return; }
    if (e.target.closest('#hero-next')) { goSlide(slideIdx + 1); startSlideAuto(); return; }

    // back button in any subview
    if (e.target.closest('[data-back]')) {
      e.preventDefault();
      goBack();
      return;
    }

    // wishlist heart toggle on product cards
    const wlToggle = e.target.closest('[data-wl-toggle]');
    if (wlToggle) {
      e.preventDefault();
      e.stopPropagation();
      const pid = parseInt(wlToggle.dataset.wlToggle);
      const s = getSession();
      wishlistLib.toggleWishlist(s?.userId || null, pid).then(nowActive => {
        // Update all heart buttons for this product across the page
        document.querySelectorAll(`[data-wl-toggle="${pid}"]`).forEach(btn => {
          btn.classList.toggle('wl-heart--active', nowActive);
          btn.setAttribute('aria-label', nowActive ? 'Remove from wishlist' : 'Add to wishlist');
          const svg = btn.querySelector('svg');
          if (svg) svg.setAttribute('fill', nowActive ? 'currentColor' : 'none');
        });
        updateWishlistBadge();
        showToast(nowActive ? 'Added to wishlist' : 'Removed from wishlist');
      });
      return;
    }

    // quick add-to-cart from product listing card
    const quickAdd = e.target.closest('[data-quick-add]');
    if (quickAdd) {
      e.preventDefault();
      const id = parseInt(quickAdd.dataset.quickAdd);
      const p = PRODUCTS.find(x => x.id === id);
      if (p) {
        // Multi-variant products can't be quick-added — the customer must
        // pick a size first, so route them into the product detail view.
        if (p.hasVariants && Array.isArray(p.variants) && p.variants.length > 0) {
          setView('product', p.id);
          return;
        }
        addToCart(p.id, p.weight || '', 1, null);
        showToast(`Added to cart · ${p.name}${p.weight ? ` (${p.weight})` : ''}`);
      }
      return;
    }

    // product card → product detail
    const card = e.target.closest('[data-product-id]');
    if (card && !e.target.closest('[data-stop]')) {
      const id = parseInt(card.dataset.productId);
      if (PRODUCTS.find(x => x.id === id)) setView('product', id);
      return;
    }

    // category card → category detail
    // Scope to the customer-facing card buttons only. The admin Categories
    // table also uses [data-cat-id] on its inline inputs; matching those here
    // hijacked clicks on the Name/Sort fields and redirected to the category.
    const catEl = e.target.closest('.cat-card, .cat-page-card');
    if (catEl) {
      e.preventDefault();
      const id = parseInt(catEl.dataset.catId);
      setView('category', id);
      return;
    }

    // nav links / generic nav
    const navEl = e.target.closest('[data-nav]');
    if (navEl) {
      const target = navEl.dataset.nav;
      if (target === 'home-scroll') {
        e.preventDefault();
        setView('home');
        const sel = navEl.dataset.target || (navEl.getAttribute('href') && navEl.getAttribute('href').startsWith('#') ? navEl.getAttribute('href') : null);
        if (sel) {
          requestAnimationFrame(() => {
            const el = document.querySelector(sel);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
        }
      } else if (target === 'home' || target === 'categories' || target === 'about' || target === 'products' || target === 'product' || target === 'contact' || target === 'blog' || target === 'cart' || target === 'checkout' || target === 'account' || target === 'order-confirmation' || target === 'admin' || target === 'privacy' || target === 'terms' || target === 'refund' || target === 'shipping' || target === 'certificates') {
        e.preventDefault();
        setView(target);
      } else if (target === 'auth') {
        e.preventDefault();
        // When logged in, the same header block toggles the account dropdown
        // instead of navigating to the auth page.
        if (getSession()) {
          toggleAccountMenu();
        } else {
          setView('auth');
        }
      }
    }
  });

  // ── Category sidebar / mobile drawer ──
  const catMobileMQ = window.matchMedia('(max-width: 899px)');
  const closeCatDrawer = () => {
    const sidebar = $('#ap-sidebar');
    const overlay = $('#ap-cat-overlay');
    const trigger = $('#ap-cat-trigger');
    if (sidebar) sidebar.classList.remove('is-open');
    if (overlay) overlay.hidden = true, overlay.classList.remove('is-open');
    document.body.classList.remove('ap-drawer-open');
    if (trigger) { trigger.setAttribute('aria-expanded', 'false'); trigger.focus(); } // return focus to the opener
  };
  const openCatDrawer = () => {
    const sidebar = $('#ap-sidebar');
    const overlay = $('#ap-cat-overlay');
    const trigger = $('#ap-cat-trigger');
    if (overlay) { overlay.hidden = false; requestAnimationFrame(() => overlay.classList.add('is-open')); }
    if (sidebar) sidebar.classList.add('is-open');
    document.body.classList.add('ap-drawer-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    const first = $('#ap-cat-list .ap-cat'); // move focus into the drawer
    if (first) first.focus();
  };

  // Category selection (sidebar rows on desktop, drawer rows on mobile).
  // Mirrors the old chip handler: only apFilter changes (search/tag untouched),
  // then we push ?cat to the URL and re-render. On mobile the drawer closes.
  document.addEventListener('click', e => {
    const btn = e.target.closest('.ap-cat');
    if (!btn) return;
    const f = btn.dataset.filter;
    apFilter = f === 'all' ? 'all' : parseInt(f);
    syncCatUrl();
    renderApFilters();
    renderProductsView();
    if (catMobileMQ.matches) closeCatDrawer();
  });

  // Drawer open/close wiring (trigger, close button, overlay, Esc).
  document.addEventListener('click', e => {
    if (e.target.closest('#ap-cat-trigger')) { openCatDrawer(); return; }
    if (e.target.closest('#ap-cat-close') || e.target.closest('#ap-cat-overlay')) { closeCatDrawer(); }
  });
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && document.body.classList.contains('ap-drawer-open')) {
      closeCatDrawer(); // focus is returned to the trigger inside closeCatDrawer
    }
  });

  // sort dropdown
  const sortSel = $('#ap-sort');
  if (sortSel) sortSel.addEventListener('change', e => {
    apSort = e.target.value;
    renderProductsView();
  });

  // view toggle (grid / list)
  document.addEventListener('click', e => {
    const vBtn = e.target.closest('.ap-view-btn');
    if (!vBtn) return;
    apViewMode = vBtn.dataset.view || 'grid';
    $$('.ap-view-btn').forEach(b => b.classList.toggle('active', b.dataset.view === apViewMode));
    renderProductsView();
  });

  // "Show All Products" reset button on empty state
  document.addEventListener('click', e => {
    if (e.target.closest('[data-ap-reset]')) {
      apFilter = 'all';
      apQuery = '';
      apActiveTag = '';
      apSort = 'featured';
      if (sortSel) sortSel.value = 'featured';
      syncCatUrl(); // keep URL in sync — drop the stale ?cat
      renderApFilters();
      renderProductsView();
    }
  });

  // qty (buy-box select)
  const qtySel = $('#pd-bb-qty');
  if (qtySel) qtySel.addEventListener('change', e => {
    pdState.qty = Math.max(1, parseInt(e.target.value) || 1);
  });

  // Add to Cart + Buy Now (desktop + mobile share handlers)
  const handleAddToCart = () => {
    const p = pdState.product;
    if (!p) return;
    const variantLabel = pdState.variants[pdState.variant] || '';
    const variantObj = (p.hasVariants && Array.isArray(p.variants))
      ? p.variants[pdState.variant] : null;
    addToCart(p.id, variantLabel, pdState.qty, variantObj ? variantObj.id : null);
    showToast(`Added to cart · ${p.name}${variantLabel ? ` (${variantLabel})` : ''}`);
  };
  const handleBuyNow = () => {
    const p = pdState.product;
    if (!p) return;
    const variantLabel = pdState.variants[pdState.variant] || '';
    const variantObj = (p.hasVariants && Array.isArray(p.variants))
      ? p.variants[pdState.variant] : null;
    addToCart(p.id, variantLabel, pdState.qty, variantObj ? variantObj.id : null);
    setView('checkout');
  };
  ['#pd-add-cart', '#pd-add-cart-mobile'].forEach(sel => {
    const el = $(sel);
    if (el) el.addEventListener('click', handleAddToCart);
  });
  ['#pd-buy-now', '#pd-buy-now-mobile'].forEach(sel => {
    const el = $(sel);
    if (el) el.addEventListener('click', handleBuyNow);
  });

  // Share — native share API where available; else WhatsApp deeplink.
  const handleShare = async () => {
    const p = pdState.product;
    if (!p) return;
    // Use the path-based /p/<slug> URL so social crawlers (which strip the
    // hash before fetching) can read the prerendered OG tags and show the
    // main product photo as the share preview.
    const url = `${location.origin}/p/${productSlug(p)}`;
    const blurb = (p.shortDescription || '').trim();
    const text = `${p.name} — ₹${p.price} at Sunnah Enterprises${blurb ? `\n${blurb}` : ''}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: p.name, text, url });
        return;
      } catch (err) {
        // User cancelled or share failed; fall through to WhatsApp.
        if (err && err.name === 'AbortError') return;
      }
    }
    const waText = `${text}\n${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(waText)}`, '_blank', 'noopener');
  };
  const shareBtn = $('#pd-share');
  if (shareBtn) shareBtn.addEventListener('click', handleShare);

  // PDP wishlist button
  const pdWishBtn = $('#pd-wishlist');
  if (pdWishBtn) pdWishBtn.addEventListener('click', () => {
    if (!pdState.product) return;
    const pid = pdState.product.id;
    const s = getSession();
    wishlistLib.toggleWishlist(s?.userId || null, pid).then(nowActive => {
      pdWishBtn.classList.toggle('btn-wishlist--active', nowActive);
      const svg = pdWishBtn.querySelector('svg');
      if (svg) svg.setAttribute('fill', nowActive ? 'currentColor' : 'none');
      const label = $('#pd-wishlist-label');
      if (label) label.textContent = nowActive ? 'Wishlisted' : 'Wishlist';
      pdWishBtn.setAttribute('aria-label', nowActive ? 'Remove from wishlist' : 'Add to wishlist');
      // Sync card hearts on other surfaces
      document.querySelectorAll(`[data-wl-toggle="${pid}"]`).forEach(btn => {
        btn.classList.toggle('wl-heart--active', nowActive);
        const s2 = btn.querySelector('svg');
        if (s2) s2.setAttribute('fill', nowActive ? 'currentColor' : 'none');
      });
      updateWishlistBadge();
      showToast(nowActive ? 'Added to wishlist' : 'Removed from wishlist');
    });
  });

  // Tag chip → filtered products view (search by tag).
  document.addEventListener('click', e => {
    const tagBtn = e.target.closest('[data-pd-tag]');
    if (!tagBtn) return;
    e.preventDefault();
    const tag = tagBtn.getAttribute('data-pd-tag') || '';
    apQuery = tag;
    apActiveTag = tag;
    apFilter = 'all';
    apSort = 'featured';
    const searchInput = $('#amzn-search-input');
    if (searchInput) searchInput.value = tag;
    setCatSearch('all'); // strip any stale ?cat so it doesn't clobber the tag filter
    setView('products');
  });

  // Active-tag pill: × clears the filter and re-renders.
  document.addEventListener('click', e => {
    if (!e.target.closest('[data-ap-clear-tag]')) return;
    apActiveTag = '';
    apQuery = '';
    const searchInput = $('#amzn-search-input');
    if (searchInput) searchInput.value = '';
    renderProductsView();
  });

  // Cart view — qty +/- and remove
  document.addEventListener('click', e => {
    const inc = e.target.closest('[data-cart-inc]');
    if (inc) { setCartQty(parseInt(inc.dataset.cartInc), cart[parseInt(inc.dataset.cartInc)].qty + 1); return; }
    const dec = e.target.closest('[data-cart-dec]');
    if (dec) { setCartQty(parseInt(dec.dataset.cartDec), cart[parseInt(dec.dataset.cartDec)].qty - 1); return; }
    const rem = e.target.closest('[data-cart-remove]');
    if (rem) { removeFromCart(parseInt(rem.dataset.cartRemove)); return; }
    const sel = e.target.closest('[data-cart-sel]');
    if (sel) { setCartSel(parseInt(sel.dataset.cartSel), sel.checked); return; }
    const selAll = e.target.closest('[data-cart-sel-all]');
    if (selAll) { setCartSelAll(selAll.dataset.cartSelAll === '1'); return; }
  });

  // mobile menu — burger toggles the secondary nav bar's visibility
  const burgerBtn = $('#navBurger');
  const secondaryBar = document.querySelector('.amzn-secondary');
  if (burgerBtn && secondaryBar) {
    burgerBtn.addEventListener('click', () => {
      secondaryBar.classList.toggle('open');
    });
    secondaryBar.addEventListener('click', e => {
      if (e.target.closest('a')) secondaryBar.classList.remove('open');
    });
  }

  /* ── HERO SLIDESHOW ──
     Slides are admin-managed (Content tab → Hero Slides). The mutable `SLIDES`
     array below is a fallback used until refreshHeroSlides() loads the live
     rows from the `hero_slides` table on boot. Each slide shape is one of:
       1. Full banner (image-only):  { banner: 'filename.jpg', link: 'products', alt: '...' }
       2. Split layout (text + image): { eyebrow, title, sub, price, save, cta, image, emoji, ctaTarget } */
  let SLIDES = [
    {
      banner: '649059158_1520293020098384_4921461452247068624_n.png',
      alt: 'Shifa-e Wellness — Natural remedies for the whole family',
      link: 'products'
    },
  ];

  let slideIdx = 0;
  let slideTimer = null;

  const renderSlides = () => {
    const track = $('#hero-slides');
    const dots = $('#hero-dots');
    const sliderRoot = $('#hero-slider');
    if (!track || !dots) return;
    setRegionBusy(track, false);

    // Resolve a slide image reference into a real src — handles three forms:
    //   1. absolute http(s) URL (DB-uploaded): use as-is
    //   2. starts with "/" (custom path): use as-is
    //   3. bare filename: legacy, prefix with images/slides/
    const resolveImg = (ref) => {
      if (!ref) return '';
      if (/^https?:\/\//i.test(ref) || ref.startsWith('/')) return ref;
      return `images/slides/${ref}`;
    };

    track.innerHTML = SLIDES.map((s, i) => {
      const ariaLabel = `${i+1} of ${SLIDES.length}`;
      // Full-banner slide: image-only, edge-to-edge, optional click target.
      // DB rows expose the URL via `bannerUrl`; legacy seeded entries use `banner`.
      const bannerSrc = s.bannerUrl || (s.banner ? resolveImg(s.banner) : null);
      if (bannerSrc) {
        const inner = `
          <div class="hero-banner">
            <img src="${esc(bannerSrc)}" alt="${esc(s.alt || '')}" />
          </div>`;
        return s.link
          ? `<a class="hero-slide hero-slide--banner" data-nav="${s.link}" role="group" aria-roledescription="slide" aria-label="${ariaLabel}" href="#">${inner}</a>`
          : `<div class="hero-slide hero-slide--banner" role="group" aria-roledescription="slide" aria-label="${ariaLabel}">${inner}</div>`;
      }
      // Split-layout slide: text on left, image on right. DB rows use
      // `imageUrl`; legacy seeded entries use `image` (a filename).
      const splitImgSrc = s.imageUrl || (s.image ? resolveImg(s.image) : null);
      return `
      <div class="hero-slide" role="group" aria-roledescription="slide" aria-label="${ariaLabel}">
        <div class="hero-slide-text">
          ${s.eyebrow ? `<div class="hero-slide-eyebrow"><span class="star">★</span>${s.eyebrow}</div>` : ''}
          <h1 class="hero-slide-title">${s.title || ''}</h1>
          ${s.sub ? `<p class="hero-slide-sub">${s.sub}</p>` : ''}
          ${(s.price || s.save) ? `
            <div class="hero-slide-price-row">
              ${s.price ? `<span class="hero-slide-price">${s.price}</span>` : ''}
              ${s.original ? `<span class="hero-slide-original">${s.original}</span>` : ''}
              ${s.save ? `<span class="hero-slide-save">${s.save}</span>` : ''}
            </div>` : ''}
          <div class="hero-slide-cta-row">
            <button class="btn btn-green" data-nav="${s.ctaTarget || 'products'}">${s.cta || 'Shop Now'}</button>
          </div>
        </div>
        <div class="hero-slide-visual">
          ${splitImgSrc
            ? `<img src="${esc(splitImgSrc)}" alt="${esc((s.title || '').replace(/<[^>]+>/g,''))}" data-hero-img />`
            : ''}
          <div class="hero-slide-fallback" style="${splitImgSrc ? 'display:none' : ''}">${s.emoji || '🍯'}</div>
        </div>
      </div>`;
    }).join('');

    // If a hero image fails to load, hide it and reveal the emoji fallback
    // that sits right next to it. Wired here (not via inline onerror=) so we
    // don't need 'unsafe-inline' in any future CSP.
    track.querySelectorAll('img[data-hero-img]').forEach(img => {
      img.addEventListener('error', () => {
        img.style.display = 'none';
        const fallback = img.nextElementSibling;
        if (fallback && fallback.classList.contains('hero-slide-fallback')) {
          fallback.style.display = 'flex';
        }
      }, { once: true });
    });

    // Hide dots/arrows + stop autoplay when there's only one slide
    const single = SLIDES.length <= 1;
    if (sliderRoot) sliderRoot.classList.toggle('hero-slider--single', single);
    dots.innerHTML = single ? '' : SLIDES.map((_, i) =>
      `<button class="hero-dot ${i === slideIdx ? 'active' : ''}" data-slide-idx="${i}" role="tab" aria-label="Go to slide ${i+1}"></button>`
    ).join('');
  };

  const goSlide = (i) => {
    if (!SLIDES.length) return;
    slideIdx = ((i % SLIDES.length) + SLIDES.length) % SLIDES.length;
    const track = $('#hero-slides');
    if (track) track.style.transform = `translateX(-${slideIdx * 100}%)`;
    $$('#hero-dots .hero-dot').forEach((d, k) => d.classList.toggle('active', k === slideIdx));
  };

  const startSlideAuto = () => {
    if (slideTimer) clearInterval(slideTimer);
    if (SLIDES.length <= 1) return;
    slideTimer = setInterval(() => goSlide(slideIdx + 1), 5500);
  };

  /* ── BLOG ──
     Mutable: the hardcoded posts below are the fallback used until
     refreshBlogPosts() loads from the `blog_posts` table on boot. */
  let BLOGS = [
    {
      slug: 'sunnah-of-honey',
      title: 'The Sunnah of Honey: A 2,500-Year-Old Wellness Secret',
      excerpt: 'Why pure Sidr honey from the Yemeni highlands has been treasured across cultures — and the modern science behind every spoonful.',
      category: 'Wellness',
      date: 'April 18, 2026',
      publishedIso: '2026-04-18T00:00:00.000Z',
      readTime: '6 min read',
      cover: 'images/gallery/berry-honey-500x500.JPG.webp',
      coverFit: 'contain',
      body: `
        <p>Long before refrigerators, antibiotics or supplement aisles, there was honey — sealed in clay jars, traded across deserts, and prescribed for almost everything. The Prophet ﷺ called honey one of the two cures, and for good reason: it does not spoil, it heals wounds, and it carries the genetic memory of every flower a bee ever touched.</p>
        <h2>What makes Sidr honey different</h2>
        <p>Sidr honey is harvested from bees that feed on the blossoms of the Sidr tree (Ziziphus spina-christi), which grows in the dry highlands of Yemen. The blooming season is short — only a few weeks each year — and a single hive yields a fraction of what regular honey hives produce. The result is a thick, dark, complex honey with a lingering caramel finish and unusually high antioxidant levels.</p>
        <h2>The healing science</h2>
        <p>Modern studies on raw, unprocessed honey show measurable benefits for wound healing, sore throats, persistent coughs in children, and even certain digestive complaints. Honey is naturally antibacterial because of its low water content and trace amounts of hydrogen peroxide. When it's also unfiltered and uncooked — like ours — the enzymes, pollen and propolis stay intact.</p>
        <h2>How to use it daily</h2>
        <p>One spoonful in warm (never boiling) water on an empty stomach. A teaspoon stirred into nigella seed oil for immunity. A drizzle over labneh, dates or oats. Children over the age of one can take it for coughs at night. Pregnant mothers find it gentle and grounding.</p>
        <p><em>Pure honey crystallises in winter — that's a sign of authenticity, not a defect. Warm the jar in a bowl of water and it returns to liquid gold.</em></p>
      `,
    },
    {
      slug: 'black-seed-cure',
      title: 'Why Black Seed is Called the Cure for Everything (Except Death)',
      excerpt: 'Habbatus Sauda, Nigella sativa, kalonji — one tiny seed, mentioned in every classical medical text from Cairo to Kerala.',
      category: 'Prophetic Medicine',
      date: 'April 9, 2026',
      publishedIso: '2026-04-09T00:00:00.000Z',
      readTime: '5 min read',
      cover: 'placeholder:black-seed',
      body: `
        <p>"In the black seed is healing for every disease except death." — these words, narrated in Sahih al-Bukhari, are perhaps the boldest claim ever made about a single ingredient. Yet the strange thing is, fourteen centuries later, modern researchers keep discovering they may not have been an exaggeration.</p>
        <h2>What is black seed, exactly?</h2>
        <p>Nigella sativa is a flowering plant native to South Asia and the Mediterranean. Its tiny black seeds taste peppery, faintly bitter, and oily. Cold-pressing them yields a deep amber oil rich in thymoquinone — the compound responsible for most of its therapeutic effects.</p>
        <h2>What the research actually shows</h2>
        <p>Peer-reviewed studies have associated black seed oil with improved blood-glucose control, reduced inflammation, healthier blood pressure, support for asthmatic airways, and a noticeable boost to immunity during seasonal illness. It also works topically for eczema, hair fall and joint pain.</p>
        <h2>How we cold-press ours</h2>
        <p>Heat destroys thymoquinone. That's why we use traditional stone presses kept under 40°C — slower, lower yield, but every drop retains its medicine. Our seeds come from a single trusted farm in Ethiopia, where the soil produces unusually high oil content.</p>
        <p>Half a teaspoon a day, taken with warm water and a date, is the classical dose. Build the habit before you build the expectation.</p>
      `,
    },
    {
      slug: 'talbina-forgotten-food',
      title: 'Talbina: The Forgotten Healing Food from the Sunnah',
      excerpt: 'A simple barley porridge the Prophet ﷺ recommended for the grieving heart — and why it works on the body too.',
      category: 'Sunnah Foods',
      date: 'March 28, 2026',
      publishedIso: '2026-03-28T00:00:00.000Z',
      readTime: '4 min read',
      cover: 'images/slides/649059158_1520293020098384_4921461452247068624_n.png',
      coverFit: 'cover',
      body: `
        <p>Among all the foods the Prophet ﷺ recommended, talbina holds a special place. Aisha (RA) narrated that he would prepare it for grieving households and tell them: "Talbina gives rest to the heart of the sick and removes some of the sorrow." It is, in essence, a porridge — but a porridge with purpose.</p>
        <h2>What goes into it</h2>
        <p>Just three ingredients: ground whole barley, milk and a touch of honey. That is all. The barley is simmered slowly in milk until the mixture turns silky and pale. Honey is stirred in only after the heat is off, so its enzymes are preserved.</p>
        <h2>Why it actually works</h2>
        <p>Whole barley is one of the richest plant sources of beta-glucan, a soluble fibre proven to lower cholesterol, stabilise blood sugar and feed beneficial gut bacteria. The gut, we now know, is closely linked to mood — which gives the Prophet's words a new layer of meaning. A calmer gut is, quite literally, a calmer heart.</p>
        <h2>Bringing it back</h2>
        <p>We mill our barley fresh in small batches and seal it the same week. Two tablespoons in a cup of warm milk is enough for a serving. Take it in the morning if you're recovering from illness, or in the evening if your mind has been carrying weight.</p>
        <p>Sometimes the oldest remedies are the ones we've simply forgotten how to cook.</p>
      `,
    },
  ];

  const blogUrl = slug => `${SITE_ORIGIN}/blog/${slug}`;
  const findBlog = slug => BLOGS.find(b => b.slug === slug) || null;

  const blogCardHtml = (b) => {
    const cover = b.cover && !b.cover.startsWith('placeholder:')
      ? `<img src="${b.cover}" alt="${esc(b.coverAlt || b.title)}" loading="lazy" style="object-fit:${b.coverFit || 'cover'}" />`
      : `<div class="blog-card-placeholder" data-placeholder="${b.cover ? b.cover.replace('placeholder:', '') : 'default'}"></div>`;
    // A real <a href> — crawlers follow these to every article; the click
    // handler below turns them into SPA navigations for humans.
    return `
      <a class="blog-card" href="/blog/${esc(b.slug)}" data-blog-slug="${esc(b.slug)}">
        <div class="blog-card-cover">${cover}<span class="blog-tag">${esc(b.category)}</span></div>
        <div class="blog-card-body">
          <div class="blog-card-meta"><span>${esc(b.date)}</span>${b.readTime ? `<span class="blog-reader-dot">•</span><span>${esc(b.readTime)}</span>` : ''}</div>
          <h3>${esc(b.title)}</h3>
          <p>${esc(b.excerpt)}</p>
          <span class="blog-card-readmore">Read article →</span>
        </div>
      </a>
    `;
  };

  const renderBlogView = () => {
    const grid = $('#blog-grid');
    if (!grid) return;
    setRegionBusy(grid, false);
    grid.innerHTML = BLOGS.map(blogCardHtml).join('');
  };

  /* ── Article page (/blog/<slug>) ──
     Replaces the old modal reader: every post is a real, linkable, indexable
     URL. scripts/prerender-products.mjs bakes the same content into
     dist/blog/<slug>/index.html for crawlers that don't run JS. */
  const blogDesc = b => (b.metaDescription || b.excerpt ||
    renderBlogBody(b.body).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()).slice(0, 200);
  const blogTitle = b => b.metaTitle || `${b.title} — Sunnah Enterprises`;
  const blogImage = b => {
    if (!b.cover || b.cover.startsWith('placeholder:')) return DEFAULT_OG.image;
    return /^https?:\/\//i.test(b.cover) ? b.cover : SITE_ORIGIN + (b.cover.startsWith('/') ? '' : '/') + b.cover;
  };

  const writeArticleJsonLd = (b) => {
    const url = blogUrl(b.slug);
    const ld = {
      '@context': 'https://schema.org/',
      '@type': 'BlogPosting',
      headline: b.title,
      description: blogDesc(b),
      image: [blogImage(b)],
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
      url,
      datePublished: b.publishedIso || undefined,
      dateModified: b.updatedIso || b.publishedIso || undefined,
      articleSection: b.category || undefined,
      author: { '@type': 'Organization', name: b.author || 'Sunnah Enterprises', url: `${SITE_ORIGIN}/` },
      publisher: {
        '@type': 'Organization',
        name: 'Sunnah Enterprises',
        logo: { '@type': 'ImageObject', url: `${SITE_ORIGIN}/images/logo/sunnah_entrprises_logo-2.jpg` },
      },
    };
    const crumbs = {
      '@context': 'https://schema.org/',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_ORIGIN}/` },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_ORIGIN}/blog` },
        { '@type': 'ListItem', position: 3, name: b.title, item: url },
      ],
    };
    const put = (id, obj) => {
      let tag = document.getElementById(id);
      if (!tag) {
        tag = document.createElement('script');
        tag.type = 'application/ld+json';
        tag.id = id;
        document.head.appendChild(tag);
      }
      tag.textContent = JSON.stringify(obj);
    };
    put('ld-article', ld);
    put('ld-article-crumbs', crumbs);
  };
  const removeArticleJsonLd = () => {
    ['ld-article', 'ld-article-crumbs'].forEach(id => {
      const t = document.getElementById(id);
      if (t) t.remove();
    });
  };

  // "Purchase this product" card for a product linked in a blog body. Clicks
  // route through the global [data-product-id] / [data-quick-add] handlers.
  const blogBuyCardHtml = p => {
    const multi = p.hasVariants && Array.isArray(p.variants) && p.variants.length > 0;
    return `
    <aside class="blog-buy" data-product-id="${p.id}">
      <div class="blog-buy-img">${p.photo
        ? `<img src="${esc(p.photo)}" alt="${esc(p.name)}" loading="lazy" />`
        : illustrationHtml(p.img, 120, null, p.name)}</div>
      <div class="blog-buy-info">
        <div class="blog-buy-eyebrow">Purchase this product</div>
        <div class="blog-buy-name">${esc(p.name)}</div>
        <div class="blog-buy-price">${multi ? 'from ' : ''}₹${p.price}${hasDiscount(p) ? ` <s>₹${p.original}</s>` : ''}${(!multi && p.weight) ? ` / ${esc(p.weight)}` : ''}</div>
      </div>
      <button type="button" class="btn btn-green blog-buy-btn" data-stop data-quick-add="${p.id}">${multi ? 'Choose size' : 'Add to cart'}</button>
    </aside>`;
  };

  let blogPostToken = 0;
  const renderBlogPost = async (slug) => {
    const token = ++blogPostToken;
    let b = findBlog(slug);
    if (!b) {
      // Direct hit / refresh on /blog/<slug> before the posts finished loading.
      await refreshBlogPosts();
      if (token !== blogPostToken) return;
      b = findBlog(slug);
    }
    if (!b) { setView('blog'); return; }

    $('#bp-crumbs').textContent = `Home / Blog / ${b.title}`;
    const catEl = $('#bp-cat');
    catEl.textContent = b.category || '';
    catEl.hidden = !b.category;
    $('#bp-title').textContent = b.title;
    const exEl = $('#bp-excerpt');
    exEl.textContent = b.excerpt || '';
    exEl.hidden = !b.excerpt;
    $('#bp-date').textContent = b.date || '';
    $('#bp-time').textContent = b.readTime || '';
    $('#bp-meta-dot').hidden = !(b.date && b.readTime);

    const cover = $('#bp-cover');
    if (b.cover && !b.cover.startsWith('placeholder:')) {
      cover.hidden = false;
      cover.style.backgroundImage = `url("${b.cover}")`;
      cover.style.backgroundSize = b.coverFit === 'contain' ? 'contain' : 'cover';
      cover.dataset.placeholder = '';
      cover.setAttribute('role', 'img');
      cover.setAttribute('aria-label', b.coverAlt || b.title);
    } else {
      cover.hidden = false;
      cover.style.backgroundImage = '';
      cover.dataset.placeholder = b.cover ? b.cover.replace('placeholder:', '') : 'default';
      cover.removeAttribute('role');
      cover.removeAttribute('aria-label');
    }

    // Give every heading a stable id so the ToC (and Google's jump-to-section
    // links) have real anchors to point at.
    const wrap = document.createElement('div');
    wrap.innerHTML = renderBlogBody(b.body);
    const heads = Array.from(wrap.querySelectorAll('h2, h3'));
    heads.forEach((h, i) => { if (!h.id) h.id = slugify(h.textContent || '') || `section-${i + 1}`; });
    // Each linked product gets a purchase card after the block that first mentions it.
    const carded = new Set();
    wrap.querySelectorAll('a[href^="/product/"]').forEach(a => {
      let key = a.getAttribute('href').slice('/product/'.length).split(/[?#]/)[0];
      try { key = decodeURIComponent(key); } catch {}
      const p = findProduct(key);
      if (!p || carded.has(p.id)) return;
      carded.add(p.id);
      let block = a;
      while (block.parentElement !== wrap) block = block.parentElement;
      block.insertAdjacentHTML('afterend', blogBuyCardHtml(p));
    });
    $('#bp-content').innerHTML = wrap.innerHTML;

    const h2s = heads.filter(h => h.tagName === 'H2');
    const toc = $('#bp-toc');
    if (h2s.length >= 2) {
      $('#bp-toc-list').innerHTML = h2s
        .map(h => `<li><a href="#${esc(h.id)}">${esc(h.textContent || '')}</a></li>`).join('');
      toc.hidden = false;
    } else {
      toc.hidden = true;
    }

    // Related posts: same category first, then anything else. Internal links
    // from every article keep the blog crawlable without a hub-only path.
    const others = BLOGS.filter(x => x.slug !== b.slug);
    const related = [
      ...others.filter(x => x.category === b.category),
      ...others.filter(x => x.category !== b.category),
    ].slice(0, 3);
    const relSec = $('#bp-related');
    if (related.length) {
      $('#bp-related-grid').innerHTML = related.map(blogCardHtml).join('');
      relSec.hidden = false;
    } else {
      relSec.hidden = true;
    }

    const url = blogUrl(b.slug);
    writeShareMeta({ title: blogTitle(b), desc: blogDesc(b), image: blogImage(b), url, type: 'article' });
    removeProductJsonLd();
    writeArticleJsonLd(b);
  };

  // Blog cards are anchors: intercept for SPA nav, but leave modified clicks
  // (new tab / middle click) to the browser.
  document.addEventListener('click', e => {
    const card = e.target.closest('[data-blog-slug]');
    if (!card) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    setView('blog-post', card.dataset.blogSlug);
  });

  // Share row on the article page.
  document.addEventListener('click', e => {
    if (e.target.id === 'bp-share-wa') {
      const title = $('#bp-title').textContent;
      window.open(`https://wa.me/?text=${encodeURIComponent(`${title} ${location.href}`)}`, '_blank', 'noopener');
      return;
    }
    if (e.target.id === 'bp-share-copy') {
      navigator.clipboard?.writeText(location.href).then(
        () => showToast('Link copied'),
        () => showToast('Could not copy link'),
      );
    }
  });

  /* ── CONTACT FORM ── */
  $('#ct-form').addEventListener('submit', async e => {
    e.preventDefault();
    const f = e.currentTarget;
    const name = f.name.value.trim();
    const phone = f.phone.value.trim();
    const email = f.email.value.trim();
    const topic = f.topic.value;
    const message = f.message.value.trim();
    const errEl = $('#ct-form-error');
    const submitBtn = f.querySelector('button[type="submit"]');

    if (!name || !message) {
      errEl.textContent = 'Please fill in your name and a message.';
      errEl.hidden = false;
      return;
    }
    errEl.hidden = true;

    if (submitBtn) { submitBtn.disabled = true; }
    const { error: msgErr } = await submitMessage({ name, email, phone, topic, message });
    if (submitBtn) { submitBtn.disabled = false; }

    if (msgErr) {
      errEl.textContent = `Could not send: ${msgErr.message || 'unknown error'}. Please try again.`;
      errEl.hidden = false;
      return;
    }

    showToast(`Thanks ${name.split(' ')[0]} — we got your message.`);
    f.reset();
  });

  /* ── SEARCH (nav) ── */
  const populateSearchCats = () => {
    const sel = $('#amzn-search-cat');
    if (!sel) return;
    sel.innerHTML = `<option value="">All</option>` + CATEGORIES.map(c =>
      `<option value="${c.id}">${esc(c.name)}</option>`
    ).join('');
  };

  const searchForm = $('#amzn-search-form');
  if (searchForm) {
    searchForm.addEventListener('submit', e => {
      e.preventDefault();
      const q = $('#amzn-search-input').value;
      const cat = $('#amzn-search-cat').value;
      apQuery = q || '';
      apActiveTag = '';
      apFilter = cat ? parseInt(cat) : 'all';
      setCatSearch(apFilter); // write/strip ?cat so applyView re-reads the right category
      setView('products');
    });
  }

  /* ── AUTH (Supabase-backed) ──────────────────────────────────────────────
     Session lives in Supabase JS client (localStorage under sb-* keys, managed
     by the SDK — do not touch directly). currentSession + currentProfile
     above are synchronously-readable caches kept in sync via onAuthChange. */

  // Synchronous accessor used by view code. Returns a session-like object
  // matching the old shape: { name, email } | null. Falls back to email if
  // profile/full_name is missing.
  const getSession = () => {
    if (!currentSession || !currentSession.user) return null;
    const u = currentSession.user;
    const name = (currentProfile && currentProfile.full_name)
      || u.user_metadata?.full_name
      || (u.email ? u.email.split('@')[0] : 'friend');
    return { name, email: u.email, phone: currentProfile?.phone, userId: u.id };
  };

  const refreshHelloBlock = () => {
    const tiny = $('#amzn-hello-tiny');
    const large = $('#amzn-hello-large');
    if (!tiny || !large) return;
    const s = getSession();
    const adminLink = $('#admin-panel-link');
    if (s && s.name) {
      tiny.textContent = `Hello, ${s.name.split(' ')[0]}`;
      large.textContent = 'Account ▾';
      // Show admin link only for admin users
      if (adminLink) adminLink.hidden = !(currentProfile && currentProfile.is_admin);
    } else {
      tiny.textContent = 'Hello, friend';
      large.textContent = 'Sign in';
      const menu = $('#amzn-account-menu');
      if (menu) menu.hidden = true;
      if (adminLink) adminLink.hidden = true;
    }
  };

  const toggleAccountMenu = (force) => {
    const menu = $('#amzn-account-menu');
    if (!menu) return;
    const open = typeof force === 'boolean' ? force : menu.hidden;
    menu.hidden = !open;
  };

  // Show signin or signup panel within the auth view
  const showAuthPanel = which => {
    const signin = $('#auth-signin');
    const signup = $('#auth-signup');
    const crumb = $('#auth-crumb');
    if (!signin || !signup) return;
    if (which === 'signup') {
      signin.hidden = true;
      signup.hidden = false;
      if (crumb) crumb.textContent = 'Home / Create account';
      const f = $('#signup-name'); if (f) f.focus();
    } else {
      signup.hidden = true;
      signin.hidden = false;
      if (crumb) crumb.textContent = 'Home / Sign in';
      const f = $('#signin-email'); if (f) f.focus();
    }
  };

  const clearAuthErrors = (formEl) => {
    formEl.querySelectorAll('.auth-error').forEach(e => e.textContent = '');
    formEl.querySelectorAll('.auth-input--err').forEach(i => i.classList.remove('auth-input--err'));
    const top = formEl.querySelector('.auth-form-error');
    if (top) { top.hidden = true; top.textContent = ''; }
  };
  const setFieldError = (formEl, fieldId, msg) => {
    const input = formEl.querySelector('#' + fieldId);
    const err = formEl.querySelector(`[data-error-for="${fieldId}"]`);
    if (input) input.classList.add('auth-input--err');
    if (err) err.textContent = msg;
  };
  const setFormError = (formEl, msg) => {
    const top = formEl.querySelector('.auth-form-error');
    if (!top) return;
    top.textContent = msg;
    top.hidden = false;
  };

  const resetAuthView = () => {
    // If user is somehow logged in but lands here, bounce home.
    if (getSession()) { setView('home'); return; }
    showAuthPanel('signin');
    ['signin-form', 'signup-form'].forEach(id => {
      const f = $('#' + id);
      if (f) { f.reset(); clearAuthErrors(f); }
    });
  };

  const isValidEmail = e => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  const isValidPhone = p => /^[0-9+\- ]{10,15}$/.test(p) && (p.replace(/\D/g, '').length >= 10);

  const setBusy = (form, busy) => {
    const btn = form.querySelector('button[type="submit"]');
    if (!btn) return;
    btn.disabled = busy;
    btn.dataset._origText = btn.dataset._origText || btn.textContent;
    btn.textContent = busy ? 'Please wait…' : btn.dataset._origText;
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    clearAuthErrors(form);
    const email = $('#signin-email').value.trim().toLowerCase();
    const password = $('#signin-password').value;
    let bad = false;
    if (!email)              { setFieldError(form, 'signin-email', 'Enter your email.'); bad = true; }
    else if (!isValidEmail(email)) { setFieldError(form, 'signin-email', 'Enter a valid email address.'); bad = true; }
    if (!password)           { setFieldError(form, 'signin-password', 'Enter your password.'); bad = true; }
    if (bad) return;

    setBusy(form, true);
    const { data, error } = await authLib.signInWithEmail({ email, password });
    setBusy(form, false);
    if (error) {
      let msg;
      if (/email not confirmed|email_not_confirmed/i.test(error.message)) {
        msg = 'Please confirm your email first. Check your inbox (and spam folder) for the confirmation link.';
      } else if (/invalid|credentials|password/i.test(error.message)) {
        msg = "We couldn't find an account with that email and password. Try again or create a new account.";
      } else {
        msg = error.message || 'Sign-in failed. Please try again.';
      }
      setFormError(form, msg);
      return;
    }
    // Explicit UI refresh (don't wait for onAuthChange, which can be slow).
    if (data?.session) {
      currentSession = data.session;
      // Fetch profile so isAdminUser() works immediately after sign-in.
      try {
        const { data: profile } = await authLib.getProfile(data.session.user.id);
        currentProfile = profile || null;
      } catch (_) {}
      refreshHelloBlock();
    }
    // If the user was trying to reach /admin or /checkout before being asked
    // to sign in, route them there now instead of going home.
    const wantedAdmin = sessionStorage.getItem('adminRedirectPending');
    const wantedRedirect = sessionStorage.getItem('postAuthRedirect');
    sessionStorage.removeItem('adminRedirectPending');
    sessionStorage.removeItem('postAuthRedirect');
    if (wantedAdmin) {
      setView('admin');
    } else if (wantedRedirect) {
      setView(wantedRedirect);
    } else {
      setView('home');
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    clearAuthErrors(form);
    const name = $('#signup-name').value.trim();
    const email = $('#signup-email').value.trim().toLowerCase();
    const phone = $('#signup-phone').value.trim();
    const password = $('#signup-password').value;
    const password2 = $('#signup-password2').value;
    let bad = false;
    if (!name || name.length < 2)        { setFieldError(form, 'signup-name', 'Enter your name.'); bad = true; }
    if (!email)                          { setFieldError(form, 'signup-email', 'Enter your email.'); bad = true; }
    else if (!isValidEmail(email))       { setFieldError(form, 'signup-email', 'Enter a valid email address.'); bad = true; }
    if (!phone)                          { setFieldError(form, 'signup-phone', 'Enter your mobile number.'); bad = true; }
    else if (!isValidPhone(phone))       { setFieldError(form, 'signup-phone', 'Enter a valid 10–15 digit number.'); bad = true; }
    if (!password || password.length < 6) { setFieldError(form, 'signup-password', 'At least 6 characters.'); bad = true; }
    if (password !== password2)          { setFieldError(form, 'signup-password2', 'Passwords do not match.'); bad = true; }
    if (bad) return;

    setBusy(form, true);
    const { data, error } = await authLib.signUpWithEmail({ email, password, fullName: name, phone });
    setBusy(form, false);
    if (error) {
      if (/already.*registered|exists/i.test(error.message)) {
        setFieldError(form, 'signup-email', 'An account with this email already exists.');
      } else {
        setFormError(form, error.message || 'Sign-up failed. Please try again.');
      }
      return;
    }
    // If email confirmation is required (default), session will be null; tell user to check email.
    if (!data?.session) {
      setFormError(form, 'Account created! Please check your email to confirm before signing in.');
      // Switch to sign-in panel after a beat
      setTimeout(() => showAuthPanel('signin'), 2000);
      return;
    }
    const wantedRedirect = sessionStorage.getItem('postAuthRedirect');
    sessionStorage.removeItem('postAuthRedirect');
    setView(wantedRedirect || 'home');
  };

  const handleGoogleSignIn = async () => {
    const { error } = await authLib.signInWithGoogle();
    if (error) showToast('Google sign-in failed: ' + error.message);
    // OAuth redirects, so no further code runs here on success.
  };

  const wireAuthOnce = () => {
    const signinForm = $('#signin-form');
    const signupForm = $('#signup-form');
    if (signinForm) signinForm.addEventListener('submit', handleSignIn);
    if (signupForm) signupForm.addEventListener('submit', handleSignUp);

    // Toggle between sign in / sign up panels
    document.addEventListener('click', e => {
      const sw = e.target.closest('[data-auth-switch]');
      if (sw) {
        e.preventDefault();
        showAuthPanel(sw.dataset.authSwitch);
      }
    });

    // Account dropdown actions
    document.addEventListener('click', async e => {
      const action = e.target.closest('[data-account-action]');
      if (action && action.dataset.accountAction === 'logout') {
        e.preventDefault();
        await authLib.signOut();
        // onAuthChange will refresh UI
        toggleAccountMenu(false);
        setView('home');
        return;
      }
      // Any other click inside the dropdown menu (My Account, My Orders) closes it.
      if (e.target.closest('#amzn-account-menu .amzn-account-item')) {
        toggleAccountMenu(false);
      }
    });

    // Google sign-in buttons (any element with data-google-signin)
    document.addEventListener('click', e => {
      if (e.target.closest('[data-google-signin]')) {
        e.preventDefault();
        handleGoogleSignIn();
      }
    });

    // Close account dropdown when clicking outside it
    document.addEventListener('click', e => {
      const menu = $('#amzn-account-menu');
      if (!menu || menu.hidden) return;
      if (e.target.closest('#amzn-account-menu')) return;
      if (e.target.closest('#amzn-hello-block')) return;
      menu.hidden = true;
    });

    // Close account dropdown on Escape
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') toggleAccountMenu(false);
    });
  };

  /* ── INIT ── */
  // 0. Paint skeleton loaders into every async-populated container so the user
  //    sees structured placeholders instead of empty space during the catalog
  //    fetch + any per-view server round-trips. Render functions below
  //    overwrite these once data arrives.
  const paintInitialSkeletons = () => {
    const slides = $('#hero-slides');
    if (slides && !slides.innerHTML.trim()) {
      slides.innerHTML = `<div class="hero-slide" role="group">${skelHero()}</div>`;
      setRegionBusy(slides, true);
    }
    const stripEl = $('#cats-strip');
    if (stripEl && !stripEl.innerHTML.trim()) { stripEl.innerHTML = skelCatStrip(6); setRegionBusy(stripEl, true); }
    const catsPage = $('#cats-page-grid');
    if (catsPage && !catsPage.innerHTML.trim()) { catsPage.innerHTML = skelCatPageGrid(12); setRegionBusy(catsPage, true); }
    const featured = $('#products-grid');
    if (featured && !featured.innerHTML.trim()) { featured.innerHTML = skelProductGrid(8); setRegionBusy(featured, true); }
    const apGrid = $('#ap-products');
    if (apGrid && !apGrid.innerHTML.trim()) { apGrid.innerHTML = skelProductGrid(12); setRegionBusy(apGrid, true); }
    const cdGrid = $('#cd-products');
    if (cdGrid && !cdGrid.innerHTML.trim()) { cdGrid.innerHTML = skelProductGrid(8); setRegionBusy(cdGrid, true); }
    const blogGrid = $('#blog-grid');
    if (blogGrid && !blogGrid.innerHTML.trim()) { blogGrid.innerHTML = skelBlogGrid(3); setRegionBusy(blogGrid, true); }
  };
  paintInitialSkeletons();

  // 1. Subscribe to auth changes BEFORE any await, so we don't miss the
  //    OAuth-redirect SIGNED_IN event that fires very early on page load.
  //    `firstAuthChange` distinguishes Supabase's INITIAL_SESSION (fired once
  //    on subscribe with the persisted session) from a real guest→signed-in
  //    transition. Without this, page reloads would re-merge local cart into
  //    server cart, doubling quantities every refresh.
  let firstAuthChange = true;
  authLib.onAuthChange(async (session) => {
    const wasFirstChange = firstAuthChange;
    firstAuthChange = false;
    const wasSignedIn = !!currentSession;
    currentSession = session;
    currentProfile = null;
    refreshHelloBlock(); // <-- update immediately with email-derived name

    // Enrich with profile (full_name) async; UI re-renders if profile loads.
    if (session?.user) {
      try {
        const { data: profile } = await authLib.getProfile(session.user.id);
        if (profile) {
          currentProfile = profile;
          refreshHelloBlock();
        }
      } catch (e) {
        dwarn('[auth] profile fetch failed:', e);
      }
    }

    if (session) {
      if (wasFirstChange) {
        // Page-load with existing session → pull server cart down (no sum).
        await syncCartFromServer();
        await wishlistLib.syncWishlistFromServer(session.user.id);
      } else if (!wasSignedIn) {
        // Real guest → signed-in transition → merge guest items up.
        await mergeCartOnSignIn();
        await wishlistLib.mergeWishlistOnSignIn(session.user.id);
        await wishlistLib.syncWishlistFromServer(session.user.id);
        // Honor a pending checkout redirect from OAuth (Google) round-trip.
        // Email/password sign-in handles its own redirect inline; this catches
        // the OAuth path where control returns here after the provider redirect.
        const wantedRedirect = sessionStorage.getItem('postAuthRedirect');
        if (wantedRedirect) {
          sessionStorage.removeItem('postAuthRedirect');
          setView(wantedRedirect);
        }
      }
      updateWishlistBadge();
    }
    // Just signed out? keep local cart as new guest cart.
  });

  // 2. Read current session synchronously (Supabase reads from localStorage).
  //    onAuthChange will also fire INITIAL_SESSION; we set this here too so the
  //    first synchronous render has the right state.
  const { data: { session: __initSession } } = await supabase.auth.getSession();
  if (__initSession) {
    currentSession = __initSession;
    if (__initSession.user) {
      try {
        const { data: profile } = await authLib.getProfile(__initSession.user.id);
        currentProfile = profile || null;
      } catch (_) {}
    }
  }

  // 3. Fetch live catalog from Supabase. Falls back to hardcoded values if it fails.
  try {
    const data = await fetchCatalog();
    if (data && data.categories.length && data.products.length) {
      CATEGORIES = data.categories;
      PRODUCTS = data.products;
    }
  } catch (e) {
    dwarn('[catalog] fetch failed, using hardcoded fallback:', e);
  }

  // 3b. Load admin-pinned items before first render so #view-products already
  //     reflects the curated top order. Degrades silently if the migration is
  //     not yet applied.
  await refreshPinnedItems();

  // 4. Inject "Continue with Google" button into auth panels (only once).
  const __googleBtnHtml = `
    <div class="auth-divider">or</div>
    <button type="button" class="btn-google" data-google-signin>
      <svg viewBox="0 0 18 18"><path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/><path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/><path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"/><path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z"/></svg>
      <span>Continue with Google</span>
    </button>`;
  const __signinForm = $('#signin-form');
  const __signupForm = $('#signup-form');
  if (__signinForm && !__signinForm.querySelector('[data-google-signin]')) {
    __signinForm.insertAdjacentHTML('afterend', __googleBtnHtml);
  }
  if (__signupForm && !__signupForm.querySelector('[data-google-signin]')) {
    __signupForm.insertAdjacentHTML('afterend', __googleBtnHtml);
  }

  // 5. Render UI
  renderCatStrip();
  renderCatsPage();
  renderProducts();
  renderComboRail();
  renderRecentRail('#home-recent-rail', '#home-recent-grid', null, 2);
  renderSlides();
  startSlideAuto();
  populateSearchCats();
  updateCartBadge();
  updateWishlistBadge();
  wireAuthOnce();
  refreshHelloBlock();

  // 5b. Live content from the admin-managed tables. Each helper degrades to
  // the hardcoded fallback when the migration hasn't been applied yet, so
  // boot never fails even on a fresh Supabase project.
  refreshHeroSlides();
  refreshFeaturedIds();
  refreshBlogPosts();

  // 6. Server cart hydration is handled by the onAuthChange INITIAL_SESSION
  //    event above (syncCartFromServer). Calling it again here would re-merge
  //    local into server and double quantities on every page load.

  // 7. Apply initial view from the URL so refresh / shared / deep links land on
  //    the right page. Also normalize any legacy "#/..." hash to a clean path.
  const __initRoute = parseLocation();
  const __cleanPath = pathFor(__initRoute.name, __initRoute.payload);
  if (location.pathname !== __cleanPath || location.hash) {
    history.replaceState(null, '', __cleanPath + location.search);
  }
  if (__initRoute.name !== 'home') applyView(__initRoute.name, __initRoute.payload);

  // Pause slideshow on hover; resume on leave
  const sliderEl = $('#hero-slider');
  if (sliderEl) {
    sliderEl.addEventListener('mouseenter', () => { if (slideTimer) clearInterval(slideTimer); });
    sliderEl.addEventListener('mouseleave', startSlideAuto);
  }

  /* ── WHY SUNNAH — scroll-reveal ── */
  const whyObserver = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('in-view');
        whyObserver.unobserve(entry.target);
      }
    });
  }, { threshold: 0.15 });

  $$('.why-step').forEach(el => whyObserver.observe(el));

  /* ── ABOUT — mirror the certificate cards (single source: #certs-grid) ── */
  const certsSrc = $('#certs-grid'), certsDest = $('#about-certs-grid');
  if (certsSrc && certsDest) certsDest.append(...[...certsSrc.children].map(el => el.cloneNode(true)));
})();
